import type { FaqEntry } from "@/components/FaqSection";
import { PROFESSIONAL_TRIAL_LIMIT } from "@shared/tiers";

/** Same wording on the three finance calculators; the number comes from the live policy in shared/tiers.ts. */
const CALC_TRIAL_FAQ: FaqEntry = {
  question: "Does reading the result use a trial use?",
  answer: `No. Reading results never uses a trial use. Copying, saving or printing uses 1 per visit. Professional tools like this one have ${PROFESSIONAL_TRIAL_LIMIT} trial uses a day.`,
};

/**
 * Genuine, tool-specific FAQ content -- real questions someone would
 * actually ask about that specific tool, with accurate answers drawn
 * from the tool's real behavior and limitations (already documented in
 * each tool's own TOOL_INFO_CONTENT). Not a generic "is my file safe"
 * question copy-pasted everywhere with the tool name swapped in --
 * each entry should only make sense for that one tool.
 *
 * Serves two purposes: real FAQPage structured data (see useFaqSchema)
 * for rich search results, and genuine, substantive visible content
 * (see FaqSection) that helps address the site's thin-content problem
 * with actual value, not padding.
 */
export const FAQ_CONTENT: Record<string, FaqEntry[]> = {
  "powerpoint-to-images": [
    {
      question: "Which sizes can I choose, and how big are the pictures?",
      answer: "Standard is 1280 pixels wide, Full HD 1920, Large 2560 and 4K 3840. The height follows the shape of your slides, so a widescreen deck gives 1920 × 1080 at Full HD and a standard 4:3 deck gives 1920 × 1440. Larger sizes make larger files and take longer on a long deck, so Full HD is a good place to start.",
    },
    {
      question: "Are hidden slides included?",
      answer: "Not unless you tick Include hidden slides, which appears only when the deck has some. File names carry each slide’s real number in the deck, so if slide 2 is hidden and left out, the pictures are slide-01, slide-03 and so on, and slide-03 is still slide 3.",
    },
    {
      question: "Should I pick JPG or PNG?",
      answer: "JPG makes smaller files and suits slides full of photos. PNG keeps small text, thin lines and flat colour sharp, at a larger size. For slides with charts or diagrams, PNG is usually the better choice.",
    },
    {
      question: "Will the pictures look exactly like the slides in PowerPoint?",
      answer: "They look very close but are drawn by this site’s own slide renderer, not by PowerPoint. Backgrounds, shapes, pictures, tables and charts are drawn; SmartArt is approximated, 3D effects, animation and embedded video are not reproduced, and a font your browser does not have is replaced by a close one. Check the preview grid before you download.",
    },
    {
      question: "Is my presentation uploaded anywhere?",
      answer: "No. The file is read and every slide is drawn in your browser tab, and the ZIP is built there too.",
    },
  ],
  "extract-from-powerpoint": [
    {
      question: "Are the extracted pictures the originals?",
      answer: "Yes. They are copied byte for byte from inside the .pptx, so nothing is re-compressed and the quality is exactly what was put into the deck. One thing to know: if a picture was cropped or recoloured on the slide in PowerPoint, the file stored in the deck is still the full, unchanged picture, and that is what you get.",
    },
    {
      question: "What does “in the design only” mean?",
      answer: "Some pictures, such as a logo or a background pattern, belong to the slide layouts or the master and not to any single slide. They are listed separately and left out unless you tick the box, so the ZIP holds what is actually on your slides by default.",
    },
    {
      question: "Which formats can the speaker notes come in?",
      answer: "Plain text (.txt), Markdown (.md) or a Word document (.docx). Each has one heading per slide with its number and title, and the notes under it. Slides with no notes are left out.",
    },
    {
      question: "Does the text export include tables, charts and SmartArt?",
      answer: "Table cells are included. Text inside charts, SmartArt diagrams and pictures is not read, and the counts shown before you extract tell you when a slide holds such objects. Text on hidden slides is skipped unless you tick Include hidden slides.",
    },
  ],
  "clean-powerpoint": [
    {
      question: "What exactly does it remove?",
      answer: "You choose from: speaker notes, comments (old and new style, with the names of who wrote them), hidden slides, the author, last-modified-by, company and manager names with the revision list, and the small preview picture of the first slide stored in the file. You can also remove the title, subject, keywords and description, and custom properties. The checklist shows what was found in your file, with names and counts, before you remove anything.",
    },
    {
      question: "Will the cleaned file still open in PowerPoint?",
      answer: "The file is rewritten as a complete package, not patched: the slide list, the links between parts and the content-type list are updated, and parts nothing points to any more are deleted. The tool then reopens its own result and checks for anything left dangling, and we test it with the same reader this site uses to draw slides and with LibreOffice. Open it once before you send it, as you would any file.",
    },
    {
      question: "If I remove a hidden slide, what happens to its pictures and links?",
      answer: "Pictures that only that slide used are removed with it, so the file also gets smaller. A link on another slide that jumped to the removed slide loses the link and keeps its text.",
    },
    {
      question: "What does it not touch?",
      answer: "Text written on the visible slides, including text hidden behind shapes or placed off the edge of a slide, is not changed. Pictures keep their own details such as the camera and place a photo was taken, and charts keep the spreadsheet data they were made from. If a slide shows a name or figure you do not want to share, edit the slide itself.",
    },
    {
      question: "Is my original file changed?",
      answer: "No. You download a new file with “-clean” added to its name, and the original stays as it was.",
    },
  ],
  "images-to-powerpoint": [
    {
      question: "Which picture types can I use?",
      answer: "JPG, PNG and GIF go in as they are. WebP and BMP pictures are converted by your browser first, to PNG when they have see-through areas and to JPG otherwise. HEIC photos from some phones cannot be read by most browsers, so save those as JPG first; the tool tells you which files it could not add.",
    },
    {
      question: "What is the difference between Fit and Fill?",
      answer: "Fit shows the whole picture inside the slide with space around it when the shapes differ. Fill covers the whole slide and trims the edges that do not match the slide’s shape, so some of the picture is cut off. Fit is safer for charts and screenshots, Fill for photos.",
    },
    {
      question: "How do I change the order of the slides?",
      answer: "Drag a picture to a new place in the list, or use the up and down arrows beside it, and remove any you do not want. New files you add are sorted by name, with numbers read as numbers, so photo2 comes before photo10. The slide order is exactly the order of the list.",
    },
    {
      question: "Are my pictures made smaller or lower quality?",
      answer: "JPG and PNG pictures that are upright and no wider than 2560 pixels go into the presentation untouched. Larger pictures are reduced to 2560 pixels on the long side so a long deck stays a sensible size, and photos stored sideways by a phone are turned upright.",
    },
    {
      question: "Do the pictures get alt text?",
      answer: "Yes. Each picture is given alternative text made from its file name, which screen readers use, and you can improve it inside PowerPoint.",
    },
  ],
  "markdown-to-powerpoint": [
    {
      question: "What do I write to make slides?",
      answer: "A line starting with # makes the title slide, and the line under it becomes the subtitle. A line starting with ## starts a new slide with that heading. Lines starting with - or 1. become bullets, with two spaces of indent for sub-points. A line of --- starts a new slide, and a line beginning Note: goes to that slide’s speaker notes. Bold, italics, links and tables written in Markdown carry over.",
    },
    {
      question: "Are pictures written as web addresses included?",
      answer: "No. The tool never fetches anything from the web, so a picture written as ![text](address) is left out and listed in the warnings under the preview. Add those pictures inside PowerPoint afterwards.",
    },
    {
      question: "Can I use my brand colour and fonts?",
      answer: "Yes. Choose Your brand, pick a colour and two fonts, or use the colour and fonts from your Brand Kit if you made one in this tab. Fonts are saved by name only, so the computer that opens the file needs them installed, or PowerPoint swaps in another. Calibri, Arial and Georgia are safe on almost every computer.",
    },
    {
      question: "Is what I write sent anywhere?",
      answer: "No. The text is turned into the presentation in your browser, and it is gone when you close the tab.",
    },
  ],
  "pdf-to-powerpoint": [
    {
      question: "What is the difference between Picture per slide and Editable text?",
      answer: "Picture per slide puts each PDF page on its slide as one picture, so it looks exactly like the PDF but the text cannot be edited. Editable text finds the text in the PDF and puts it into text boxes you can change, over a picture of the page without its text. It is a best effort: line breaks, spacing, columns and fonts are approximated.",
    },
    {
      question: "Why does some text stay in the picture?",
      answer: "Some PDFs draw letters as shapes instead of text, and scanned pages are only pictures of text. Neither can be turned into editable text without reading the picture. After converting, the tool tells you how many pieces of text became editable and how many stayed in the background.",
    },
    {
      question: "What size are the slides?",
      answer: "The slide takes the size and shape of the first PDF page, kept between 4 and 56 inches on the long side, which is PowerPoint’s limit. A page with a different shape from the first is fitted inside its slide with space around it.",
    },
    {
      question: "Does it read scanned PDFs?",
      answer: "No. A scanned page becomes a picture only, in either mode. There is no text recognition in this tool.",
    },
  ],
  "split-powerpoint": [
    {
      question: "What can I split a presentation by?",
      answer: "Three ways: a slide range such as 1-3 and 7 (all in one file, or each range as its own file), every N slides such as every 5, or one file per slide in a ZIP. In the first way you can also click slides in the picture grid instead of typing numbers, and the grid shows which file each slide goes into before you press the button.",
    },
    {
      question: "Do the new files keep the theme, notes and pictures?",
      answer: "Yes. Each new file is a complete presentation with the original’s slide master, layouts, theme and slide size. Each slide keeps its speaker notes, pictures, charts and tables. What belonged only to slides you did not take, such as their notes pages, comments, charts and pictures nobody else uses, is left out.",
    },
    {
      question: "Why are the new files not much smaller than the original when I split it into parts?",
      answer: "Every part carries its own copy of the slide master, layouts and theme, and a picture used on several slides is stored in each file that uses it. Splitting a deck into ten parts therefore gives ten files that together are somewhat larger than the original. If size matters, use Compress PowerPoint on the parts.",
    },
    {
      question: "What happens to links, sections and the preview picture?",
      answer: "A link on a slide that jumped to a slide you did not take is removed and its text stays; links between slides you did take still work. Section names are kept when you take a range and nothing is reordered. A small preview picture stored in the file is dropped when the first slide of the new file is not slide 1, because it would show the wrong slide.",
    },
    {
      question: "Is my presentation uploaded anywhere?",
      answer: "No. The file is opened and each new file is built in your browser tab, and the ZIP is made there too.",
    },
  ],
  "compress-powerpoint": [
    {
      question: "How does it make the file smaller?",
      answer: "By re-saving the JPEG and PNG pictures inside the deck at a lower quality and, where a picture is stored with far more pixels than it is shown with on its slide, at a lower resolution. Most of a presentation’s size is its photos, so this is where the savings are. Text, shapes and charts are not pictures and do not shrink.",
    },
    {
      question: "What do Default, Medium and Strong mean?",
      answer: "Default re-saves pictures at good quality and no more than 220 dots per inch for the size they are shown. Medium uses lower quality and 150 dots per inch, and Strong lower still at 110. You see the real size of all three files and can download any of them. Target size instead gives the sharpest version that is at or under a limit you type, such as 10 MB, and tells you if even the smallest result is over it.",
    },
    {
      question: "Will it change my slides or remove transparency?",
      answer: "It never changes the slide itself: the same text, layout, notes, animations and picture frames, with pictures of lower quality. A PNG that has see-through areas stays a PNG, so transparency is kept; it is only made smaller by reducing its pixel size. A photograph saved as an opaque PNG can become a JPEG on Medium and Strong when that is much smaller. WebP is not used because PowerPoint does not support it.",
    },
    {
      question: "What does it leave alone?",
      answer: "EMF, WMF and SVG pictures, GIFs, video and audio are left exactly as they are, and the tool lists them before you start. A picture stored sideways, one that uses a transparent-colour effect, and one that would not get smaller are also left alone. A deck that is mostly video will not get much smaller.",
    },
    {
      question: "What are the optional removals?",
      answer: "Three boxes that are off unless you tick them: the small preview picture stored in the file, the speaker notes, and layouts that no slide uses. They change what is in the file, so they are never done for you. Comments, hidden slides and author names are not touched here; Clean PowerPoint for Sharing handles those.",
    },
  ],
  "organize-slides": [
    {
      question: "How do I reorder, delete and duplicate slides?",
      answer: "Drop your .pptx and every slide appears as a picture. Drag a picture to a new place, or use its arrow buttons, which also work from the keyboard. The copy button duplicates a slide and the bin deletes it. Remove hidden slides deletes every slide that is hidden when presenting, in one click. Nothing changes in your file until you press Make new presentation.",
    },
    {
      question: "What does a duplicate keep?",
      answer: "A copy of a slide gets its own copy of the speaker notes and of any chart on it, so you can edit the copy without touching the original. Pictures are shared, so a copy adds almost nothing to the file size. Comments are not copied.",
    },
    {
      question: "Do links between slides still work after I reorder?",
      answer: "Yes. A link from one slide to another follows the slide to its new place. A link that jumped to a slide you deleted loses the link and keeps its text.",
    },
    {
      question: "Why did my section names disappear?",
      answer: "PowerPoint cannot keep a list of sections that disagrees with the order of the slides, so the section names are dropped when you reorder or duplicate, and the tool tells you. If you only delete slides, the sections stay.",
    },
    {
      question: "Is anything uploaded?",
      answer: "No. The slides are drawn and the new presentation is built in your browser tab.",
    },
  ],
  "merge-powerpoint": [
    {
      question: "Does the merged deck keep each deck’s design?",
      answer: "Yes. By default every deck keeps its own slide master, layouts and theme, which PowerPoint supports in one file, so slides from different decks look the way they did. Pictures, layouts and other parts with the same file name in two decks are kept apart, so nothing is overwritten. If every layout of the later decks has a layout with the same name in the first deck, you can also choose to put all slides on the first deck’s design.",
    },
    {
      question: "Why does it say my decks cannot be merged as editable slides?",
      answer: "Slides only look right at the slide size they were made for. If the decks have different sizes, for example one widescreen and one 4:3, putting them in one file would stretch or crop the slides, so the tool does not do it and says which deck differs. Merge as pictures works for any sizes, or you can change the slide size in PowerPoint first.",
    },
    {
      question: "What is Merge as pictures?",
      answer: "Every slide of every deck is drawn to a picture and the pictures become one slide each. The result looks like the slides but nothing in it is editable: no text, notes, links or charts. It is the fallback for decks that cannot be merged as editable slides, and a way to make one flat deck.",
    },
    {
      question: "What is left out when decks are merged as editable slides?",
      answer: "Comments in the second and later decks, because their authors would collide with the first deck’s. Fonts embedded in the later decks are not carried over, so a font missing on the computer is replaced. The later decks’ notes pages use the first deck’s notes master. The result page lists what applied to your files.",
    },
    {
      question: "Is my presentation uploaded anywhere?",
      answer: "No. Each deck is read and the merged file is built in your browser tab; nothing is sent to a server.",
    },
  ],
  "find-replace-powerpoint": [
    {
      question: "Does it find text that PowerPoint splits into pieces?",
      answer: "Yes. PowerPoint often stores one phrase as several pieces because of a spell-check mark or a change of format. This tool joins the pieces of each paragraph before searching, so a phrase is found even when it is split. The replacement takes the formatting of the piece where the phrase starts.",
    },
    {
      question: "What does it search, and what does it leave alone?",
      answer: "Slide text, tables, speaker notes if you tick that box, and optionally the slide master and layouts. Text inside pictures, charts, SmartArt and grouped shapes that PowerPoint stores in separate parts is not searched, and the page says so when the deck contains them.",
    },
    {
      question: "Can I replace on some slides only?",
      answer: "Yes. Every slide with a match is listed with a picture, the text before and after, and a tick box. Untick a slide to leave it as it is. Nothing is written until you download.",
    },
    {
      question: "Is my presentation uploaded?",
      answer: "No. The file is read and rewritten in your browser tab; nothing is sent to a server.",
    },
  ],
  "replace-fonts-powerpoint": [
    {
      question: "Does it change the theme fonts too?",
      answer: "Yes. If a font you replace is a theme font, the theme is updated as well, so text that follows the theme changes with it. The list marks theme fonts so you can see which ones they are.",
    },
    {
      question: "Why do bullets stay the same?",
      answer: "Bullet symbols often use special symbol fonts such as Wingdings. Changing those would turn bullets into letters, so bullet and symbol fonts are never touched.",
    },
    {
      question: "Will the new font appear on every computer?",
      answer: "Only if the computer has it installed. The font is saved by name, not embedded, so Calibri, Arial, Georgia and Times New Roman are the safe choices. If you replace a font that was embedded in the file, its embedded copy is removed.",
    },
    {
      question: "Does it change the text size?",
      answer: "No. A wider font can make a line wrap or run past its box, so look through the slides after replacing.",
    },
  ],
  "add-slide-numbers": [
    {
      question: "Are these real slide numbers?",
      answer: "Yes. Each number is a PowerPoint slide-number field, so it keeps up when you move slides. The total in 3 / 12 is plain text because PowerPoint has no total-slides field, so it will not update if you add slides later.",
    },
    {
      question: "Can the title slide have no number but the next slide be number 1?",
      answer: "Yes. Tick Leave the first slide without a number and start at 0, so the second slide shows 1.",
    },
    {
      question: "What if a slide already has a number?",
      answer: "It is left as it is, and the result says how many slides already had one.",
    },
    {
      question: "Can I remove slide numbers?",
      answer: "Yes, choose Remove slide numbers. It takes the number off every slide. Numbers that sit on the layouts or the slide master are left alone.",
    },
  ],
  "watermark-powerpoint": [
    {
      question: "Does a watermark protect the deck from copying?",
      answer: "No. It is an ordinary shape on each slide, named Watermark, that anyone can select and delete in PowerPoint. It marks a file; it does not protect it.",
    },
    {
      question: "Can I use my logo?",
      answer: "Yes. Choose Picture and select a PNG or JPEG. Set its width, opacity and position. The picture stays on your computer.",
    },
    {
      question: "Behind the content or on top?",
      answer: "Behind keeps the slide readable, but a full-page picture or solid shape on a slide can hide it. On top is always visible. You can choose all slides or a list such as 1, 3, 5-7.",
    },
    {
      question: "Can I remove it later?",
      answer: "Yes. Remove watermarks takes off the watermarks made by this tool. Watermarks made by other programs, or placed on the slide master, are not touched.",
    },
  ],
  "resize-slides": [
    {
      question: "What do Fit, Fill and Stretch mean?",
      answer: "When the new page has a different shape, something has to give. Fit keeps every proportion and leaves some space unused. Fill keeps proportions and lets content run past the edge. Stretch fills the page and squashes or widens pictures and shapes.",
    },
    {
      question: "Is the text scaled too?",
      answer: "By default yes: font sizes, line widths, indents and spacing are scaled with the shapes. Turn that off to keep every font size as it is.",
    },
    {
      question: "What is not recalculated?",
      answer: "SmartArt layouts, the insides of grouped shapes and some animations. Charts and diagrams are resized as frames. The result lists how many it found.",
    },
    {
      question: "Has this been checked in PowerPoint?",
      answer: "The result opens in LibreOffice and python-pptx in our tests, but it has not been checked in desktop PowerPoint, so look through the slides afterwards.",
    },
  ],
  "accessibility-checker-powerpoint": [
    {
      question: "Does a clean report mean my deck is accessible?",
      answer: "No. It checks what can be read from the file: alt text, titles, text size, table headers, links, reading order, media, the language of the text, the document title and the contrast of text on solid colours. It cannot judge whether alt text is any good, text inside pictures, text on gradients or pictures, or captions.",
    },
    {
      question: "Does it check colour contrast?",
      answer: "Partly. It compares the text colour with the solid fill behind it, in shapes and in table cells with their own fill, against the WCAG minimum of 4.5 to 1 (3 to 1 for large text). Text on the slide background, a picture or a gradient is not judged, so use PowerPoint's own checker for those.",
    },
    {
      question: "Does it check the language of the text?",
      answer: "Yes. A slide whose text has no language set is listed, because a screen reader needs the language to pronounce it. In PowerPoint, select the text and use Review, Language, Set Proofing Language.",
    },
    {
      question: "Can I fix alt text here and keep the file?",
      answer: "Yes. Type the alt text, a slide title or the document title next to the problem and the page builds a corrected copy of your .pptx with those words written into it. Your original file is not changed.",
    },
    {
      question: "Is my file uploaded?",
      answer: "No. The deck is read in your browser and the corrected copy is made there too.",
    },
    {
      question: "Does it write alt text for me?",
      answer: "No. Nothing is written for you. You type the alt text, or mark a picture as decorative, and only what you fill in is added to the file.",
    },
    {
      question: "What can be fixed here?",
      answer: "Alt text and decorative marks for pictures, charts, diagrams and objects; the title of a slide that has an empty title box; and the document title. Other problems are listed so you can fix them in PowerPoint.",
    },
    {
      question: "Is the report saved anywhere?",
      answer: "No. It is made in your browser and you can download it as a CSV. Nothing is uploaded.",
    },
  ],
  "extract-charts-powerpoint": [
    {
      question: "Where do the numbers come from?",
      answer: "From the copy PowerPoint stores inside each chart, which holds the values as they were last saved. They are numbers, not formulas.",
    },
    {
      question: "What is Original workbooks?",
      answer: "Many charts also contain a whole Excel workbook that opens when you choose Edit Data. This option gives you that workbook as it is, with its formulas and any other sheets.",
    },
    {
      question: "Why is a chart missing?",
      answer: "A chart pasted as a picture has no data. A chart linked to an outside workbook may have no stored numbers. Both are listed in the notes.",
    },
    {
      question: "Is anything uploaded?",
      answer: "No. The deck is read in your browser.",
    },
  ],
  "word-to-powerpoint": [
    {
      question: "How does it decide where a slide starts?",
      answer: "Each Heading 1 starts a slide, or choose Heading 2 so that Heading 1 becomes a section slide. The paragraphs and lists under a heading become its bullets and long sections continue on another slide.",
    },
    {
      question: "Can I change what goes on the slides?",
      answer: "Yes. The outline is shown as text before anything is built. Edit it, and the preview redraws as you type.",
    },
    {
      question: "What is left out?",
      answer: "Pictures, text boxes, comments, footnotes, headers, footers, charts and equations. The page lists what it found and left out.",
    },
    {
      question: "Does it summarise the document?",
      answer: "No. The words on the slides are the words in the document, split into slides. Shorten long paragraphs yourself in the outline.",
    },
  ],
  "excel-to-powerpoint": [
    {
      question: "How are large sheets handled?",
      answer: "Rows are split over several slides and the header row is repeated on each one. You choose the rows per slide and the most columns shown. Very wide sheets are cut to the first columns because a slide cannot show more readably.",
    },
    {
      question: "Are hidden sheets, rows and columns included?",
      answer: "No. They are left out, as are charts, pictures and cell colours.",
    },
    {
      question: "Are the tables linked to the workbook?",
      answer: "No. Cells are written as the text Excel displays, so the slides do not update if the workbook changes.",
    },
    {
      question: "Which files work?",
      answer: ".xlsx, .xls and CSV, up to 25 MB.",
    },
  ],
  "slide-deck-maker": [
    {
      question: "Does it write the slides for me?",
      answer: "No. The slides contain exactly the words you type. The tool provides the layout, the theme and the structure.",
    },
    {
      question: "Can I edit the result in PowerPoint?",
      answer: "Yes. The slides use real title and body placeholders, so Outline view, the Slide Master and Reuse Slides work as normal.",
    },
    {
      question: "Can I use my brand colour and fonts?",
      answer: "Yes. Choose Your brand, pick a colour and two fonts, or use the colour and fonts from your Brand Kit if you made one in this tab. Fonts are saved by name, so the computer that opens the file needs them.",
    },
    {
      question: "Is anything uploaded?",
      answer: "No. Your text and pictures stay in your browser and are gone when you close the tab.",
    },
  ],
  "photo-slideshow-maker": [
    {
      question: "Is there music?",
      answer: "No. The slideshow has no sound. You can add music in PowerPoint.",
    },
    {
      question: "Does it play by itself?",
      answer: "Yes. Set the seconds per slide and PowerPoint moves on by itself in Slide Show view; set 0 to wait for a click. Tick Start again at the end to loop.",
    },
    {
      question: "Will the transitions play everywhere?",
      answer: "They are saved in the file and play in PowerPoint. Other programs may play fewer of them. The preview here shows the slides but does not play the transitions.",
    },
    {
      question: "Can I make a video?",
      answer: "Yes. Save the slideshow, then use PowerPoint to Video on this site to make an MP4 (with optional music), or PowerPoint to GIF for a short loop. In PowerPoint itself, use Export, Create a Video.",
    },
  ],
  "repair-powerpoint": [
    {
      question: "What kinds of damage can it fix?",
      answer: "Structure problems: a missing or wrong file list, a missing slide list, slides that point at a picture, chart or layout that is not in the file, duplicate slide numbers, and a zip whose index is damaged. Each fix is listed in the result.",
    },
    {
      question: "What can it not fix?",
      answer: "Content that is physically gone, for example a file cut off while copying, a picture whose bytes are damaged, or a slide whose own text file is broken beyond reading. Such pieces are dropped and listed under Could not be kept.",
    },
    {
      question: "Does it open password-protected files?",
      answer: "No. A file locked with a password is scrambled, so there is nothing to repair until it is unlocked. Use Password Protect PowerPoint in Unlock mode if you have the password. Old .ppt files are not supported either.",
    },
    {
      question: "Is my original changed or uploaded?",
      answer: "Neither. The repair runs in your browser and writes a new file. Your original is left as it is.",
    },
    {
      question: "Will PowerPoint open the repaired file without a warning?",
      answer: "It is checked by the site's own consistency checks and by LibreOffice. It has not been tried in desktop PowerPoint, so open it there and look through the slides before you rely on it.",
    },
  ],
  "redact-powerpoint": [
    {
      question: "Is the text really removed, or just covered?",
      answer: "Really removed. Each match is replaced by solid block characters in the file's own text, so nothing is hidden underneath. Pictures you choose are replaced by a black picture of the same size. The result is read again to check nothing matching is left.",
    },
    {
      question: "What does it not catch?",
      answer: "Text that is part of a picture, such as a screenshot or a scan, cannot be found, so use the picture options on those slides. Video and audio are not changed. Names that appear only in a file's title or in an embedded object may need a manual look. The result lists what it could not check.",
    },
    {
      question: "What can I search for?",
      answer: "Emails, phone numbers, US Social Security numbers, card numbers, web addresses, IP addresses, money amounts and dates, plus your own words and your own patterns (regular expressions). It looks on slides, speaker notes, the master and layouts, charts, SmartArt, comments and alt text.",
    },
    {
      question: "Can I get the text back?",
      answer: "No. Keep your original. A redacted slide cannot be restored.",
    },
    {
      question: "Is anything uploaded?",
      answer: "No. The deck is read and rewritten in your browser tab. A request that counts the use goes to this site when you download and carries no part of your file.",
    },
  ],
  "powerpoint-to-html": [
    {
      question: "What is in the HTML file?",
      answer: "Everything: the slides, their pictures and, if you choose, the speaker notes. It is one file with no links to other files, so it opens from a USB stick or an email attachment with no internet connection.",
    },
    {
      question: "What is the difference between the two ways?",
      answer: "Text and pictures rebuilds each slide as readable web text with its images: small, searchable and fits any screen. Exact look draws every slide as a picture, so it looks like the original but the text is part of the picture.",
    },
    {
      question: "What is not carried over?",
      answer: "Charts, SmartArt, video, audio, animations and transitions. The result lists what was left out of your deck. Use Exact look if a chart or diagram matters.",
    },
    {
      question: "Are the speaker notes included?",
      answer: "Only if you tick the box, so notes are not shared by accident. With notes included, the N key shows them.",
    },
    {
      question: "Is anything uploaded?",
      answer: "No. The page is built in your browser tab from your file.",
    },
  ],
  "powerpoint-to-video": [
    {
      question: "Is there narration?",
      answer: "No. Each slide is a still picture shown for the time you choose, with an optional music track. Speaker notes are not read out and animations are not recorded.",
    },
    {
      question: "How long can the video be?",
      answer: "Up to 20 minutes. Long decks take a while because every slide is drawn and encoded in your browser tab.",
    },
    {
      question: "What video do I get?",
      answer: "An MP4 (H.264), at 480p, 720p or 1080p, with a fade in and out if you want one. It plays on phones, TVs, YouTube and most chat apps.",
    },
    {
      question: "Can it use the timings saved in my deck?",
      answer: "Yes. If slides have an automatic advance time, tick Use the times saved in the deck. Slides without a time get the default.",
    },
    {
      question: "Is anything uploaded?",
      answer: "No. The deck and the music are processed in your browser with the same open-source engine the site's video tools use. The first run downloads that engine (about 25 MB).",
    },
  ],
  "powerpoint-to-gif": [
    {
      question: "How long can a GIF be?",
      answer: "Up to 90 seconds. A GIF has 256 colours and grows with every second; for a longer show use PowerPoint to Video.",
    },
    {
      question: "Can I make it play once?",
      answer: "Yes. Untick Loop forever and the GIF plays once and stops on the last slide.",
    },
    {
      question: "Why does it look grainy on photos?",
      answer: "GIF can show only 256 colours at a time. Text and flat diagrams look fine; photos look better in a video.",
    },
    {
      question: "Is anything uploaded?",
      answer: "No. The slides are drawn and joined in your browser. The first run downloads the open-source video engine (about 25 MB).",
    },
  ],
  "powerpoint-templates": [
    {
      question: "Can I use these for my own work?",
      answer: "Yes. They are starter decks made by this site, with example text for you to replace. Use them for any purpose.",
    },
    {
      question: "What is the difference between .pptx and .potx?",
      answer: "A .pptx opens as a normal deck. A .potx is a PowerPoint template: double-click it and PowerPoint starts a new untitled presentation from it, so the starter is never overwritten. The .potx files were checked in LibreOffice and have not been tried in desktop PowerPoint.",
    },
    {
      question: "Can I change the text and look before downloading?",
      answer: "Yes. Open it in the Slide Deck Maker. It starts the deck from the template, and you change the text, the slides, the theme and your brand colour there.",
    },
    {
      question: "Is the example text real advice or real figures?",
      answer: "No. It is placeholder text to show what goes where. Replace all of it, including the figures.",
    },
  ],
  "template-theme-maker": [
    {
      question: "What does it change?",
      answer: "The twelve theme colours, the heading and body fonts, and the theme's name. It can also keep all, one or none of the slides, and take out speaker notes, comments and the author's name.",
    },
    {
      question: "Will my existing slides change colour?",
      answer: "Only where they use theme colours. Shapes and text that were given a fixed colour or font stay as they are, and the result counts how many there are. Decks made by some other tools fix every colour; for those only new content follows the theme.",
    },
    {
      question: "Does the .potx work in PowerPoint?",
      answer: "It is the same package with the presentation part marked as a template, as PowerPoint writes it. It was checked in LibreOffice and by the site's own checks; it has not been tried in desktop PowerPoint.",
    },
    {
      question: "Is anything uploaded?",
      answer: "No. The deck is read and rewritten in your browser tab.",
    },
  ],
  "password-protect-powerpoint": [
    {
      question: "How strong is the lock?",
      answer: "It is the Office 2010-and-newer \"Encrypt with Password\": AES-256 with SHA-512 and 100,000 hashing rounds. The strength depends on your password, so use a long one.",
    },
    {
      question: "Can you recover a lost password?",
      answer: "No. Nobody can, here or anywhere else. Keep the password safe and keep your unlocked original.",
    },
    {
      question: "Will PowerPoint open the locked file?",
      answer: "It uses the same format PowerPoint writes, and it was cross-checked in both directions against an independent open-source implementation. It has not been opened in desktop PowerPoint, so open the locked copy once to check the password before you delete the original.",
    },
    {
      question: "What can it not unlock?",
      answer: "Files locked by Office 2007 (older Standard encryption), files protected with a certificate, old .ppt files, and \"Restrict editing\" settings, which are not encryption.",
    },
    {
      question: "Does my password leave the browser?",
      answer: "No. The lock is made and removed in your browser tab, and neither the file nor the password is sent anywhere.",
    },
  ],
  "presentation-studio": [
    {
      question: "What does it do that the single tools do not?",
      answer: "It runs up to six steps on one copy of the deck in a fixed, sensible order: size, slide numbers, watermark, accessibility check, clean for sharing, compress. You get one finished file and a report of what each step did.",
    },
    {
      question: "Are the steps the same as the separate tools?",
      answer: "Yes. Each step uses the same engine as the matching single tool on this site, with the common options.",
    },
    {
      question: "Does it fix accessibility problems?",
      answer: "No. It checks and reports. Use the PowerPoint Accessibility Checker to add alt text and titles, then bring the fixed file back.",
    },
    {
      question: "Can I build a deck here?",
      answer: "No. Start in the Slide Deck Maker or from a template, then finish the deck here.",
    },
    {
      question: "Is anything uploaded?",
      answer: "No. Everything runs in your browser tab, and your file is not sent anywhere.",
    },
  ],
  "merge-pdf": [
    {
      question: "How do I combine PDF files into one?",
      answer: "Add your files, drag them into the order you want, choose pages from a file if you only need some, then press Merge and download the combined PDF. It all happens in your browser.",
    },
    {
      question: "Does merging add a watermark?",
      answer: "No. The merged file contains only your pages, with nothing stamped on them. Only its document properties name FreeDailyPro as the software that made the file.",
    },
    {
      question: "What doesn't carry over when I merge PDFs?",
      answer: "Bookmarks do: each file gets one top-level bookmark with its own bookmarks beneath it (if you use Arrange all pages, only the per-file bookmarks are kept). Form fields do not stay fillable, and a digital signature does not stay valid because merging builds a new file.",
    },
    {
      question: "Can I put pages from different files in a new order?",
      answer: "Yes. Add your files, then open Arrange all pages. Every page of the merged file shows as a small preview, in the order it will be written. Drag a page anywhere (even between files), or use its arrows. Each page can also be rotated or deleted. Changing the file list, a page range or a Choose pages setting afterwards starts the arrangement again, so you never merge stale pages.",
    },
    {
      question: "Can I merge JPG or PNG pictures with my PDFs?",
      answer: "Yes. Drop JPG or PNG files in with your PDFs. Each picture becomes one page on a US Letter sheet, turned sideways for a wide picture, and it is made in your browser, not uploaded. Word files are not merged here: save them as PDF first.",
    },
    {
      question: "Does merging change the page size or orientation of my original PDFs?",
      answer: "No — each file's own pages keep their original size and orientation exactly as they were. Merging only combines the files into one document in the order you choose; it doesn't resize, rotate, or reformat any page.",
    },
    {
      question: "Is there a limit to how many PDFs I can merge at once?",
      answer: "There's no fixed limit on the number of files — you can add as many as you need and reorder them before merging. The practical limit is your device's own memory for very large batches, since everything processes in your browser.",
    },
    {
      question: "Will this work on a password-protected PDF?",
      answer: "Yes. If a PDF needs a password to open, you'll be asked for it as soon as you add the file — type it in and the file is unlocked right in your browser (it's never uploaded). The merged file won't have a password. If you skip a locked file, it's simply left out of the merge.",
    },
    {
      question: "Are my files actually uploaded anywhere?",
      answer: "No — merging runs entirely in your browser using your device's own processing power. Your PDFs never leave your device, there's no server upload involved, and nothing about their contents is visible to us at any point.",
    },
    {
      question: "Can I merge files with different page sizes, like some Letter and some A4?",
      answer: "Yes — each page keeps its own original size in the merged document. The result is one PDF where different pages can genuinely be different sizes, which is completely normal and how the original files looked; merging doesn't force everything onto a single uniform page size.",
    },
    {
      question: "Will merging a lot of large PDFs on my phone cause it to freeze or crash?",
      answer: "It's possible with a large enough batch — since everything processes using your device's own memory, a phone merging many large files (dozens of pages each, several files) has less headroom than a laptop. If you hit this, try merging in smaller batches (a few files at a time, then merge those results together) rather than everything at once.",
    },
    {
      question: "I added the same file twice by accident — do I need to start over?",
      answer: "No — you'll see a warning if a duplicate is detected, but it won't block you. Just remove the extra copy from the list using its own remove button, then continue reordering and merging normally.",
    },
    {
      question: "What's the difference between this and Organize PDF?",
      answer: "Merge PDF combines separate files into one document — its whole job is taking multiple PDFs and producing a single new one. Organize PDF works on pages within a single PDF you already have — reordering, deleting, or rotating individual pages using real page previews. If you need to combine files, use Merge; if you need to fix the page order or remove a page inside one PDF you already have, use Organize (there's a direct link to it after merging).",
    },
  ],
  "split-pdf": [
    {
      question: "Can I split a PDF into every single page as its own file?",
      answer: "Yes — click the \"Every page\" button next to the range field to fill it in automatically (no need to type out every page number by hand), then split as usual. Each page downloads as its own file, packaged together as one ZIP when there's more than one.",
    },
    {
      question: "What's the difference between splitting into separate files and combining ranges?",
      answer: "Splitting gives you one download per range you specify. Combining takes the ranges you list and merges just those pages into a single new PDF, in the order you listed them — useful for pulling out and reordering specific pages rather than making several small files.",
    },
    {
      question: "Do I get one ZIP file or a bunch of separate downloads?",
      answer: "When your split produces more than one file, a \"Download all N files as ZIP\" button appears (N is the number of files) above the individual file list — one click gets everything in a single archive. The individual download buttons stay available too, in case you only want one or two of the resulting files rather than all of them.",
    },
    {
      question: "What's the difference between Split PDF and Extract Pages & Text?",
      answer: "Split is built around page ranges and multi-file output — cutting one PDF into several new ones, or combining specific ranges into a single reordered file. Extract Pages & Text is the more direct tool if you only need one specific page pulled out, or if you need the actual text content rather than a page-range PDF. If you're not sure which you need: ranges and multiple output files means Split; a single page or the text itself means Extract.",
    },
    {
      question: "Will this work on a password-protected PDF?",
      answer: "Yes. If a PDF needs a password to open, you'll be asked for it as soon as you add the file — type it in and the file is unlocked right in your browser (it's never uploaded). The split pages won't have a password.",
    },
    {
      question: "Are my files uploaded to a server?",
      answer: "No — splitting runs entirely in your browser. Your PDF never leaves your device, and there's no server involved in reading or dividing its pages.",
    },
    {
      question: "Will splitting a very long PDF into every page crash my phone?",
      answer: "It's possible with a large enough document — generating dozens or hundreds of individual page files, then zipping them together, uses your device's own memory. On a phone, try splitting a long document in smaller batches of ranges (e.g. pages 1-50, then 51-100) rather than every single page at once if you run into slowdowns.",
    },
    {
      question: "Can I choose which pages go into which output file, not just fixed ranges?",
      answer: "Yes — each comma-separated group becomes its own file (or its own section, if combining). \"1-3, 7, 10-12\" produces pages 1 through 3 as one group, page 7 alone as another, and 10 through 12 as a third — you're not limited to strictly sequential ranges.",
    },
    {
      question: "Can I split into equal-sized chunks, like every 3 pages, without typing every range by hand?",
      answer: "Yes — enter the group size in the \"split every N pages\" field and click Apply; it fills in the range field automatically (e.g. every 3 pages on a 10-page document becomes 1-3, 4-6, 7-9, 10). The last group is simply whatever's left over if the page count doesn't divide evenly.",
    },
  ],
  "compress-pdf": [
    {
      question: "How do I compress a PDF to under 1 MB?",
      answer: "Drop your PDF, choose Target size, type 1 and keep the unit on MB, then compress. The steps run gentlest first and stop as soon as the file fits. If it cannot get that small, you get the smallest version it reached, with its final size and a note.",
    },
    {
      question: "Will compressing blur my text?",
      answer: "In Balanced, no: text stays real, selectable text and only the pictures inside are reduced. Aggressive, Extreme and Target size can turn pages into pictures when they have to go further, and text on those pages is then drawn as part of the picture, so it is not selectable. Each level is marked in the list, so you can download a gentler one.",
    },
    {
      question: "Why did compressing barely change my file size?",
      answer: "The bytes in a PDF are mostly in its pictures. If your PDF is mostly text, or is a scan that is already compressed (JBIG2 or JPEG 2000), Balanced has little to shrink without changing how it looks, and the tool tells you so. Aggressive then turns pages into pictures level by level, and Extreme goes on to grey and black-and-white pages; every level is a downloadable file and text on converted pages is no longer selectable. If it has photos, try Balanced first.",
    },
    {
      question: "Will compressing a PDF make the text impossible to select or search?",
      answer: "In Balanced, yes: it only tidies the file and shrinks the pictures inside, so text stays real, selectable text, and links, bookmarks and form fields are kept. Aggressive and Extreme, and Target size when it has to go that far, turn pages into pictures, so text on those pages is no longer selectable; each level is listed with its size and marked, and you can download an earlier level instead.",
    },
    {
      question: "Can compressing make my file bigger?",
      answer: "Not any more: if a result isn't noticeably smaller than your original, the tool says so and keeps your original instead of offering the larger file. A file that is already under your Target size is left alone.",
    },
    {
      question: "What's the difference between Balanced, Aggressive, Extreme and Target size?",
      answer: "Balanced tidies the file and shrinks the pictures inside, first to about 140 dots per inch and then picture by picture, so text stays selectable (the last level can soften fine detail in photos). Aggressive does that and then turns pages into pictures in finer and finer steps. Extreme goes further and makes every page grey, then pure black and white. Target size lets you set an exact limit, like \"under 2 MB\", works through the levels gentlest first and stops as soon as the file fits, aiming a little under the limit so it passes size checks.",
    },
    {
      question: "Are my files uploaded to a server to compress them?",
      answer: "No. Compression, including the resizing of pictures, runs entirely in your browser. Your PDF never leaves your device.",
    },
    {
      question: "Will a scanned or image-heavy PDF compress differently than a text document?",
      answer: "Yes. A scan is one big picture per page, so it has the most room to shrink. A short, mostly-text PDF has the least, and is the case where the tool will tell you there is nothing worth shrinking.",
    },
  ],
  "rotate-pdf": [
    {
      question: "Does rotating a PDF page keep the text selectable?",
      answer: "A 90-degree rotation does, since it just flips a rotation flag rather than touching the page content. A custom angle (for a slightly skewed scan, for example) redraws that specific page as vector content at its original sharpness, not as a picture, so its text stays selectable. Links and form fields on that page are not kept.",
    },
    {
      question: "Can I rotate just one page instead of the whole document?",
      answer: "Yes — each page has its own rotate control in the preview, so you can fix individual pages (a sideways-scanned page mixed into an otherwise correct document, for example) without rotating everything.",
    },
    {
      question: "My double-sided scan has every other page upside down — do I have to fix each one by hand?",
      answer: "No — use Rotate Odd Pages or Rotate Even Pages to fix every other page in one click, a common result of scanning a double-sided document as one long PDF where alternating sides come out flipped.",
    },
    {
      question: "What's the difference between this and fixing page order in Organize PDF?",
      answer: "Rotate PDF only changes each page's orientation — it doesn't reorder or remove pages. Organize PDF is the tool for reordering pages or dropping ones you don't need; if a document needs both fixing (rotating a sideways page AND reordering), do either one first, then use Continue to move to the other tool with the result.",
    },
    {
      question: "Can I type an exact angle instead of only 90-degree steps?",
      answer: "Yes — each page's rotation field accepts any angle, not just multiples of 90. Useful for a scan that's slightly crooked rather than fully sideways, where a small correction (like 3 or 5 degrees) looks better than a full quarter-turn.",
    },
    {
      question: "Are my files uploaded to a server?",
      answer: "No — rotation, including custom-angle rendering, runs entirely in your browser, including the page previews you see before applying anything. Your PDF never leaves your device.",
    },
    {
      question: "Will rotating change the page size or add white borders?",
      answer: "A 90-degree rotation just flips the page's own width and height, no white borders added. A custom angle does add some background around the rotated content, since redrawing a page at an angle within a rectangular page needs somewhere for the corners to go — this is a real, visible tradeoff of the custom-angle approach, not a bug.",
    },
    {
      question: "Can I preview the rotation before downloading?",
      answer: "Yes — each page thumbnail updates immediately to show the rotation you've applied, before you click Apply rotation. Nothing is saved to a file until you download the result.",
    },
    {
      question: "Does rotating a page reduce quality or remove its text?",
      answer: "A turn of 90, 180 or 270 degrees redraws nothing; it only changes the page's rotation setting, so text stays selectable and images keep their pixels. A custom angle redraws the page as vector content at its original resolution, still without turning it into a picture.",
    },
    {
      question: "What angles can I type, and which way is positive?",
      answer: "Anything from -180 to 180 degrees, with positive meaning clockwise. Decimals are accepted, and the nudge buttons move by 0.1. A value outside the range is wrapped, so 270 becomes -90, which is a quarter turn to the left.",
    },
    {
      question: "Why did the links on a page stop working after a custom angle?",
      answer: "That page is rebuilt on a new page sized to hold the turned content, and links and form fields on it are not carried across. Pages turned in exact quarter steps keep theirs, so use those if a link matters.",
    },
    {
      question: "Can I rotate only some pages?",
      answer: "Yes. Every page has its own arrows and angle box, and the odd and even buttons act on alternate pages. Pages you leave at zero are not touched at all in the saved file.",
    },
    {
      question: "Is my original file changed, and can I adjust after applying?",
      answer: "The original stays as it is; the tool builds a new file named after yours with -rotated added. Changing any angle after applying discards that result, so you can adjust and press Apply rotation again before downloading.",
    },
  ],
  "pdf-to-word": [
    {
      question: "Does converting a scanned PDF to Word actually give me editable text?",
      answer: "Yes — any page that's a scan or photo with no selectable text is automatically read with OCR instead of just being copied as-is, so scanned pages come out as real, editable text too. This takes longer per page than a text-based page, since it has to be read visually.",
    },
    {
      question: "Will tables in my PDF come out as real, editable Word tables?",
      answer: "Yes, tables are automatically detected and rebuilt as genuine Word tables, sized proportionally to how they appeared in the original — you don't need to tell the tool a table is coming.",
    },
    {
      question: "What's the difference between this and OCR PDF?",
      answer: "This produces an editable Word document — for when you need to actually change the content. OCR PDF adds a real, searchable text layer to a scanned PDF while keeping it a PDF — for when you just need to select, search, or copy text without switching document formats. If you want to keep the PDF format, use OCR PDF; if you need to edit the content, use this.",
    },
    {
      question: "Will my document look exactly the same in Word as it did as a PDF?",
      answer: "Word text flows and reflows the way Word documents always do — this isn't a fixed-position copy of the PDF page, so small spacing and line-break differences from the original are normal and expected. The words, structure, formatting (font, bold/italic, color), and images are what carry over accurately, not pixel-exact positioning.",
    },
    {
      question: "Which languages can scanned pages be read in?",
      answer: "English by default. Before converting you can choose Hindi, English + Hindi, Spanish, French, German, Portuguese or Italian. Pick the language the scan is actually in: reading with the wrong language gives wrong characters, and English-only is also faster than reading two languages at once.",
    },
    {
      question: "What happens to columns, bullet lists, links, headers and form fields?",
      answer: "Pages with two columns are read column by column instead of line by line across both; bullet lists become real Word lists; web links stay clickable; filled-in form fields (typed text, ticked boxes) are written out next to their labels; and running headers and page numbers move into the Word header and footer instead of cluttering the body.",
    },
    {
      question: "Can I stop a conversion partway through if it's taking too long?",
      answer: "Yes — a Stop option appears while converting, useful for a long, OCR-heavy scanned document where you'd rather cancel and try a smaller file than wait for every page.",
    },
    {
      question: "Does it handle a document that's part real text, part scanned pages?",
      answer: "Yes — each page is judged individually rather than the whole document being treated one way or the other, so a scanned page mixed into an otherwise text-based document (a contract with a scanned signature page, for instance) still converts correctly.",
    },
    {
      question: "Are my files uploaded to a server, including for OCR?",
      answer: "No — both text extraction and OCR run entirely in your browser. Your PDF never leaves your device at any point in the process.",
    },
    {
      question: "What languages does the OCR support?",
      answer: "English and Hindi are automatically recognized, no need to specify which one a scanned page uses in advance. Hindi text extracts as real, correct Unicode text with a font that actually supports Devanagari characters.",
    },
  ],
  "pdf-to-jpg": [
    {
      question: "What happens if my PDF has more than one page?",
      answer: "When the result is exactly one image — a one-page PDF, or a single page you picked from a longer one — it downloads as that image, named with its page number (like report-page-03.jpg). Anything more downloads as one ZIP; with several PDFs each gets its own folder.",
    },
    {
      question: "Can I convert just one specific page instead of the whole PDF?",
      answer: "Yes — enter a page range (like \"3\", \"1, 3-5\" or \"4-\" for page 4 to the end) when one PDF is loaded, and only those pages are included. Leave it blank to convert every page.",
    },
    {
      question: "Can I export as PNG instead of JPG?",
      answer: "Yes — PNG is lossless, so fine text and thin lines stay perfectly crisp with no compression artifacts, at the cost of a larger file than JPEG. JPG is the better default for most pages; PNG is worth choosing for a page where compression artifacts would actually be noticeable.",
    },
    {
      question: "What do the quality levels (108, 144, 216, 300 dpi) mean?",
      answer: "Each button shows the resolution of the images in dpi (dots per inch): 144 dpi suits screens, 300 dpi (Print) suits printing. Higher levels also use less JPEG compression — Maximum renders at a higher resolution with less compression (sharper, especially for text, but a larger file), Standard renders smaller and more compressed (faster, smaller file, a bit softer). High is a reasonable middle ground and the default.",
    },
    {
      question: "Are my PDFs uploaded to a server?",
      answer: "No — every page is rendered to an image entirely in your browser, and the ZIP is built locally too. Your PDFs never leave your device.",
    },
    {
      question: "What's the difference between this and Extract Pages & Text?",
      answer: "This converts pages into image files (JPG/PNG) — useful for sharing as pictures or posting where a PDF isn't accepted. Extract Pages & Text pulls pages out as a real, smaller PDF (still a PDF, not an image) or gets the plain text content. If you need an actual PDF page rather than a picture of one, use Extract instead.",
    },
    {
      question: "Will text stay sharp and readable in the converted image?",
      answer: "At Standard quality, small text may look a bit soft once resized or shared elsewhere. For text-heavy pages meant to stay readable at small sizes, High or Maximum quality (and PNG, if compression artifacts matter) holds up noticeably better.",
    },
    {
      question: "Can I convert multiple PDFs at once?",
      answer: "Yes — add several PDF files, and each one converts independently, with its pages kept in their own folder inside the resulting ZIP (PDFs that share a name become \"name\" and \"name (2)\"). If one PDF can't be read, the others still convert and it is listed.",
    },
    {
      question: "Which dpi should I pick?",
      answer: "Standard (108) and High (144) suit viewing on screen and sharing. Maximum (216) gives more detail for zooming. Print (300) is for physical printing and makes the largest files. If unsure, High is a sensible middle choice and is selected when the tool opens.",
    },
    {
      question: "Why did I get one image in one case and a ZIP in another?",
      answer: "If the result is exactly one image, such as a one-page PDF or a single chosen page, it downloads directly. Two or more images are packed into a ZIP, with a folder for each PDF when you converted several at once.",
    },
    {
      question: "How do I write a page range?",
      answer: "Separate items with commas and use hyphens for spans: 1, 3-5 converts page 1 and pages 3 to 5, and 8- means page 8 to the end. Repeated pages are removed. A range outside the document stops the run with an error message.",
    },
    {
      question: "Why is a page lower in resolution than the setting I chose?",
      answer: "A page with very large dimensions would need more than 16 million pixels at your chosen resolution, which is more than a browser can reliably draw. The tool lowers the scale for that page and tells you how many pages were reduced.",
    },
    {
      question: "What happens if one of several PDFs is damaged or locked?",
      answer: "It is skipped and named in a warning, with a reason such as password-protected or damaged, and the other PDFs still convert. If none can be read, the tool stops with an error message instead.",
    },
  ],
  "pdf-to-excel": [
    {
      question: "Will this correctly detect a table if the page also has other text on it?",
      answer: "Yes. The tool looks for runs of at least 3 rows whose columns line up, and writes only those tables to the workbook. Headings, captions, paragraphs and two-column text around a table are left out, not forced into cells.",
    },
    {
      question: "Are numbers and dates real numbers and dates in Excel?",
      answer: "Yes, by default. A column of amounts, currency, percentages or dates is written as real values with a matching format, so SUM, sorting and filters work. Codes, ZIP codes, phone numbers and numbers with leading zeros stay text on purpose. Dates like 09/02/2026 can be day-first or month-first; the tool works it out from the other dates in the column, and if it cannot it leaves them as text and lets you pick the order. You can also turn conversion off to get plain text.",
    },
    {
      question: "What if my table continues over several pages?",
      answer: "A table whose columns sit in the same place on the next page is joined into one table, and a repeated heading row is dropped. Choose \"One sheet per page\" instead if you want the pages kept apart, or \"Everything on one sheet\" to stack all tables on a single sheet.",
    },
    {
      question: "What if my PDF's table only has 1 or 2 rows?",
      answer: "A table that small is not reliably told apart from ordinary text, so it may not be found. If no table is found you get a clear message and no file, not an empty workbook.",
    },
    {
      question: "Will this work on a scanned PDF?",
      answer: "Yes. Pages with no selectable text are read with OCR (pick the language of the scan), and the words are then laid out into tables like any other page. The first scan downloads the language data, so it needs an internet connection and is slower; check numbers read from a scan against the original.",
    },
    {
      question: "What's the difference between this and Excel to PDF?",
      answer: "This pulls table data out of an existing PDF into an editable spreadsheet. Excel to PDF goes the other direction. Use whichever matches the direction you need.",
    },
    {
      question: "Are my files uploaded to a server?",
      answer: "No. Table detection, OCR and the spreadsheet are all made in your browser. Your PDF never leaves your device.",
    },
    {
      question: "Will merged cells or multi-line entries convert correctly?",
      answer: "Not reliably. Detection works best on tables laid out in aligned columns. A cell whose text wraps over several lines may come out as extra rows, and merged cells are not recreated. The preview shows the first rows of every sheet so you can check before downloading.",
    },
    {
      question: "What should I do if no tables were found?",
      answer: "The PDF may be mostly text (try PDF to Word), or its tables may have fewer than 3 rows or be drawn as a picture. If it is a scan, make sure \"Read scanned pages with OCR\" is on and the language matches.",
    },
    {
      question: "How does it decide what counts as a table?",
      answer: "It looks for at least three consecutive rows, each made of two or more separate pieces of text, whose columns line up closely. Ordinary paragraphs, bullet lists and two-column article text do not meet that test, so they are not written to the workbook.",
    },
    {
      question: "Why does it say no tables were found?",
      answer: "Either the pages have no readable text at all, which usually means a scan with OCR switched off, or the text exists but nothing forms a grid of three or more aligned rows. The message tells you which of the two applies.",
    },
    {
      question: "Does it work on scanned PDFs?",
      answer: "Yes, when Read scanned pages with OCR is on. Pages without selectable text are read with text recognition first, and the result carries a warning to check figures against the original, since scan quality limits accuracy.",
    },
    {
      question: "What happens with a table that continues on the next page?",
      answer: "With the join option on, a table on the following page whose columns sit in the same places is merged into one, and a repeated heading row is dropped. The join is not offered in One sheet per page mode.",
    },
  ],
  "word-to-pdf": [
    {
      question: "Will the PDF look like my Word document?",
      answer: "Close, but not identical. Headings, bold and italic, colours, bullet and numbered lists, tables with borders and shading, pictures, links, tabs, page breaks, headers and footers with page numbers, and your page size and margins are kept. Fonts are matched to the nearest standard PDF font (Calibri and Arial become Helvetica-style, Times New Roman and Cambria become Times-style), so lines can break in slightly different places than in Word. Text boxes, shapes, charts and multi-column layouts are not reproduced by that mode, so when a file has them the tool switches to “Exact look”, which draws every page exactly as Word lays it out (as pictures with a hidden text layer so Latin-script text can still be searched).",
    },
    {
      question: "Which file types work?",
      answer: "Word documents saved as .docx, and plain text (.txt, in UTF-8 or UTF-16). Older .doc, .rtf and .odt files are turned away with a message: open them in Word or LibreOffice and save as .docx first.",
    },
    {
      question: "What about Chinese, Japanese, Korean, Hindi, Arabic and other scripts?",
      answer: "Latin, Greek and Cyrillic text (including accents, curly quotes, dashes and the € and ₹ signs) is drawn as real, selectable text. Chinese, Japanese, Korean, Hindi and other Indic scripts, Arabic, Hebrew, Thai and emoji are drawn as pictures by your browser so they look right, but that text cannot be selected, copied or searched in the PDF. The tool lists these characters before you convert. If your browser cannot draw them they would show as question marks; in that case use Save as PDF in Word.",
    },
    {
      question: "What is \"Text only\"?",
      answer: "It ignores the formatting and writes just the words in a plain, readable layout on A4 or US Letter. It gives the smallest file and is useful when the document's own layout does not matter.",
    },
    {
      question: "Can I still edit the document after converting it to PDF?",
      answer: "Not easily. PDF is a final format. Edit the original Word file and convert again.",
    },
    {
      question: "Is my file uploaded anywhere?",
      answer: "No. The conversion runs in your browser and the file never leaves your device.",
    },
    {
      question: "How do I choose between the three modes?",
      answer: "Keep formatting gives real selectable text and preserves headings, lists, tables, pictures and headers. Exact look draws every page as Word lays it out, including text boxes and columns, but as pictures. Text only keeps just the body text and page breaks.",
    },
    {
      question: "Can I select and copy text in the PDF afterwards?",
      answer: "In Keep formatting and Text only, yes for supported scripts. In Exact look the pages are pictures with a hidden text layer, so Latin-script text can be searched. Scripts such as Chinese or Arabic are drawn as images and cannot be selected in any mode.",
    },
    {
      question: "Why does my PDF break lines differently from Word?",
      answer: "Word's fonts are replaced by the closest standard PDF fonts, which have slightly different letter widths. Lines can wrap earlier or later, and a page can end at a different place. Exact look follows Word's own layout more closely.",
    },
    {
      question: "Which page size and margins does the PDF use?",
      answer: "With Keep formatting, the page size and margins stored in your document are used. The A4 or US Letter choice applies to .txt files and Text only mode, where the document has no page setup of its own to follow.",
    },
  ],
  "excel-to-pdf": [
    {
      question: "Are hidden sheets, columns and rows included?",
      answer: "No, by default. Like Excel's own Save as PDF, hidden sheets, hidden columns and hidden or filtered-out rows are left out, and the result tells you what was left out. If you do want them, tick “Include hidden data” before converting.",
    },
    {
      question: "Will long text be cut off?",
      answer: "No. Columns are sized from their content and long text wraps onto extra lines inside the cell. A sheet too wide for one page continues on the following pages, with its first column repeated, instead of dropping columns.",
    },
    {
      question: "Does it keep colours, bold and charts?",
      answer: "Colours, bold and borders, yes: cell fills, font colours, bold and italic, borders and alignment from your workbook are kept (you can untick this for a plain printout), and values are printed as Excel displays them (dates, currency, percentages). Conditional formatting, charts and pictures are not reproduced; when a sheet has charts or pictures, the result says how many were not included. If the exact printed look matters, use Save as PDF in Excel.",
    },
    {
      question: "What happens to a title above the table?",
      answer: "A title or heading above the table (a merged cell or a single filled cell) is printed once at the top, and the real column headings are the ones repeated on every page.",
    },
    {
      question: "Which file types work?",
      answer: "Excel files (.xlsx, .xlsm, .xls) and CSV files. A CSV is printed exactly as written. Other files (a Word document, say) are turned away with a message.",
    },
    {
      question: "What about Chinese, Japanese, Hindi, Arabic and emoji?",
      answer: "Latin, Greek and Cyrillic text, accents, curly quotes and the € and ₹ signs are drawn correctly. Chinese, Japanese, Korean, Hindi and other Indic scripts, Arabic, Hebrew, Thai and emoji cannot be drawn yet and would appear as “?”. You are warned before converting.",
    },
    {
      question: "Is my file uploaded anywhere?",
      answer: "No. The conversion runs in your browser and the file never leaves your device.",
    },
    {
      question: "Which file types does it accept?",
      answer: "Excel workbooks in .xlsx, .xlsm or .xls form, and .csv files. Word, PowerPoint, PDF and OpenDocument files are rejected with a message naming the type it saw, so you can open the matching tool instead.",
    },
    {
      question: "Why are some rows or columns missing from the PDF?",
      answer: "Hidden sheets, hidden columns and filtered-out rows are skipped, like Excel's own Save as PDF. If the workbook has any, an Include hidden data checkbox appears; tick it and convert again to bring them in.",
    },
    {
      question: "Will my charts and pictures appear?",
      answer: "No. Charts and pictures are not reproduced, and conditional formatting is not applied. The result summary includes a note telling you how many charts or pictures were left out, so you know before you share it.",
    },
    {
      question: "What happens with workbooks that have several sheets?",
      answer: "Every visible sheet is included, each starting on its own page under a heading with the sheet's name. Hidden sheets are left out unless you tick Include hidden data. The summary shows the number of sheets and pages produced.",
    },
    {
      question: "How are dates and numbers shown?",
      answer: "As Excel displays them, so a cell formatted as currency, a percentage or a date prints that way, and numbers are right-aligned. Formulas are not printed; you see their displayed results.",
    },
  ],
  "powerpoint-to-pdf": [
    {
      question: "Will my slide backgrounds, images, and layout be included?",
      answer: "Yes. Each slide is converted as a real visual snapshot: its background (solid, gradient or picture), colours, shapes, images, tables, charts and layout, not just its text. You get a strip of every page to check before you download.",
    },
    {
      question: "Will the text in the PDF be selectable or searchable?",
      answer: "Yes, by default. Each page is a picture of the slide, with an invisible copy of its text placed over it so you can search with Ctrl+F and copy text. Untick “Make the text searchable and copyable” for the smallest file. Text in Chinese, Japanese, Hindi, Arabic and similar scripts shows correctly in the picture but is not part of the searchable layer.",
    },
    {
      question: "Are charts, SmartArt, 3D effects, or embedded video included?",
      answer: "Column, bar, line, area and pie charts are drawn with their data, though not always pixel-identical to PowerPoint. SmartArt is approximated; 3D effects and animations aren't reproduced, and embedded video/audio isn't included in a static PDF page.",
    },
    {
      question: "Does this support the older .ppt format, not just .pptx?",
      answer: "No — only .pptx (the modern, XML-based PowerPoint format) is supported. If you have a .ppt file, open it in PowerPoint (or a free alternative like LibreOffice Impress) and use Save As to save a .pptx copy first, then convert that.",
    },
    {
      question: "Does this handle both standard (4:3) and widescreen (16:9) presentations?",
      answer: "Yes — each page is sized to match your presentation's real slide dimensions, whichever aspect ratio it was created in, never forced to a fixed size like A4 or Letter.",
    },
    {
      question: "Are my files uploaded to a server?",
      answer: "No — reading the presentation and generating the PDF both happen entirely in your browser. Your file never leaves your device.",
    },
    {
      question: "What do the Fast / Default / Sharp quality options change?",
      answer: "They control the resolution and JPEG quality each slide is rendered at — Fast is quicker and produces a smaller file, Sharp gives the most detail at the cost of a larger file and slower conversion. Large files and small screens default to Fast automatically, though you can still switch before converting.",
    },
    {
      question: "Will hidden slides be included?",
      answer: "Not by default. Tick “Include hidden slides” before converting if you want them. The choice is not remembered for the next file, and the result tells you how many hidden slides were left out. Speaker notes are never printed.",
    },
    {
      question: "What should I do after converting if the file needs to be smaller or combined with other PDFs?",
      answer: "Use Compress PDF to shrink the file, or Merge PDF to combine it with other PDFs into one document — either works fine on the converted result.",
    },
    {
      question: "Which quality setting should I pick?",
      answer: "Default balances size and detail. Fast renders slides 1280 pixels wide for quicker, smaller output, Default uses 1920, and Sharp uses 2560 for the best detail with a larger file and slower runs on big decks.",
    },
    {
      question: "Are hidden slides and speaker notes included?",
      answer: "Hidden slides are left out unless you tick Include hidden slides, and the summary tells you how many were skipped. Speaker notes are never printed; each page is just the slide itself.",
    },
    {
      question: "Can I search and copy text in the resulting PDF?",
      answer: "Yes, while Make the text searchable and copyable is ticked, because an invisible text layer is placed over each slide picture. Scripts such as Chinese, Japanese, Hindi and Arabic show visually but are not part of that layer.",
    },
    {
      question: "Can I convert an old .ppt file?",
      answer: "Not directly. Files ending in .ppt or .odp are refused with a message. Open them in PowerPoint or LibreOffice, save as .pptx, then drop that file here. A renamed non-presentation file is also detected and rejected.",
    },
    {
      question: "What page size does the PDF use?",
      answer: "Each page matches the dimensions of your presentation, so standard and widescreen decks keep their own proportions. The tool does not fit slides onto A4 or Letter sheets.",
    },
  ],
  "image-to-pdf": [
    {
      question: "Why did my phone photo come out sideways?",
      answer: "It should not: phone photos store their pixels sideways with a note saying how to turn them, and this tool applies that note when you add the photo, so the thumbnail, the rotate button and the PDF all agree. If an image is still the wrong way up in the page strip, use the rotate button on it.",
    },
    {
      question: "Can I make the page the same size as my image?",
      answer: "Yes: choose “Original image size”. The page then matches each image at the resolution stored in the image (96 dpi when it has none), or at 72, 96, 150 or 300 dpi if you pick one.",
    },
    {
      question: "Can I reorder the images before they're combined?",
      answer: "Yes — drag to reorder them, or use the up/down buttons on each image, before generating the PDF. The final document follows whatever order you set.",
    },
    {
      question: "Will the images be distorted to fit the page?",
      answer: "No — each image is fit to the page size you choose without being stretched or distorted. For the cleanest result, photograph documents against a plain, well-lit background beforehand, since the tool doesn't correct for shadows or an off-angle photo.",
    },
    {
      question: "Does this accept WEBP or iPhone HEIC photos, not just JPG and PNG?",
      answer: "Yes. JPG, PNG, WebP, GIF (first frame only), BMP, SVG and HEIC (the format an iPhone saves by default) are accepted; all but JPG and PNG are converted when you add them. TIFF is not supported yet: convert it to JPG or PNG first. A file that cannot be read is reported by name and does not stop the others from being converted.",
    },
    {
      question: "Can I choose Portrait or Landscape orientation for the page?",
      answer: "Yes — once you've picked a page size (or a custom one), choose Portrait or Landscape to control which way the page is oriented, independent of how your images themselves are oriented.",
    },
    {
      question: "Can I combine PDF files with images in the same document?",
      answer: "No — this tool only accepts images. If you need to combine a PDF with images, first convert the images to their own PDF here, then use Merge PDF to combine that with your other PDF files.",
    },
    {
      question: "Is there a margin around each image on the page?",
      answer: "Yes, and you choose it: None, Small or Normal (half an inch). By default an image is scaled down to fit inside the margin, never enlarged. “Fill the page” instead covers the whole page and crops the edges, which suits photo prints.",
    },
    {
      question: "Are my images uploaded to a server?",
      answer: "No. Everything, including HEIC conversion and building the PDF, runs in your browser. Your images never leave your device.",
    },
    {
      question: "What if my images are different sizes or aspect ratios?",
      answer: "Each image is fit to the same chosen page size independently, scaled down to fit within the margins while keeping its own aspect ratio — mixed sizes and orientations in the same batch work fine, each one just gets centered on its own page (or half-page, if pairing two per page).",
    },
  ],
  "pdf-password": [
    {
      question: "How strong is the password protection on this?",
      answer: "It uses AES-256, the strongest encryption standard the PDF format offers. How hard the file is to open then depends on your password: a long, unique one is far stronger than a short or common one, so the tool rates your password as you type.",
    },
    {
      question: "How do I know the password actually worked?",
      answer: "The tool checks it for you. After protecting, it opens the new file with the password you typed and compares the page count, title and form fields with your original, and shows \"Checked: opens with your password\" before you download. You can still open the download in any PDF reader to see the password prompt.",
    },
    {
      question: "Will every PDF reader open it?",
      answer: "Every current reader, browser and phone opens AES-256 files, and passwords with accents or other alphabets work. Only very old readers from before 2006 may not.",
    },
    {
      question: "Can I remove a password from a PDF that already has one?",
      answer: "Not with this tool — it only adds protection to a file that doesn't have it yet. There's currently no \"unlock\" mode for removing an existing password, even if you know it.",
    },
    {
      question: "What happens if I forget the password I set?",
      answer: "There's no recovery option — the password is genuinely required to open the file, with no backdoor or reset. Write it down somewhere safe before you close this tab; a forgotten password means the file is permanently inaccessible through normal means.",
    },
    {
      question: "Is my password sent anywhere?",
      answer: "No — encryption runs entirely in your browser, including deriving the encryption key from your password. Neither your file nor your password ever reaches a server.",
    },
    {
      question: "Can I protect a PDF that's already password-protected?",
      answer: "Yes. If the PDF already has a password, you'll be asked for it when you add the file — it's unlocked in your browser first, and then the new password you choose is applied.",
    },
    {
      question: "Does the password protect against editing, or just opening the file?",
      answer: "Just opening — this sets a password required to view the document's content at all. It doesn't separately restrict printing, copying, or editing once someone has the password and the file is open.",
    },
    {
      question: "What encryption does it use?",
      answer: "AES-256, applied to the whole document with the password you choose. A reader must be given that password before it shows the pages. Very old PDF software may not support this level, whereas current browsers and readers do.",
    },
    {
      question: "What happens when I tick the block options?",
      answer: "A random owner password is generated and never shown, and the permissions you picked are set as flags. Readers are asked to refuse printing, copying or editing. The password you typed is still the one that opens the file.",
    },
    {
      question: "Can you recover my password if I forget it?",
      answer: "No. Nothing is stored anywhere and there is no reset, because the encryption depends on the password itself. Keep it in a password manager and keep the unlocked original.",
    },
    {
      question: "What is the Checked line after protecting?",
      answer: "The tool opens the result with your password in the browser's PDF reader and compares it with the original: page count, title and form field names must match. If the check fails, the file is not offered. The line reports pages, size and AES-256.",
    },
    {
      question: "Does it hide the file name or remove metadata?",
      answer: "No. The file keeps its name and size, and the tool does not strip title or author before encrypting. Only the document content is protected, so rename the file or clean its properties yourself if those details are sensitive.",
    },
  ],
  "pdf-watermark": [
    {
      question: "Can I leave the first page clean, or watermark only some pages?",
      answer: "Yes. Tick Skip the first page to leave a cover untouched, or type pages such as 2-5, 8 in the Pages box. Leave the box empty to stamp every page. If you use both, the first page is dropped after the range is read, so 1-4 with Skip the first page gives pages 2 to 4.",
    },
    {
      question: "Is the watermark text real text or a picture?",
      answer: "Text in the Latin alphabet is added as real, searchable text, so it stays sharp when printed at any size. Text in other scripts, such as Chinese, Arabic or Hindi, is drawn as a picture so it displays correctly, and the tool tells you when that applies.",
    },
    {
      question: "Can I control where the watermark is placed or how transparent it is?",
      answer: "Yes — choose from five position presets (center or any corner) or Tiled to repeat the watermark across the whole page, and adjust opacity with a slider from barely visible to fully solid.",
    },
    {
      question: "Does the watermark actually prevent someone from copying or editing the document?",
      answer: "No — a watermark is a visual signal (marking a document as draft, confidential, or branded with a logo), not a security measure. If the goal is actually restricting access, pair this with PDF Password, or use that instead.",
    },
    {
      question: "Can I use my own logo as the watermark instead of text?",
      answer: "Yes — switch to Image / Logo mode and upload a PNG or JPEG. It's scaled proportionally to the page width and placed using the same position and opacity controls as text.",
    },
    {
      question: "What's Tiled position for?",
      answer: "It repeats the watermark across the entire page in a grid pattern instead of placing it once — useful for a document likely to be cropped, screenshotted, or partially photographed, where a single corner or center watermark could be cut out of the visible portion entirely.",
    },
    {
      question: "Will the watermark cover up important content on the page?",
      answer: "It can, depending on opacity and position — a low opacity (10-20%) keeps the underlying content fully readable while still visibly marking the page. A high opacity centered watermark is more likely to obscure text underneath it, which may be exactly the intent for a heavily restricted draft.",
    },
    {
      question: "Are my files or logo image uploaded to a server?",
      answer: "No — watermarking, including embedding an image watermark, runs entirely in your browser. Neither your PDF nor your logo image ever leaves your device.",
    },
    {
      question: "Can I remove a watermark I added by mistake?",
      answer: "Not with this tool — once a watermark is drawn onto the page content and saved, it's part of the document like any other page content. Keep your original, un-watermarked file if you might need it again.",
    },
    {
      question: "What image formats are supported for the logo?",
      answer: "PNG and JPEG. If your logo is in another format (like WEBP or SVG), convert it to PNG first using an image converter, then upload the PNG here.",
    },
    {
      question: "Which image formats work for the logo?",
      answer: "PNG and JPEG are used directly. Other formats your browser can decode, such as WebP, GIF, BMP or SVG, are converted to PNG first. TIFF is refused with a message asking you to convert it. A JPEG that carries a rotation tag is straightened before use.",
    },
    {
      question: "Can I watermark in a language other than English?",
      answer: "Yes. Latin text is drawn as real bold Helvetica text. Hindi, Marathi and Nepali are embedded as real text with a font from this site, and other scripts, such as Chinese or Arabic, are drawn by your browser and placed as a picture.",
    },
    {
      question: "What does Tiled do?",
      answer: "It repeats the text or logo across each page in a grid, so a crop or partial photo still shows part of the mark. Text repeats with a gap based on its size, and logos are laid out in rows that are offset from one another.",
    },
    {
      question: "Does a watermark stop people copying the text?",
      answer: "No. The page text stays selectable and the file stays editable; the stamp only labels it. To require a password before the file opens, use PDF Password.",
    },
    {
      question: "Why did my long text come out smaller than the size I chose?",
      answer: "For single positions, the tool measures the turned text and reduces its size if it would not fit inside the smallest page in the document, leaving about 40 points of room at each edge. The Size label says long text shrinks to fit.",
    },
  ],
  "extract-pdf": [
    {
      question: "Can I extract plain text from a scanned PDF?",
      answer: "Not directly — text extraction only works on PDFs with real, selectable text, and a scanned document has no underlying text layer to pull from. Run it through OCR PDF first to add a real, searchable text layer, then extract from that result.",
    },
    {
      question: "What's the difference between extracting pages and extracting text?",
      answer: "Extracting pages pulls specific pages out as a new, smaller PDF — still a real PDF document. Extracting text pulls the underlying selectable text content out as a plain text file, with all formatting stripped.",
    },
    {
      question: "What's the difference between this and Split PDF?",
      answer: "Extract is built around a single deliverable: one set of pages picked out, or the text content. Split PDF is for when you need several separate output files from ranges (or want to split into equal-sized chunks) — if you're producing multiple files, Split is the more direct tool; if you just need one set of pages or the text, use Extract.",
    },
    {
      question: "Can I click on pages to select them instead of typing page numbers?",
      answer: "Yes — click any page thumbnail to select or deselect it, and the selection stays in sync with the text field either way. Clicking is usually faster for a handful of scattered pages; typing a range (like \"3-8\") is faster for a large contiguous block.",
    },
    {
      question: "Does the order I select pages in matter?",
      answer: "No — extracted pages always come out in their original document order, regardless of the order you clicked or typed them in. If you need pages in a different order than the source document, use Organize PDF after extracting.",
    },
    {
      question: "Are my files uploaded anywhere?",
      answer: "No — both page and text extraction run entirely in your browser, including generating the page thumbnails. Your PDF never leaves your device.",
    },
    {
      question: "Will text extraction preserve formatting like bold or bullet points?",
      answer: "No — the output is plain text with all formatting, fonts, and layout stripped. If you need to preserve formatting, PDF to Word is a better fit for getting an editable document out of a PDF.",
    },
    {
      question: "What happens if I don't select any pages?",
      answer: "The Extract Pages button stays available but produces an error if you try to run it with nothing selected — select at least one page (by clicking a thumbnail or typing a page number) before extracting.",
    },
    {
      question: "Can I choose pages that are not next to each other?",
      answer: "Yes. Type a list separated by commas or semicolons, with ranges, such as 1, 3-5, 9, or click individual thumbnails. A trailing dash such as 5- means page 5 through to the last page.",
    },
    {
      question: "Does the extracted PDF keep bookmarks and form fields?",
      answer: "Bookmarks that lead to the kept pages are carried across, and so are the form definitions for fields on those pages, so fillable forms stay fillable. Bookmarks that point to pages you dropped are not included.",
    },
    {
      question: "What does the text file look like?",
      answer: "It is plain UTF-8 text. Each selected page begins with a marker line, --- Page N ---, then paragraphs separated by blank lines. Table rows use tabs between cells, a two-column page is read down the first column and then the second, and running headers and page numbers are kept.",
    },
    {
      question: "Why do I get a scanned-image message in Extract Text mode?",
      answer: "None of the selected pages had selectable text, which usually means each page is a picture. The tool says so and links to OCR PDF. Run that first to add a text layer, then extract the text from the result.",
    },
    {
      question: "How is the output file named?",
      answer: "Pages mode uses your file name, then -pages- and the lowest and highest selected page, such as report-pages-3-7.pdf. Selecting 1, 5 and 9 gives 1-9 in the name even though only three pages are inside. Text mode ends in -text.txt.",
    },
  ],
  "organize-pdf": [
    {
      question: "Does reordering pages change their actual content?",
      answer: "No — this only changes page order and which pages are included, using real page thumbnails so you can see exactly what you're changing. It doesn't alter the content of any individual page.",
    },
    {
      question: "Can I undo removing a page by mistake?",
      answer: "Yes — removed pages can be brought back with Undo Remove at any point before you download the final file.",
    },
    {
      question: "Can I drag pages to reorder them, or only use the arrow buttons?",
      answer: "Both — drag any page thumbnail to its new position directly, or use the up/down arrow buttons on each page if you'd rather not drag (useful on a touchpad, with a screen reader, or if you just prefer clicking).",
    },
    {
      question: "What's the difference between this and Merge PDF?",
      answer: "Merge PDF combines separate files into one document. Organize PDF works on the pages within a single PDF you already have — reordering them, or removing ones you don't need — without adding any other file into the mix. If you need to combine files first, start with Merge, then use Organize on the result if the page order still needs adjusting.",
    },
    {
      question: "Can I pull specific pages out as their own separate file instead of just reordering?",
      answer: "Not directly here — Organize is for adjusting order and removing pages within one output file. If you need specific pages as their own separate file, Extract Pages & Text or Split PDF are the more direct tools for that.",
    },
    {
      question: "Will this work on a password-protected PDF?",
      answer: "Yes. If a PDF needs a password to open, you'll be asked for it as soon as you add the file — type it in and the file is unlocked right in your browser (it's never uploaded). The organized file won't have a password.",
    },
    {
      question: "Are my files uploaded anywhere?",
      answer: "No — this runs entirely in your browser, including generating the page thumbnails you see. Your PDF and its contents never leave your device.",
    },
    {
      question: "What happens if I remove every page?",
      answer: "You can't save a PDF with zero pages — Save Changes is disabled once every page has been removed, and you'll need to bring at least one back with Undo Remove before you can continue.",
    },
    {
      question: "Does reordering change what is on the pages?",
      answer: "No. Pages are copied across as they are, so only order, inclusion, extra copies, added blanks and any rotation you request differ. Bookmarks follow their pages, a bookmark for a removed page is dropped, and fillable form fields remain fillable.",
    },
    {
      question: "What happens to form fields when I duplicate a page?",
      answer: "The first copy keeps its field names, and each further copy gets its fields renamed, with a number added, so the copies do not share one value or clash. That keeps a duplicated fillable page usable in the saved file.",
    },
    {
      question: "How does the rotate icon work here?",
      answer: "Each press adds 90 degrees clockwise to the page's existing rotation and the thumbnail turns to show it. It is a display setting, not a redraw. For angles that are not quarter turns, use Rotate PDF.",
    },
    {
      question: "Why is Save changes greyed out?",
      answer: "It stays disabled until something differs from the original: a new order, a removed, duplicated or blank page, or a rotation. The header says no changes yet until then. It also needs at least one page left in the list.",
    },
  ],
  "add-page-numbers": [
    {
      question: "Can I start numbering from something other than page 1?",
      answer: "Yes — you can set any starting number, which is useful when this document will be bound together with others and needs to continue their numbering rather than restarting at 1.",
    },
    {
      question: "Where can the page numbers be placed?",
      answer: "You choose the position (bottom center and bottom right are the most common conventions) before generating the numbered file, from six options covering the top and bottom of the page in left, center, or right alignment.",
    },
    {
      question: "Can I show \"Page 1 of 10\" instead of just \"1\"?",
      answer: "Yes — choose the \"Page X of Y\" format, or a custom prefix format if you want something else (like \"p. 1\" or a section label) before the number.",
    },
    {
      question: "Can I skip numbering the first page, like a cover or title page?",
      answer: "Yes — check \"Skip the first page,\" and numbering starts from the next page instead, still beginning at your chosen starting number rather than skipping a number for the unnumbered cover.",
    },
    {
      question: "Can I number only some pages, not the whole document?",
      answer: "Yes — enter a page range (like \"2-10\") and only those pages get a number; everything outside the range is left untouched. This is separate from — and takes priority over — the skip-first-page option, since a range can express any subset a single \"skip the first page\" checkbox can't.",
    },
    {
      question: "Does this support Bates numbering for legal documents?",
      answer: "No — this adds simple sequential page numbers, not the more specialized Bates numbering format (with fixed-width numbers, document codes, and prefixes) some legal document management systems require. If you need genuine Bates numbering specifically, a dedicated legal document tool built around that standard is the right choice.",
    },
    {
      question: "Are my files uploaded to a server?",
      answer: "No — numbering runs entirely in your browser. Your PDF never leaves your device.",
    },
    {
      question: "What should I do after numbering if the layout needs to stay locked?",
      answer: "Use Flatten PDF next to lock the final layout in place, then Compress PDF if the file needs to be smaller for sharing — numbering first, then flattening, then compressing is the order that keeps each step's result accurate for the next.",
    },
    {
      question: "Why does my second page show 1 when I skip the first page?",
      answer: "Numbering counts only the pages that actually receive a number, so the first numbered page gets your Start at value, just as in a book with an unnumbered title page. To have page two labelled 2, set Start at to 2 while skipping the first.",
    },
    {
      question: "What does the Total pages box do?",
      answer: "It appears only for the Page 1 of 10 format and replaces the automatic total after the word of. Left empty, the total is the start value plus the number of numbered pages, minus one. It is shown in the same number style as the page numbers.",
    },
    {
      question: "Will numbers sit upright on rotated or cropped pages?",
      answer: "Yes. The position is worked out from the page as you see it, taking any stored rotation and visible crop area into account, so a scan carrying a rotation setting still gets its number along the edge you chose.",
    },
    {
      question: "Can I use letters or Roman numerals?",
      answer: "Yes, through Number style: lowercase or uppercase Roman, or lowercase or uppercase letters. Letters carry on past z as aa, ab and so on. Start at must be a whole number of 1 or more, which is then converted to the chosen style.",
    },
  ],
  "ocr-pdf": [
    {
      question: "Does OCR work on handwritten text?",
      answer: "OCR is built for printed, typed text — handwriting recognition is a fundamentally different, much less reliable technology, so results on handwritten pages should be checked carefully rather than trusted outright.",
    },
    {
      question: "Why does OCR take longer than a normal PDF operation?",
      answer: "Each scanned page has to actually be read visually to recognize the text, rather than just having existing text copied out — that visual analysis is inherently slower than working with a PDF that already has real, selectable text.",
    },
    {
      question: "What's the difference between this, OCR Tool, and PDF to Word?",
      answer: "This tool keeps the result a PDF — it adds an invisible, searchable text layer while the page looks exactly the same. OCR Tool pulls the recognized text out as its own separate output (plain text or a new document), not a modified version of the original PDF. PDF to Word converts the whole document into an editable Word file. Use this one when you want to keep the PDF format but make it searchable; use the others when you need the text itself or an editable document instead.",
    },
    {
      question: "Will this change how my PDF looks?",
      answer: "No — the output looks pixel-identical to the original. The recognized text is added as an invisible layer positioned to match each word's real location, underneath the visible scan; nothing about the visual appearance changes.",
    },
    {
      question: "Can I stop it partway through a long document?",
      answer: "Yes — a Stop option appears while processing, useful for a long, multi-page scanned document where you'd rather cancel and try a smaller file than wait for every page.",
    },
    {
      question: "Are my files uploaded to a server?",
      answer: "No — OCR runs entirely in your browser, including the visual text recognition itself. Your PDF never leaves your device.",
    },
    {
      question: "What happens if my PDF already has real text on every page?",
      answer: "Nothing gets changed — every page passes through untouched, and you'll see a message confirming no page needed OCR, rather than the tool silently doing unnecessary work on a document that's already searchable.",
    },
    {
      question: "What languages does the OCR support?",
      answer: "English is currently supported. If your scanned document is in another language, results may be unreliable or garbled — check the output carefully before relying on it.",
    },
    {
      question: "Will the PDF look different afterwards?",
      answer: "No. The original page images are kept exactly as they were. The recognised text is added on top as an invisible layer drawn with zero opacity, which is why selecting, searching and copying start to work while the page appearance stays the same.",
    },
    {
      question: "Can I get the text as a plain text or Word file?",
      answer: "Not from this tool. Its output is always a PDF with a hidden text layer. You can select and copy text from the result, or use the OCR Tool for separate text output or PDF to Word for an editable document.",
    },
    {
      question: "Why is the first run slower than later ones?",
      answer: "On first use the recognition engine and the language data have to be downloaded before any page is read, which needs an internet connection. If the engine cannot start, you get a message asking you to check your connection and try again.",
    },
    {
      question: "Why are some words wrong or in a strange order?",
      answer: "Accuracy follows scan quality. When a first reading looks poor, with fragmented words, wrong-script characters or low confidence, the tool reads the page again as one block of text and keeps the better result, but blurry or skewed pages can still come out badly.",
    },
  ],
  "html-to-pdf": [
    {
      question: "Can this convert a live webpage by just pasting in its URL?",
      answer: "No — this renders HTML content you paste in or drop as a local file, directly. Most sites block cross-origin access to their content from a browser-based tool for security reasons, so fetching an arbitrary live webpage by URL isn't supported. Most browsers can already save a specific webpage as a PDF through the Print dialog's \"Save as PDF\" option.",
    },
    {
      question: "Will external images in my HTML show up in the PDF?",
      answer: "Images referenced by URL may not appear if their source blocks cross-origin loading — images embedded directly as data URLs always work correctly.",
    },
    {
      question: "Can I drop a local .html file instead of pasting the code?",
      answer: "Yes — drop a .html file and its content loads directly into the text box, the same as if you'd pasted it. It's read locally in your browser, not uploaded anywhere.",
    },
    {
      question: "Does this support CSS styling, or just plain text?",
      answer: "Yes — inline styles and <style> tags in your HTML are both rendered, so real visual styling (colors, fonts, layout) carries through to the PDF, not just plain unstyled text.",
    },
    {
      question: "What happens with content that's longer than one page?",
      answer: "It automatically splits across multiple pages, using the page size you choose (A4, Letter or Legal) for the page breaks — long content isn't cut off or forced onto a single oversized page.",
    },
    {
      question: "Are my files uploaded to a server?",
      answer: "No — rendering and converting both happen entirely in your browser. Neither your HTML nor a dropped file is ever uploaded anywhere.",
    },
    {
      question: "Can JavaScript in my HTML run and affect the output?",
      answer: "No. The HTML is drawn in your browser inside a frame that blocks scripts, so JavaScript never runs and content a script would create does not appear. Only the static markup and its styles are converted to the PDF.",
    },
    {
      question: "Why would I use this instead of my browser's own Print to PDF?",
      answer: "Print to PDF is the right choice for converting a webpage you're already viewing. This tool is for HTML you're generating, editing, or don't have loaded as a live page — pasting a code snippet, a saved .html file, or HTML produced by another tool, without needing to open it as an actual page first.",
    },
    {
      question: "Can I type a website address and get a PDF of that page?",
      answer: "No. The tool converts HTML that you paste or drop, and it does not fetch live pages, because most sites block that kind of cross-origin access from a browser. Use your browser's Print dialog and choose Save as PDF for a page you have open.",
    },
    {
      question: "Why is one of my images missing?",
      answer: "A picture linked from another site may refuse to load into the render area. When an image fails, it is replaced with a blank space so the rest of the page still converts. Images embedded as data URLs always work.",
    },
    {
      question: "Will the text and links in the PDF work?",
      answer: "Text is selectable through an invisible layer for words made of Western European characters, and links to http, https and mailto addresses are clickable. Other scripts appear in the page picture but cannot be searched or copied.",
    },
    {
      question: "How does it choose where pages break?",
      answer: "Table rows, images, list items, paragraphs, headings, code blocks, quotes and figures are kept whole where possible instead of being sliced. You can also force a break by giving an element a page-break-before or break-before value of page or always.",
    },
    {
      question: "Does JavaScript in my HTML run?",
      answer: "No. The HTML is drawn in a frame that blocks scripts, so content a script would create does not appear. Only the static markup and its styles are converted, and a document longer than 200 pages must be split into parts.",
    },
  ],
  "fillable-forms": [
    {
      question: "Can I add fillable fields to a PDF that doesn't already have them?",
      answer: "No — this fills in a PDF's existing fillable form fields, it doesn't add new ones to a plain document. Most scanned forms and \"print and fill by hand\" PDFs don't have real fillable fields to begin with.",
    },
    {
      question: "What does \"flatten\" do, and can I undo it?",
      answer: "Flattening makes your filled-in answers a permanent part of the page instead of still-editable form fields. It's one-way — once you download a flattened PDF, the values can't be edited as fields again in that specific copy.",
    },
    {
      question: "Does it overwrite my original PDF?",
      answer: "No. It reads your file, applies your answers to an in-memory copy and offers that as a new download named filled_ followed by the original filename. The file on your device is not changed.",
    },
    {
      question: "Why does it say my PDF has no fields?",
      answer: "Either the file really has no fillable fields, as with scans and print-and-handwrite forms, it is an XFA form, or its only fields are signatures or buttons. The warning message tells you which case applies and suggests PDF Editor or Sign PDF.",
    },
    {
      question: "Can it open a password-protected PDF?",
      answer: "No. Loading is attempted without a password, and a protected or corrupted file produces a message that it could not be read. Remove the password with a PDF tool first, then bring the unlocked copy here.",
    },
    {
      question: "What if my text is too long or in another language?",
      answer: "A field with a character limit shows the limit, and text over it is reported as not filled while every other answer is still saved. Answers in other scripts work: characters the standard font cannot draw are placed as pictures.",
    },
  ],
  "compare-pdf": [
    {
      question: "Does this show exactly what changed between two versions of a document?",
      answer: "Yes — it's built specifically to surface the real differences between two versions, which matters most for exactly the kind of document (a contract, a financial statement) where an unnoticed change has real consequences.",
    },
    {
      question: "Does this compare the visual appearance of the pages, or just the text?",
      answer: "Just the text — this compares the real, underlying text content of both PDFs, not a pixel-level visual rendering. A purely visual difference (a font change, a color change, an image shifted slightly) with no actual text change behind it won't be flagged. If visual layout differences matter as much as wording, that's not what this catches.",
    },
    {
      question: "Can I view the differences side by side instead of one interleaved list?",
      answer: "Yes — switch to Side by Side view to see the original and revised text in separate columns, with changed lines aligned next to each other, rather than a single unified list with + and - markers.",
    },
    {
      question: "Will this work on scanned PDFs?",
      answer: "Only if they already have a real text layer — a pure scan with no selectable text has nothing for this tool to compare. Run it through OCR PDF first to add a real, searchable text layer, then compare the OCR'd versions.",
    },
    {
      question: "What's the difference between this and Redact PDF?",
      answer: "Compare shows you what changed between two versions — it doesn't modify either file. Redact permanently removes sensitive content from a single PDF. A common real sequence: compare two versions to spot something that shouldn't be shared, then redact it from the file before sending.",
    },
    {
      question: "Can I download a report of the differences?",
      answer: "Yes — Download Report saves a plain text file with every added and removed line marked, so you have a record of the comparison outside the browser tab.",
    },
    {
      question: "Are my files uploaded to a server?",
      answer: "No — comparison runs entirely in your browser, using the same diff engine as the Diff Checker tool. Neither PDF ever leaves your device.",
    },
    {
      question: "Is there a limit to how large a document this can compare?",
      answer: "Yes — if either PDF has more than a few thousand lines of text, comparison is skipped rather than risking your browser becoming unresponsive on a very large document. For a document that size, comparing smaller sections separately is a reasonable workaround.",
    },
    {
      question: "Does it compare how the pages look?",
      answer: "No. It compares text only. A changed font, colour, logo or image position with identical wording is reported as no change, while wording changes, additions and removals are found, with the exact words marked inside edited paragraphs.",
    },
    {
      question: "Why is a reflowed document not shown as changed?",
      answer: "Lines are joined into paragraphs using the gaps between them, hyphenated line ends are rejoined, and a paragraph continuing over a page break is merged. Different line wrapping or page breaks with the same wording therefore count as identical.",
    },
    {
      question: "What is left out of the comparison?",
      answer: "Page numbers, and short lines repeated in the top or bottom band of at least two pages, which are treated as running headers and footers, are dropped. Pages with no text are also skipped, and they are listed so you know.",
    },
    {
      question: "Why does it say my file looks scanned?",
      answer: "One of the PDFs produced no text at all, usually because its pages are images. The message names which file, original or revised, and links to OCR PDF. Add a text layer there, then compare the converted versions.",
    },
    {
      question: "What do the downloadable reports contain?",
      answer: "A PDF report and a plain text report, both named pdf-comparison-report, built from the results on screen: the two file names, their page counts, the change total and the list of changes. They are generated on your device when you press the button.",
    },
  ],
  "repair-pdf": [
    {
      question: "Can this recover a PDF that's genuinely missing data, not just corrupted?",
      answer: "No — it fixes structural corruption, like broken cross-reference tables or malformed objects from an interrupted save or a bad file transfer. It can't recover content that was never actually written to the file, like a truncated download.",
    },
    {
      question: "What happens if the file can't be repaired at all?",
      answer: "You'll get a clear message saying so, rather than a broken or incomplete download that looks like it worked but didn't. A few other tools (PDF to Word, PDF to JPG) use different parsing paths and sometimes succeed at pulling out at least some content even when a full structural repair can't — worth trying if the file matters.",
    },
    {
      question: "How do I know the repair actually recovered everything?",
      answer: "Check the reported page count against what you expect, and open the repaired file to confirm the content looks right — recovery is best-effort, not a guarantee. A structurally broken file can sometimes only be partially recovered even when the tool reports success.",
    },
    {
      question: "What kinds of PDF damage does this actually fix?",
      answer: "Broken cross-reference tables, malformed PDF objects, and similar structural issues — the kind of damage that typically comes from an interrupted save, a bad file transfer, or a buggy program that wrote the file incorrectly. It parses the file in a lenient recovery mode that tolerates this kind of damage, then rebuilds a clean, valid PDF from what it can read.",
    },
    {
      question: "Are my files uploaded to a server?",
      answer: "No — the entire repair attempt runs in your browser. Your damaged PDF never leaves your device.",
    },
    {
      question: "Will the repaired file look exactly the same as it should have?",
      answer: "Usually, if the damage was purely structural — the actual page content is unaffected by cross-reference or object-table corruption, so a successful repair typically looks identical to what the file was meant to be. If content itself was genuinely lost (not just the structure around it), that content is gone regardless of how the file is repaired.",
    },
    {
      question: "Does a PDF that opens fine in my browser still need repairing?",
      answer: "Not necessarily — browsers and some PDF readers are more lenient about minor structural issues than others. If a file opens and displays correctly everywhere you've tried, there's likely nothing to fix. Repair matters most when a file fails to open, shows an error, or displays incorrectly in a specific program.",
    },
    {
      question: "What should I do once the file is successfully repaired?",
      answer: "If it needs to be protected before sharing, PDF Password works on the repaired file the same as any other PDF — repair itself doesn't add or remove any password protection.",
    },
    {
      question: "What kinds of damage can it fix?",
      answer: "Structural problems such as a wrong or missing cross-reference index, junk before the header, a missing end marker, or a file cut short by an interrupted download. It rebuilds the document from whatever complete pages are still readable.",
    },
    {
      question: "What does Recovered 4 of 9 pages mean?",
      answer: "The file declared nine pages but only four were still complete, usually because it was truncated. The missing pages were never fully written, so they cannot be restored. The tool flags the file as cut off in that case.",
    },
    {
      question: "Will the repaired file match the original exactly?",
      answer: "Not necessarily. Recovery is best effort, and the page notes ask you to confirm the content is what you expect. Pages with a missing font, image or content stream are left out of a scan-based rebuild.",
    },
    {
      question: "Why was my file rejected as not a PDF?",
      answer: "The first bytes were checked and did not look like a PDF header. The message names what the file seems to be, such as a Word, Excel or PowerPoint file, a JPEG, PNG or GIF picture, or an older Office file.",
    },
    {
      question: "What can I do if repair fails?",
      answer: "You get a message saying no complete page could be found. Other tools read files differently, so the page offers links to PDF to Word and PDF to JPG, which can sometimes still pull out part of the content.",
    },
  ],
  "crop-pdf": [
    {
      question: "Does the crop apply to every page, or can I choose?",
      answer: "You choose — apply the same crop to All pages, just This page (the one you're previewing), or a specific page range. All pages remains the default for the common case of trimming a consistent margin throughout a document.",
    },
    {
      question: "What if my pages aren't all sized or positioned the same way?",
      answer: "The box you draw is stored as a share of the page, so the same proportions are applied to each page you chose, whatever that page's own size (the preview shows the page you are on, and the box is not tied to the first page). Where content sits in a different place from page to page (common in scans photographed slightly differently each time), the same proportions can cut some of it off. Before cropping, the tool compares each page's content with your box and lists any page it would cut, so you can widen the box or crop only a page range where the same region genuinely applies.",
    },
    {
      question: "What does Auto-trim actually do?",
      answer: "It scans every page your crop applies to (just the page you are previewing when This page is chosen, or the pages in your range) for where the real content starts and ends — treating anything close to white as background — and selects one area that holds the content of all of them, as a starting point you can still adjust by dragging afterward. It works well on a clean white background with margins; it's less reliable on a page with a colored or textured background, since it can't tell background from content as clearly.",
    },
    {
      question: "Can I crop just a header or footer strip off every page?",
      answer: "Yes — drag a selection covering everything except the header/footer strip you want removed, then apply to All pages. The crop keeps whatever's inside your selected region and hides the rest. The hidden strip is still stored in the file, so for anything sensitive use Redact PDF (/tool/redact-pdf) instead.",
    },
    {
      question: "Are my files uploaded to a server?",
      answer: "No — cropping, including Auto-trim's content detection, runs entirely in your browser. Your PDF never leaves your device.",
    },
    {
      question: "Can I undo a crop after downloading?",
      answer: "No — once applied and downloaded, the crop is permanent in that file. Keep your original, uncropped file if you might need the trimmed content again.",
    },
    {
      question: "What's the difference between this and Redact PDF?",
      answer: "Crop only changes each page's visible boundaries (its CropBox). It hides what is outside the box but does not delete it, and software that ignores crop boxes can still get at it. Redact PDF (/tool/redact-pdf) genuinely removes specific content from a page. If the goal is deleting sensitive information, not just trimming margins, use Redact PDF instead.",
    },
    {
      question: "What should I do after cropping if the file needs to be smaller or the orientation is off?",
      answer: "Use Compress PDF to shrink the file for sharing, or Rotate PDF if a page's orientation also needs fixing — either works fine on the cropped result.",
    },
    {
      question: "Is the cropped content deleted?",
      answer: "No. The tool only sets a CropBox on each chosen page, which tells viewers what part to show. The rest of the page content is still in the file and can be recovered by software that ignores crop boxes, so cropping is not a way to remove sensitive material. For permanent removal use Redact PDF (/tool/redact-pdf).",
    },
    {
      question: "How does Auto-trim decide where the content is?",
      answer: "It renders each target page at reduced size, treats pixels darker than near-white as content, finds the outermost content on all four sides, and adds padding around it. The Padding menu next to Auto-trim sets how much (none, 2%, 5% or 10% of the page; 5% by default). The default leaves room at the bottom-right for the small trial mark. For several pages it combines the results into one box. Blank pages are skipped.",
    },
    {
      question: "What does the cut-off warning mean?",
      answer: "Before cropping, each page's detected content area is compared with your box. If content reaches outside it by more than a hairline, those pages are listed. Widen the box to fit includes them, Crop anyway proceeds, and Cancel lets you keep editing.",
    },
    {
      question: "Can I crop each page differently?",
      answer: "Not in one pass. One box applies to every page in the chosen scope. Run the tool again with This page or a Page range for each different region, feeding the downloaded file back in for the next one.",
    },
    {
      question: "Will rotated pages be cropped where I drew the box?",
      answer: "Yes. The box is drawn on the page as displayed, and the tool converts it to the page's stored coordinates using its rotation and any existing crop area, so a rotated scan is cropped where you drew.",
    },
  ],
  "pdf-editor": [
    {
      question: "Can I add my own text, shapes, and annotations to any PDF?",
      answer: "Yes — add text, freehand drawing, rectangle and ellipse shapes, and highlights directly onto the real page, with a live preview showing exactly where each addition will land before you download.",
    },
    {
      question: "Can this rewrite or edit the PDF's existing text?",
      answer: "No — this adds new content on top of the page (annotations), it doesn't rewrite or reflow the document's own existing body text. If you need to actually edit the underlying text content, PDF to Word converts the PDF into an editable Word document instead.",
    },
    {
      question: "What's the difference between this and Sign PDF?",
      answer: "This is for general annotation — text, shapes, highlights, freehand marks anywhere on the page. Sign PDF is specifically built around placing a signature (drawn, typed, or imported). If you're adding a signature specifically, Sign PDF's dedicated signature tools are more direct than drawing one freehand here.",
    },
    {
      question: "What's the difference between this and Redact PDF?",
      answer: "This adds visible content on top of a page — it doesn't remove anything underneath. Redact genuinely deletes content from the document. A highlight or box drawn here doesn't hide or remove whatever's beneath it in the underlying file the way a real redaction does.",
    },
    {
      question: "Can I undo a mistake before downloading?",
      answer: "Yes — every annotation has its own remove (X) button, and Clear All removes everything at once. Nothing is permanent until you click Apply & Download.",
    },
    {
      question: "Are my files uploaded to a server?",
      answer: "No — everything, including rendering the page previews you see, happens entirely in your browser. Your PDF never leaves your device.",
    },
    {
      question: "Can I fill in an existing fillable form with this?",
      answer: "You can, by placing text boxes over the form's fields, but if the PDF already has real fillable form fields, using those directly (rather than manually placed text) gives cleaner, better-aligned results — check for real form fields first before resorting to manual placement.",
    },
    {
      question: "Can I change the color or size of shapes and text?",
      answer: "Not currently — text, shapes, and freehand strokes use fixed default styling. If you need specific colors or sizes for a particular use case, that level of customization isn't available here yet.",
    },
    {
      question: "Can I change text that is already on the page?",
      answer: "Yes, with Edit text. Click a line, change or clear it, and the new words are drawn in a standard font over a white patch that covers the old line. The old words remain in the file underneath. It will not work on scanned pages.",
    },
    {
      question: "What happens to my original file?",
      answer: "It is left alone. The tool reads it, draws your additions into a copy and gives you that copy as a download named edited_ followed by the original filename. You can edit again by reopening the original.",
    },
    {
      question: "Can I work across several pages in one go?",
      answer: "Yes. Items are remembered per page as you move with the arrows, and a counter shows how many have been added in total. Pressing 'Apply & Download' writes all of them into the file together.",
    },
    {
      question: "Can I resize or rotate an item after placing it?",
      answer: "No resizing or rotating is offered. Select lets you drag an item to a new position, change its colour, text size, bold, line width or tint, or remove it. To change a shape's size, delete it and draw it again.",
    },
    {
      question: "Does it work on a phone or tablet?",
      answer: "The drawing and moving modes respond to touch, with page scrolling suppressed during a drag. The page preview is a fixed-width picture, so a larger screen makes precise placing, small shapes and long edits noticeably easier.",
    },
  ],
  "redact-pdf": [
    {
      question: "Can I still select and search the text on a redacted page?",
      answer: "Yes. Only what lies under your boxes is removed; the rest of the page keeps its real text, fonts, links and bookmarks. The one exception is a page that cannot be edited exactly, which is turned into a picture of itself with the boxes painted black. The notice under the result names any such page and why, and its text is then no longer selectable or searchable. Run OCR PDF on the result if you need it searchable again.",
    },
    {
      question: "When is a page turned into a picture, and how will I know?",
      answer: "Only when the content under a box cannot be cut out exactly: a curved or irregular drawing that sticks out of the box, a gradient fill, an unusual font encoding or writing direction, a picture in a format the tool cannot read, or a picture with a stencil mask. The result then lists each such page with its reason in a red notice, and the message under the Download button says how many pages were affected. If no notice appears, no page was turned into a picture.",
    },
    {
      question: "What does Select all detectors do?",
      answer: "It ticks every automatic detector at once (email addresses, phone numbers, card numbers, SSN-style numbers, EIN-style numbers and likely names). Click it again to clear them. Likely names is a best guess, so review every mark before you apply.",
    },
    {
      question: "Does redacting actually remove the content, or just hide it visually?",
      answer: "It permanently removes the underlying content in the redacted area, not just draws a black box over it visually — an important distinction, since a visual-only cover can sometimes still be selected or extracted from the original data underneath.",
    },
    {
      question: "Can I search for text instead of manually drawing every box?",
      answer: "Yes — type what you're looking for (a name, account number, or any other text) and click Find & Mark to automatically mark every place it appears across the document. Review the marks before applying — search finds exact matches within individual text runs, so a name split across two separate runs (an unusual line wrap, for instance) may not be caught, and manual marking is still worth double-checking for anything critical.",
    },
    {
      question: "What's the difference between this and Watermark?",
      answer: "This genuinely removes content from the document. Watermark is purely visual — it stamps text or a logo on top of the page without removing or hiding anything underneath, and the original content stays fully present and extractable. If the goal is actually deleting sensitive information, use Redact; if the goal is labeling a document (draft, confidential, branded), use Watermark.",
    },
    {
      question: "Does this also remove hidden metadata, like the document's author name?",
      answer: "Only if you check \"Also clear document metadata\" before applying — that clears title, author, subject, keywords, and producer/creator fields, since a PDF's metadata can independently reveal who created it even after the visible content is redacted. It's off by default since not every redaction needs it.",
    },
    {
      question: "Will pages I don't mark be affected at all?",
      answer: "No — pages with no redaction marks are not touched. On a marked page only the content under your boxes changes; a page is turned into a picture only if it cannot be edited exactly, and the tool says so.",
    },
    {
      question: "Are my files uploaded to a server?",
      answer: "No — both marking and applying redactions, including text search, run entirely in your browser. Your PDF never leaves your device.",
    },
    {
      question: "Can I undo a redaction after downloading?",
      answer: "No — once applied and downloaded, the redacted content is genuinely gone from that file, not recoverable. Keep your original, unredacted file if you might need the removed content again for a different purpose.",
    },
    {
      question: "What should I do after redacting if the document needs to stay restricted?",
      answer: "Use PDF Password next to require a password to open the redacted file — redaction removes specific content, but doesn't by itself restrict who can open the resulting document.",
    },
    {
      question: "Is the text under a black box really deleted?",
      answer: "Yes. Every character whose box touches a redaction box is deleted from the page's drawing instructions, including invisible text, and the letters after it keep their place. A picture that overlaps a box has the pixels under the box painted black and is stored again, so the original pixels are not in the file; a picture is re-saved in the same way even where only a corner of it is covered, so a JPEG there is re-encoded. Lines and rectangles are cut to what lies outside the box, and links, notes and form fields under a box are removed along with their values. Page thumbnails and the alternative-text labels of a tagged PDF are dropped from marked pages, and the old versions of changed content are deleted from the file before it is saved. Text on a page you did not mark is not touched, so mark every page that needs it.",
    },
    {
      question: "What do the comment and metadata checkboxes remove?",
      answer: "The first deletes every comment-style annotation on every page, plus links and form values that contain your searched text and bookmarks whose titles match; the outline is rewritten only if a bookmark had to go. Clearing metadata empties every entry of the document information (title, author, subject, keywords, creator and any custom ones) and drops the XMP metadata.",
    },
    {
      question: "What do the pattern checkboxes match?",
      answer: "Email addresses, ten-digit phone numbers with an optional country code, card-style runs of 13 to 19 digits with optional spaces or dashes, and SSN-style numbers written like 123-45-6789. They match formats only, so other long digit runs can be marked too; review the boxes.",
    },
    {
      question: "Why does search find nothing in my scanned PDF?",
      answer: "Search reads the page's text layer, and a scan is just a picture. If no page has text, the tool says so and suggests drawing boxes by hand or running OCR PDF first. In a mixed file only the pages with text can be searched.",
    },
    {
      question: "What does an empty marked area warning mean?",
      answer: "When none of a page's boxes had any text, picture, drawing or form field under it, the tool warns that nothing was found under the marked areas on that page, a hint that they may sit in the wrong place. The black bar is still drawn. On a page that became a picture the check looks for non-white pixels and names the number of empty boxes instead.",
    },
  ],
  "sign-pdf": [
    {
      question: "Is this a legally certified digital signature?",
      answer: "No — this embeds a drawn image of your signature into the page, which is appropriate for most everyday agreements. Documents requiring a certified digital signature with cryptographic verification (some legal and government filings) need a different, more formal signing process.",
    },
    {
      question: "How does the other person actually sign their part?",
      answer: "Fields marked for the other party are a labeled placeholder, not a fillable box — send them both the downloaded file and the Sign PDF link shown on screen. They open the same file here and get the same real signature tools (draw, type, or import) to fill in just their marked spots, then download their own completed copy.",
    },
    {
      question: "What's the difference between this and a PDF editor?",
      answer: "This tool is built specifically around placing signatures — draw, type, or import one, position it, done. A PDF editor is for broader changes to a document's own text or images. If you need to edit content beyond adding a signature, a PDF editor is the more direct tool.",
    },
    {
      question: "Can I lock the document so the signature can't be moved or removed after signing?",
      answer: "Not directly in this tool — once you're done placing all signatures and any edits are final, Flatten PDF converts everything, including signature fields, into permanent, non-editable page content, which is the more direct way to make a signed document tamper-evident against casual editing.",
    },
    {
      question: "Is my signature image uploaded anywhere?",
      answer: "No — whether you draw, type, or import a signature, everything happens entirely in your browser. The signature and the PDF itself are never uploaded to a server; the finished, signed PDF is generated and downloaded locally.",
    },
    {
      question: "Can I save my signature to reuse it next time?",
      answer: "Not automatically between sessions right now — each visit starts fresh. If you have a signature image saved on your device already (a photo of your real signature, for example), importing that same file each time is the closest equivalent to a reusable saved signature.",
    },
    {
      question: "What signature methods are available?",
      answer: "Three: draw it with your mouse, trackpad, or finger on a touchscreen; type your name and have it rendered in a signature-style script font; or import an existing signature image you already have saved.",
    },
    {
      question: "Does this work for documents that need multiple people to sign?",
      answer: "Yes — assign fields to different parties, and each person opens the same file in this tool to fill in just their own marked spots with the same real signing options, rather than a plain unverified text box.",
    },
    {
      question: "Do I need to upload a PDF before I can see how this works?",
      answer: "No — the name, initials, signature setup, and field options are all visible and usable as soon as the page loads. Uploading a PDF is only needed for the last step: placing those fields on the actual document and downloading the signed result.",
    },
  ],
  "book-writing-toolbox": [
    {
      question: "Which languages and scripts can the book and manuscript PDFs print?",
      answer:
        "Latin (all accented letters, including Polish, Czech, Turkish, Romanian and Vietnamese), Greek (including polytonic), Cyrillic, Hebrew, Arabic, Devanagari (Hindi, Marathi, Nepali) and Thai. Hebrew and Arabic are set right to left. Chinese, Japanese, Korean, Bengali, Tamil, Telugu, Gujarati, Punjabi, Kannada, Malayalam, Sinhala, Lao, Khmer, Burmese, Georgian, Armenian and Ethiopic are not available in PDFs yet. The check runs before the preview or download starts, so nothing is used or charged, and the message names exactly which text could not be drawn. EPUB and Word files leave the font to the reading app or Word, so any script works there.",
    },
    {
      question: "Do I have to pick a specific publishing platform, or can I skip that?",
      answer:
        "You can leave it on \"Use default\" — the tool behaves the same way it always has, using KDP's real trim sizes and margin rules as the baseline. Picking a specific platform (IngramSpark, Barnes & Noble Press, Lulu, or an ebook-only platform) is worth doing once you know where you're actually submitting, since it switches the trim size list, margin math, and ISBN guidance to match that platform's own real requirements instead of KDP's.",
    },
    {
      question: "What's the difference between the preview and a real download?",
      answer:
        "Preview and the Pre-flight Check are unlimited, and use the exact same rendering the paid download does — the only difference is a visible watermark, so you can check pagination, margins, and layout as many times as you want before spending anything. A Book Writing Toolbox subscription ($29.99 a month, 7-day trial) removes the watermark from previews and downloads, so the clean, submission-ready file is included.",
    },
    {
      question: "Is the EPUB or the manuscript file included in the subscription?",
      answer:
        "Yes. The clean book PDF, the manuscript file (PDF or .docx) and the EPUB are all included in the Book Writing Toolbox subscription: $29.99 a month, with a 7-day trial. Previews are unlimited and watermarked for everyone. The $1 Day Pass and the $4.99 plan do not cover it.",
    },
    {
      question: "Can I work on more than one book at once?",
      answer:
        "Yes — the small library icon next to your book's title (\"My Books\") lists every book you've started on this device, lets you switch between them, and start a new one without losing progress on the others. Everything autosaves locally as you go, so switching between books is instant and nothing is deleted until you explicitly delete a project yourself.",
    },
    {
      question: "If I pick a custom trim size, will it definitely work on my target platform?",
      answer:
        "Custom size is accepted exactly as you type it, with no independent check against that platform's real acceptance rules — it's meant for a size that platform genuinely supports but isn't in this tool's own list yet. The Pre-flight Check will tell you whether your dimensions happen to match a known real KDP size, but for any other platform, it's worth a quick check against that platform's own current trim size list before a final submission.",
    },
    {
      question: "What happens if I leave the ISBN field blank?",
      answer:
        "It depends on the platform you've selected. KDP, Barnes & Noble Press, and Lulu will each assign you a free ISBN automatically at submission — it just only works on that one platform, so buy your own if you want the same ISBN to work everywhere. IngramSpark has no free option at all, so you'll need to buy a real ISBN (from Bowker, a local ISBN agency, or as a paid add-on through IngramSpark itself) before submitting there. Kobo doesn't need one — it assigns its own internal identifier if you leave it blank.",
    },
    {
      question: "Is my book's content ever saved on your servers?",
      answer:
        "No. While you're writing, your draft autosaves only to your own browser's local storage and is never uploaded anywhere. Previewing, running the Pre-flight Check, and downloading do send your content to a server briefly, since real typesetting and EPUB generation genuinely can't happen inside a browser alone — but that content is only held in memory long enough to generate the file you asked for, then discarded. Nothing is written to a database or kept afterward.",
    },
    {
      question: "Can I switch which platform I'm targeting after I've already started writing?",
      answer:
        "Yes, at any point — your chapters, cover images, and everything else you've written stay exactly as they are. Switching platforms only re-checks your current trim size against the new platform's real list: if your size isn't one it supports, it switches to Custom (keeping your existing dimensions as a starting point) or to that platform's own default, whichever fits.",
    },
  ],
  "catalog-maker": [
    {
      question: "Is this meant for a printed catalog or a digital one?",
      answer: "It works for either — the finished PDF is print-ready but also perfectly usable as a digital catalog to share or email directly.",
    },
    {
      question: "Which CSV columns does the importer expect?",
      answer: "Name, SKU, price, category and description, then optionally a minimum order and an order link. If the first row has at least two recognised headings, such as product, code, cost or group, it is used as the header. Otherwise columns are read in that order. Rows without a name are skipped.",
    },
    {
      question: "Which currencies can I choose?",
      answer: "US dollar, Euro, Pound, Rupee, Canadian dollar, Australian dollar, Yen, Singapore dollar, Dirham and Rand. Numeric prices are formatted with the chosen currency; text such as 'Ask for quote' is left exactly as typed.",
    },
    {
      question: "Can I put a QR code or barcode on the back page?",
      answer: "Yes. In Product list mode each product can have an order link: it is printed under the product card as a clickable link and a small QR code (only http and https links are accepted). A QR code or barcode on the back page is available only in Combine files mode. There, choose QR Code and enter a link, or Real Barcode with Code 128, EAN-13, EAN-8, UPC-A, Code 39 or ITF-14, a value and an optional caption. The value must be valid for the chosen barcode type.",
    },
    {
      question: "What page sizes are available?",
      answer: "Product list mode offers US Letter and A4. Combine files offers seventeen sizes: Letter, A4 and a range of book trim sizes from 4.25 x 7 inches up to 8.5 x 8.5 inches, including landscape and square formats.",
    },
    {
      question: "What file types does Combine files accept?",
      answer: "PDF, Word .docx, plain .txt, and PNG, JPEG, WebP and HEIC images. Older .doc files and spreadsheets are skipped with a warning. The optional cover image is separate and accepts PNG or JPEG only.",
    },
  ],
  "magazine-maker": [
    {
      question: "Can I control the layout of each page individually?",
      answer: "No. The whole issue shares one layout: you pick 1, 2 or 3 text columns, the page size and an accent colour, and every article starts on a new page.",
    },
    {
      question: "Does Write articles understand Markdown?",
      answer: "Yes. In article text, # and ## headings, **bold**, *italic*, - bullet lists and 1. numbered lists are laid out in the PDF, whether you type them, paste them or load a .md or .txt file. Emoji can't be printed in the PDF and are left out; a note under the text box tells you when an article has any.",
    },
    {
      question: "Can the cover have a picture?",
      answer: "Yes. In Write articles mode, tick Cover page and add an optional cover image; the magazine title and issue are printed over it. Without an image the cover is a plain block of your accent colour.",
    },
    {
      question: "When is a trial use taken?",
      answer: "When you download, not when you build. Building and previewing cost nothing. Downloading the same built issue again costs nothing extra; changing the content and downloading again counts as a new export.",
    },
    {
      question: "Can I import a Word document as an article?",
      answer: "In Write articles mode you can load only .txt or .md files, or paste text. Word .docx files belong in Combine files mode, where each one is converted and kept as its own section with a title you choose.",
    },
    {
      question: "How are columns and pictures handled?",
      answer: "You select 1, 2 or 3 text columns for the whole issue. The headline, author line and picture span the full page width at the top of an article's first page, and the body text then flows down each column in turn.",
    },
    {
      question: "What file types does Combine files take?",
      answer: "PDF, Word .docx, plain .txt and PNG, JPEG, WebP or HEIC images. Pages are shrunk to fit US Letter, so differently sized sources receive white margins. The warning message mentions only PNG and JPEG, but the file picker also accepts WebP and HEIC.",
    },
    {
      question: "What are the Masthead and Contributors pages?",
      answer: "They are optional text pages in Combine files mode. Tick one, type its content, such as publisher and contact details or a list of writers, and it is added to the front matter. An empty box adds no page.",
    },
    {
      question: "Is the 'In this issue' page automatic?",
      answer: "Yes. In Write articles mode it lists each article's title (shortened past 60 characters), the author and the starting page number. Every page also carries a page number at the bottom and the magazine title along the top.",
    },
  ],
  "document-assembler": [
    {
      question: "Does it add a table of contents?",
      answer: "Yes, automatically, and you can switch it off. Each entry uses the section title you typed and carries the page number where that section starts; the entries are also clickable links.",
    },
    {
      question: "Can I reorder the files?",
      answer: "Yes. Use the up and down arrows next to a file, or remove one, and the contents page and page numbers follow the new order when you assemble.",
    },
    {
      question: "Are my files uploaded?",
      answer: "No. The files are read and combined in your browser, and the finished PDF is offered as a download. Nothing is stored on our side.",
    },
    {
      question: "Can I combine documents that started in different formats?",
      answer: "Yes — the assembler is built to bring together content from multiple source documents into one final, consistent PDF, regardless of each piece's original format.",
    },
    {
      question: "Does it change my original files?",
      answer: "No. The assembled document is a new PDF built in memory and offered as a download. Your source PDFs, Word files and images are only read, never modified or replaced.",
    },
    {
      question: "What happens to pages that are a different size?",
      answer: "Each page is scaled to fit inside the page size you selected while keeping its proportions, and rotated pages are turned upright first. A page that is a different shape from the target ends up with blank space on its sides or top and bottom.",
    },
    {
      question: "Will bookmarks and links survive?",
      answer: "The tool adds one bookmark per section and nests a file's own bookmarks under it. Web and e-mail links on source pages are moved to where their text now sits. Links that point to other pages inside the original file are not described as preserved.",
    },
    {
      question: "What is the List of Images option?",
      answer: "It adds a page listing the pictures found in the assembled document along with their page numbers. It is off by default. If nothing is found, the page says so. Whole-page scans are treated as pages, not as figures.",
    },
    {
      question: "Which page sizes can I choose?",
      answer: "Seventeen are offered: US Letter, A4, and book trim sizes ranging from 4.25 x 7 inches to 8.5 x 8.5 inches, including 6 x 9, 7 x 10, a landscape 8.25 x 6 and two square formats.",
    },
  ],
  "compress-image": [
    {
      question: "What's the difference between the percentage modes and Custom Target?",
      answer: "Light, Medium, and Strong each guarantee a specific percentage reduction (10%, 25%, 50% smaller) rather than a vague quality label. Custom Target instead lets you set an exact file size limit, useful when you have a specific size requirement (like a 2MB upload cap) rather than a general \"make it smaller\" goal.",
    },
    {
      question: "Will converting a PNG to JPG first help compress it further?",
      answer: "Often yes, if you don't need transparency — JPG images generally compress much further than PNGs before quality loss becomes noticeable, so converting first can get you a meaningfully smaller final file.",
    },
    {
      question: "Does compressing actually reduce quality, or is it lossless?",
      answer: "Honestly, it's lossy — Light, Medium, and Strong aim for at least a 10%, 25% or 50% smaller file by re-encoding the image at a lower quality setting, which does mean some real, if often visually subtle, quality loss. \"Without losing quality\" isn't an accurate way to describe any of these modes; the goal is keeping the result looking as close to the original as possible at the target size, not zero quality change.",
    },
    {
      question: "Will my transparent PNG or WebP stay transparent after compressing?",
      answer: "Yes. A PNG or WebP that has transparent areas is saved as WebP so the transparency is kept. A photo with nothing transparent is saved as a JPG. The result shows which format you got and says when transparency was kept.",
    },
    {
      question: "Are my images uploaded to a server?",
      answer: "No — compression runs entirely in your browser, including the size-target search. Your image never leaves your device at any point.",
    },
    {
      question: "What does Manual Quality mode do differently from the percentage presets?",
      answer: "It gives you direct control over the compression quality slider yourself, rather than a percentage target — useful if you want to visually judge the quality-vs-size tradeoff yourself instead of trusting a fixed target.",
    },
    {
      question: "What happens if my Custom Target is smaller than the image can realistically reach?",
      answer: "The tool searches down to the lowest reasonable quality setting and returns the smallest result it can produce — if that's still larger than your target, you'll see the actual result size rather than a silently wrong or failed compression.",
    },
    {
      question: "Can I compress multiple images at once?",
      answer: "Yes — switch to Several files and add up to 5 images at a time on the trial; the batch counts as one use, taken when you download. For bigger batches with the same operation, Bulk Image Processor handles many files in one pass.",
    },
    {
      question: "Why did my PNG come out as a JPG?",
      answer: "Photos are saved as JPG because that format shrinks photographic detail far more effectively than PNG. Only a picture with a transparent background is saved as WebP, which keeps the transparency. The downloaded file name gets the matching extension, so it always reflects the real format inside.",
    },
    {
      question: "What is the difference between Light, Medium, Strong and Custom Target?",
      answer: "The first three work out a goal from the original file, 90%, 75% or 50% of its size. Custom Target uses a number you type in KB or MB. Use the percentage buttons for general slimming and Custom Target when a form states a maximum.",
    },
    {
      question: "Why does it say the target could not be reached?",
      answer: "Even at the lowest quality tried, the file stayed above your number. If Don't change dimensions is ticked (it is unticked by default for Custom Target), pixels were not allowed to shrink. Untick it, or raise the target, and compress again. The result shown is the smallest the tool could produce at full size.",
    },
    {
      question: "Does compressing remove location data from a photo?",
      answer: "It does. The picture is redrawn and saved again, so metadata such as the GPS position and camera details is not carried into the new file. The page states this next to the options, which makes the tool useful before sharing a photo publicly.",
    },
    {
      question: "Can I compress a photo straight from an iPhone?",
      answer: "iPhone photos are fine. HEIC files open directly, and the result is saved as a JPG, or WebP if it has transparency. If a HEIC file cannot be read, the page says it may be damaged or an unusual variant, instead of failing without a message.",
    },
  ],
  "resize-crop": [
    {
      question: "Why can't I set an exact width and height at the same time?",
      answer: "With \"Maintain aspect ratio\" on (the default), changing one dimension automatically adjusts the other so the image never distorts — which means you can't force two independent, unrelated dimensions unless they already match the original proportions. Turn the lock off if you need specific, independent dimensions; you can then pick a fit mode (Cover crops the overflow instead of stretching, Contain pads the leftover space, Inside and Outside keep the shape).",
    },
    {
      question: "Can I crop and resize in the same step?",
      answer: "They're two separate modes, but after applying a crop, a \"Resize this crop\" button hands the cropped result directly into Resize mode as the new working image — no need to download and re-upload to do both in sequence.",
    },
    {
      question: "Are there crop shapes for social media, like square or 16:9?",
      answer: "Yes — Freeform, 1:1 (square), 16:9 (widescreen), 4:5 (portrait), and 9:16 (story/reel format) presets constrain the drag selection to that exact ratio, so you don't have to eyeball a precise aspect ratio by hand.",
    },
    {
      question: "Will turning off \"Maintain aspect ratio\" actually distort my image?",
      answer: "Only with the Fill fit mode, which is the starting choice: it stretches the picture to your exact width and height. Choose Cover to fill the exact size by cropping the overflow, Contain to fit inside the exact size and fill the rest with a colour, or Inside and Outside to keep the shape and let one side differ from what you typed.",
    },
    {
      question: "What are the fit modes?",
      answer: "The same five the FreeDailyPro API uses. Inside fits within the box and keeps the shape, Contain fits within the exact box and pads the rest, Cover fills the exact box and crops what sticks out, Fill stretches to the exact box, and Outside keeps the shape and is at least as big as the box. They only matter when you give both a width and a height with the aspect ratio lock off.",
    },
    {
      question: "Can I turn, mirror or make the picture black and white while resizing?",
      answer: "Yes. In Resize mode you can rotate by 90, 180 or 270 degrees, mirror left to right or top to bottom, and switch on black and white, all in the same step. The picture is turned first, so the width and height you type are for the turned picture. There is also a Never make it bigger than the original option.",
    },
    {
      question: "Can I resize an image to be larger than the original?",
      answer: "Yes, but enlarging beyond the original resolution means the extra pixels are interpolated, not genuinely new detail — the result can look softer or slightly blurry compared to an image that was actually captured at that size.",
    },
    {
      question: "Are my images uploaded to a server?",
      answer: "No — both resizing and cropping run entirely in your browser using the canvas element. Your image never leaves your device.",
    },
    {
      question: "Can I preview the result before downloading?",
      answer: "Yes — Resize mode shows a live preview of the actual output dimensions as you type, and Crop mode shows the selection directly on the image as you drag, so you can see what you're getting before committing to either.",
    },
    {
      question: "What's the difference between this and Bulk Image Processor?",
      answer: "This handles one image at a time with a visual preview and precise control over dimensions or crop selection. Bulk Image Processor is for applying the same operation across many images at once, without the same level of per-image visual precision.",
    },
    {
      question: "Why does the height change when I type a width?",
      answer: "Maintain aspect ratio is ticked by default, so the other side is recalculated to keep the original proportions. Untick it to enter both values independently, accepting that the picture will stretch or squash.",
    },
    {
      question: "How do the centimetre and inch options work?",
      answer: "The tool converts your number into a pixel width using the DPI field, which starts at 300, and fills in the Width box when you press Set width. The percent option multiplies the original width instead.",
    },
    {
      question: "Can I crop to an exact output size?",
      answer: "Yes, in two steps within one run. Choose a ratio such as 1:1, select the area, then enter a number in Then scale to width. The crop is made first and scaled to that width afterwards, with the height following automatically.",
    },
    {
      question: "What happens to the file format?",
      answer: "Output matches the input type for JPEG, PNG, WebP, GIF and BMP files, and the name gets a resized_ or cropped_ prefix. A HEIC photo is saved as JPG. The file name keeps your original base name.",
    },
    {
      question: "What is the Resize this crop button?",
      answer: "After a crop, it passes the cropped result straight into Resize mode as the new working picture, so you can change its dimensions without downloading and uploading it again.",
    },
  ],
  "convert-image": [
    {
      question: "Which format should I use for a logo or icon?",
      answer: "PNG, if you need transparency or sharp edges — it's the right choice for graphics with hard lines or a transparent background, unlike JPG which doesn't support transparency at all.",
    },
    {
      question: "Does WebP work everywhere I'd want to use an image?",
      answer: "Most modern browsers and platforms support WebP, and it typically produces smaller files than JPG at similar quality. For very old software or specific compatibility requirements, JPG or PNG remain the safer, more universally-supported choice.",
    },
    {
      question: "What happens if I convert a transparent PNG to JPEG?",
      answer: "JPEG has no transparency support at all, so the transparent areas get filled with a solid color instead — you'll see a color picker appear (white by default) before converting, so you can choose what fills those areas rather than it happening invisibly. This can't be undone from the resulting JPEG afterward.",
    },
    {
      question: "Does this support GIF or other formats?",
      answer: "Yes. You can convert to PNG, JPEG, WebP, AVIF, GIF, BMP, ICO and TIFF, and open JPEG, PNG, WebP, GIF, AVIF, TIFF, BMP, SVG and iPhone HEIC pictures. The GIF made here is a still picture with at most 256 colours. Only the first frame of an animated GIF is used in the browser; the FreeDailyPro API keeps animations when converting GIF or WebP to GIF or WebP.",
    },
    {
      question: "What's the difference between this and WebP Converter?",
      answer: "This tool converts between all eight formats (PNG, JPEG, WebP, AVIF, GIF, BMP, ICO and TIFF) and can resize, turn and remove metadata in the same step. WebP Converter is a more focused, single-purpose tool for converting into WebP, with its own quality slider. If you only ever convert to WebP, that dedicated tool may be quicker; this one is more flexible if you need other formats or options.",
    },
    {
      question: "Will the image quality change during conversion?",
      answer: "Converting to PNG, GIF, BMP, ICO or TIFF does not add compression loss (GIF still reduces a picture to 256 colours). JPEG, WebP and AVIF are lossy and use the Quality slider, which starts at 92. Repeated JPEG conversions (JPEG to JPEG to JPEG) compound the loss each time, the same as any lossy format.",
    },
    {
      question: "Are my images uploaded to a server?",
      answer: "No — conversion runs entirely in your browser using the canvas element. Your image never leaves your device.",
    },
    {
      question: "Can I convert multiple images at once?",
      answer: "Not here — this handles one image at a time. For converting a batch of images together, Bulk Image Processor handles multiple files in one pass.",
    },
    {
      question: "Why did my transparent PNG get a white background as a JPEG?",
      answer: "JPEG cannot store transparency, so the see-through pixels must become a solid color. The tool fills them with white unless you choose another Fill color in the panel that appears. The same applies to 24-bit BMP. Once saved as JPEG the transparency cannot be recovered, so keep the PNG.",
    },
    {
      question: "What quality value should I choose for JPEG or WebP?",
      answer: "The slider runs from 40 to 100 and starts at 92. The page notes that 90 and above looks the same to most eyes, while lower values give smaller files. It appears for JPEG, WebP and AVIF; PNG, GIF, BMP, ICO and TIFF have no quality setting because they are not lossy.",
    },
    {
      question: "Can I convert a photo taken on an iPhone?",
      answer: "iPhone photos work. HEIC and HEIF files open, and the tool pre-selects JPEG as the output since that is what most people need. If the file cannot be read, a message says it may be damaged or use an unusual HEIC variant.",
    },
    {
      question: "Does it convert every frame of an animated GIF?",
      answer: "Only the first frame is converted, and a note appears on the page when you load a GIF. The result is a still image, so you need a tool built for animated formats if the movement matters.",
    },
    {
      question: "Will the width and height change when I convert?",
      answer: "Not unless you ask. By default the picture keeps its pixel size. Under More options you can set a width, a height or both, and choose how the picture fits (inside, contain, cover, fill or outside), with an option to never make it bigger than the original.",
    },
    {
      question: "How do I make a favicon or Windows icon?",
      answer: "Choose ICO as the format and tick the sizes you want: 16, 32 and 48 pixels cover browser tabs and shortcuts, and 256 is for large Windows icons. All the sizes are packed into one .ico file, and a picture that is not square is centred with see-through padding. A square source image gives the best result.",
    },
    {
      question: "Can I keep the camera and location details of a photo?",
      answer: "By default they are removed. Under More options you can tick Keep camera, date and location details, which copies them into the new file when both the original and the result are JPEG. For other formats the details are always removed.",
    },
    {
      question: "Which options does the API have that the website does not?",
      answer: "The API also offers lossless WebP and AVIF, progressive JPEG, colour detail (chroma subsampling), PNG compression level and 256-colour palette PNG, encoder effort, sharpen and blur, and it keeps animated GIF and WebP animations. The website offers format, quality, background colour, resize with fit modes, rotate and flip, black and white, turning photos upright, keeping or removing details, print resolution, icon sizes and BMP depth.",
    },
  ],
  "remove-background": [
    {
      question: "Why does the first image take longer to process than the next one?",
      answer: "The first run loads a small helper file into your browser, which can take a little while depending on your connection. Every image after that is much faster, since the file stays cached for the rest of your session.",
    },
    {
      question: "Does this work well on very large photos?",
      answer: "Yes — photos over 2000px on the longest side are automatically scaled down for the output, which keeps processing fast and avoids memory issues without a noticeable quality loss for typical uses like profile pictures or product photos.",
    },
    {
      question: "The helper file didn't load — what's going on?",
      answer: "It automatically retries a few times before giving up, so a brief connection blip usually resolves itself. If it keeps failing, a restrictive network (some corporate or school firewalls block file downloads like this) is the most common cause — reloading the page, or trying a home network or mobile data, usually resolves it.",
    },
    {
      question: "Is my photo uploaded anywhere?",
      answer: "No. Everything runs in your browser on your own device. Only a small helper file is downloaded (once, and cached); your photo is never sent anywhere.",
    },
    {
      question: "Will this work well on hair, fur, or fine details?",
      answer: "Reasonably well for typical subjects, but fine, wispy edges (loose hair strands, fur, lace) are the hardest case for any automatic background removal, including this one — results on those details may look slightly rougher than a smooth-edged subject like a product photo against a plain background.",
    },
    {
      question: "Can I remove backgrounds from multiple photos at once?",
      answer: "Not here — this handles one image at a time. There's currently no dedicated batch mode for background removal specifically.",
    },
    {
      question: "What file format does the result download as?",
      answer: "PNG — specifically chosen because it supports the transparency this tool produces, unlike JPG which doesn't support transparent backgrounds at all.",
    },
  ],
  "flip-rotate-image": [
    {
      question: "Why does my phone photo look correct on the phone but sideways everywhere else?",
      answer: "This happens when the phone's own orientation metadata isn't read correctly by whatever program or platform the photo ends up on — the image data itself is stored one way, with a separate flag saying how to display it, and not everything respects that flag consistently.",
    },
    {
      question: "Can I rotate by an angle other than 90 degrees?",
      answer: "This tool handles 90-degree increments and horizontal/vertical flips — for a precise custom angle to fix a slightly skewed photo, that's a different kind of correction than a standard rotate/flip.",
    },
    {
      question: "Can I rotate counter-clockwise, or only clockwise?",
      answer: "Both — there are separate clockwise and counter-clockwise buttons, plus a direct 180-degree option for a photo that's fully upside down, so you don't need to click clockwise three times to get the same result.",
    },
    {
      question: "How do I know if the orientation is actually fixed before downloading?",
      answer: "The preview updates immediately after each flip or rotate, showing exactly what the downloaded file will look like — check it there before downloading rather than guessing.",
    },
    {
      question: "Will this change my image's file size a lot?",
      answer: "No — the output uses the same format as your original file (JPEG stays JPEG, PNG stays PNG), so a simple flip or rotate shouldn't meaningfully change the file size the way converting to a different format would.",
    },
    {
      question: "Are my images uploaded to a server?",
      answer: "No — flipping and rotating both run entirely in your browser using the canvas element. Your image never leaves your device.",
    },
    {
      question: "Can I combine multiple flips and rotations before downloading?",
      answer: "Yes — each click applies on top of the current result, so you can flip and then rotate (or any combination) before downloading the final version, without needing to download and re-upload between steps.",
    },
    {
      question: "What's the difference between this and Image Filters?",
      answer: "This handles orientation only — flipping and 90-degree rotation. Image Filters is for visual adjustments like brightness, contrast, grayscale, and sepia. If you need both, either tool works fine on the other's output afterward.",
    },
    {
      question: "What is the difference between flipping and rotating?",
      answer: "Flipping mirrors the picture along one axis, so left and right or top and bottom swap and text reads backwards. Rotating turns it, so the picture stays readable but faces a new direction.",
    },
    {
      question: "Can I rotate by 15 degrees?",
      answer: "No. Rotation works in fixed 90 degree steps only: clockwise, counter-clockwise or a full 180. Small straightening angles are a different job, and this tool has no slider for them.",
    },
    {
      question: "Why does the downloaded file start with rotated_ or flipped_?",
      answer: "The prefix tells you which kind of change you made. Any quarter turn or 180 turn gives rotated_, a mirror only gives flipped_, and the original file extension and format are kept.",
    },
    {
      question: "Will the picture lose quality?",
      answer: "PNG stays lossless, but a JPEG is saved again at a fixed quality of 0.92, so a very slight second compression is possible. Embedded camera metadata is not kept in the new file.",
    },
    {
      question: "My photo looks upright on my phone but sideways elsewhere. Will this help?",
      answer: "Yes. The tool loads the picture as your browser displays it, with stored orientation applied, so you turn it until the preview looks right and download. The saved copy has the new orientation built into its pixels.",
    },
  ],
  "image-filters": [
    {
      question: "What's the best way to fix a photo that's too dark?",
      answer: "A modest brightness boost combined with a slight contrast increase usually looks more natural than pushing brightness alone to an extreme — large, single-adjustment changes tend to wash out the image rather than genuinely fixing it.",
    },
    {
      question: "Can I preview the changes before downloading?",
      answer: "Yes — adjustments preview in real time as you move the sliders, so you can see the effect before committing to a download.",
    },
    {
      question: "Do the preset filters (Sepia, Grayscale, etc.) replace the sliders, or work together?",
      answer: "They stack together — picking Sepia and then boosting Brightness applies both at once, rather than one replacing the other. If a combination looks off, switch back to \"None\" to reset just the preset while keeping your slider adjustments.",
    },
    {
      question: "Can I adjust saturation, or only brightness and contrast?",
      answer: "All three — Brightness, Contrast, and Saturation each have their own slider. Saturation at 0% produces a fully grayscale result; above 100% makes colors more vivid than the original.",
    },
    {
      question: "Is the blur amount adjustable?",
      answer: "No — Blur is a single fixed preset, not an adjustable slider. If you need a different blur intensity, that level of control isn't available here currently.",
    },
    {
      question: "Are these \"AI\" or \"professional\" filters, or something simpler?",
      answer: "Simpler, and honestly so — these are real CSS image filters (the same filter technology browsers use natively), applied directly to the pixel data. They're genuinely effective for common adjustments, but this isn't AI-based enhancement or a simulation of professional color-grading software.",
    },
    {
      question: "Are my images uploaded to a server?",
      answer: "No — every filter and adjustment is applied entirely in your browser. Your image never leaves your device.",
    },
    {
      question: "Can I apply the same filter settings to multiple images at once?",
      answer: "Not here — this handles one image at a time, and settings reset when you upload a new one. There's no batch mode for applying identical filter settings across several images.",
    },
    {
      question: "Do the presets and sliders work together?",
      answer: "Yes. A preset such as Sepia and the Brightness, Contrast and Saturation sliders stack rather than replace each other. Blur is applied first, then the colour changes are calculated on top.",
    },
    {
      question: "What does Effect strength do?",
      answer: "For Grayscale, Sepia and Invert it blends between the original and the full effect, so 50 percent gives a halfway look. For Blur it sets how soft the picture becomes, scaled to the width of the image.",
    },
    {
      question: "Is the download the same size as the preview?",
      answer: "The preview is reduced to at most 1200 pixels on its longest side, but the download applies the same settings to the full-resolution picture, so you get the original dimensions.",
    },
    {
      question: "What ranges do the sliders cover?",
      answer: "Brightness and Contrast run from 50 to 200 percent, Saturation from 0 to 200, and Effect strength from 0 to 100, with Blur starting at 5. In every case 100 on the three adjustment sliders means no change.",
    },
    {
      question: "Why do results match on iPhone and desktop?",
      answer: "Colour changes are calculated pixel by pixel in code rather than through the canvas filter feature, which Safari on iPhone does not support. That keeps the downloaded file identical to the preview on every browser.",
    },
  ],
  "document-scanner": [
    {
      question: "Does this automatically detect and straighten the document's edges?",
      answer: "No — the straighten step needs the four corners placed manually onto the document's actual corners in the photo. Reliably detecting a document's edges automatically against an arbitrary background is genuinely hard to get right, so this offers the honest, practical alternative: point at where the corners actually are, and get a mathematically correct straightening from that.",
    },
    {
      question: "Can I crop out the background before scanning?",
      answer: "Yes — after uploading or capturing a photo, drag to select just the document before continuing. This also makes the later straightening step easier, since there's less background clutter to work around.",
    },
    {
      question: "What does the Grayscale option actually do?",
      answer: "Converts the document to black-and-white, which is the classic \"scanned document\" look and often produces a smaller file size than keeping full color. \"Keep Color\" preserves the original colors if that matters for your document (a diagram with color-coding, for instance).",
    },
    {
      question: "Can I save the result as a PDF instead of an image?",
      answer: "Yes — after enhancing, choose to download as either an image file or a PDF, depending on which format you actually need.",
    },
    {
      question: "Can I use my camera directly instead of uploading an existing photo?",
      answer: "Yes — capture a photo directly with your device's camera as an alternative to uploading one you already have.",
    },
    {
      question: "What does the Contrast slider control?",
      answer: "How strongly light and dark areas are separated — higher contrast makes text darker and the background lighter, which is often what gives a scan its clean, crisp \"scanned\" look rather than looking like a flat photo.",
    },
    {
      question: "Are my photos uploaded to a server?",
      answer: "No — cropping, straightening, and enhancement all run entirely in your browser. Your photo never leaves your device.",
    },
    {
      question: "Can I rotate the document if it's sideways or upside down?",
      answer: "Yes — a rotate option is available during the enhance step, for a document photographed at the wrong orientation.",
    },
    {
      question: "Does the Take photo with camera button ask for camera permission?",
      answer: "It opens a file chooser set to prefer the rear camera, so on most phones your own camera app appears and you return with the shot. The page does not open a live video feed itself. On many desktop browsers the button simply shows the normal file dialog.",
    },
    {
      question: "Why did the automatic corners land in the wrong place?",
      answer: "The finder looks for the largest bright region and takes its four extreme points, so a light tablecloth, a bright window or a page that touches the photo edge can fool it. Drag each round handle onto the real corner and press Straighten.",
    },
    {
      question: "How do I scan several pages into one PDF?",
      answer: "Finish the first page, press Add page & scan next, and choose the next photo. The Download PDF button then shows the running page count. Download PNG only ever saves the page currently on screen, so use the PDF for multi-page work.",
    },
    {
      question: "What is the difference between A4, US Letter and Fit the scan?",
      answer: "A4 and US Letter place the image on a standard sheet with an 18-point margin, turning the sheet sideways when the scan is wider than tall. Fit the scan makes the page exactly as large as the image at 150 dpi, with no margin.",
    },
    {
      question: "What does the Contrast slider do to color and grayscale scans?",
      answer: "It runs from 0 to 120 in steps of 5 and starts at 60. The same contrast curve is applied in both modes; in Grayscale the page is first reduced to shades of gray, in Keep Color each color channel is adjusted separately.",
    },
  ],
  "camera-capture": [
    {
      question: "Can I switch between my front and rear camera?",
      answer: "Yes — if your device has multiple cameras, a dropdown to switch between them appears once camera access is granted. The first camera your browser lists by default isn't always the one you want.",
    },
    {
      question: "Is my photo uploaded anywhere?",
      answer: "No — capturing and any cropping happens entirely in your browser. Nothing is uploaded until you choose to download or copy the result yourself.",
    },
    {
      question: "I clicked Block by mistake. How do I get the camera prompt back?",
      answer: "A web page cannot reopen the prompt once it is refused. Use the camera or lock icon beside the address bar to set camera access for this site back to Allow, then reload and press Start Camera again. The tool also shows a message explaining which step failed.",
    },
    {
      question: "Can I choose the back camera on my phone?",
      answer: "Yes. Press Front / rear camera to switch between the user-facing and outward-facing camera. When the browser exposes several cameras by name, a drop-down also appears, which is useful on laptops with an external webcam or phones with several lenses.",
    },
    {
      question: "What format and file name will the photo have?",
      answer: "PNG by default, or JPG at 92 percent quality if Save as JPG is ticked while the preview is live. Names look like camera-20260101-120000, with a date and time, and the crop adds -cropped. Copy puts the cropped image on your clipboard.",
    },
    {
      question: "How sharp will the photo be?",
      answer: "It matches the resolution printed above the preview. The tool requests up to 4096 by 2160 pixels, but the browser gives the nearest size your camera supports, so many webcams will return less. Tools cannot recover detail the sensor never recorded.",
    },
    {
      question: "Is the photo saved anywhere after I leave the page?",
      answer: "No. The captured image exists only in the open page until you press a download button or Copy. There is no gallery or history, so leaving or refreshing the page discards an unsaved photo. Download it first if you want to keep it.",
    },
  ],
  "bulk-image": [
    {
      question: "Does this resize images, or just compress and convert them?",
      answer: "Both. Besides format and quality, More options lets you set a width and height with five fit modes (Inside, Contain, Cover, Fill, Outside), or just a Longest side, plus rotate and mirror, black and white, a print resolution, and keep or remove camera and location details. Everything applies to every picture in the batch.",
    },
    {
      question: "What happens with a very large batch of images?",
      answer: "Since everything processes locally in your browser, an especially large batch can slow down on lower-powered devices — keep an eye on your browser's memory usage if you're processing a large number of files at once.",
    },
    {
      question: "Do I get one download per image, or everything together?",
      answer: "One ZIP file containing every processed image — not a separate download prompt for each file, which most browsers throttle or flag as suspicious past a handful of downloads in quick succession.",
    },
    {
      question: "Can I stop partway through a large batch?",
      answer: "Yes — a Stop option appears while processing, and whatever was already finished before you stop is still included in the downloaded ZIP, rather than being discarded.",
    },
    {
      question: "Does every image get the same quality setting and output format?",
      answer: "Yes — one format and one set of options applies to the whole batch. There's no way to set different quality levels or sizes for different files within the same batch run.",
    },
    {
      question: "Are my images uploaded to a server?",
      answer: "No — both the compression/conversion and the ZIP packaging happen entirely in your browser. Your images never leave your device.",
    },
    {
      question: "Can I add more images after I've already started selecting a batch?",
      answer: "Yes — each drop adds to the current selection rather than replacing it, so you can build up a batch across several drops before processing.",
    },
    {
      question: "What image formats can I convert to?",
      answer: "PNG, JPEG, WebP, AVIF, GIF, TIFF, BMP and ICO — the same eight formats and the same options as the single-image Convert Image Format tool, applied across the whole batch at once. WebP and AVIF depend on your browser being able to write them, and the menu says so when it cannot.",
    },
    {
      question: "Which output formats can I choose?",
      answer: "Any of PNG, JPEG, WebP, AVIF, GIF, TIFF, BMP and ICO, chosen once for the whole batch. Files are renamed with the matching extension, and if two end up with the same name, the second gets a number added so that nothing inside the ZIP overwrites anything else.",
    },
    {
      question: "Is there a limit on how many images I can process?",
      answer: "The tool sets no fixed count. In practice your browser's memory is the limit, since everything is held and converted on your device. If a big batch is sluggish, split it into smaller runs and download a ZIP for each.",
    },
    {
      question: "What does the Longest side box do?",
      answer: "It caps the larger of the width and height in pixels, keeping the proportions. A picture already smaller than the number is left at its size. Leaving the box empty keeps all pictures at their original dimensions. If you type a width or height under More options, those are used instead.",
    },
    {
      question: "Can I stop in the middle of a batch?",
      answer: "Yes. Press Stop while processing. The tool finishes the picture it is on, ends the run and still downloads a ZIP containing everything converted so far, along with a message saying how many were done out of the total.",
    },
    {
      question: "Why did some files get bigger instead of smaller?",
      answer: "Re-encoding does not always shrink a file, particularly when converting a well-compressed JPEG to PNG or raising quality. Such results are flagged as larger than the original in the list, so you can lower the quality, pick WebP or reduce Longest side.",
    },
  ],
  "webp-converter": [
    {
      question: "Should I use WebP for a website I'm building?",
      answer: "Usually yes, if page-load speed matters — WebP typically produces smaller files than JPG or PNG at similar visual quality. Keep a JPG or PNG backup if you need compatibility with very old software or specific legacy requirements.",
    },
    {
      question: "Can this tool convert WebP back to JPG or PNG?",
      answer: "Yes — drop an existing WebP file and the tool automatically detects it, offering JPG or PNG as the output format instead of WebP. JPG has its own quality slider, starting at 92%.",
    },
    {
      question: "What's the difference between this and Convert Image?",
      answer: "This tool is focused specifically on WebP — converting into it with a dedicated quality slider, or out of it to JPG/PNG. Convert Image handles conversion between PNG, JPEG, WebP, AVIF, GIF, BMP, ICO and TIFF in either direction, with a format picker and a quality slider for the lossy formats. If WebP is always one side of your conversion, this dedicated tool is quicker; for any other conversion, Convert Image is the right tool.",
    },
    {
      question: "What does the quality slider actually control?",
      answer: "How much WebP's lossy compression simplifies the image when converting INTO WebP — higher values keep more detail at a larger file size, lower values compress harder at the cost of visible quality loss. 80% is a common, well-balanced default for most photos. When converting a WebP back out to JPG the same slider sets the JPG quality (starting at 92%); PNG is lossless, so it has no quality setting.",
    },
    {
      question: "Is WebP conversion always smaller than the original?",
      answer: "Usually, but not guaranteed — a small or already-heavily-compressed source image can occasionally end up similar in size or slightly larger after WebP conversion, especially at a high quality setting. Check the reported result size before assuming it's always smaller.",
    },
    {
      question: "Will transparency be preserved either direction?",
      answer: "Converting into WebP or into PNG both preserve transparency. Converting a transparent WebP into JPG loses it, since JPG has no transparency support at all — choose PNG instead if the source has transparency you want to keep.",
    },
    {
      question: "Are my images uploaded to a server?",
      answer: "No — conversion in either direction runs entirely in your browser using the canvas element. Your image never leaves your device.",
    },
    {
      question: "What image formats can I convert into WebP?",
      answer: "Any standard format your browser can display as an image — JPG and PNG are the most common, but any browser-renderable image source works as input.",
    },
    {
      question: "Will converting to WebP make my image smaller?",
      answer: "Often, but not always. The confirmation message shows the new size in KB, so compare it with the size in the file bar. A lower quality value usually saves more, at the cost of visible artifacts.",
    },
    {
      question: "Can I convert a WebP file back to JPG or PNG?",
      answer: "Yes. Drop the WebP file and choose JPG or PNG under Convert to. JPG is selected first, and the output is saved with your original name and the new extension.",
    },
    {
      question: "What happens to transparent areas?",
      answer: "Transparency is kept when converting a PNG to WebP. When a WebP becomes a JPG, transparent areas are filled with a colour because JPG has no transparency; white is the starting colour and you can change it. Choose PNG to keep it.",
    },
    {
      question: "Can I resize or change the picture while converting?",
      answer: "Yes. Open More options to set a width and height (with the same five fit modes as Convert Image), rotate or mirror the picture, make it black and white, and keep or remove camera and location details. Details can be kept only when both the original and the result are JPG. If your browser cannot write WebP, the tool says so instead of producing a wrong file.",
    },
    {
      question: "Does it open formats like HEIC or TIFF?",
      answer: "The loader can read iPhone HEIC, TIFF and, where your browser supports it, AVIF, as well as common web formats, and then writes WebP. A file it cannot decode shows an error message naming the file.",
    },
  ],
  "passport-photo": [
    {
      question: "Can this fix a photo with a busy or shadowed background?",
      answer: "Partly. The Make background white + auto-fit head button cuts you out and puts a plain white background behind you, which works best on an evenly lit photo with a fairly plain background. It cannot fix harsh shadows on your face or hair, and a very busy background can leave rough edges, so check the preview before you download. For the best result, take the photo in even, natural light against a plain white or light-colored wall.",
    },
    {
      question: "Can I print multiple copies on one sheet?",
      answer: "Yes — after selecting a size template, you can lay out multiple copies on a single printable sheet instead of downloading just one photo at a time.",
    },
    {
      question: "Does a correctly-sized photo mean it's guaranteed to be accepted?",
      answer: "No — this handles standard photo dimensions accurately, but real government requirements often include rules beyond size alone (background color, facial expression, glasses or head-covering rules, how recently the photo was taken, and more), and requirements can change. Always check your destination country's current official photo requirements directly before submitting — a correctly-sized photo isn't automatically a compliant one.",
    },
    {
      question: "Which countries' size presets are included?",
      answer: "US (2\u00d72 in), UK/EU (35\u00d745 mm), Canada (50\u00d770 mm), India (51\u00d751 mm), and China visa (33\u00d748 mm) — covering the most commonly needed standard dimensions, though not every country's specific requirements.",
    },
    {
      question: "What resolution is the output photo?",
      answer: "300 DPI at the selected template's real physical dimensions — the standard resolution most photo printing and official submission processes expect.",
    },
    {
      question: "Are my photos uploaded to a server?",
      answer: "No — resizing, cropping, and print-sheet layout all happen entirely in your browser. Your photo never leaves your device.",
    },
    {
      question: "What size is the print sheet, and how many photos fit on it?",
      answer: "A standard 4\u00d76 inch sheet at 300 DPI — how many copies fit depends on the selected photo size template, since smaller photo dimensions naturally fit more copies per sheet.",
    },
    {
      question: "Can I adjust the zoom and position of my face within the frame?",
      answer: "Yes — zoom and horizontal/vertical position sliders let you fine-tune the framing after uploading, so you can center your face correctly within the required dimensions rather than being stuck with an automatic crop.",
    },
    {
      question: "Does the Canada preset cover everything Canada requires?",
      answer: "It gets the photo dimensions (50\u00d770 mm) right, but Canada has an unusual additional requirement this tool genuinely can't fulfill: printed passport photos must be taken or verified by a commercial photographer, with a stamp and a signed guarantor statement on the back of one print. Check current IRCC requirements directly for the full process, not just the photo dimensions.",
    },
    {
      question: "Which photo sizes does it offer?",
      answer: "Twelve templates: US Passport or Visa at 2 x 2 inches (600 x 600 px at 300 dpi), UK, EU / Schengen Visa, Australia, Japan, New Zealand, Ireland, Singapore and South Korea at 35 x 45 mm (413 x 531 px), Canada at 50 x 70 mm, India at 51 x 51 mm, and China Visa at 33 x 48 mm. The exact output size is shown under the Photo Size menu. New Zealand, Ireland, Singapore and South Korea are marked as not yet compared with the official page; each template links the official page to confirm the rules.",
    },
    {
      question: "Will my photo be accepted by the embassy or passport office?",
      answer: "The tool cannot promise that. It only sizes and crops to a template. Authorities set rules on background, expression, glasses and recency, and they change, so confirm the current requirements of the issuing authority before submitting.",
    },
    {
      question: "What do the File size limit options do?",
      answer: "You can choose No limit or under 50, 100, 240 or 500 KB. The single photo is saved at the highest JPEG quality that fits, down to a minimum. The print sheet does not use this limit.",
    },
    {
      question: "What is in the print sheet?",
      answer: "A 4 x 6 inch page at 300 DPI, set portrait or landscape whichever fits more copies of your template, with thin grey outlines between photos as cutting guides. It is saved as a JPEG.",
    },
    {
      question: "What does the auto-fit button do, and what can go wrong?",
      answer: "It cuts out the person, puts a white background behind them, and sets Zoom and Position to aim the head at the middle of the allowed range. It is an estimate, so check the face against both ovals and fine-tune manually.",
    },
  ],
  "screenshot-tool": [
    {
      question: "Can it capture a whole web page, not just what is on screen?",
      answer: "Yes, by stitching, but you do the scrolling. Choose Capture whole page, share the tab or window, then scroll it slowly yourself. About twice a second the tool takes a picture, lines it up with the one before and joins them into one tall image, all in your browser. A website cannot scroll or read another page for you, so anything you do not scroll past is not in the result. Sticky headers and footers appear once, videos and animations are a single frame, side-to-side scrolling is not captured, and long blank stretches or repeating content cannot be lined up (the tool tells you instead of guessing). The image is capped at 16,000 px tall. Nothing is uploaded.",
    },
    {
      question: "Why do I have to select the area after capturing instead of before?",
      answer: "Browser security blocks a website from drawing a selection box directly onto your screen before capturing, since that would let any site potentially screen-record parts of your screen at will. This works around that safely: capture the full screen or tab first, then drag to select and crop afterward for the same end result.",
    },
    {
      question: "What if my browser doesn't support copying images to the clipboard?",
      answer: "Chrome, Edge, and Safari all support writing images directly to the clipboard. If yours doesn't, the Download option still works as a reliable fallback.",
    },
    {
      question: "What's the delay option for?",
      answer: "It gives you time to set up your screen after granting access and before the capture actually happens — open a menu, hover a tooltip, or arrange a window the way you want it to look, then the screenshot is taken automatically once the countdown ends.",
    },
    {
      question: "Why does my browser minimize itself after I pick \"Entire Screen\"?",
      answer: "This is normal browser/operating system behavior, not something this tool does or can prevent — \"Entire Screen\" is meant to capture your whole desktop, so the browser has to step out of the way right after you choose that option, or it would just end up capturing a picture of itself instead of whatever's behind it. If you want to bring a different window to the front first, set a short delay before clicking Take Screenshot — that's exactly what the delay option is there for.",
    },
  ],
  "collage-maker": [
    {
      question: "Do all my photos need to be the same shape to make a good collage?",
      answer: "Not required, but photos with roughly similar aspect ratios (all portrait, or all landscape) tend to look more balanced in a grid layout than mixing very different shapes — a mix can leave some cells looking cropped or oddly spaced.",
    },
    {
      question: "How many photos can I combine into one collage?",
      answer: "Between 2 and 9 photos, using a choice of grid layouts depending on how many you're combining.",
    },
    {
      question: "How many photos can one collage hold?",
      answer: "Up to nine. If you add more, the extras are left out and a warning says how many. A given layout may use fewer: the 2, 3, 4, 5 and 6 layouts hold exactly that many photos, and any surplus photos are marked as not used.",
    },
    {
      question: "How large will the finished collage be?",
      answer: "It depends on the layout, the Size per photo and the Spacing. For example, the 4 (grid) layout at 500 px with spacing 8 gives 2 x 500 plus 3 x 8, so 1024 by 1024 pixels. The 1200 px setting makes much larger images.",
    },
    {
      question: "What happens when my photos have different shapes?",
      answer: "Each photo is enlarged until it covers its cell, centred, and whatever overflows is cropped away. Nothing is stretched. If an important part is being cut, reorder the photos so that picture goes into a better-suited cell, or crop it beforehand.",
    },
    {
      question: "Can I rearrange the photos after uploading?",
      answer: "Yes. Each file in the list has an up arrow, a down arrow and a remove button, and the cells fill in that order. Changing anything clears the old preview, so press Generate Collage again to see the new arrangement.",
    },
    {
      question: "Can I use iPhone HEIC photos?",
      answer: "Yes. HEIC, HEIF, TIFF and AVIF files are handled alongside JPEG, PNG, WebP, GIF and BMP, though AVIF depends on your browser. If a file cannot be read, a message names it and no collage is made.",
    },
  ],
  "compress-video": [
    {
      question: "Why does compressing a video take so much longer than an image?",
      answer: "Compression re-encodes every single frame of the video, the most processing-intensive thing any video tool here does. Medium/Strong and Custom Target all run two full encoding passes to hit their target reliably, so expect real processing time, especially for longer or higher-resolution videos.",
    },
    {
      question: "Why does my phone video fail to process right away?",
      answer: "Some phone-recorded videos, particularly .mov files, use a codec this tool can't read. If processing fails immediately, try re-exporting the video from your phone's own share options first, which often converts it to a more widely-compatible format.",
    },
    {
      question: "Why is the result not exactly 25 or 50 percent smaller?",
      answer: "Those buttons aim the encoder at a size computed from your original, then adjust once if it lands over. The result is meant to be at or under the target, and the page prints the real percentage, so a result a little smaller than the label is normal.",
    },
    {
      question: "Will compressing lower the picture quality?",
      answer: "Yes, to some degree. The video is re-encoded, so detail is traded for size, and Strong or a small Custom Target costs more than Light. Choosing Lower resolution to 720p reduces sharpness on large screens but helps reach small targets.",
    },
    {
      question: "What does the Manual Quality slider control?",
      answer: "The Compression amount maps to a constant-quality setting, where higher percentages mean heavier compression and a longer wait. The tool also caps the bitrate near 90 percent of the source's, so an already efficient file is less likely to grow.",
    },
    {
      question: "Why did it say the target was not reached?",
      answer: "The encoder could not get below your size at the chosen settings. The page shows how small it got. Try Strong, a larger target, a shorter source, or tick Lower resolution to 720p, which removes pixels and so helps most.",
    },
    {
      question: "I dropped a MOV or MKV file. What format do I get back?",
      answer: "Always MP4. The result is H.264 video with AAC audio, saved as compressed_ followed by the original name with an .mp4 ending. That format plays on nearly every phone, browser and chat app.",
    },
    {
      question: "How do I compress a video to 25 MB?",
      answer: "Drop the video, choose Custom Target and press 25 MB (email) or type 25 in the Target size box, then press Compress Video. The page encodes toward that size and checks the real file, and repeats up to twice if it came out over. There are also 8 MB, 16 MB (WhatsApp), 50 MB and 100 MB shortcuts.",
    },
    {
      question: "Will the file always be under my target?",
      answer: "It is counted as reached only when it is at or under your size. If a very long or detailed video cannot get that small, the page says it missed and shows how small it got. Then tick Lower resolution to 720p, trim the video or pick a larger target.",
    },
    {
      question: "Can I cut part of the video out first?",
      answer: "Yes, in Custom Target mode. Fill in Trim start and Trim end as seconds or m:ss, such as 0:30 and 2:15. Only that part is kept, and the target size is for that part. Because size is bitrate times length, a shorter clip keeps more quality at the same size.",
    },
    {
      question: "Is my video uploaded?",
      answer: "No. The video is compressed by an engine running in your browser, so the file never leaves your device. The engine itself, about 25 MB, is downloaded once before the first video job.",
    },
  ],
  "convert-video": [
    {
      question: "Which video format should I use if I'm not sure what's expected?",
      answer: "MP4 is the safest default — it has the widest compatibility across devices and platforms of any common video format.",
    },
    {
      question: "Why does converting take a while for a large file?",
      answer: "Converting between formats generally re-encodes the entire video, not just relabels the file, so larger files or longer videos take noticeably longer to process.",
    },
    {
      question: "What is the difference between MP4 and MP4 for web?",
      answer: "In this tool they produce the same kind of file: both are written with the fast-start setting, which puts the playback index at the start so it can begin before fully downloading. The labels differ, but the settings used are the same either way.",
    },
    {
      question: "Why did my conversion take only seconds?",
      answer: "When a video is already H.264 with AAC audio and you choose MP4 or MOV, the streams are copied into the new container without re-encoding. That is quick and loses no quality. The summary line says the video was copied into the new format.",
    },
    {
      question: "What are the settings for re-encoded MP4 and WebM?",
      answer: "MP4 and MOV re-encodes use H.264 at CRF 23 with the veryfast preset and AAC audio at 128 kbps. WebM uses VP8 at 1 Mbps with Vorbis audio at 96 kbps and limits the width to 1280 pixels.",
    },
    {
      question: "Why is the converted file bigger than the original?",
      answer: "A re-encode can raise size, notably when going from WebM to H.264 MP4, which the tool notes in its summary. Repackaging does not grow a file. For a smaller result, run the output through the Compress Video tool.",
    },
    {
      question: "How do I convert MOV to MP4?",
      answer: "Drop the MOV file, leave the output on MP4 and press Convert. When the picture is H.264 and the sound is AAC, the streams are copied into an MP4 in seconds with no quality loss. Other codecs, such as the HEVC many phones record, are re-encoded, which takes longer.",
    },
    {
      question: "Which MP4 should I pick for a website?",
      answer: "Either. In this tool MP4 and MP4 for web write the same fast-start file, with the playback index at the front so a page can start playing before the whole file downloads. Use WebM only when you specifically need a .webm file.",
    },
    {
      question: "Is my video uploaded?",
      answer: "No. The conversion runs in your browser, so the video is not sent to a server. The video engine, about 25 MB, is downloaded once before your first conversion.",
    },
    {
      question: "Can I convert audio-only files or formats like MKV and AVI?",
      answer: "The picker accepts files that look like video, including common extensions beyond MP4, and anything the engine can read can be converted to the four outputs. Audio-only files are rejected here; use the Audio Converter for those.",
    },
  ],
  "extract-audio": [
    {
      question: "What format does the extracted audio come out as?",
      answer: "A standalone MP3 file, saved separately from the original video — useful for turning a recorded video into a podcast-style audio file or just keeping the sound without the visuals.",
    },
    {
      question: "Why did the first video tool I used take longer to load?",
      answer: "The first time you use any video tool on this site, your browser downloads a small video-processing engine (about 25MB). After that, it's cached, and every video tool — including this one — loads instantly.",
    },
    {
      question: "Which output formats can I pick?",
      answer: "MP3, WAV (uncompressed), M4A (AAC), OGG and FLAC, plus Original audio. The lossy ones are encoded at high quality: MP3 at VBR level 2, OGG at quality 5 and M4A at 192 kbit/s. WAV and FLAC are lossless.",
    },
    {
      question: "What exactly does Original audio do?",
      answer: "It copies the sound track without re-encoding. AAC becomes .m4a, MP3 stays .mp3, Opus becomes .opus, Vorbis becomes .ogg and FLAC stays .flac. If the track uses any other codec, the tool saves an MP3 instead and shows a notice.",
    },
    {
      question: "How do I extract just one section?",
      answer: "Tick Extract a specific time range instead of the whole video. Drag both slider handles or type Start and End. The start must be earlier than the end, and times beyond the video length are clamped to its end.",
    },
    {
      question: "What if my video has no sound?",
      answer: "You are told as soon as the file loads: This video has no audio track, with a note that nothing was used from your trial, and Extract Audio stays off. MP4, MOV, WebM and MKV are checked from the file itself; for other containers the same message appears when you press Extract Audio, instead of the button doing nothing.",
    },
    {
      question: "Can I take the audio from several videos at once?",
      answer: "Yes. Choose Several videos (ZIP) at the top, pick Save as once, and drop the videos. Each is extracted with that format and you download one ZIP; a video with no sound is marked on its own row and does not stop the others. The whole batch counts as one use, taken when you download (one file or the ZIP) and never when you press Extract all or if every video fails; up to 5 videos on the trial, more on a paid plan. The time range applies to one video at a time.",
    },
    {
      question: "Why was my file refused?",
      answer: "Files that look like audio (mp3, wav, flac, ogg, m4a, aac, opus) are rejected because there is no video to take sound from. A file whose type is not a video is also turned away. Drop an MP4, MOV, WebM, MKV or similar.",
    },
    {
      question: "Why is the first extraction slower?",
      answer: "The FFmpeg engine, about 25 MB, has to be fetched before work starts, and the status line says so. Later runs can reuse your browser's cached copy, but how quickly that happens depends on your browser and network.",
    },
  ],
  "merge-video": [
    {
      question: "Can I merge clips with different resolutions or frame rates?",
      answer: "Yes. Clips that differ are re-encoded to one size and one frame rate, so a mismatch won't break the merge. Under the clip list the page says which clips differ from the output size, and after merging it tells you if frame rates were converted. Clips with very different original qualities may still look uneven where they join.",
    },
    {
      question: "Can I reorder clips after adding them?",
      answer: "Yes. Each clip has up and down arrow buttons; the clips play in the order they are listed.",
    },
    {
      question: "What happens to clips of different shapes, such as a phone clip held upright and a landscape one?",
      answer: "Choose the Output shape: same as the first clip, landscape 1920 x 1080, portrait 1080 x 1920 or square 1080 x 1080. Clips that do not match are scaled to fit with black bars, or, if you tick Fill the frame, scaled up and cropped at the edges. They are never stretched.",
    },
    {
      question: "Can I fade between clips?",
      answer: "Yes. Tick Fade between clips for a 0.5 second crossfade of picture and sound at each join. The overlap makes the result 0.5 second shorter per join (a very short clip gets a shorter fade). It is a simple fade, not a choice of transition styles.",
    },
  ],
  "screen-recorder": [
    {
      question: "Can I add narration after I've already recorded?",
      answer: "No — turn on the microphone toggle before you start recording if you want narration included. It can't be added retroactively to a recording that's already finished.",
    },
    {
      question: "Should I record a specific region or the full screen?",
      answer: "Recording just a selected region re-draws each frame through a canvas before recording, which uses more processing than a raw full capture. For very long recordings on an older machine, recording the full capture and cropping it afterward with the Trim Video tool may perform better.",
    },
    {
      question: "What file format does the screen recorder save?",
      answer: "It saves MP4 (H.264 video with AAC audio) when your browser can record that, which is common in recent Chrome, Edge and Safari. Otherwise it saves WebM. The page tells you which one you got, and the file name carries the date and time so recordings do not overwrite each other.",
    },
    {
      question: "Can I record just one window or part of the screen?",
      answer: "Yes, in two steps. The browser's sharing dialog lets you choose a whole screen, one window or one tab. Then, on the preview, you can drag a rectangle and press Record Selected Region. The Record Selected Region button appears only once the rectangle is at least 20 pixels across and high.",
    },
    {
      question: "Will my voice and the computer's sound both be recorded?",
      answer: "With Microphone on, your voice is mixed with any computer audio the sharing dialog provided, into one track. If microphone access is refused, the tool says so and records the video plus computer audio only. A level meter shows whether the microphone is picking you up.",
    },
    {
      question: "Why does my browser ask for permission more than once?",
      answer: "Screen sharing, the microphone and the camera are separate permissions, and the browser asks for each. The camera is requested when you switch the bubble on. If you refused one earlier, many browsers stop asking, and you must re-allow it in the site settings.",
    },
    {
      question: "Does pausing leave a gap in the recording?",
      answer: "No. Pause stops the recorder and the timer, and Resume continues the same file, so the paused stretch is simply not in the clip. Stop ends the take and shows the player. If you close the sharing from the browser's own Stop sharing button, the recording ends the same way.",
    },
  ],
  "subtitle-adder": [
    {
      question: "Can I turn subtitles off after adding them?",
      answer: "No — this burns subtitles directly into the video's picture permanently. If you might need to change or remove the captions later, keep your original SRT file saved separately as well.",
    },
    {
      question: "What subtitle format does this accept?",
      answer: "Standard SRT format — the common \"1 / 00:00:00,000 --> 00:00:05,000 / Hello, world!\" style timing that most subtitle editors and video tools export.",
    },
    {
      question: "Are the subtitles burned in or added as a track?",
      answer: "Burned in by default, which draws the words into the picture permanently. If you tick the option to add a subtitle track, they are stored as a separate, switchable track instead, as long as the video's formats allow it.",
    },
    {
      question: "Which subtitle files can I upload?",
      answer: "You can paste SRT text or upload .srt, .vtt, .ass or .ssa files. WebVTT and ASS or SSA are converted to plain SRT, so styling inside ASS files is dropped and only the text and timing are used.",
    },
    {
      question: "Does it support Hindi, Arabic or Chinese?",
      answer: "Latin text plus Hindi, Tamil, Telugu, Bengali, Gujarati, Kannada, Malayalam, Punjabi, Arabic, Hebrew, Thai and other scripts work. Chinese, Japanese and Korean are rejected with a message, because their fonts are too large to bundle.",
    },
    {
      question: "Why did it burn the subtitles in when I asked for a track?",
      answer: "A separate track needs the video and audio to fit an MP4 without re-encoding. If the codecs do not qualify, such as with some WebM files, the tool shows a notice and falls back to burning the captions into the picture.",
    },
    {
      question: "What does the error about a line number mean?",
      answer: "The tool checks your SRT before processing. It reports the line where a time such as 00:00:01,000 --> 00:00:04,000 is missing or malformed, or where the end time is not after the start time. Fix that line and try again.",
    },
  ],
  "thumbnail-gen": [
    {
      question: "How do I get a sharp, non-blurry thumbnail?",
      answer: "Pick a frame with a clear, in-focus subject and good lighting — a thumbnail pulled from a blurry transition or motion-heavy moment rarely looks good on its own. Scrubbing slowly near a scene change usually finds a sharper frame than scrubbing quickly and guessing.",
    },
    {
      question: "What image formats and sizes can I export?",
      answer: "PNG by default, or JPG at 92 percent quality if you tick Save as JPG. Sizes are the video's own size, 1280 x 720, 1080 x 1920 or 1080 x 1080. Files are named after the video, the time of the frame and, where used, the size and title tags.",
    },
    {
      question: "Can I save more than one frame from the same video?",
      answer: "Yes. Each press of Capture Thumbnail adds another image to the gallery below. Download them one at a time, or once you have more than one, press Download all as ZIP to receive a single archive with every capture.",
    },
    {
      question: "What does Suggest frames actually do?",
      answer: "It jumps to ten evenly spaced moments, makes a small preview of each, and scores them for sharpness. Frames that are almost black or nearly white are skipped and shown faded. The sharpest remaining one gets a ring and a star. Click any preview to jump there.",
    },
    {
      question: "My iPhone video will not show in the player. What now?",
      answer: "Some browsers cannot play iPhone HEVC video. The tool says so, hides the player, and asks you to type the time of the frame. Pressing Capture Thumbnail then takes a little longer because a processing engine is downloaded and used to read that frame.",
    },
    {
      question: "What is the difference between Fill and Fit?",
      answer: "Both apply when you choose a fixed size. Fill scales the frame until it covers the whole picture and crops whatever overflows. Fit scales the frame to sit inside the picture and adds black bars where it does not reach, so nothing is cut off.",
    },
  ],
  "trim-video": [
    {
      question: "Will trimming reduce my video's quality?",
      answer: "Individual segment trims use a fast method that doesn't re-encode the video, keeping quality identical to the original. The trade-off is that it can only cut precisely at the video's existing keyframes, so a segment may start up to a second or two off from the exact time you typed.",
    },
    {
      question: "Can I combine multiple trimmed segments into one file?",
      answer: "Yes — use Merge All to combine every segment you've added into a single file, in the order they're listed. Merging re-encodes the combined result, which takes longer than trimming alone.",
    },
    {
      question: "What is the difference between Exact and Fast?",
      answer: "Exact re-encodes the clip to MP4 (H.264 video, AAC audio at 128 kbit/s) so it starts at your typed time, but takes longer. Fast copies the streams without re-encoding, so it is quick and lossless, yet it starts at the nearest earlier key frame.",
    },
    {
      question: "How do I type times?",
      answer: "Plain seconds such as 65.5, minutes and seconds such as 1:05.5, or hours:minutes:seconds. End must be later than start, and start must fall inside the video's length, otherwise a message appears and nothing is processed.",
    },
    {
      question: "Can I put several pieces into one file?",
      answer: "Yes. Add a segment for each piece, press Trim All Segments, then press Merge All & Download. Pieces are joined in the order listed and saved as one MP4. Merging needs at least two finished segments.",
    },
    {
      question: "Why does it say it is downloading a processing engine?",
      answer: "The cutting is done by FFmpeg running inside your browser, and that engine, about 25 MB, is fetched from a public CDN the first time you trim. It is the engine that is downloaded; your video is never sent anywhere.",
    },
    {
      question: "What format do the downloaded clips have?",
      answer: "Exact cuts and merged files are MP4. A Fast cut keeps the original file extension, unless that segment fell back to an Exact cut, in which case it is MP4. The toast message after trimming states which applied.",
    },
  ],
  "video-to-gif": [
    {
      question: "Is there a recommended video length for making a GIF?",
      answer: "Keep the source clip under about 10 seconds if possible — GIFs don't compress as efficiently as video, so longer clips can produce surprisingly large file sizes and take a while to generate.",
    },
    {
      question: "Why is my GIF so much larger than the video?",
      answer: "GIF stores frames with a simple colour-palette method and no video compression between frames, so it grows fast with width, frame rate and duration. Lower Width, drop Frames per second, shorten Length, or choose a Largest file value and let the tool step the settings down.",
    },
    {
      question: "What does the Largest file setting actually do?",
      answer: "It encodes a short sample, estimates the full size, and lowers width and frame rate until the estimate fits with a safety margin. It then makes the real GIF and, if it is still too big, steps down up to two more times. If nothing fits, it reports how many seconds would.",
    },
    {
      question: "Can the GIF include sound?",
      answer: "No. The GIF format has no audio track, so the sound from your video is dropped. If you need the audio too, keep the clip as a video and use the Compress Video tool to make it small enough to share.",
    },
    {
      question: "How do I make the GIF play once instead of looping?",
      answer: "Tick Play once (no loop) before pressing Create GIF. Leave it unticked for a GIF that repeats forever. Changing the checkbox clears any earlier result, so you will need to create the file again.",
    },
    {
      question: "Why does processing fail straight away on my phone video?",
      answer: "Some phone-recorded .mov files use a codec the in-browser engine cannot decode. Re-exporting the clip from your phone's share or save options, or converting it to MP4 first, normally gets past it. A blocked network can also stop the engine download.",
    },
  ],
  "audio-compressor": [
    {
      question: "What bitrate should I use for a voice memo versus music?",
      answer: "128 kbps is a reasonable, widely-used balance for spoken word and most music. Go lower (64-96 kbps) for voice memos or podcasts where small size matters more than fidelity, or higher (192-256 kbps) for music where quality matters more.",
    },
    {
      question: "How does this calculate the target file size?",
      answer: "It's calculated directly from the file's length — audio bitrate encoding is predictable enough that hitting a target size doesn't need the trial-and-error search that video compression requires.",
    },
    {
      question: "Will the output always be an MP3?",
      answer: "Yes. Whatever you upload, the compressed file is encoded as MP3 with the bitrate chosen by the mode, and the download name ends in .mp3. If you need to stay in the original format, this tool is not the right choice.",
    },
    {
      question: "How does Target size work?",
      answer: "You type a size in MB. The tool divides 97 percent of that by the file's length to find a bitrate, never going below 32 kbps, and encodes once. Because MP3 overhead varies, the result can land slightly off, and extremely small targets come out larger than asked.",
    },
    {
      question: "Why are some bitrates greyed out in Manual Bitrate?",
      answer: "Any value at or above the file's own bitrate cannot make it smaller and often makes it bigger, so those chips are disabled. The note beneath shows the roughly measured bitrate of your file, which tells you which values are usable.",
    },
    {
      question: "Why did it say the file is already well compressed?",
      answer: "The re-encoded MP3 came out the same size or larger than your original, which happens with already efficient files such as Opus voice notes. Rather than hand you a worse file, the tool shows no download and suggests keeping the original.",
    },
    {
      question: "Does Convert to mono really halve the file size?",
      answer: "Not by itself. The bitrate you or the preset chose is applied either way, so the size stays near that target. Mono gives a single voice the whole bitrate, which can sound a little cleaner at low settings, but it is not a size guarantee.",
    },
  ],
  "audio-converter": [
    {
      question: "When should I use WAV or FLAC instead of MP3?",
      answer: "WAV and FLAC are uncompressed or losslessly compressed, so they sound identical to the source but produce much larger files. Use those when quality matters more than size, like archiving a master recording — MP3 remains the safest default for the widest compatibility.",
    },
    {
      question: "What does Same as original do?",
      answer: "It estimates the bitrate of your source from its file size and duration and encodes the output at a matching bitrate. The summary line states the figure used. It is the best choice when you want the same quality in a different format.",
    },
    {
      question: "What happens if I drop a file that is not audio?",
      answer: "It is turned away as soon as it loads: a message says Couldn't read this audio file, it may be damaged or not audio, and that nothing was used from your trial, and Convert stays off. A text file renamed .mp3 is caught this way. Formats your browser cannot play but that are real audio (such as WMA or AMR) are still accepted so the converter can try them.",
    },
    {
      question: "Can I convert several files at once?",
      answer: "Yes. Choose Several files (ZIP) at the top, pick the format and quality once, and drop the files. Each is converted with those settings (Same as original uses each file's own bitrate) and you download one ZIP. A file that cannot be converted is marked on its own row and does not stop the others. The whole batch counts as one use, taken when you download (one file or the ZIP) and never when you press Convert all or if every file fails; up to 5 files on the trial, more on a paid plan.",
    },
    {
      question: "Can I trim the file after converting it?",
      answer: "Yes. Next to Download there is a Continue with Audio Trimmer button that carries the converted file over, so you do not need to download and re-upload it.",
    },
    {
      question: "Why is my WAV file so much bigger than the MP3?",
      answer: "WAV stores uncompressed 16-bit audio, so its size depends only on the length of the recording. MP3, OGG and M4A discard some detail to be small. Choose a lossy format if size matters and WAV or FLAC if you want to keep everything.",
    },
    {
      question: "Will the converted file keep its title and artist tags?",
      answer: "The tool copies the metadata of the source into the new file, so tags such as title and artist normally come across. Cover art is kept for MP3, M4A and FLAC outputs, and left out of OGG and WAV.",
    },
    {
      question: "Does it change my original file?",
      answer: "No. The original stays untouched on your device, and you download a separate new file. If the output format matches the input's extension, the new file gets -converted added to its name so the two cannot be confused.",
    },
    {
      question: "How accurate is the estimated size?",
      answer: "It is bitrate multiplied by duration, so for MP3, OGG and M4A it is close but approximate, since encoders vary the rate. It is not shown for WAV or FLAC, whose size follows the audio content instead.",
    },
  ],
  "audio-merger": [
    {
      question: "When is a trial use taken for merging audio?",
      answer: "When you download the merged file, not when you press Merge Audio. Merging and listening to the result cost nothing, a merge that fails costs nothing, and downloading the same result again in the same visit does not take another use.",
    },
    {
      question: "Can I merge audio files that are in different formats?",
      answer: "Yes — clips are re-encoded to a consistent format during merging, so mixing different source formats (like an MP3 and a WAV) won't break the merge.",
    },
    {
      question: "Can I merge an MP3 and a WAV into one file?",
      answer: "Yes. Each clip is decoded on its own and the joined result is encoded once into the format you pick in Output format, so mixed sources are fine. Leave it on Same as first file to follow the top clip, or choose MP3, WAV, OGG, M4A or FLAC explicitly.",
    },
    {
      question: "How is the quality of the merged file decided?",
      answer: "For MP3, OGG and M4A output, the bitrate is estimated from the first clip's file size and length and rounded down to a standard step, so it never exceeds that clip. If the first clip is WAV or FLAC and you choose a lossy format, 192 kbps is used. WAV and FLAC are lossless.",
    },
    {
      question: "How do I add a pause between clips?",
      answer: "Use Gap between clips: none, 0.25, 0.5, 1, 2, 3 or 5 seconds. The silence is added after every clip except the last one, and the Total length line includes it. You cannot set a different pause for each junction.",
    },
    {
      question: "Can I crossfade between clips?",
      answer: "Yes. Crossfade between clips (up to 5 seconds) overlaps the end of each clip with the start of the next using an equal-power curve, so the merged file is shorter by the overlap: two 1 s clips with a 0.5 s crossfade make 1.5 s. A crossfade is never longer than half of either clip, so a short clip shortens it, and a note says so. A gap and a crossfade are alternatives; choosing one clears the other.",
    },
    {
      question: "Can I change the order by dragging?",
      answer: "Yes. Drag a row by its grip (mouse, finger or pen), or use the Move up and Move down buttons, which also work from the keyboard. The list order is the playing order, and every row shows its length and sample rate.",
    },
    {
      question: "Will clips with different sample rates play at the right speed?",
      answer: "Yes. All clips are brought to the first clip's sample rate, so none is played at the wrong speed or pitch, and a note names each clip that was resampled, for example 2nd clip resampled 48 \u2192 44.1 kHz. Put the clip whose rate matters first.",
    },
    {
      question: "Why is the Merge Audio button disabled?",
      answer: "It needs at least two files in the list, it is disabled while a merge is running, and it is also blocked when the combined size is over the allowed limit, in which case a notice under the list explains it. Add another clip or remove a large one.",
    },
    {
      question: "What will the downloaded file be called?",
      answer: "The name says what it is: merged-2-clips.mp3 for two clips, merged-5-clips.wav for five, with the extension of the chosen output format, so it never looks like one of your own files.",
    },
  ],
  "audio-normalizer": [
    {
      question: "Is my audio uploaded?",
      answer: "No. Measuring and changing the volume both run in your browser, so the recording is not uploaded. The only download is the audio engine, about 25 MB, the first time an audio or video tool needs it.",
    },
    {
      question: "Does it show the loudness before and after?",
      answer: "Yes. After normalizing, a line under the player shows the measured loudness before and after in LUFS and the gain applied in dB. The \"after\" figure is measured on the saved file, not guessed.",
    },
    {
      question: "Is this the same as just making everything as loud as possible?",
      answer: "No — this targets a broadcast-standard loudness level (EBU R128, the same standard used by broadcasters and streaming platforms), which is different from simple peak normalization. It's built to make speech and music sound consistently loud without clipping or feeling overly compressed.",
    },
    {
      question: "What is the difference between LUFS and peak normalization?",
      answer: "LUFS targets perceived loudness over the whole file, which is what streaming and broadcast standards use. Peak normalization only raises the loudest instant to a ceiling, so a file with one sharp spike can still sound quiet. The first four targets use LUFS; Peak to -1 dB is the peak method.",
    },
    {
      question: "Which target should I choose for a podcast?",
      answer: "Podcast (-16 LUFS) uses a peak ceiling of -1.5 dB. Spotify normalises to -14 LUFS, so pick Spotify / streaming (-14 LUFS) for that; the YouTube option uses the same -14 LUFS as a common streaming reference (YouTube publishes no official target). Broadcast is -23 LUFS. Audiobook (ACX) aims at about -20 dB RMS (LUFS used as a proxy) with peaks held to -3 dB. Check the requirements of the place you are publishing.",
    },
    {
      question: "Why did my file end up quieter than the target?",
      answer: "Reaching the target would have pushed loud peaks past the ceiling for that preset, so the tool stops the gain at the safe limit and says so in the summary. A single constant gain cannot fix this; reducing the peaks needs a limiter or compressor.",
    },
    {
      question: "Does it change the sound quality or the format?",
      answer: "It changes only the volume, with no compression or equalizer. The file is re-encoded in the same family as the original, so a WAV stays lossless while an MP3 is encoded again once and may lose a trace of quality.",
    },
    {
      question: "Can it normalize several files together so they match?",
      answer: "It handles one file at a time. Running each file through the same target gives them similar loudness, though they are measured and adjusted separately, so tracks with very different peaks can still end up a little apart.",
    },
  ],
  "audio-scrambler": [
    {
      question: "Is this enough to fully anonymize someone's identity in a recording?",
      answer: "It's a real, effective way to make a voice hard to recognize casually, but it isn't forensic-grade anonymization — speech patterns, cadence, and word choice can still potentially identify someone regardless of pitch shift. For genuinely high-stakes anonymity needs, treat this as one layer, not the only one.",
    },
    {
      question: "Is the voice scrambled or encrypted, and can it be undone?",
      answer: "No. The tool shifts the pitch up or down by a number of semitones and keeps the timing. Anyone with the output can shift it back by the opposite amount and get close to the original voice, so it is a disguise, not protection.",
    },
    {
      question: "Why can't I click Disguise Voice?",
      answer: "Both Preview 8 s and Disguise Voice are disabled while the slider is at 0 semitones, because nothing would change. They are also disabled during processing or when the file is over the size limit shown in a notice.",
    },
    {
      question: "Does the audio get faster or slower?",
      answer: "No. The pitch is moved and then the tempo is compensated, so the recording keeps its original length. Voices sound deeper at negative values and lighter or higher at positive ones, and the words stay at the same speed.",
    },
    {
      question: "What does Preview 8 s actually do?",
      answer: "It processes only the first eight seconds of your file at the current setting and plays it back, so you can compare settings quickly. Changing the slider clears the preview, and the full file is only made when you press Disguise Voice.",
    },
    {
      question: "Will repeating the process make it harder to reverse?",
      answer: "Not much. Two shifts simply add up, so +6 followed by -6 returns near the start. The page suggests a large shift followed by a different one for a stronger effect, but each pass also adds a little audible processing noise.",
    },
    {
      question: "Do I need permission to record someone?",
      answer: "The FCC has no rules on individuals recording their own phone calls, but some state laws restrict recording. Laws differ by place and situation, so get consent from the people involved where in doubt. This is general information, not legal advice. Check the official source.",
      source: { label: "FCC: Recording Telephone Conversations", url: "https://www.fcc.gov/consumers/guides/recording-telephone-conversations" },
    },
  ],
  "audio-speed-changer": [
    {
      question: "Will speeding up my audio make it sound like a chipmunk?",
      answer: "No — unlike simply playing a file faster (which raises pitch, the classic \"chipmunk\" effect), this keeps voices and music sounding natural at a different speed by adjusting speed and pitch independently.",
    },
    {
      question: "How extreme a speed change can this handle?",
      answer: "It works well up to about 2x or down to about 0.5x. Pushed further than that, audio can start to sound artifacted or robotic, since the technique has to stretch or compress the waveform more aggressively while trying to preserve pitch.",
    },
    {
      question: "Will the voice sound higher when I speed it up?",
      answer: "No. The tool changes the tempo while holding the pitch, so a 1.5x file still has the same voice tone, only faster. If you hear a slight warble, it comes from the stretching method, not a pitch change.",
    },
    {
      question: "What speeds can I pick?",
      answer: "Anything from 0.5x to 4x in steps of 0.05 using the slider, plus preset buttons for 0.5x, 0.75x, 1x, 1.25x, 1.5x, 2x, 2.5x, 3x and 4x. The label also shows how long the new file will be.",
    },
    {
      question: "Can I change the pitch as well as the speed?",
      answer: "Yes. A separate Pitch slider moves the pitch up or down by as much as an octave (12 semitones), independently of the speed slider. Leave it in the middle and the pitch stays exactly as it was. The result keeps your file's own sample rate.",
    },
    {
      question: "Can I hear the result before I download it?",
      answer: "Yes. After you press Change Speed the processed audio appears on the page with its exact length, and you can play it there. Playing it costs nothing; a trial use is only taken when you download.",
    },
    {
      question: "Why is Change Speed disabled?",
      answer: "It is disabled while the speed sits at 1x and the pitch is unchanged, since that would produce an identical file. Move a slider or choose a preset. It is also disabled during processing and if the file exceeds the size limit that a notice would show.",
    },
    {
      question: "Does it work on a video file?",
      answer: "The upload accepts audio, but the processing maps only the first audio stream and drops any picture, so a video that your browser lets through would come out as audio only. Use a video tool if you need picture and sound to stay together.",
    },
    {
      question: "What is the output format and name?",
      answer: "The format follows the source family: WAV stays WAV, FLAC stays FLAC, M4A-type files stay M4A, OGG-type files stay OGG and everything else becomes MP3. The name is speed_ plus the multiplier and the original name.",
    },
  ],
  "audio-trimmer": [
    {
      question: "Is the trim point exact, or snapped to the nearest available point?",
      answer: "It's exact — this re-encodes the audio to trim precisely at the time you specify, unlike a fast video-style copy trim that snaps to the nearest keyframe. It takes a little more processing time in exchange for that precision.",
    },
    {
      question: "Will trimming lower the sound quality?",
      answer: "WAV and FLAC are written losslessly. MP3, M4A and OGG are encoded again at high settings (MP3 VBR level 2, M4A 192 kbit/s, OGG quality 5), so a very small generation loss is possible. Keep your original file.",
    },
    {
      question: "How precise is the cut?",
      answer: "The slider moves in 0.1 second steps, and the Start and End boxes accept any decimal value. Because the clip is re-encoded, it begins at the time you set instead of snapping to the nearest frame boundary of the source.",
    },
    {
      question: "Can I cut a section out of the middle instead of keeping a range?",
      answer: "Yes. Choose Remove selection above the slider, set the start and end of the part to delete (a cough, an ad read) and press Remove Section. The part before and the part after are joined with a few milliseconds of fade so the join does not click. Keep selection, the default, does the opposite and keeps only the range.",
    },
    {
      question: "Can I trim a video file?",
      answer: "Yes, the sound of a video can be trimmed here: drop an MP4, MOV, WebM or MKV and the result is saved as audio (M4A for MP4, otherwise MP3). A video with no audio track is reported as soon as it loads. To take the whole sound track out without trimming, use Extract Audio.",
    },
    {
      question: "What happens if I type an end time before the start time?",
      answer: "A message appears under the Start and End boxes naming both times, the box keeps what you typed so you can fix it, and the Trim button stays off until the times make sense.",
    },
    {
      question: "What does the Ringtone (30 s) button do?",
      answer: "It leaves the start where it is and sets the end 30 seconds later, or at the end of the file if that comes first. You can still adjust either end afterward, and add fades before trimming.",
    },
    {
      question: "What format will the trimmed file be?",
      answer: "The same family as the one you dropped: WAV, FLAC, M4A or OGG when it matches one of those, and MP3 for anything else. The download name is trimmed_ followed by the original name with the new extension.",
    },
    {
      question: "Can I cut an MP3 without losing quality?",
      answer: "Not bit for bit. Every trim here decodes the clip and encodes it again, so an MP3 is written again at high settings (VBR level 2) and a very small generation loss is possible. The cut point stays exact in exchange. If you need a lossless copy, choose a WAV or FLAC file, which are written without loss, and keep your original MP3.",
    },
    {
      question: "Can I make an iPhone ringtone?",
      answer: "This tool cuts the clip to the times you set (the Ringtone (30 s) button sets a 30-second end for you), but it keeps your file's format and does not save the .m4r file an iPhone needs. For an iPhone ringtone, use the Ringtone Maker, which saves an M4R for iPhone or an MP3 for Android.",
    },
    {
      question: "Why is the Trim Audio button greyed out?",
      answer: "It is disabled while the file's length is still being read, while a trim is running, or when the size notice says the file is too large to process. Wait for the player to show a duration, then it becomes active.",
    },
  ],
  "audio-comparer": [
    {
      question: "Is this an exact match like Shazam uses to identify songs?",
      answer: "No, and it's worth being upfront about that — this is a real spectral/acoustic similarity comparison (frequency content and loudness pattern over time), not exact byte-for-byte or commercial audio-fingerprinting matching. Two different recordings of the same song will usually score high, and two completely different tracks will score low, but this isn't forensic-grade duplicate detection.",
    },
    {
      question: "Does it matter if the two files are different lengths?",
      answer: "Not significantly — files of different lengths are compared on a normalized time basis, each divided into equal segments, so a shortened or extended version of the same audio can still score reasonably high.",
    },
  ],
  "noise-remover": [
    {
      question: "Can this remove background noise from a busy, changing environment like a crowded room?",
      answer: "Not well — it handles steady, constant background noise well (hum, hiss, fan noise), but isn't the same as AI-based voice isolation in tools like Krisp or Adobe Podcast Enhance, which can separate a voice from more chaotic or shifting background noise. For a steady noise floor, this genuinely helps; for a complex noisy environment, results will be more limited.",
    },
    {
      question: "What do Light, Standard and Aggressive actually change?",
      answer: "They set how many decibels of noise the filter tries to remove, 12, 20 or 35, and how far above your file's measured noise floor sound still counts as noise, 8, 13 or 20 dB. Higher settings clean more but risk dulling the voice.",
    },
    {
      question: "When should I use the Voice level?",
      answer: "Choose it for speech with changing background noise like fans cycling on, traffic or keyboard clicks. It uses a small RNNoise voice model, loaded from this site, and is not suited to music, which can sound damaged by it.",
    },
    {
      question: "How does the tool know how loud the noise is?",
      answer: "It decodes the file in your browser and averages the power of the quietest quarter of short 50 millisecond windows, ignoring digital silence. That value sets the filter's noise floor, limited to the range -80 to -20 dB, so the setting is tuned to each file.",
    },
    {
      question: "What does the dB figure under the player mean?",
      answer: "After processing, the tool compares the same quietest 10 percent of 100 millisecond stretches before and after. The drop in power there is shown as the reduction. It describes the pauses, not the speech, so it is an indication rather than a guarantee.",
    },
    {
      question: "Why do I hear a watery or robotic sound afterwards?",
      answer: "Strong spectral reduction removes some of the voice along with the noise. Go back a level, and avoid Aggressive on thin recordings. If the noise is not steady, try the Voice level or record again in a quieter space.",
    },
  ],
  "silence-remover": [
    {
      question: "Does this only trim silence from the start and end?",
      answer: "No — it trims silence throughout the entire file, looking for any stretch quieter than the threshold and at least as long as the minimum duration you set, wherever it occurs.",
    },
    {
      question: "Can I keep a pause I want, like a dramatic beat?",
      answer: "Yes. Every pause that would be cut is listed with its time range (for example 0:01 to 0:03). Un-tick any pause to leave it exactly as it was; Cut all and Keep all change every pause at once. The length shown under the list updates as you tick, and Preview the result plays the outcome on the page without using a trial use.",
    },
    {
      question: "What do I do if it's cutting parts that shouldn't be silent?",
      answer: "Try lowering the threshold — a more negative dB value is stricter about what actually counts as \"silent,\" which reduces the chance of trimming quiet-but-intentional audio.",
    },
    {
      question: "What does the silence threshold actually mean?",
      answer: "It is a level in dB. The audio is split into 20 millisecond windows, and a window counts as silent if its loudest channel is below the threshold. At -35 dB quiet room tone is usually caught, while -15 dB would treat most soft speech as silence.",
    },
    {
      question: "What does the Auto threshold button do?",
      answer: "It decodes the file in your browser, estimates the background noise from its quietest parts and sets the slider 10 dB above that, rounded to a multiple of 5 and kept between -60 and -15 dB. A message tells you the noise level it found.",
    },
    {
      question: "Will it remove every pause completely?",
      answer: "With Remove them completely, it cuts each qualifying stretch and keeps only about 15 milliseconds of quiet beside the speech, so words are not clipped but no pause of 0.1 second or more is left behind. The shorten options leave a pause of 0.3, 0.5 or 1 second instead, and the result line says which one was used, for example 1 pause shortened to 0.3 s.",
    },
    {
      question: "Why did it say no silence was found?",
      answer: "No stretch was both quieter than the threshold and longer than the Minimum Duration. Try a higher threshold, such as -30 or -25 dB, or shorten the minimum to 0.3 seconds. If the whole file is below the threshold, lower it instead.",
    },
    {
      question: "Does it change the sample rate?",
      answer: "No. The saved file has the sample rate of the file you dropped, so a 44.1 kHz recording comes back at 44.1 kHz, not 48 kHz. The result line states the rate it was saved at. Only the format family can differ for unusual inputs, as the next answer explains.",
    },
    {
      question: "How much pause is left?",
      answer: "That is the Pauses menu. Remove them completely leaves only about 15 milliseconds of quiet beside the speech, so a 3.5 second clip with a 1.5 second pause comes out at about 2.0 seconds. Shorten to 0.3, 0.5 or 1 s leaves a pause of about that length, so the same clip with 0.5 s comes out at about 2.5 seconds. The list under the sliders shows each pause before anything is cut.",
    },
    {
      question: "Does it change the format or quality of my file?",
      answer: "The kept audio is passed through a temporary 16-bit WAV and then encoded to the source family: WAV stays WAV, FLAC stays FLAC, M4A-type becomes M4A, OGG-type becomes OGG, otherwise MP3. Lossy files are therefore encoded once more. The sample rate is the one your file had (a 44.1 kHz file stays 44.1 kHz), and the result line states it.",
    },
  ],
  "voice-recorder": [
    {
      question: "What audio format does the recording save as?",
      answer: "It depends on your browser, usually WebM or MP4 audio. If you need a specific format like MP3 or WAV for another program, run the downloaded file through the Audio Converter tool afterward.",
    },
    {
      question: "Why does the page say microphone access was denied?",
      answer: "Browsers ask for permission only once per site, and a Block choice is remembered. Click the lock or info icon beside the address bar, set Microphone to Allow, and reload the page. The message on the page repeats these steps and also covers installed shortcuts.",
    },
    {
      question: "What file format will I get?",
      answer: "Whatever your browser can record, tried in this order: WebM with Opus, WebM, MP4 and OGG with Opus. Chrome and Edge usually give WebM, while some Safari versions give an M4A file. Download as MP3 converts the take into a widely playable MP3.",
    },
    {
      question: "Does pausing leave a gap of silence in the recording?",
      answer: "No. Pause suspends the recorder itself, and the timer stops counting, so the paused time is not in the file. Resume continues the same take. The timer shows paused next to the time while you wait.",
    },
    {
      question: "What happens if I close the tab or the browser crashes?",
      answer: "The recording is lost, because it exists only in that tab's memory until you download it. While you are recording, the browser shows a confirmation if you try to close or leave the page. Download the file as soon as you stop.",
    },
    {
      question: "Can I choose a different microphone, like a headset?",
      answer: "Yes, when your browser reports more than one input a Microphone menu appears before recording. Pick the device by name. If a chosen device was unplugged since you selected it, the tool falls back to the default microphone.",
    },
  ],
  "word-counter": [
    {
      question: "Do character counts include spaces?",
      answer: "Yes, the character count reflects everything in the text box exactly as typed, including spaces — useful specifically because most platforms with a character limit (like social media) count spaces too.",
    },
  ],
  "case-converter": [
    {
      question: "Does Title Case capitalize every single word?",
      answer: "No — Title Case tools generally lowercase small connecting words automatically (like \"a,\" \"the,\" and \"and\"). Check the result against your specific style guide if you need exact control over which words get capitalized, since guides don't always agree on this.",
    },
    {
      question: "Which small words does Title Case leave in lowercase?",
      answer: "The words a, an, the, and, but, or, for, nor, on, at, to, by, in, of, up and as stay lowercase in the middle of a line. The first and last word of each line are always capitalized, whatever they are.",
    },
    {
      question: "Does it keep acronyms like NASA or API?",
      answer: "Yes, when your text is mixed case: a word already typed as NASA, API or iPhone keeps its capitals in Title Case and Sentence case. If the whole text is in ALL CAPS there is nothing to tell an acronym from a shout, so every word is lowercased first and an acronym needs to be retyped.",
    },
    {
      question: "Can I undo a conversion?",
      answer: "The Restore original button returns the text you had before your first conversion, even if you clicked several styles since. It disappears once you edit the box yourself, so use it before typing anything further.",
    },
    {
      question: "What happens to apostrophes in camelCase or snake_case?",
      answer: "Apostrophes are removed without splitting the word, so fox's becomes foxs. Other punctuation acts as a separator and is dropped, which means a phrase like 'user-id (new)' turns into the three words user, id and new.",
    },
    {
      question: "Does it work with accented letters and other alphabets?",
      answer: "Yes for Title Case, Sentence case and the programming styles, which recognize letters in any script, so a word beginning with an accented letter is capitalized on that first letter. Scripts without upper and lower case are left as they are.",
    },
    {
      question: "Is my text uploaded when I convert it?",
      answer: "No. The conversion runs in your browser, so the text you paste is not sent anywhere and is gone when you close the page.",
    },
  ],
  "text-paraphraser": [
    {
      question: "Is the rewritten text ready to use as-is?",
      answer: "Treat it as a starting draft to edit, not a finished result — the best writing still benefits from a human pass to make sure it actually sounds like you and fits the context it's going into.",
    },
    {
      question: "Is this a language model?",
      answer: "No. It applies hand-written rules: phrase swaps, a short single-word list and, on Strong, a few sentence-level changes. There is no learning and no text generation, which is why the changes are modest and each one is highlighted for you to keep or undo.",
    },
    {
      question: "What is the difference between Light, Medium and Strong?",
      answer: "Light swaps whole phrases, such as in order to becoming to. Medium adds single words, such as big becoming large or buy becoming purchase. Strong adds the rest: moving a because clause to the front, replacing But, Also and So at a sentence start, and switching between contracted and full forms.",
    },
    {
      question: "Why does Rewrite again sometimes give the same result?",
      answer: "Only rules with more than one alternative, and the optional because reorder on Strong, vary between runs. Many phrases have a single replacement, so a text built from those reads the same each time. Try a different strength instead.",
    },
    {
      question: "Can I undo a single change?",
      answer: "Yes. Every changed spot is shaded, and clicking it puts the original words back right there without touching the rest. Hovering shows the original text. The change count under the output goes down as you restore spots.",
    },
    {
      question: "Will it work on languages other than English?",
      answer: "No. The rules are English phrases, and any line with no Latin letters is passed through unchanged. Text in another Latin-script language may be left alone or have an English-looking word swapped by accident, so use the tool for English only.",
    },
    {
      question: "Is my text sent anywhere?",
      answer: "No. The rewriting runs in your browser, so the text you paste stays on your device. Only a plain usage tick (the tool name and an anonymous device id) is recorded.",
    },
    {
      question: "Will it keep my meaning?",
      answer: "It aims to: every swap comes from a hand-checked list, not a language model. Still read the result, because a word that fits one sentence can be slightly off in another. Click any highlighted change to put the original back.",
    },
  ],
  "grammar-checker": [
    {
      question: "Can this catch every kind of writing mistake?",
      answer: "It's good at catching mechanical errors — misplaced commas, subject-verb mismatches, doubled words — but can miss context-dependent issues, like a sentence that's grammatically correct but doesn't say what you actually meant. A final read-through by a person is still worth doing for anything important.",
    },
  ],
  "lorem-ipsum": [
    {
      question: "Should I match the placeholder text length to my real content?",
      answer: "Yes — generating placeholder text at roughly the same length as your real content gives a much more accurate sense of how a layout will actually look once it's filled in. A layout that looks fine with two short placeholder sentences can break entirely once real, longer content goes in.",
    },
    {
      question: "What does the Start with \"Lorem ipsum dolor sit amet\" switch change?",
      answer: "With it on, Paragraphs mode starts the first paragraph with the classic sentence, Sentences mode makes the first sentence that line, and Words mode begins with its first eight words. With it off, everything is random.",
    },
    {
      question: "How much text can I generate at once?",
      answer: "Up to 1000 paragraphs, sentences or words per press. A larger number is reduced to 1000, and zero or an invalid entry becomes 1, with a short notice under the controls explaining the change.",
    },
    {
      question: "How long are the generated paragraphs and sentences?",
      answer: "A paragraph contains between 3 and 7 sentences, and a sentence has between 4 and 12 words, sometimes with a comma. Each sentence starts with a capital letter and ends with a period.",
    },
    {
      question: "Is this the original Latin passage?",
      answer: "No. The words come from the traditional filler vocabulary, but they are picked at random, so the sentences have no meaning. Only the optional opening line is the familiar fixed sentence.",
    },
    {
      question: "What does Wrap in <p> tags produce?",
      answer: "Each paragraph is enclosed in opening and closing paragraph tags and placed on its own line. The option appears only for Paragraphs, and the word and character counts above the output ignore the tags.",
    },
  ],
  "find-replace": [
    {
      question: "What file types can I upload for find and replace?",
      answer: "Pasted text works directly, and you can also upload .txt, .json, .csv, .md, .html, .xml, or .docx files — the result downloads back in the same format you uploaded.",
    },
    {
      question: "Why might a replacement be missing in my .docx file?",
      answer: "For a .docx file, the replace happens inside the actual document so formatting is preserved — but if a search term happens to span a formatting change (part bold, part not, for example), it won't be found at that exact split point. This is a real limitation of editing a Word document this way, not specific to this tool.",
    },
    {
      question: "Is my Word formatting kept when I edit a .docx?",
      answer: "Yes. The text inside the document's runs is edited in place, so fonts, images and layout stay. A phrase split across several formatted pieces is still found, and the replacement takes the formatting of the piece where the match begins.",
    },
    {
      question: "What does Preserve case do?",
      answer: "It makes the replacement copy the style of each match: an all-capitals match gives an all-capitals replacement, a Capitalized match gives a replacement with a capital first letter, and an all-lowercase match uses your text as typed.",
    },
    {
      question: "How do I reuse part of the match in the replacement?",
      answer: "Turn Regex on, put parentheses around the part in Find, and write a dollar sign plus the group number (one, two and so on) in Replace with. A dollar sign and ampersand insert the whole match, and a dollar sign with a name in angle brackets inserts a named group. In this mode \\n and \\t become a new line and a tab.",
    },
    {
      question: "Is the search case sensitive, and what counts as a whole word?",
      answer: "Not by default; switch on Case sensitive to make it so. Whole word requires that the match is not touched by a letter, digit or underscore on either side, and it understands letters from any language, not just English.",
    },
    {
      question: "Which files can I upload, and what about old .doc files?",
      answer: ".txt, .json, .csv, .md, .html, .xml and .docx. A .doc file is refused with advice to save it as .docx in Word first, and any other extension gets a message listing the supported types.",
    },
    {
      question: "Can I find and replace in a Word document?",
      answer: "Yes. Drop the .docx file, set Find and Replace with, press Replace All and download the corrected file. The text is edited in place, so fonts, images and layout stay.",
    },
    {
      question: "Does find and replace support regex?",
      answer: "Yes, JavaScript regex syntax with $1 style groups. Switch Regex on, put parentheses around the part you want to reuse and write $1 in Replace with.",
    },
    {
      question: "Are headers, footers and footnotes changed in a .docx?",
      answer: "Not yet. Only the main body of the document is edited; headers, footers and footnotes are left as they are.",
    },
    {
      question: "Is my file uploaded when I use find and replace?",
      answer: "No. Pasted text and files are processed in your browser and nothing is sent to a server.",
    },
  ],
  "text-reverser": [
    {
      question: "Why does my text look unchanged after removing duplicates?",
      answer: "If your text doesn't have any repeated lines or words, or is already in order, the result will look identical to the input — that's correct, not a sign the tool didn't run. A confirmation message shows exactly what happened either way.",
    },
    {
      question: "Does duplicate removal ignore capitalization or extra spaces?",
      answer: "No — both dedupe functions compare exactly, including whitespace and capitalization. Trim extra spaces or match case first if near-identical lines or words aren't being caught as duplicates.",
    },
    {
      question: "Does Reverse Text break emoji or accented letters?",
      answer: "No. It splits the text into user-perceived characters before reversing, so a family emoji, a flag, a skin-tone emoji or a letter with a combining accent is moved as one unit instead of being torn apart.",
    },
    {
      question: "What is the difference between Reverse Lines and Reverse Words?",
      answer: "Reverse Lines swaps the order of the lines, so the last line becomes first, while each line's own text is unchanged. Reverse Words keeps the lines where they are and reverses the order of the words inside each one.",
    },
    {
      question: "Which copy is kept when duplicates are removed?",
      answer: "The first one. The comparison uses a trimmed and optionally lowercased version of each line or word, but the copy that stays is the original, untouched text, so its capital letters and spacing are preserved.",
    },
    {
      question: "Is there a size limit on the text?",
      answer: "The code sets no cap on the amount of text. Very large pastes can make the page slow, because everything is processed in your browser tab at once, so split an enormous file into parts if the page lags.",
    },
    {
      question: "Why does Sort A-Z leave my lines looking the same?",
      answer: "They were probably already in order, and the message after the click says so. Sorting is also numeric aware, so file2 comes before file10, which can look unsorted if you expect plain character order.",
    },
  ],
  "text-to-speech": [
    {
      question: "Can I download the spoken audio as a file?",
      answer: "No — browsers deliberately don't expose the underlying audio stream from this speech API for privacy and security reasons, so there's no file to capture. This tool is built to read text aloud in the browser, so it does not produce an audio file.",
    },
    {
      question: "Why do I only see one or two voice options?",
      answer: "The voices and languages available come entirely from your own device and browser, not from this site. Install additional language or voice packs in your operating system's accessibility or language settings to get more options here.",
    },
    {
      question: "The Speak button is disabled. What is wrong?",
      answer: "Either the text box is empty, or the browser has not reported any voices. Voices often load a moment after the page opens, and a browser or system without speech synthesis shows none. Wait briefly, then try another browser or install a system voice.",
    },
    {
      question: "Why did a different voice read my Hindi or Chinese passage?",
      answer: "The tool checks the writing system of each sentence. If it differs from your selected voice's language, it looks for an installed voice for that script and uses it. If your device has none, the selected voice is used and may read it poorly.",
    },
    {
      question: "Can I start from the middle of the text?",
      answer: "Yes, once speech has started and the text has more than one sentence. A panel shows every sentence; click the one you want and speech restarts from it. Speed, pitch and volume settings are applied from that sentence on.",
    },
    {
      question: "Why did the speech stop when I edited the text?",
      answer: "Changing the text while it is being spoken stops playback on purpose, because the sentence list changes under it. Make your edit, then press Speak again to start from the beginning.",
    },
  ],
  "password-generator": [
    {
      question: "Is a longer password really more secure than a complex one?",
      answer: "Generally yes — a 16-character password of mixed letters and numbers is often stronger than a 10-character one packed with symbols, since password strength grows much faster with length than with character variety.",
    },
    {
      question: "How random are the passwords it creates?",
      answer: "Each character is chosen with crypto.getRandomValues, your browser's cryptographic random source, using a method that avoids bias toward the first characters of a set. If a chosen type (say, a digit) is missing, the whole password is drawn again, so every password that qualifies is equally likely. Ordinary Math.random is not used anywhere in the process.",
    },
    {
      question: "What does the entropy figure mean?",
      answer: "Entropy in bits measures how hard a secret is to guess, doubling with every extra bit. For a password it starts from length times the base-2 logarithm of the number of possible characters, then takes off the passwords your settings rule out (for example, ones with no digit), and the page shows both numbers. The labels are Weak under 45 bits, Medium from 45, Strong from 70 and Very Strong from 100. The crack time beside it assumes 10 billion guesses a second against a fast-hashed copy; it is an estimate for a password drawn at random like this one, not a promise, and it says nothing about a password you reuse or that has leaked.",
    },
    {
      question: "Should I choose a password or a passphrase?",
      answer: "A passphrase is easier to type and remember. Five words from the 7,776-word list plus the added number come to about 74 bits. The default 16-character password with all four groups comes to about 103 bits, but it really needs a password manager.",
    },
    {
      question: "Can I stop symbols that a website rejects?",
      answer: "You can. With Symbols switched on, an Allowed symbols box appears holding the default set. Delete any character the site refuses, or type your own list. If you empty it, the default set is used again, so switch Symbols off to remove them completely.",
    },
    {
      question: "Is my password sent anywhere, or does it use up a trial use to see one?",
      answer: "It is made in your browser and never sent or stored. The only download is the word list for passphrases. A password appears when the page opens and each time you press Generate, without using a trial use; copying one takes a trial use for the visit.",
    },
    {
      question: "What is the difference between this page and Password Generator & Checker?",
      answer: "This page makes new passwords and passphrases, with the entropy maths. The Password Generator & Checker is for pasting in a password you already have and seeing how it holds up against common words, names, keyboard runs and repeats. Each page links to the other.",
    },
    {
      question: "Does the tool keep a history of what it generated?",
      answer: "History is not kept. Results sit only in the open page and are replaced when you press Generate again, and switching between Password and Passphrase clears them. Nothing is stored for later, so copy a result before you leave or reload the page.",
    },
  ],
  "fancy-font-generator": [
    {
      question: "Will these stylized fonts work everywhere I paste them?",
      answer: "Mostly. These are genuine Unicode characters, not an actual font, so they keep their look when pasted as plain text, unlike a real font that might not be installed on the viewer's device. But an app or device without glyphs for them shows empty boxes, and some sites filter unusual characters, so test where it matters.",
    },
    {
      question: "Are these accessible to screen readers?",
      answer: "No — since they're stylized Unicode symbols rather than normal text, screen readers won't read them the way regular text is read. Use them sparingly in places where accessibility matters.",
    },
    {
      question: "Are these real fonts?",
      answer: "No. They are Unicode look-alike characters, such as mathematical bold letters, that happen to resemble styled text. Because they are different characters, not formatting, they can be pasted as plain text, but they also behave differently from ordinary letters in search, spell check and screen readers.",
    },
    {
      question: "Why do some characters show as empty boxes?",
      answer: "The device or app showing the text has no glyph for that character in any of its fonts. Older systems and some apps lack the mathematical, circled or squared letter ranges. Try another style, or use plain text where the audience may have older devices.",
    },
    {
      question: "What happens to numbers and punctuation?",
      answer: "Digits are restyled by Bold, Double-Struck, Sans-Serif, Sans Bold, Monospace, Fullwidth, Circled and Bubble (filled). Other styles leave them as typed. Punctuation and spaces stay unchanged, except in Upside Down, which flips several marks and reverses the string.",
    },
    {
      question: "Will a site accept this in a username or form field?",
      answer: "That depends on the site. Many fields allow only plain letters and digits, and some filter or normalise unusual characters. The tool cannot tell, so test in the actual field and keep a plain-text version for anywhere identity or search matters.",
    },
    {
      question: "What do the decoration buttons do?",
      answer: "They wrap whichever styled text you copy in a border: sparkles, stars, corner brackets, a dreamy star line, flames, blossoms or arrow dashes. None is the default and adds nothing. The emoji borders will look different on each device.",
    },
  ],
  "invisible-text-generator": [
    {
      question: "Why doesn't the invisible character work on some platforms?",
      answer: "Not every platform accepts every invisible Unicode character the same way — some platforms filter specific Unicode ranges that are commonly used for exactly this purpose. Try a different variant if one doesn't work.",
    },
    {
      question: "How many invisible characters can I copy?",
      answer: "From 1 to 500 in one copy. Type a number, drag the slider or press 1, 5, 10 or 50. A 0 becomes 1 and anything above 500 becomes 500, and a note says so.",
    },
    {
      question: "What is U+2800?",
      answer: "The Braille Pattern Blank, a Unicode character that draws nothing but takes up the width of a letter. Because it counts as a real character, many forms accept it where an empty field is refused.",
    },
    {
      question: "Why was my blank name removed?",
      answer: "Some platforms strip or reject blank characters, and which ones change over time. Paste the text into the Test box to confirm what you copied, then try another character such as Hangul Filler.",
    },
  ],
  "text-sorter": [
    {
      question: "Why isn't my mixed-case list sorting the way I expect?",
      answer: "Turn on \"ignore case\" if your list mixes capitalized and lowercase entries — without it, capital letters sort before lowercase ones in standard alphabetical order, which often isn't the order you actually want.",
    },
    {
      question: "Does Remove duplicates keep the first or the last copy?",
      answer: "The first. Lines are compared after trimming, and in lower case when Ignore case is on, but the line that stays is the original first occurrence with its own capitalisation. Later matching lines are removed before any sorting happens.",
    },
    {
      question: "What does Natural numbers do?",
      answer: "With it on, digits are compared by value, so item 2 comes before item 10. With it off, comparison is character by character and item 10 comes before item 2 because 1 sorts before 2. Leave it on for file names and numbered lists.",
    },
    {
      question: "How does Sort by Length decide between lines of equal length?",
      answer: "Shorter lines come first, and lines with the same length are ordered alphabetically using the same rules as Sort Alphabetically. Turn on Reverse (Z to A) to get the longest lines first.",
    },
    {
      question: "Can I get my original list back?",
      answer: "Yes, once. Undo appears after any action and restores the text from just before the last button press. It does not keep a history, so run Copy Result or keep your own copy before experimenting with several changes.",
    },
    {
      question: "What does Clean only (keep order) do?",
      answer: "It applies Trim spaces, Remove blank lines and Remove duplicates as ticked, and leaves the order alone. If Reverse is switched on it flips the list. The summary line reports how many lines were removed.",
    },
  ],
  "email-extractor": [
    {
      question: "Will this catch an email address written as \"name at domain dot com\"?",
      answer: "No — the tool only catches correctly-formatted email addresses. An address deliberately obscured to avoid spam scrapers (like spelling out \"at\" and \"dot\") won't be recognized as a real address.",
    },
    {
      question: "Does it send email or check that addresses are real?",
      answer: "Neither. It only recognises text shaped like local-part@domain.tld. An address with a typo in the domain or a mailbox that was closed years ago is listed just like a working one.",
    },
    {
      question: "What does the bracket option recognise?",
      answer: "With the switch on, [at], (at), [dot] and (dot) in any letter case, with or without spaces around them, become @ and a full stop before matching. So jo [at] example [dot] org is read as jo@example.org. Plain words without brackets are left alone.",
    },
    {
      question: "How are duplicate addresses handled?",
      answer: "Two addresses count as the same when they match ignoring case. The first spelling found is kept, with the domain lowercased, and the later ones are dropped. The counter shows the number of unique addresses, not the number of times they appeared.",
    },
    {
      question: "Which matches are skipped on purpose?",
      answer: "Anything whose last part is a file type such as png, jpg, gif, svg, webp, css, js, json, woff, ttf, mp4 or pdf, and image names with a size suffix like icon@2x.png. This stops page source and asset names from being mistaken for contacts.",
    },
    {
      question: "What is in the CSV, and does the comma option affect it?",
      answer: "The CSV has the header email,domain and one row per address, using the current sort and lowercase settings. The comma-separated switch changes only what Copy All puts on the clipboard; the CSV always has one address per row.",
    },
  ],
  "url-extractor": [
    {
      question: "Will this find a domain name that's typed without http:// in front of it?",
      answer: "No — this catches full URLs starting with http:// or https://. A bare domain typed on its own in plain text (like just \"example.com\") isn't picked up, since there's no reliable way to distinguish that from a regular word ending in a domain-like suffix.",
    },
    {
      question: "Why was one of my links not listed?",
      answer: "The most common reasons are that it has no https://, http://, ftp:// or www. start, that it is a mailto: or relative link, or that it is shorter than eight characters. A link glued directly to a preceding word or an at sign is also skipped.",
    },
    {
      question: "Which tracking parameters does Remove tracking take out?",
      answer: "Any parameter starting with utm_, plus fbclid, gclid, msclkid, mc_cid, mc_eid, igshid, yclid and ref_src. Other query parameters and the part after # are kept. Links that become identical after cleaning are merged into one entry.",
    },
    {
      question: "What does the CSV contain?",
      answer: "The first row is the header url,domain. Each following row has one entry and its domain, written without the protocol, path or www and in lower case. With Unique domains only switched on, both columns hold the host name. The file is called extracted-urls.csv.",
    },
    {
      question: "Can I paste HTML source instead of plain text?",
      answer: "Yes. The pattern stops at spaces, angle brackets, double quotes and backticks, so addresses inside href and src attributes are picked up. Relative paths in those attributes are not, because they do not start with a protocol or www.",
    },
    {
      question: "Does it check whether the links work?",
      answer: "No. It never connects to any of the addresses; it only recognises their shape in your text. A dead link and a live link are listed the same way, so test important ones in a browser or a link checker.",
    },
  ],
  "slug-generator": [
    {
      question: "Should my slug match my page title exactly?",
      answer: "Not necessarily — keeping slugs reasonably short and focused on the main keywords of the title tends to work better for both readability and search engines than the full title verbatim, especially once a title runs long.",
    },
    {
      question: "How do I make slugs for many titles at once?",
      answer: "Paste one title per line. The result has one slug per line in the same order, and a line that cannot be converted is left blank so the rest stay aligned. Copy Result copies the whole list.",
    },
    {
      question: "What happens to accented letters and characters like the German sharp s?",
      answer: "Accents are stripped so an e with an acute accent becomes e. A handful of letters that do not decompose are mapped by hand, for example the sharp s becomes ss, the o with a stroke becomes o and the ae ligature becomes ae.",
    },
    {
      question: "Can I use an underscore or a dot instead of a hyphen?",
      answer: "Yes. Type one character into Separator. Spaces and runs of hyphens in your text collapse into that single character, and separators at the start and end are trimmed. Leaving the box empty restores the hyphen.",
    },
    {
      question: "Why is one line of my result blank?",
      answer: "That title had no letters or digits the tool keeps, for instance a title in Japanese or only punctuation. Switch on Keep non-Latin letters to retain letters from any script; the warning above the result tells you how many lines were affected.",
    },
    {
      question: "How does Max length shorten a slug?",
      answer: "If the slug is longer than your number, the tool cuts it to that length and then backs up to the last separator, so the result ends on a whole word. With no separator inside the limit, it cuts at the exact character count.",
    },
  ],
  "reading-level-checker": [
    {
      question: "What's the fastest way to lower a reading level score?",
      answer: "Shorter sentences and simpler, more common words are the two biggest levers. Breaking one long sentence with several clauses into two or three shorter ones usually moves the score more than swapping individual words.",
    },
  ],
  "ascii-art-generator": [
    {
      question: "Why does my ASCII art look broken when I paste it somewhere?",
      answer: "ASCII art displays correctly only in a monospaced font. If you paste it somewhere with a proportional font, the alignment will look broken even though the text itself is correct — code editors, terminals, and plain-text files almost always use a monospaced font by default, which is why this works well there but not in a regular word processor.",
    },
    {
      question: "Why can't I type accents, Hindi or emoji?",
      answer: "The banner fonts only contain plain ASCII characters, meaning English letters, digits and common punctuation. If any character falls outside that range the tool shows a message and draws nothing until you remove it.",
    },
    {
      question: "How do I stop the art wrapping in my README or terminal?",
      answer: "Look at the width note under the preview. If it exceeds 80 columns, shorten the text, put each word on its own line, or choose a narrower font such as Small or Mini. Putting the art in a code block also stops the layout being reflowed.",
    },
    {
      question: "Does the zoom slider change what I copy?",
      answer: "No. Preview zoom only changes the on-screen size of the preview, between 8 and 22 pixels of text size. The copied text is the same whatever the slider position.",
    },
    {
      question: "How are several lines handled?",
      answer: "Each line is drawn as a separate banner and the banners are stacked with an empty line between them. Alignment then pads shorter banners on the left when Centre is selected, so they sit in the middle of the widest one.",
    },
    {
      question: "Which fonts are available, and are they all pure ASCII?",
      answer: "There are twelve: Standard, Big, Slant, Small, Mini, Banner3, Block, ANSI Shadow, Doom, Graffiti, Ghost and Bubble. Most draw with ordinary keyboard symbols, but ANSI Shadow uses solid block and line-drawing characters, so it may not display in every plain-text setting.",
    },
  ],
  "markdown-html-converter": [
    {
      question: "Does this support every Markdown feature, like tables?",
      answer: "It covers the common Markdown syntax — headers, bold, links, lists, and so on — but isn't a full implementation of every possible Markdown extension. Double-check unusual formatting, tables, or nested structures after converting, since those are the areas most likely to need a manual fix.",
    },
  ],
  "age-calculator": [
    {
      question: "Can I use this for something other than calculating a birthday age?",
      answer: "Yes — entering any two dates instead of a birth date works the same way, useful for counting days until a deadline, the exact duration of an event, or how many days pregnant someone is.",
    },
    {
      question: "How does it handle a 29 February birthday?",
      answer: "In years that are not leap years, the next birthday is counted on 1 March, and a note under the result says so. Your current age in years, months and days is still worked out from the real birth date you entered.",
    },
    {
      question: "What changes when I switch on Include the end date?",
      answer: "By default the start day is counted and the end day is not, so 1 June to 2 June is 1 day. With Include the end date on, both ends count and the same pair gives 2 days. The business-day count follows the same rule.",
    },
    {
      question: "Are weekends and public holidays excluded?",
      answer: "With Business days only (Mon-Fri) switched on, Saturdays and Sundays are skipped. Public holidays are not excluded, and the page states this under the result. If holidays apply to you, subtract them from the figure yourself.",
    },
    {
      question: "What happens if I enter a birth date in the future?",
      answer: "No age is shown. Instead the tool reports that the date is in the future and gives the number of days between today and that date, which can be handy for counting down to an expected date.",
    },
    {
      question: "Why might another calculator differ by a day or a month?",
      answer: "Methods vary. This one adds whole calendar months to the start date and then counts leftover days, using local dates with no clock times. Other sites may assume 30-day months, use time zones, or include the final day, and each choice shifts the answer.",
    },
  ],
  "bmi-calculator": [
    {
      question: "What is a healthy weight for my height?",
      answer: "The calculator shows the weights that give a BMI of 18.5 and 24.9 at your height (22.9 at the top end if you choose the Asian cut-offs), and how many kg or lb you would need to lose or gain to reach that range.",
    },
    {
      question: "Is BMI a complete measure of my health?",
      answer: "No — BMI doesn't distinguish between muscle and fat, so it can be misleading for very muscular individuals or older adults with reduced muscle mass. It's a useful general screening number, not a complete picture of health on its own.",
    },
    {
      question: "How is BMI calculated on this page?",
      answer: "Weight in kilograms is divided by the square of height in meters. Imperial entries are converted first, using 2.54 cm per inch and 0.45359237 kg per pound. The answer is displayed to one decimal place and recalculated on every keystroke.",
    },
    {
      question: "What are the category cut-offs?",
      answer: "Under 18.5 is Underweight, 18.5 up to but not including 25 is Normal, 25 up to but not including 30 is Overweight, and 30 or more is Obese. These are the usual adult bands, and the tool applies them to every entry.",
    },
    {
      question: "Why does it say my height or weight is out of range?",
      answer: "Heights from 100 to 250 cm and weights from 30 to 300 kg are accepted, or the same span in feet, inches and pounds. Anything outside shows a message and no result. If you type a height like 1.7, the message asks for centimeters.",
    },
    {
      question: "Where does the healthy range line come from?",
      answer: "It shows the weights that produce BMI 18.5 and BMI 24.9 at your height, found by multiplying each value by your height in meters squared. It is the main formula run backwards, so it changes only when your height changes.",
    },
    {
      question: "Does BMI work for athletes or older adults?",
      answer: "It can mislead. The formula cannot tell muscle from fat, so a muscular person may land in Overweight, and an older adult who has lost muscle may land in Normal. Treat the number as a rough screen, not a verdict.",
    },
    {
      question: "What is a healthy BMI?",
      answer: "For adults, 18.5 up to 24.9 is the usual healthy range. Under 18.5 is underweight, 25 to 29.9 is overweight and 30 or more is obese. With the Asian cut-offs ticked, the healthy range ends at 22.9 and obese starts at 27.5.",
    },
    {
      question: "Is BMI different for Asian adults?",
      answer: "Yes, the lines are lower. Tick the Asian cut-offs box and the page moves overweight to 23 and obese to 27.5, then recalculates your category and the weight you would need to reach the healthy range.",
    },
    {
      question: "Can children and teenagers use it?",
      answer: "No. Adult categories do not apply under 20. If you enter an age under 20 the page hides the category and explains that, for ages 2 to 19, BMI is read as a percentile for age and sex on the CDC growth charts. Ask a doctor about a child's weight.",
    },
    {
      question: "What are obesity classes 1, 2 and 3?",
      answer: "They are the WHO bands for adults: class 1 is a BMI of 30 to 34.9, class 2 is 35 to 39.9 and class 3 is 40 or more. The table under your result shows all eight WHO classes and marks the one you are in.",
    },
    {
      question: "What is the waist-to-height ratio?",
      answer: "Your waist divided by your height. Enter an optional waist measurement and the page shows the ratio and compares it with the common guideline of keeping your waist under half your height.",
    },
    {
      question: "Who should not rely on BMI?",
      answer: "Children and teenagers, pregnant women, very muscular people and older adults who have lost muscle. BMI is a screening number, not a diagnosis, so talk to a doctor about your own health.",
    },
    {
      question: "Is my data saved?",
      answer: "No. Height and weight stay in your browser and are never stored or uploaded.",
    },
  ],
  "bmr-macro-calculator": [
    {
      question: "What input do people most commonly get wrong?",
      answer: "Activity level — being honest about how active you actually are, not how active you'd like to be, gives a meaningfully more accurate calorie and macro estimate than an optimistic guess.",
    },
  ],
  "body-fat-calculator": [
    {
      question: "Why do my results seem to change between measurements?",
      answer: "Measure at the same time of day and in the same way each time you check — small differences in where and how you measure can shift the result more than an actual change in body composition would.",
    },
  ],
  "calorie-calculator": [
    {
      question: "Should I hit this calorie number exactly every day?",
      answer: "Calorie needs are an estimate, not an exact number. Treat the result as a starting point to adjust from based on how your actual weight trends over a few weeks, rather than a precise target to hit exactly every day.",
    },
    {
      question: "Why does a weight-loss target stop at a certain number?",
      answer: "Loss targets are never set more than 25% below your maintenance calories, and never below 1,500 calories a day for men or 1,200 calories a day for women. When two loss goals would land on the same limited number, they are shown as one row marked capped. Eating below these levels should only be done under medical supervision.",
    },
  ],
  "compound-interest": [
    {
      question: "Does compounding frequency actually make a meaningful difference?",
      answer: "Yes — more frequent compounding (monthly versus annually, for example) produces a slightly larger balance over long time horizons at the same stated rate. It's worth checking which compounding frequency your actual account uses.",
    },
    {
      question: "What does the start date do in the compound interest calculator?",
      answer: "It is optional. Enter the day the money starts earning interest and each row of the year-by-year table shows the date its year ends, with the date of the final balance above it. Leave it empty and the table shows plain year numbers, as before.",
    },
    {
      question: "Does the start date change the interest earned?",
      answer: "No. The date only labels the timeline. Interest is worked out from the rate, the compounding and the number of years you enter, so the balance is the same for any start date. If your term is not a whole number of months, the final date is rounded to the nearest month and the page says so.",
    },
    {
      question: "Can I choose whether contributions go in at the start or end of each period?",
      answer: "Yes. Under Contributions made, pick At the end of each period or At the start of each period. Contributions at the start earn interest for one extra period, so the final balance is a little higher, and the Details text and the note under the result name whichever option is selected.",
    },
    {
      question: "Which compounding options can I pick?",
      answer: "Annually, semi-annually, quarterly, monthly or daily.",
    },
    {
      question: "Which currencies does it support?",
      answer: "USD, EUR, GBP, INR, CAD, AUD, JPY, SGD, AED and ZAR.",
    },
    {
      question: "How do I see the result in today's money?",
      answer: "Enter an optional inflation rate. Today's money is the final balance divided by (1 + inflation) raised to the number of years.",
    },
    {
      question: "What is not included in the result?",
      answer: "Taxes, fees and changing rates are not included. It is an estimate only, not investment advice.",
    },
    CALC_TRIAL_FAQ,
    {
      question: "Can I use this to plan 401(k) retirement savings?",
      answer: "It shows general compound growth. For a 401(k) projection with employer match and the IRS contribution limit, use the 401(k) Calculator, the next step in the Planning for Retirement guide.",
    },
  ],
  "currency-converter": [
    {
      question: "Will I get exactly this rate if I actually exchange money?",
      answer: "Exchange rates shift throughout the day, and for an actual transaction, your bank or payment processor may use a slightly different rate than the market rate shown here — check the rate your specific provider will actually apply.",
    },
  ],
  "discount-calculator": [
    {
      question: "Do two stacked discounts add together?",
      answer: "No — for stacked discounts (like 20% off, then an additional 10% off), apply them sequentially rather than adding the percentages together. 20% then 10% off isn't the same as 30% off in one step.",
    },
  ],
  "fire-calculator": [
    {
      question: "How is this different from a regular retirement calculator?",
      answer: "This is the most honest, complete version of the question — what it would actually take to be fully financially independent, not just comfortably retired at a traditional age.",
    },
    {
      question: "How is the FIRE number calculated?",
      answer: "Annual expenses divided by the withdrawal rate as a decimal. With the sample 40,000 and 4 percent that is 1,000,000. The Lean and Fat rows use 70 percent and 150 percent of your expenses with the same rate.",
    },
    {
      question: "Why does it use a real return?",
      answer: "To keep everything in today's dollars. Return and inflation are combined as (1 + return) divided by (1 + inflation), minus 1; with 7 and 2.5 that is about 4.39 percent. Your yearly savings are also treated as a constant amount in today's money, so they do not grow with inflation.",
    },
    {
      question: "What does More than 100 years mean?",
      answer: "The balance did not reach the target within 100 yearly steps, usually because savings and growth are small next to the target. Try lowering expenses, raising savings, or checking that the return you entered is higher than inflation.",
    },
    {
      question: "What is the age line under the result?",
      answer: "If you enter your age, the tool adds the years to reach the target and rounds up, giving the age by which you could reach it. It appears only when a time is found. It assumes you keep the same plan from today.",
    },
    {
      question: "Does the 4 percent rule guarantee my money lasts?",
      answer: "No. The 4 percent figure is a rule of thumb drawn from historical 30-year periods, and the tool simply divides by whatever rate you type. A longer retirement, lower returns or early losses can change the outcome, so treat the result as an estimate from your assumptions.",
    },
    {
      question: "What does the plan start date do in the FIRE calculator?",
      answer: "It is optional. Enter the date you start saving toward the target and the result adds the month you reach it, for the main result and for the lean and fat variants. Leave it empty and you see only the number of years and months, as before.",
    },
    {
      question: "Is the FIRE date a prediction?",
      answer: "No. It is the start date plus the years and months the calculator works out from your inputs, which assume a constant return, constant savings and a fixed withdrawal rate. Real markets vary, so treat the month as a rough guide and try a lower return to see how far it moves.",
    },
    {
      question: "What inputs does the FIRE calculator need?",
      answer: "Annual expenses in retirement (in today's dollars), current savings or investments, the amount saved each year, return, inflation and withdrawal rate. Your age and a plan start date are optional.",
    },
    {
      question: "What are the default return, inflation and withdrawal rate?",
      answer: "The starting values are 7% return, 2.5% inflation and a 4% withdrawal rate. The 401(k) calculator starts with the same 2.5% inflation, so the two tools agree until you change it. You can edit all three.",
    },
    {
      question: "What withdrawal rate should I use if I retire very early?",
      answer: "The 4% rule was tested on 30-year retirements, so people retiring much earlier often use 3.25% to 3.5%. A lower rate raises the target.",
    },
    {
      question: "Why does cutting expenses help so much?",
      answer: "It speeds up FIRE twice: it lowers the target and raises what you can save each year. The tool takes the amount saved each year as its own input, so enter the higher figure to see the effect.",
    },
    {
      question: "What are Lean FIRE and Fat FIRE?",
      answer: "Lean FIRE uses 70% of your expenses and Fat FIRE uses 150%, both with the same withdrawal rate. All three targets are shown side by side with the time to reach each.",
    },
    CALC_TRIAL_FAQ,
    {
      question: "How does this relate to a 401(k) retirement calculator?",
      answer: "This calculator finds the total portfolio needed to retire early. To project how much a workplace 401(k) could contribute toward it, with employer match and IRS limits, use the 401(k) Calculator.",
    },
  ],
  "401k-calculator": [
    {
      question: "Should I contribute enough to get my full employer match?",
      answer: "Generally yes — if your employer offers a matching contribution, contributing at least enough to get the full match is usually worth prioritizing before other savings goals, since it's an immediate, guaranteed return that's hard to match elsewhere.",
    },
    {
      question: "What is the plan start date in the 401(k) calculator?",
      answer: "It is optional. Enter the date your first contribution year begins and each row of the table shows the calendar months of that year next to the age, with the start and end dates of the projection above it. Leave it empty and the table shows ages only, as before.",
    },
    {
      question: "Does the plan start date change the projected balance or the contribution limit?",
      answer: "No. The date only labels the years. The projection still runs in whole years from your current age, and the contribution limit used is the one stated on the page, held at today's level. Use the date to see which calendar years your projection covers, not to model a change in the rules.",
    },
    {
      question: "What inflation rate does the 401(k) calculator start with?",
      answer: "It starts at 2.5% a year, the same starting value as the FIRE calculator, and it only affects the line that shows your balance in today's dollars. Change it to match your own assumption.",
    },
  ],
  "gpa-calculator": [
    {
      question: "Will this match my school's official GPA exactly?",
      answer: "It uses the standard 4.0 scale, but some schools use a slightly different scale for plus/minus grades (an A- might be 3.7 or 3.67 depending on the institution). Check your school's specific scale if you need an exact match to your official transcript.",
    },
    {
      question: "What grade points do the US scales use?",
      answer: "Both give A 4.0, A- 3.7, B+ 3.3, B 3.0, B- 2.7, C+ 2.3, C 2.0, C- 1.7, D+ 1.3, D 1.0, D- 0.7 and F 0. They differ only in A+, which is 4.0 on the 4.0 scale and 4.3 on the 4.3 scale.",
    },
    {
      question: "What values does the India 10-point scale use?",
      answer: "O is 10, A+ 9, A 8, B+ 7, B 6, C 5, P 4 and F 0. The result is called CGPA, and the card adds an approximate percentage by multiplying by 9.5, a common university rule but not universal.",
    },
    {
      question: "How does weighting work?",
      answer: "With Weighted ticked, an Honors course adds 0.5 and an AP / IB course adds 1.0 to its grade point, so an AP A counts as 5.0. The bonus is skipped for an F, and the option does not exist on the India scale.",
    },
    {
      question: "How is the cumulative GPA calculated?",
      answer: "Your previous GPA multiplied by previous credits is added to this term's quality points, and the sum is divided by the combined credits. So a long history with many credits changes only slowly when you add one term.",
    },
    {
      question: "Why does my result differ from my school's?",
      answer: "Schools differ. Some use 3.67 for an A-, some count only the best attempt of a repeated course, and rounding rules vary. This tool uses the fixed values above, so treat the answer as an estimate and check it against your transcript.",
    },
  ],
  "gst-calculator": [
    {
      question: "Does this work for any country's GST system?",
      answer: "It's built around common GST structures like India's, Canada's, and Australia's, but GST rates and rules vary by country. Confirm the specific rate and rules that apply to your situation before relying on the result for anything official.",
    },
    {
      question: "Which GST rates can I select?",
      answer: "The list offers 0%, 0.25%, 3%, 5%, 18% and 40%. The page notes that these reflect the structure in force since 22 September 2025. Check that the rate fits your item, because the tool does not classify goods.",
    },
    {
      question: "What is the difference between CGST, SGST and IGST?",
      answer: "For a sale within one state, the tax is split equally into central (CGST) and state (SGST) parts. For a sale between states, a single integrated tax (IGST) applies. The Type of supply menu switches between these layouts.",
    },
    {
      question: "What is UTGST here?",
      answer: "For a union territory without a legislature, the state half is called UTGST. Choosing that supply type shows CGST and UTGST at half the selected rate each instead of CGST and SGST.",
    },
    {
      question: "How does Remove GST work out the base price?",
      answer: "It divides the GST-inclusive total by one plus the rate as a decimal, rounds the base to two places, and treats the difference as GST. At 18 percent, you divide by 1.18.",
    },
    {
      question: "What is the amount in words line for?",
      answer: "Indian invoices often state the total in words. The tool writes your total using lakh and crore, such as Rupees One Lakh Eighteen Thousand Only, and includes paise when present. Always proofread it before issuing the invoice.",
    },
  ],
  "ideal-weight-calculator": [
    {
      question: "Should I treat this number as my exact personal target?",
      answer: "No — these formulas don't account for frame size, muscle mass, or individual body composition. Treat the range as a general reference point among several possible ways to think about a healthy weight, not a precise personal target.",
    },
  ],
  "income-tax-calculator": [
    {
      question: "Is this an exact calculation of what I'll owe?",
      answer: "It's a solid estimate based on standard rates and brackets, but doesn't account for every deduction, credit, or unusual circumstance that could apply to your specific situation. Use it for planning, not as a substitute for filing your actual return.",
    },
    {
      question: "How much tax will I pay in 2026?",
      answer: "Enter income, filing status and state. The calculator applies 2026 federal brackets and the standard deduction, adds Social Security and Medicare (FICA) and state tax, and shows total tax, take-home income and effective rate.",
    },
    {
      question: "What is the difference between marginal and effective tax rate?",
      answer: "The marginal rate is the bracket your last dollar falls in. The effective rate is total tax divided by income, always lower because lower brackets are taxed at lower rates.",
    },
    {
      question: "Does it include state income tax?",
      answer: "Yes. Pick your state and its tax is added; states with no income tax are marked as such and show zero for that line. For California it also adds State Disability Insurance on its own line.",
    },
    {
      question: "Does it include California SDI?",
      answer: "Yes. For California employees it deducts State Disability Insurance at 1.3% of all 2026 wages, with no wage cap, as its own line, so take-home pay matches a pay stub more closely. It is taken from gross wages, so a 401(k) contribution does not lower it, and it is not applied when you tick self-employed. Other states' disability and paid-leave contributions (for example New York, New Jersey and Washington) are not included, and the page says so.",
    },
    {
      question: "Which year's state tax rates does it use?",
      answer: "Mostly 2026. California uses the 2025 Franchise Tax Board schedule because the 2026 one is not published yet, and Vermont and North Dakota use 2025 bracket widths. The note under your result names the tax year and the source for the state you picked.",
    },
    {
      question: "Can I see take-home pay per paycheck instead of per year?",
      answer: "Yes. Use Show amounts per to switch every figure to a month, or to a semi-monthly, biweekly or weekly paycheck. Each is the yearly amount divided by 12, 24, 26 or 52. A real paycheck also depends on your W-4 and employer deductions.",
    },
    {
      question: "Can I use it if I am self-employed?",
      answer: "Yes. Tick the self-employed box and it calculates self-employment tax instead of employee FICA (and no California SDI, which only applies to employee wages).",
    },
    {
      question: "Does filing status change the result?",
      answer: "Yes. Single, married filing jointly, married filing separately and head of household each have their own brackets and standard deduction.",
    },
    {
      question: "Is my income data saved or uploaded?",
      answer: "No. Every number stays in your browser and is never sent to a server.",
    },
  ],
  "india-income-tax": [
    {
      question: "Does this account for both old and new tax regimes in India?",
      answer: "Check which regime the calculator is estimating under before relying on the result — India's tax system offers different slab structures depending on which regime you choose, and the right answer for you depends on your specific deductions and income sources.",
    },
  ],
  "job-experience-calculator": [
    {
      question: "Does this handle overlapping employment periods correctly?",
      answer: "Enter each role's actual start and end dates as separate entries — the tool totals your real experience across all the roles you list, which is useful when you've had gaps, overlaps, or multiple concurrent roles to account for.",
    },
  ],
  "loan-calculator": [
    {
      question: "Does a lower monthly payment always mean a better loan?",
      answer: "Not necessarily — even a small difference in interest rate makes a real difference in total interest paid over a long loan term. It's worth shopping for the best rate before committing, not just comparing monthly payments.",
    },
    {
      question: "What is the first payment date, and what should I enter?",
      answer: "It is the date your first loan payment is due. Enter the date in your loan agreement or on your first statement. If you do not know it yet, leave the suggested date, the 1st of next month. Every later payment falls on the same day of the following months, and the schedule and payoff date show real calendar dates from it.",
    },
    {
      question: "Does the first payment date change my payment or the interest?",
      answer: "Not in this calculator. The date only puts calendar dates on the schedule and the payoff date. Interest is worked out on whole monthly periods, so the payment and totals stay the same whatever date you choose. Some lenders charge extra interest when the first payment is more than a month after the loan starts; that is not included here.",
    },
    {
      question: "What happens if my payment date is the 31st?",
      answer: "A payment on the 29th, 30th or 31st moves to the last day of shorter months, such as Feb 28 (Feb 29 in a leap year) or Apr 30, and goes back to your day in longer months, so the dates never drift earlier.",
    },
  ],
  "mortgage-calculator": [
    {
      question: "Does a bigger down payment only lower my monthly payment?",
      answer: "It can also help you avoid private mortgage insurance if it gets you above the typical 20% equity threshold, which is worth checking with your specific lender — so a larger down payment can save money beyond just the smaller monthly amount.",
    },
    {
      question: "What is the first payment date, and what should I enter?",
      answer: "It is the date your first mortgage payment is due. Enter the due date shown on your loan estimate or closing documents. If you do not know it yet, leave the suggested date, the 1st of next month. Every later payment falls on the same day of the following months, and the schedule, chart and payoff date show real calendar dates from it.",
    },
    {
      question: "Why is my first payment usually a month or more after closing?",
      answer: "Mortgage interest is paid in arrears, so each payment covers the month that just ended. If you close in the middle of a month, you pay interest for the days left in that month at closing, and your first full payment is then due on the 1st of the month after next. Close on June 20, for example, and the first payment is usually due August 1. Your lender's closing disclosure gives the exact date.",
    },
    {
      question: "Does changing the first payment date change my monthly payment or interest?",
      answer: "Not in this calculator. The date only puts calendar dates on the schedule, the chart and the payoff date. Principal and interest are worked out from whole monthly periods, so the amounts stay the same whatever date you choose. The interest you pay at closing for the first partial month is a separate closing cost that this estimate does not include.",
    },
    {
      question: "What happens if my payment date is the 31st?",
      answer: "A payment on the 29th, 30th or 31st moves to the last day of shorter months, such as Feb 28 (Feb 29 in a leap year) or Apr 30, and goes back to your day in longer months. Most lenders set the due date to the 1st, so this rarely matters in practice.",
    },
  ],
  "paycheck-calculator": [
    {
      question: "Will this match my actual paycheck exactly?",
      answer: "It gives a general estimate — your actual paycheck also depends on employer-specific deductions like health insurance premiums or retirement contributions, which vary by employer and aren't accounted for here.",
    },
  ],
  "percentage-calculator": [
    {
      question: "What's the difference between percentage change and percentage difference?",
      answer: "These are often confused — the \"% change\" tab specifically measures how much a value increased or decreased relative to its starting point, which is usually what people actually mean by \"percent change,\" as distinct from a symmetric percentage difference calculation.",
    },
  ],
  "pregnancy-due-date": [
    {
      question: "How accurate is this compared to what my doctor tells me?",
      answer: "This is a standard estimate used as a starting reference point — an ultrasound-based estimate from your care provider is generally more accurate for your actual situation, especially if your cycle isn't a typical 28 days.",
    },
  ],
  "period-calculator": [
    {
      question: "Can I use this as birth control?",
      answer: "No. It estimates ovulation from your average cycle, and real cycles vary from month to month, so the dates can be off by days. It is not birth control and not medical advice.",
    },
    {
      question: "What should I enter if my cycles are irregular?",
      answer: "Tick \"My cycle length varies\" and enter your shortest and longest cycle. Every date then becomes an earliest-to-latest range, and the ranges get wider for later cycles. If your cycles are very irregular, talk to a doctor or nurse.",
    },
    {
      question: "Is anything I enter saved?",
      answer: "No. Your dates stay in this page and are not stored or sent anywhere. Saving the calendar file or summary uses one trial use.",
    },
  ],
  "pregnancy-scan-calculator": [
    {
      question: "Is this my appointment schedule?",
      answer: "No. It shows typical date windows from published guidance. Your midwife, doctor or hospital decides which scans you are offered and when, and their schedule overrides this page.",
    },
    {
      question: "Why do some scans have no dates?",
      answer: "Early dating or viability scans and later growth scans have no single standard window, so they are listed as \"only if your provider advises\" rather than guessing a date.",
    },
    {
      question: "Where do the windows come from?",
      answer: "NHS England screening programme timings for combined screening and the anomaly scan, and ACOG's description of the anatomy scan as usually 18 to 22 weeks. The source and the \"as of\" date are shown on the tool.",
    },
  ],
  "random-number-generator": [
    {
      question: "Is this random enough to trust for something like a raffle drawing?",
      answer: "Yes, as long as the seed box is empty. Then the numbers come from your browser's crypto.getRandomValues, a cryptographically secure source, and the tool throws away any draw that would favour some values, so every value in your range is equally likely. If you type a seed the tool switches to a repeatable generator (sfc32) that is not secure, and says so beside the result: anyone with the seed can recreate the list, so never use a seed for a raffle.",
    },
    {
      question: "Can it give decimals, such as 0.5 to 1.5 with 3 decimal places?",
      answer: "Yes. Set Decimal places (0 to 10). The tool picks from every number with that many decimals between your minimum and maximum, both ends included, so 0.5 to 1.5 with 3 places chooses from 1,001 values. A limit with more decimals than you allowed is rounded inward: the minimum up and the maximum down, never outside what you typed.",
    },
    {
      question: "Can it roll dice, flip a coin, pick lottery numbers or choose from a list?",
      answer: "Yes. Dice takes NdM such as 2d6, d20 or 3d8+2 and shows each die and the total. Coin flips up to 10,000 times and counts heads and tails. Lottery picks different numbers from 1 to a pool you set, sorted, so 6 of 49 gives a classic ticket; it also states the exact odds of any one ticket. List takes one item per line and draws some or shuffles all; the list never leaves your browser.",
    },
    {
      question: "Why does it say it is not possible to get 6 unique numbers from 1 to 5?",
      answer: "Because only 5 different values exist there. The message says how many values the range holds and what to change: lower How many, widen the range, allow more decimal places or turn off No duplicates.",
    },
    {
      question: "Does generating use up one of my trial uses?",
      answer: "No. Numbers appear straight away. A trial use is taken when you copy or download them, once for the same numbers in a visit; a new draw is a new output.",
    },
  ],
  "recipe-scaler": [
    {
      question: "Does scaling work well for baked goods specifically?",
      answer: "Scaling works cleanly for most ingredients, but things like baking times, pan sizes, and spice levels don't always scale linearly. Treat the scaled quantities as a strong starting point, not an exact guarantee, especially for baked goods.",
    },
    {
      question: "What number formats can it read?",
      answer: "Whole numbers, decimals with a point or comma, fractions like 1/2, mixed numbers like 1 1/2, and single fraction characters for half or one and a half. Ranges such as 2-3 or 2 to 3 are scaled at both ends. A dash, asterisk or bullet before the amount is fine.",
    },
    {
      question: "Why did a bracketed amount not change?",
      answer: "Only the first quantity at the start of each line is scaled. In 1 (14 oz) can tomatoes, the 1 is multiplied but 14 oz stays, since it describes the can size. Doubled, the line reads 2 (14 oz) cans tomatoes.",
    },
    {
      question: "How are the new amounts rounded?",
      answer: "For cups, spoons and plain counts, results snap to kitchen fractions such as a quarter, a third or three quarters when within about 1/32 of one; otherwise they show up to two decimals. Lines using g, kg, mg, ml, l, dl, cl, oz or lb are shown as decimals rounded to two places.",
    },
    {
      question: "What if scaling gives part of an egg?",
      answer: "An egg line with a fractional result gets a note under it: beat one egg and use the right share, or round. The tool does not decide for you, because rounding changes texture in a cake far more than in a stew.",
    },
    {
      question: "Can it adjust seasoning or baking time?",
      answer: "No. It multiplies amounts by one factor and leaves times, temperatures and pan sizes alone. Salt, spices and raising agents often need less than a straight multiple at large scale, so adjust them by taste or from a tested recipe.",
    },
  ],
  "rmd-calculator": [
    {
      question: "Are the RMD age rules the same for everyone?",
      answer: "No — RMD rules and the exact starting age have changed periodically in recent tax legislation. Confirm the current age threshold applies to your specific account type and birth year before relying on the result.",
    },
    {
      question: "What age do RMDs begin?",
      answer: "Age 72 if born before 1951, 73 if born 1951 to 1959, and 75 if born in 1960 or later. The tool works this out from the birth year you enter. Before that year it shows a message instead of an amount, plus a what-if figure for the starting age.",
    },
    {
      question: "Which balance should I enter?",
      answer: "The account value on December 31 of the year before the distribution year, as the field label says. Use the figure on your year-end statement rather than today's balance, and enter the distribution year separately in the RMD for the year box.",
    },
    {
      question: "How is the divisor chosen?",
      answer: "It is looked up in the Uniform Lifetime Table by the age you reach during the year: 26.5 at 73, 24.6 at 75, 20.2 at 80. The required amount is the balance divided by it. The table runs from 72 to 120, and ages outside that range are held at the ends.",
    },
    {
      question: "Does it apply to Roth accounts?",
      answer: "The tool does not tell account types apart; it divides whatever balance you type. Roth IRAs have no required distributions for the original owner during life, so this calculation is not meant for them. Check your plan rules for any workplace Roth account.",
    },
    {
      question: "What if my spouse is the sole beneficiary and much younger?",
      answer: "A different table can give a smaller required amount in that case, and this tool does not include it. The same goes for inherited accounts, which follow other rules. The note on the page points to IRS Publication 590-B for those situations.",
    },
  ],
  "roman-numeral-converter": [
    {
      question: "How do I convert a number to Roman numerals?",
      answer: "Type any whole number from 1 to 3,999,999 and the Roman numeral appears instantly, built from thousands, hundreds, tens and ones. Above 3,999 the thousands get a bar over the letters.",
    },
    {
      question: "How do I convert Roman numerals back to numbers?",
      answer: "Type the numeral in the Roman numeral box, for example MMXXVI. A smaller letter before a larger one is subtracted (IV, IX, XL, CM); otherwise letters are added. MMXXVI = 2026.",
    },
    {
      question: "How do I write a date or year in Roman numerals?",
      answer: "Use the date boxes and pick your order, such as Month.Day.Year (US), and a separator: dot, middle dot, slash, hyphen or space. Each part is converted separately, so 7.4.1776 becomes VII.IV.MDCCLXXVI, a form popular for tattoos, weddings and anniversaries. Dates need a year from 1 to 3,999.",
    },
    {
      question: "How do I write numbers above 3,999 in Roman numerals?",
      answer: "Put a bar (a vinculum) over the letters: a bar multiplies by 1,000, so V with a bar is 5,000 and I V with bars is 4,000. This converter goes up to 3,999,999 and shows the bar over the letters; when you copy, the bar is the Unicode combining overline. To type a barred numeral, put the barred group in brackets, such as (V) for 5,000 or (XII)CCCXLV for 12,345. Without bars, MMMCMXCIX (3,999) is the largest value, because no letter is bigger than M and a letter repeats at most three times.",
    },
    {
      question: "How do I see the working for a Roman numeral?",
      answer: "Under the result, open How 1,994 is written (for example). It splits the number by place, thousands, hundreds, tens and ones, so 1994 = M + CM + XC + IV, and joins the pieces. A reference table of 1 to 100 and of the years 1950 to 2050 sits below it; copying or downloading it uses one of your trial uses for the visit.",
    },
    {
      question: "What do I, V, X, L, C, D and M stand for?",
      answer: "I = 1, V = 5, X = 10, L = 50, C = 100, D = 500 and M = 1,000.",
    },
    {
      question: "How do you write 2026 in Roman numerals?",
      answer: "MMXXVI: MM (2000) + XX (20) + VI (6). Type 2026 in the number box to see the same working.",
    },
    {
      question: "How do you write 4 in Roman numerals?",
      answer: "IV, one less than five. IIII is only seen on some clock faces; the converter reads it and tells you the standard form is IV.",
    },
    {
      question: "What is the largest Roman numeral?",
      answer: "In standard form it is 3,999, MMMCMXCIX. A bar over a letter multiplies it by 1,000, and this converter goes up to 3,999,999 that way.",
    },
    {
      question: "Is there a zero in Roman numerals?",
      answer: "No. The Roman system has no symbol for zero, so 0 and negative numbers cannot be converted.",
    },
    {
      question: "Is what I type sent anywhere?",
      answer: "No. The conversion runs entirely in your browser and nothing is uploaded.",
    },
  ],
  "sales-tax-calculator": [
    {
      question: "Does this account for different tax rates within the same state or country?",
      answer: "It's built around the general rate structure for the region you select, but local jurisdictions can layer on their own additional rates. Confirm the specific combined rate for your exact location before relying on it for anything official.",
    },
    {
      question: "How do I calculate sales tax backwards?",
      answer: "Choose Remove Sales Tax and enter the price that already includes tax. The tool divides by 1 plus the rate to find the price before tax, and the tax is the difference. For example, $108 with 8% tax is $100 before tax and $8 of tax.",
    },
    {
      question: "What is the sales tax in my state?",
      answer: "Pick your state to fill in its average combined rate or its state-only base rate from the Tax Foundation midyear 2026 table (rates as of 1 July 2026). Your own rate can be higher or lower because cities, counties and districts add their own, so check your exact rate with your state's revenue department.",
    },
    {
      question: "Is sales tax charged on shipping?",
      answer: "It depends on the state and on how the shipping is billed. Some states tax shipping when the item is taxable and others do not. In the several-items view you tick whether shipping is taxable, so you decide; check your state's revenue department for its rule.",
    },
    {
      question: "Why do rates differ within a ZIP code?",
      answer: "A ZIP code is a mail route, not a tax area. One ZIP can cross city, county and special-district lines, and each can add its own tax. That is why the ZIP box fills in a rate only where the state has a single rate, and otherwise asks you to look up your street address with your state's official page.",
    },
  ],
  "self-employment-tax": [
    {
      question: "Does this cover my full tax liability as a freelancer?",
      answer: "No — this covers only the Social Security and Medicare portion (self-employment tax specifically). Budget for regular income tax on top of this estimate, not instead of it.",
    },
  ],
  "sip-calculator": [
    {
      question: "Which input has the biggest effect on the projected result?",
      answer: "The expected return rate — it's the single biggest driver of the projected result and the hardest to predict accurately. Try the calculation at a few different reasonable rates to see a realistic range rather than anchoring on one optimistic number.",
    },
    {
      question: "What formula does it use?",
      answer: "For each month the balance becomes (previous balance + that month's instalment) x (1 + annual rate / 12), starting from the lumpsum. The instalment is the monthly amount, multiplied by (1 + step-up) once for each completed year.",
    },
    {
      question: "Is the expected return a prediction?",
      answer: "No. It is simply a number you enter and the tool holds it constant. Real returns vary and can be negative, so the output only shows what would follow from that assumption. Try several values rather than relying on one.",
    },
    {
      question: "Is the interest compounded monthly or yearly?",
      answer: "Monthly. An entered rate of 12 percent becomes 1 percent per month, which compounds to slightly more than 12 percent over a full year. Figures from tools that use a yearly effective rate will therefore differ a little.",
    },
    {
      question: "How does the yearly step-up work?",
      answer: "The instalment stays the same for 12 months, then rises by the step-up percentage, and again every 12 months after that. With a step-up of 10 and a monthly amount of 5000, year two is 5500 per month.",
    },
    {
      question: "What does the today's money line mean?",
      answer: "It appears when inflation is above zero. The final balance is divided by (1 + inflation) raised to the number of years, giving a rough sense of purchasing power. It does not change the future value shown above it.",
    },
    {
      question: "What does the first contribution date do in the SIP calculator?",
      answer: "It is optional. Enter the date of your first monthly investment and the year-by-year table shows the real months each year covers, such as Jan 2027 to Dec 2027, and the result shows your last contribution and the date the maturity value is reached. Leave it empty and the table shows plain year numbers, as before.",
    },
    {
      question: "Does the start date change the future value or the returns?",
      answer: "No. The date only puts calendar dates on the plan. Returns still compound monthly on whole months, so the future value, the invested amount and the gains are the same for any date. A start day of the 29th to 31st moves to the last day of shorter months, so a plan that starts on Jan 31 has its second contribution on Feb 28.",
    },
    {
      question: "Can I add a one-time lumpsum to my SIP?",
      answer: "Yes. Lumpsum (optional) is a one-time amount present from the start, and the monthly growth starts from it.",
    },
    {
      question: "What is the effective annual return when I enter 12%?",
      answer: "About 12.68% a year, because returns compound monthly at the annual rate divided by 12.",
    },
    {
      question: "Is each instalment invested at the start or end of the month?",
      answer: "The estimate assumes each instalment is invested at the start of the month.",
    },
    {
      question: "Which currencies are supported, and does it show lakh and crore?",
      answer: "Ten currencies: USD, EUR, GBP, INR, CAD, AUD, JPY, SGD, AED and ZAR. With INR selected, the total is also written in lakh or crore once it reaches one lakh.",
    },
    {
      question: "What does the year-by-year table show?",
      answer: "Press Show year-by-year table to see Contributed, Interest and Balance at the end of each year, with the last row ending at the end of your period.",
    },
    CALC_TRIAL_FAQ,
    {
      question: "Is a SIP calculator the same as a 401(k) retirement calculator?",
      answer: "No. This projects regular monthly investments with an optional step-up. For US workplace retirement savings with employer match and the IRS contribution limit, use the 401(k) Calculator.",
    },
  ],
  "social-security-calculator": [
    {
      question: "Does it matter when I choose to start claiming benefits?",
      answer: "Yes, significantly — claiming earlier than your full retirement age permanently reduces your monthly benefit, while delaying past it increases it. This is one of the most consequential timing decisions in retirement planning, worth modeling at a few different claiming ages.",
    },
  ],
  "state-tax-calculator": [
    {
      question: "Do all US states have income tax?",
      answer: "No — state tax rates and structures vary widely, and some states have no income tax at all. Select your specific state to see how its rate structure applies to your income.",
    },
    {
      question: "Which states have no income tax?",
      answer: "Alaska, Florida, Nevada, New Hampshire, South Dakota, Tennessee, Texas, Washington and Wyoming do not tax wages. The state list marks each of them. Washington still has payroll programs such as Paid Family and Medical Leave, which are shown as separate lines.",
    },
    {
      question: "How is state income tax calculated?",
      answer: "The tool subtracts the state's standard deduction and personal exemptions for your filing status, then applies each bracket's rate to the slice of income inside it. The brackets are added up and any state credit is taken off. The breakdown table shows every bracket.",
    },
    {
      question: "Do cities charge income tax too?",
      answer: "Some do. Pick your state, then a city or county to add its own local income tax line: New York City and Yonkers, every Maryland county and Baltimore City, Philadelphia, Detroit, Kansas City, St. Louis and Columbus. Other cities and counties are not included, so add them yourself.",
    },
    {
      question: "Which state has the highest income tax?",
      answer: "Use Compare all states: it ranks every state for your income and shows how much more or less you would pay than in your own state. The answer depends on your income, because some states tax at a flat rate and others use brackets.",
    },
    {
      question: "Are payroll programs like California SDI included?",
      answer: "They are shown as their own lines, apart from state income tax: California SDI, New York Paid Family Leave, New Jersey TDI and FLI, Washington Paid Family and Medical Leave and WA Cares, Massachusetts PFML and Paid Leave Oregon. They are employee shares charged on wages, with their wage caps, and each line names its source.",
    },
    {
      question: "Does it use my exact tax return?",
      answer: "No. It is a planning estimate from your income, filing status and the state's standard deduction and exemptions. Credits, federal-tax deductions, phase-outs and local taxes that are not listed are not modelled, so check your state's revenue department before filing.",
    },
  ],
  "timezone-converter": [
    {
      question: "Does this account for daylight saving time automatically?",
      answer: "Yes — the conversion reflects the actual current time offset for each location, including daylight saving adjustments where applicable, rather than a fixed year-round offset.",
    },
    {
      question: "Can it convert my whole event agenda?",
      answer: "Yes. If your project has an agenda (from the Event Day Schedule or an imported CSV), it appears below the converter in the zones you picked, with a marker such as +1 day where the date changes. A session's own time zone is used if it has one; otherwise the agenda zone shown above the table. Copy it as text or download it as CSV.",
    },
    {
      question: "How do I find a time when everyone in different time zones can meet?",
      answer: "Use the meeting planner under the converter. It lays out the day hour by hour in every zone you chose, shades the hours that fall inside working hours on each zone's own clock (09:00 to 17:00 unless you change it), and says in words when everyone is at work. Click an hour to use it. If nobody's hours overlap, it says so and suggests widening the hours or accepting an early or late slot for one person.",
    },
    {
      question: "Can I share the time with other people or put it in my calendar?",
      answer: "Yes. Copy share link makes a web address holding only the zones, the time and the working hours, so whoever opens it sees the same plan; nothing is saved on a server. Download calendar file makes an .ics file with one event at the exact moment, which every calendar app shows in the viewer's own zone, and lists the time in each zone in its description. Each takes one trial use.",
    },
    {
      question: "What happens on the night the clocks change?",
      answer: "The grid for that day has 23 or 25 hour columns, with * on the hour where the clock changes. A time that does not exist (the clocks jump forward) or happens twice (they go back) is flagged, and the first occurrence is used. The planner also shows the next clock change for each zone, such as New York going back one hour on Sunday 1 November 2026 at 2:00 am, taken from your device's time zone data.",
    },
    {
      question: "Why do the lists show cities and not just zone IDs?",
      answer: "Each zone shows a city and country with its abbreviation and offset, such as Mumbai, India (IST, UTC+5:30), and the technical zone ID (Asia/Kolkata) underneath. You can still search by city, country, abbreviation or zone ID. Pick a city rather than UTC or GMT when you are planning ahead, because a city follows its own daylight saving changes and UTC never moves.",
    },
  ],
  "tip-calculator": [
    {
      question: "How do I calculate a tip?",
      answer: "Enter the bill and choose a tip percentage; the tool shows the tip and new total. A 20% tip on $50 is $10, for a $60 total.",
    },
    {
      question: "How much should I tip at a restaurant?",
      answer: "In the US, 15% to 20% of the pre-tax bill is common for sit-down service, more for excellent service. Customs vary by country.",
    },
    {
      question: "Does this handle splitting a bill fairly if people ordered different amounts?",
      answer: "This tool handles an even split well, but if the group had noticeably different orders, splitting evenly isn't always fair — for itemized splitting, you'd want to divide the bill by item first before applying a tip.",
    },
    {
      question: "Should the tip be calculated before or after tax?",
      answer: "Customs differ, so the tool lets you choose. For US dollars the tip starts on the pre-tax amount, the usual US custom; for other currencies it starts on bill plus tax. Use the Tip on the amount before tax switch to change either.",
    },
    {
      question: "How does the split work when the total doesn't divide evenly?",
      answer: "Calculations are done in whole cents, pence or similar. The remainder is spread by giving some people one extra unit, and a note explains how many pay the higher and lower amounts, so shares sum to the total.",
    },
    {
      question: "What does rounding each share up do?",
      answer: "With the switch on, each person's share is raised to the next whole unit of the chosen currency, and the extra money is added to the tip. The tip and total therefore rise slightly.",
    },
    {
      question: "Why is there a currency selector for a tip calculation?",
      answer: "It sets the symbol, the number of decimal places and the rounding unit. For example, yen has no decimals. It also selects a local tipping hint where one is available. It does not convert amounts.",
    },
    {
      question: "What are the limits on the inputs?",
      answer: "The bill and tax cannot be negative, the bill must be under one billion, people must be a whole number from 1 to 500, and the tip percentage cannot be negative or above 1000. A tip over 100% is allowed but shows a warning. Otherwise a red message explains what to fix.",
    },
    {
      question: "Is anything I enter saved?",
      answer: "No. The calculation runs in your browser and nothing is stored or sent.",
    },
  ],
  "uk-tax-calculator": [
    {
      question: "Will this match my actual take-home pay exactly?",
      answer: "This estimates a standard tax code — if you have unusual circumstances (multiple jobs, benefits in kind, or a non-standard tax code), your actual deductions may differ from this general estimate.",
    },
    {
      question: "Which tax year and rates does it use?",
      answer: "The 2026/27 tax year. National Insurance is 8 percent between 12,570 and 50,270 pounds and 2 percent above that. Student loan is 9 percent over the plan threshold: Plan 1 26,900, Plan 2 29,385, Plan 4 33,795, Plan 5 25,000. Postgraduate loan is 6 percent over 21,000. Compare these with gov.uk.",
    },
    {
      question: "What are the Scottish bands?",
      answer: "After the personal allowance: starter 19 percent on the first 3,967 pounds, basic 20 percent up to 16,956, intermediate 21 percent up to 31,092, higher 42 percent up to 62,430, advanced 45 percent up to 125,140 of total income, and top 48 percent above that.",
    },
    {
      question: "How do the three pension types differ?",
      answer: "Salary sacrifice cuts your pay before tax, National Insurance and student loan are worked out. Net pay arrangement lowers pay for income tax only. Relief at source takes the contribution from taxed pay and grosses it up by 20 percent, widening your tax bands, with the pot figure shown in brackets.",
    },
    {
      question: "What is the employer National Insurance figure?",
      answer: "It is what your employer pays on top of your salary, worked out here as 15 percent of pay above 5,000 pounds a year, after any salary sacrifice. It is shown for information and does not reduce the take-home pay figure.",
    },
    {
      question: "Why does my take-home differ from my payslip?",
      answer: "The estimate assumes a standard tax code, one salary for the whole year and no other income. Payslips reflect your actual tax code, pay-period timing, benefits in kind, workplace pension rules and how a student loan is collected. Treat the result as a rough guide and check your payslip or HMRC for exact figures.",
    },
  ],
  "unit-converter": [
    {
      question: "How do I convert between units?",
      answer: "Choose a category such as length or weight, enter a value and pick the from-unit. The result appears instantly, and the list below shows the value in every other unit of that category.",
    },
    {
      question: "How do I convert cm to inches or kg to pounds?",
      answer: "Pick Length or Weight, type the value and choose centimeters or kilograms. One inch is exactly 2.54 cm and one pound is exactly 0.45359237 kg.",
    },
    {
      question: "Does it matter if I mean US or UK units, like fluid ounces?",
      answer: "Yes — double-check which specific unit variant you need for things like fluid ounces or tons. US and UK definitions differ for several common units, and picking the wrong one gives a subtly wrong answer.",
    },
    {
      question: "How accurate are the conversions?",
      answer: "Factors use exact definitions wherever they exist, such as 1 inch = 25.4 mm, 1 pound = 0.45359237 kg and 1 mile = 1609.344 m. Output is rounded to ten significant digits for display, which is far finer than everyday tasks need.",
    },
    {
      question: "Why are there two kinds of gallon and ton?",
      answer: "The US gallon is 3.785411784 liters while the UK gallon is 4.54609 liters, and the short ton is 2000 pounds against 2240 for the long ton. The list names each variant, so choose the one your source document uses.",
    },
    {
      question: "What is the difference between a kilobyte and a kibibyte here?",
      answer: "A kilobyte is 1000 bytes and a kibibyte is 1024 bytes, and the same split applies at mega, giga and tera level. Storage makers usually quote the first and operating systems often report the second, which is why numbers differ.",
    },
    {
      question: "How does the Fahrenheit and Celsius conversion work?",
      answer: "Temperature uses formulas rather than a single multiplier because the scales start at different zero points. Celsius to Fahrenheit multiplies by 9/5 and adds 32. Kelvin and Rankine are included too, and negative inputs are accepted.",
    },
    {
      question: "Can it convert cups to grams?",
      answer: "Yes, in the Cooking tab, for nine common ingredients: flour, granulated, brown and powdered sugar, butter, honey, milk, oil and water. It uses a typical weight per US cup, so the grams are approximate. A cup of flour in particular weighs more if it is scooped from the bag than if it is spooned in and leveled. For baking that needs precision, weigh the ingredient.",
    },
    {
      question: "Which horsepower does it use?",
      answer: "Both are listed. Mechanical horsepower (33,000 foot-pounds per minute) is 745.6998715822702 W, and metric horsepower (75 kilogram-force meters per second) is 735.49875 W. They differ by about 1.4 percent, so pick the one your source uses.",
    },
    {
      question: "Can I see several target units at once?",
      answer: "All at once, in a way. After you enter a value, the list below the result shows it in every other unit of the chosen category simultaneously, so you can read several answers without touching the To menu. Only the From unit is excluded from the list.",
    },
    {
      question: "Is -40 the same in Celsius and Fahrenheit?",
      answer: "Yes. -40 degrees Celsius is exactly -40 degrees Fahrenheit, and it is the only temperature where the two scales agree. Pick Temperature, type -40 and both read the same; Kelvin shows 233.15.",
    },
    {
      question: "Can I link to a conversion?",
      answer: "Yes. As you change the category, the units or the value, the address bar updates (for example ?c=temperature&from=celsius&to=fahrenheit&v=-40). Copy it to share that exact conversion, or reload the page to come back to it. The pages for popular pairs, such as centimeters to inches, have their own addresses under /tool/unit-converter/.",
    },
    {
      question: "Is my data sent anywhere?",
      answer: "No. All conversions run in your browser.",
    },
  ],
  "vat-calculator": [
    {
      question: "Is the VAT rate the same for every product in a country?",
      answer: "Not always — VAT rates differ significantly by country and sometimes by product category within a country. Confirm the rate that applies to your specific situation rather than assuming a standard rate.",
    },
    {
      question: "What is the difference between Add VAT and Remove VAT?",
      answer: "Add VAT starts from a price without tax and adds the percentage on top. Remove VAT starts from a total that already includes tax and works out the net price and the tax portion contained in it.",
    },
    {
      question: "How is VAT removed from a gross price?",
      answer: "The gross amount is divided by one plus the rate as a decimal. For a 20 percent rate you divide by 1.20. The VAT is then the gross minus the net. Simply taking 20 percent off the total would give the wrong answer.",
    },
    {
      question: "Can I use a rate that isn't in the list?",
      answer: "Yes. Choose Custom / Other, or just overwrite the VAT Rate (%) box with any number, including decimals. The calculation uses whatever rate is in the box, so enter the correct one for your sale.",
    },
    {
      question: "Why does choosing a country change the currency?",
      answer: "Each country entry carries a display currency, and the amounts are formatted in it. It only changes the formatting and symbol. It does not convert money between currencies.",
    },
    {
      question: "Are the built-in rates always current?",
      answer: "No. They are a stored list with the as-of date shown at the bottom of the tool, and governments change rates. Treat them as a convenient starting point and verify the rate that applies to your goods or services.",
    },
  ],
  "water-intake-calculator": [
    {
      question: "Should I drink exactly this amount every day regardless of conditions?",
      answer: "Treat it as a reasonable baseline to adjust from — climate and individual sweat rate both affect actual hydration needs meaningfully, so you'd typically want more on hot days or during more intense exercise.",
    },
  ],
  "cover-letter-maker": [
    {
      question: "How long should a cover letter be?",
      answer: "About 250 to 350 words, one page. The tool counts the words and tells you when you are over.",
    },
    {
      question: "Can I send the letter as it comes out?",
      answer: "Treat it as a draft. Read it, add one sentence only you could write, and check every fact before you send it.",
    },
  ],
  "pay-stub-generator": [
    {
      question: "Does the pay stub calculate my taxes?",
      answer: "No. You type the rate for each tax and the tool applies it to gross pay. Use your payroll provider's figures, because real withholding uses brackets and limits.",
    },
    {
      question: "Can I use this stub for a loan or rental application?",
      answer: "No. A stub is a record of pay that was actually paid. Showing a stub for pay that was not paid is fraud.",
    },
  ],
  "proposal-builder": [
    {
      question: "How are the prices added up?",
      answer: "The tool reads the number at the end of each price line, such as Design: $1,200, and adds them into the total.",
    },
    {
      question: "Is a proposal legally binding?",
      answer: "A signed proposal can create an agreement, but it is not a full contract. For large projects have a lawyer review the terms.",
    },
  ],
  "rental-application-maker": [
    {
      question: "Does the tool store what applicants write?",
      answer: "No. It only makes a blank form from the property details you type. Applicants fill in the printed or downloaded form, outside the site.",
    },
    {
      question: "Can I run a background check with this form?",
      answer: "The form includes a consent line, but a check needs separate written permission and a screening provider. Rules vary by state and city, so check them first.",
    },
  ],
  "bill-of-sale-generator": [
    {
      question: "Is a general bill of sale enough for a vehicle sale?",
      answer: "Check whether your state requires a particular official form in addition to, or instead of, a general bill of sale — vehicle title transfers often have their own specific paperwork requirements beyond a standard bill of sale.",
    },
  ],
  "business-card": [
    {
      question: "Can I preview how the card will actually look printed?",
      answer: "Pair this with the Mockup Generator tool to see the finished card in a realistic device or print mockup before ordering, since a flat design file can look different from how it'll actually appear once printed and held.",
    },
    {
      question: "What size is the card and how much bleed does the PDF have?",
      answer: "The finished card is 3.5 by 2 inches. Each PDF page is 3.75 by 2.25 inches, because 0.125 inch of bleed is added on every edge. The background and the coloured strip along the bottom are extended into that bleed, while text stays inside the trim area.",
    },
    {
      question: "Can I place my logo on the card?",
      answer: "Not as a separate element. The only image option is a background photo that covers the whole card and is darkened by an overlay. A logo placed inside that photo would be cropped to fit and dimmed along with everything else.",
    },
    {
      question: "What is in the print-ready PDF?",
      answer: "Two pages, front first and back second, each a 300-pixels-per-inch picture of the card plus bleed. Before ordering, check your print shop's own requirements for bleed, safe margin and colour mode, as the file is RGB and not a press-prepared document.",
    },
    {
      question: "Why does some of my text look smaller than I expected?",
      answer: "Each line is fitted to the width inside a 0.2 inch margin. The name starts large and steps down toward a minimum size if it is long, and the same happens to the title, company and contact lines. Shorten the text if you want it larger.",
    },
    {
      question: "Can the back have a different colour or photo?",
      answer: "No. Both sides share the same colour circle or photo and the same text colour. The back differs only in its content, which is the company name in large type with the optional tagline beneath it.",
    },
  ],
  "contract-templates": [
    {
      question: "Is this template a substitute for legal advice?",
      answer: "No — this is a general template, not a substitute for legal advice on anything with significant money or risk involved. For an important or high-value agreement, having a lawyer review the final version is worth the cost.",
    },
  ],
  "employment-offer-letter": [
    {
      question: "Should I send an NDA along with the offer letter?",
      answer: "If the role will expose the new hire to confidential information from day one, pairing this with the NDA Generator makes sense — drafting an NDA after they've already seen sensitive information defeats the point of having one.",
    },
    {
      question: "What is a job offer letter?",
      answer: "A job offer letter is a written document in which an employer formally offers a candidate a position. It usually states the job title, start date, pay, who the person reports to, any benefits, and what the candidate must do to accept, such as signing and returning it by a set date. It is normally sent after interviews and before the first day of work.",
    },
    {
      question: "What should a job offer letter include?",
      answer: "At minimum: the role and start date, compensation and pay period, the reporting line, whether the job is at-will or for a fixed term, any conditions such as a background check or proof of work authorization, and a signature and acceptance block. This generator includes those sections for a permanent role, and for an internship it covers duration and whether it is paid or unpaid.",
    },
    {
      question: "Is a job offer letter legally binding?",
      answer: "An offer letter records the terms offered, and once accepted it can carry legal weight, but how much depends on its wording and on local employment law. Many US offers state that employment is at-will, which means either side can end it at any time. Have a lawyer review the letter before you rely on it for anything beyond a straightforward hire.",
    },
    {
      question: "Can I send the letter as a Word file or PDF?",
      answer: "Yes. After you preview the letter you can download it as a Word document, so you can still edit it, or as a PDF that is ready to send. You can add your company logo and a signature image before you download.",
    },
  ],
  "eviction-notice-generator": [
    {
      question: "Does this account for my state's specific eviction rules?",
      answer: "Eviction procedures and required notice periods are heavily regulated and vary by state and city. Check your specific local legal requirements carefully, since an improperly issued notice can delay or invalidate the whole process.",
    },
  ],
  "expense-tracker": [
    {
      question: "Can I attach receipts?",
      answer: "Yes. Attach a photo (JPG, PNG, WebP) or a PDF of up to 10 MB to any entry and a thumbnail shows beside it. The file stays in your browser tab and is never uploaded. CSV and receipts (ZIP) downloads a folder of your receipts next to the CSV, and the Receipt column in the CSV points to each file.",
    },
    {
      question: "Can I see spending by month?",
      answer: "Yes. Pick a month, or set a From and To date, and the list, totals, category bars and every download cover only those entries. With entries in more than one month a By month chart shows costs and income side by side. Repeat monthly adds one entry for each month from the entry date up to a date you choose.",
    },
    {
      question: "Can I export my tracked expenses for taxes or bookkeeping?",
      answer: "Yes — expenses are tracked in a structured way that can be reviewed or exported, useful for staying on top of deductible business costs throughout the year rather than reconstructing them at tax time.",
    },
    {
      question: "How does the budget comparison work?",
      answer: "Enter a number in Budget (optional). The tool subtracts the total of all entries and shows Remaining Budget in green, or Over Budget By in red when spending exceeds it, with a line stating spent versus budget. A blank or zero budget hides the panel.",
    },
    {
      question: "Which categories can I choose from?",
      answer: "Business has eleven: office and supplies, software and subscriptions, travel, meals and entertainment, a marketing heading, equipment, professional services, rent and utilities, payroll and contractors, taxes and fees, other. Personal has eight: Food, Transport, Housing, Utilities, Entertainment, Health, Shopping, Other.",
    },
    {
      question: "Can I fix an entry I typed wrongly?",
      answer: "Yes. Press the pencil beside the entry to load its date, category, description and amount back into the form, change what you need, and press the button again to save. The X cancels the edit. The trash button deletes an entry at once, with no undo.",
    },
    {
      question: "What does the CSV contain?",
      answer: "Four columns: Date, Category, Description and Amount, with amounts to two decimals and every field in quotes, in the order shown on screen. Avoid starting a description with =, +, - or @, because a spreadsheet may read such a cell as a formula.",
    },
    {
      question: "Will my expenses survive closing the tab?",
      answer: "Only for this visit. They stay while the tab is open and are erased when you close it, receipts included. Nothing is stored online, so export a CSV, Excel sheet, PDF or ZIP, or save the project file, if the records matter to you.",
    },
  ],
  "government-forms-hub": [
    {
      question: "Does this fill out the forms for me?",
      answer: "No — this is a directory of direct links to real, official government forms, so you can find the exact form you need without searching government sites from scratch. It doesn't fill in or file the forms on your behalf.",
    },
  ],
  "invention-disclosure-worksheet": [
    {
      question: "Is this a substitute for filing an actual patent application?",
      answer: "No — this documents your invention's details in a structured way as a starting record, useful before consulting a patent attorney, but it isn't itself a patent filing or a legal protection on its own.",
    },
    {
      question: "How does this relate to a patent filing?",
      answer: "The worksheet helps you organize notes about an invention. It does not file anything. The USPTO describes a provisional application as a lower-cost first filing that establishes an early filing date and lasts 12 months, after which a nonprovisional application must follow. This is not legal advice. Check the official source or ask a registered patent practitioner.",
      source: { label: "USPTO: Provisional Application for Patent", url: "https://www.uspto.gov/patents/basics/apply/provisional-application" },
    },
  ],
  "invoice-generator": [
    {
      question: "Should I use a consistent invoice numbering system?",
      answer: "Yes — keeping a consistent invoice numbering system from the start makes bookkeeping and tracking which invoices have been paid significantly easier once you have more than a handful.",
    },
    {
      question: "How are the discount and tax calculated?",
      answer: "The subtotal is quantity times price, added up over all rows. The discount percentage is taken from the subtotal, and the tax percentage is then applied to the discounted amount. Each step is rounded to two decimal places, and a discount above 100 percent is treated as 100.",
    },
    {
      question: "Which currencies can I choose?",
      answer: "US dollar, euro, pound, rupee, Canadian dollar, Australian dollar, yen, Singapore dollar, dirham and rand. The choice sets the symbol and number format on the page and in the PDF. It does not convert amounts from one currency to another.",
    },
    {
      question: "Can I write names or notes in Hindi or with accents?",
      answer: "Hindi and accents both work. Accented Latin letters, Greek, Cyrillic, the rupee sign and Devanagari can all appear. Some scripts are drawn as pictures, so they show correctly but cannot be selected as text. If a character cannot be drawn, a warning appears after download.",
    },
    {
      question: "What happens when the invoice has many lines?",
      answer: "The table continues on additional pages, each starting with a note giving the invoice number and the word continued. On invoices of more than one page, every page is numbered in the form Page 1 of 2 near the bottom.",
    },
    {
      question: "Why will the tool not produce a PDF yet?",
      answer: "It needs a business name, a client name, at least one line with a description, a quantity above zero on every used line and a price of zero or more. The first missing item is named in a message, and nothing is generated until it is fixed.",
    },
  ],
  "lease-agreement-generator": [
    {
      question: "Does this account for my state's specific landlord-tenant laws?",
      answer: "Landlord-tenant law varies significantly by state and even by city. Check your specific local requirements, like required disclosures or security deposit limits, before finalizing — this general template can't account for every local rule.",
    },
  ],
  "nda-generator": [
    {
      question: "Is a general NDA template enough for a high-stakes business relationship?",
      answer: "For anything involving significant intellectual property or a high-stakes business relationship, having a lawyer review the specific terms before signing is worth doing — treat this as a solid general starting template, not a final word.",
    },
    {
      question: "Is this a one-way or mutual NDA?",
      answer: "It is mutual. The wording covers confidential information disclosed by either party, and both sides take on the same duties. Party A and Party B each hold and use the other's information only for the stated purpose. If just one side shares secrets, a one-way form fits better.",
    },
    {
      question: "What is the difference between the two year fields?",
      answer: "Disclosure period (years) is how long new disclosures fall under the agreement, starting from the date in the document. The second field is how many years confidentiality lasts afterwards. Trade secrets are handled separately in the text and stay protected as long as they remain trade secrets.",
    },
    {
      question: "Why can't I press Preview or download?",
      answer: "Party A Name, Party B Name and Governing State/Country must all be filled in. Until then Preview, Word and PDF stay disabled and an amber note reminds you. The purpose and year fields come pre-filled, so only those three need your input.",
    },
    {
      question: "Can I edit the clauses after downloading?",
      answer: "Yes, if you choose the Word download. The .docx opens in a normal word processor where you can reword or add clauses. The PDF is meant for reading and signing, so make your changes in the Word file first, then save your own PDF.",
    },
    {
      question: "What does the governing law field do?",
      answer: "Whatever you type, such as Texas, USA or England and Wales, is inserted into the Governing Law clause as the place whose laws apply. The tool does not check that it is a real or suitable jurisdiction, so choose one that both parties accept.",
    },
  ],
  "power-of-attorney-generator": [
    {
      question: "Should I have this reviewed by a lawyer before signing?",
      answer: "Power of attorney is a significant legal authority to grant someone. For anything beyond a simple, low-stakes situation, having this document reviewed by a lawyer before signing is genuinely worth doing.",
    },
  ],
  "product-description-writer": [
    {
      question: "Can I use the generated description as-is?",
      answer: "Treat it as a solid first draft to personalize, not a finished listing — the specific details that make your product distinct from similar ones are worth adding in by hand.",
    },
  ],
  "promissory-note-generator": [
    {
      question: "Do I really need a written note for an informal loan between friends?",
      answer: "Putting even an informal loan in writing, however casually, meaningfully reduces the odds of a disagreement later about what was actually agreed to — worth doing even for loans between friends or family.",
    },
  ],
  "receipt-maker": [
    {
      question: "What details should every receipt include?",
      answer: "Include a receipt number and date on every receipt you generate — it's a small habit that makes reconciling records against bank statements much easier later.",
    },
    {
      question: "Why is the Generate button greyed out?",
      answer: "A business name is needed, plus at least one complete item, and no half-filled rows. A row missing its description, quantity or price is outlined in amber with a message below the list. Complete the row or remove it to continue.",
    },
    {
      question: "Is the full card number printed on the receipt?",
      answer: "Never. For Credit Card or Debit Card the box accepts digits only and stops at four. The PDF prints them in the form **** **** **** followed by those four digits, so the real number never appears.",
    },
    {
      question: "How does tax appear on the receipt?",
      answer: "When the Tax percentage is above zero, the PDF lists a Subtotal, then a line with your tax label and percentage, then TOTAL. When it is zero, only TOTAL is shown. You must type the rate that applies where you trade.",
    },
    {
      question: "Does the on-screen preview match the PDF exactly?",
      answer: "Not entirely. The preview is a simple mock-up showing each item with quantity and price and the TOTAL, but it leaves out the subtotal and tax lines. The PDF is the authoritative version, so check the downloaded file.",
    },
    {
      question: "What does the signature option add?",
      answer: "It places a Received by line and a Date line beneath the Thank you message. If the page is too full for them to fit below, they are placed on a second page of the same file.",
    },
  ],
  "resume-builder": [
    {
      question: "What should I do with my resume after building it here?",
      answer: "Run it through PDF Editor for any final touches, then Compress PDF before sending — a resume that's too large can bounce from an applicant tracking system's upload limit or sit at the bottom of a recruiter's inbox while it loads.",
    },
    {
      question: "Why are Preview and Download greyed out?",
      answer: "Both stay disabled until Full Name is filled and at least one Education entry has both a Degree and a School. That is a rule of this tool, so enter your highest qualification or the course you are taking, even if you would rather keep education brief.",
    },
    {
      question: "How do I create bullet points under a job?",
      answer: "Write one achievement per line in the bullets box for that entry. Blank lines are ignored, and the tool adds the bullet character itself, so do not type your own dash or dot or you will end up with two markers.",
    },
    {
      question: "What actually differs between the three templates?",
      answer: "Classic centers the name and contact line and uses gray divider rules. Modern left-aligns everything, colors the name and headings teal and adds a teal bar across the top of the PDF. Minimal is left-aligned with uppercase headings, no dividers and slightly smaller text.",
    },
    {
      question: "Can I write my resume in a language other than English?",
      answer: "Accented Latin, Greek, Cyrillic and the rupee sign use an embedded font in the PDF. Other scripts, including Hindi, Chinese and Arabic, are drawn as pictures, so they display correctly but that text cannot be selected or searched in the PDF.",
    },
    {
      question: "Should I download the PDF or the Word file?",
      answer: "Use the PDF when the layout must stay fixed and the employer accepts it. Use the Word file when you want to keep editing or when an application asks for .docx. Both come from the same entries, so after changing the form, download again.",
    },
  ],
  "startup-name-generator": [
    {
      question: "What should I check before committing to a generated name?",
      answer: "Check domain availability and existing trademark registrations before committing to any generated name — a great-sounding name is only useful if you can actually secure the matching web address and legally use it.",
    },
  ],
  "user-manual-writer": [
    {
      question: "In what order should I write the sections?",
      answer: "Write the Troubleshooting section last, after you've documented the normal workflow — it's much easier to anticipate what will actually confuse someone once you've walked through the full process yourself.",
    },
    {
      question: "Does this tool write the manual for me?",
      answer: "No. It formats what you enter. The only built-in text is a one-line placeholder in each of six starter sections, and those should be replaced. The tool adds the title block, version line, Contents list and section numbers around your own writing.",
    },
    {
      question: "How do I get a PDF?",
      answer: "Press Print / Save as PDF, then pick Save as PDF as the printer in the dialog your browser opens. Only the manual preview is printed; the editing panel is hidden for printing. Page margins are set to 0.75 inch.",
    },
    {
      question: "Why is there no Contents list on my manual?",
      answer: "The Contents list appears only when the manual has more than two sections. With one or two sections it is left out. Add a third section and it shows up, listing each heading in order with its number.",
    },
    {
      question: "Can I reorder sections or save my work?",
      answer: "Not directly. Sections stay in the order they were added, and there is no move button. Nothing is stored either, so a refresh restores the defaults. Copy your text somewhere safe if you may come back to it later.",
    },
    {
      question: "Can I add pictures or formatting?",
      answer: "No. Section bodies are plain text, with your line breaks kept as typed. For screenshots or diagrams, print the manual to PDF and combine it with images in another editor, or use a word processor from the start.",
    },
    {
      question: "Is there an international standard for instructions for use?",
      answer: "IEC/IEEE 82079-1 covers principles and general requirements for preparing instructions for use of products. This tool does not certify compliance with it. Check the official source and any rules for your product and market.",
      source: { label: "IEC: IEC/IEEE 82079-1", url: "https://webstore.iec.ch/en/publication/29075" },
    },
  ],
  "border-radius-generator": [
    {
      question: "Can I set each corner independently instead of matching them all?",
      answer: "Yes — unlink the corners to adjust each one independently, which can create a distinctive \"blob\" or organic shape effect that's become a popular design trend, rather than just a uniform rounded rectangle.",
    },
    {
      question: "What order are the four border-radius values in?",
      answer: "Clockwise from the top left: top left, top right, bottom right, bottom left. Equal values are shortened, so 10px 20px means top left and bottom right are 10px and the other two are 20px.",
    },
    {
      question: "Should I use px or %?",
      answer: "Pixels give the same curve at any size. Percentages follow the box, so 50% on a square makes a circle. Percent radii in this tool go up to 50%, so switching from px lowers any larger value to 50% and tells you which corners changed.",
    },
    {
      question: "How do I make a blob shape?",
      answer: "Press Blob shape for an elliptical starting point, then move the sliders. Elliptical corners have a horizontal and a vertical radius, which CSS writes as horizontal values, a slash, then vertical values.",
    },
    {
      question: "Why does a huge radius like 9999px make a pill?",
      answer: "CSS never lets the curves on one side add up to more than that side's length, so it scales them down until they just fit. On a wide, short box that leaves fully rounded ends, a pill. This tool's sliders stop at 200px, so type 9999px into your own CSS for that effect.",
    },
  ],
  "box-shadow-generator": [
    {
      question: "Should I use a strong, dark shadow for a modern look?",
      answer: "Usually not — subtle shadows with low opacity and moderate blur tend to look more modern and professional than large, dark, sharp-edged ones. It's easy to overdo a shadow effect without realizing it until you compare it against a more restrained version.",
    },
  ],
  "clip-path-generator": [
    {
      question: "Does clip-path resize my image, or just crop it?",
      answer: "It crops rather than resizes — make sure your underlying image or content is large enough that the important parts survive being cropped into the shape you choose.",
    },
    {
      question: "What order should I click points in when building a custom shape?",
      answer: "Place points in order around the outline, either clockwise or counter-clockwise, rather than jumping across the shape — otherwise the polygon can end up with crossed, self-intersecting edges.",
    },
    {
      question: "How many preset shapes are there?",
      answer: "Sixteen: Triangle, Trapezoid, Parallelogram, Pentagon, Hexagon, Octagon, Star, Cross, Arrow, Diamond, Circle, Ellipse, Message Bubble, Blob 1, Blob 2 and Diagonal Section. Circle and Ellipse use circle() and ellipse(); the other fourteen use polygon().",
    },
    {
      question: "How many points do I need for a custom shape?",
      answer: "At least three. Until then the preview stays empty and the CSS box asks you to add points. Pressing Copy CSS with fewer than three points shows an error instead of copying anything.",
    },
    {
      question: "Can I move a point after I place it?",
      answer: "No. You can remove only the most recent point with Undo Point, or wipe everything with Clear All. If a point in the middle is wrong, undo back to it, or clear and redraw the outline.",
    },
    {
      question: "What does the copied CSS look like?",
      answer: "A single declaration such as clip-path: polygon(50% 0%, 0% 100%, 100% 100%); that you paste onto an element. Custom shapes use the same form, with each point written as a percentage pair to one decimal place.",
    },
    {
      question: "Why does my copied shape not fill my image the same way as the preview?",
      answer: "Percentages refer to the element's own box. If your element has a different width and height than the 192 pixel square, the same polygon is stretched to match, so angles will look different on a wide or tall element.",
    },
  ],
  "color-converter": [
    {
      question: "Why would I need HSL instead of just hex?",
      answer: "HSL is often the easiest format to work with when you want to tweak a color's lightness or saturation without changing its underlying hue — adjust one HSL value and convert back to hex once you're happy with it.",
    },
    {
      question: "Which input formats does it understand?",
      answer: "HEX with 3, 4, 6 or 8 digits (the hash is optional), rgb() and rgba(), hsl() and hsla(), hwb(), plain comma or space separated triples of numbers, and CSS color names such as tomato or rebeccapurple.",
    },
    {
      question: "Why is the CMYK value marked as a screen estimate?",
      answer: "It is calculated directly from the RGB numbers with a simple formula. Real printing involves inks, paper and color profiles, so a press may reproduce the color differently. Use the CMYK row as a rough guide, not a print specification.",
    },
    {
      question: "What happens if my color has transparency?",
      answer: "If you enter a value with alpha, such as an 8-digit hex or rgba(), the RGB and HSL rows are written as rgba() and hsla() with the alpha figure, and the HEX row includes the extra pair of digits.",
    },
    {
      question: "What does Nearest CSS name mean?",
      answer: "The tool compares your color with the list of named CSS colors and shows the closest one. If your color is not on the list, the name is only an approximation, so do not assume it matches exactly.",
    },
    {
      question: "Why do I get a red error under the box?",
      answer: "The text could not be read as a color. Check for typos, a stray character in the hex code, or an unsupported notation. The message gives working examples like #ff6600, rgb(255, 102, 0) and orange.",
    },
  ],
  "color-palette": [
    {
      question: "Can I generate a palette from an existing color I already have?",
      answer: "Yes — start from a specific color and generate a full, coordinated palette built around it, rather than starting from a completely random set.",
    },
    {
      question: "How does each harmony mode build its colors?",
      answer: "Complementary alternates the base hue with its opposite, 180 degrees away, stepping lightness between them. Analogous spreads hues across 60 degrees centered on the base. Triadic cycles three hues 120 degrees apart. Monochromatic keeps one hue and steps lightness evenly from dark to light.",
    },
    {
      question: "Why does Generate New give me the same palette again?",
      answer: "Only Random mode uses chance. The other modes are calculated from the base color, mode and count, so the same inputs always return the same swatches. Change the base color, press Random Base, or pick a different count to see something new.",
    },
    {
      question: "Does Random mode use my base color?",
      answer: "No. Random fills the strip with independent random hex colors and ignores the Base Color swatch. If you want results related to your color, choose one of the four other harmonies.",
    },
    {
      question: "What happens if two swatches would come out identical?",
      answer: "A palette never shows the same hex twice. When clamping or a grey base would create a repeat, the tool shifts that swatch's lightness in small steps until it becomes distinct from the others.",
    },
    {
      question: "What exactly gets copied?",
      answer: "Clicking one swatch copies that color as a lowercase hex code like #3366cc. Copy All copies every hex in the current strip, in order, separated by a comma and a space, ready to paste into a note or a CSS variable list.",
    },
  ],
  "color-picker-image": [
    {
      question: "Why did I pick a slightly different color than I expected?",
      answer: "Zoom in before picking if you need real precision — a single pixel's color can shift subtly across a gradient or textured area, so the exact spot you click matters more than it might seem.",
    },
    {
      question: "Why is the color I picked slightly different from the one in my design file?",
      answer: "The tool reads the decoded pixel, so JPEG compression, resizing and color profile conversion can all shift a value by a few steps. For the most dependable match, sample a flat area of a PNG or of the original export instead of a compressed copy.",
    },
    {
      question: "What does the picked color look like in each format?",
      answer: "You get hex such as #3366cc, rgb(51, 102, 204) and hsl(220, 60%, 50%) for the same pixel. HSL numbers are rounded to whole degrees and percentages, so converting an HSL value back can land a unit away from the original.",
    },
    {
      question: "Does it work on an animated GIF?",
      answer: "The image is drawn once onto the canvas when it loads, so you sample whichever frame the browser paints, normally the first. To read a color from a later frame, export that frame as a still image and upload the still.",
    },
    {
      question: "Is there a maximum image size?",
      answer: "The upload area warns when a file is larger than 50 MB. The picture is held at full pixel size in memory on a canvas, so very large photos can be slow or fail on a phone with limited memory.",
    },
    {
      question: "Does clicking the image copy the color automatically?",
      answer: "No. A click only displays the values. Press the copy icon next to HEX, RGB or HSL to place that text on your clipboard, and a short confirmation message shows exactly what was copied.",
    },
  ],
  "contrast-checker": [
    {
      question: "What contrast ratio counts as accessible?",
      answer: "WCAG accessibility guidelines generally require a 4.5:1 ratio for normal text and 3:1 for large text to be considered readable enough for accessibility compliance — this tool checks your specific color pair against those thresholds.",
    },
    {
      question: "What ratios do I need to pass WCAG AA?",
      answer: "Normal text needs 4.5:1 and large text needs 3:1. AAA raises these to 7:1 and 4.5:1. Non-text parts like icons, input borders and focus rings need 3:1 under success criterion 1.4.11, which the tool lists on its own line.",
    },
    {
      question: "What counts as large text?",
      answer: "WCAG defines it as at least 18 point regular, or 14 point bold, which is roughly 24 pixels or 18.66 pixels bold on screen. The tool does not measure your text, so you decide which row applies.",
    },
    {
      question: "What does the Use ... (passes AA) button do?",
      answer: "When your pair falls short of 4.5:1, it tries darker versions of the text color on a light background, or lighter ones on a dark background, until the ratio reaches 4.5:1. Clicking it sets the text color to that value.",
    },
    {
      question: "Which color formats can I type?",
      answer: "HEX (with or without the hash), rgb(), hsl() and CSS color names are accepted, along with simple number triples. If either box cannot be read, the ratio shows a dash and a red message appears.",
    },
    {
      question: "Does it handle semi-transparent colors?",
      answer: "Not in a meaningful way. It compares the two color values as given and does not blend an alpha channel with the background underneath. Convert transparent colors to their solid blended result first, then test that pair.",
    },
  ],
  "favicon-generator": [
    {
      question: "Will a detailed logo work well as a favicon?",
      answer: "Start with a simple, high-contrast source image — favicons are shown very small, so intricate details in a complex logo often just disappear at that size.",
    },
    {
      question: "Exactly which files are in the ZIP?",
      answer: "Six PNGs, one ICO and two text files: favicon-16x16.png, favicon-32x32.png, favicon-48x48.png, apple-touch-icon.png (180 pixels), android-chrome-192x192.png, android-chrome-512x512.png and favicon.ico, plus site.webmanifest (which points at the 192 and 512 icons) and README.html-snippet.txt with the link tags. Unzip the bundle straight into your site folder, and set the name and short_name in site.webmanifest before use.",
    },
    {
      question: "What sizes are inside favicon.ico?",
      answer: "Three: 16, 32 and 48 pixels. Each is stored as a PNG image inside the ICO container, which every current browser reads. Some very old programs that expect bitmap-style icon entries may not understand that arrangement.",
    },
    {
      question: "What is the difference between Crop to fill and Fit whole image?",
      answer: "Crop to fill enlarges the picture until the square is covered and trims whatever sticks out, centered. Fit whole image shrinks it until everything is visible, leaving blank bands on the shorter side, which show your background color if one is on.",
    },
    {
      question: "Will the background stay transparent?",
      answer: "Yes for most files, unless you tick Background. With it off, empty areas stay transparent in the ico and in every PNG except the Apple touch icon, which always gets a solid background (white if you pick no color), because iOS shows transparent icons on black. With it on, the chosen color fills the full square behind the image in all six PNGs and the ico.",
    },
    {
      question: "What do I paste into my site?",
      answer: "The link tags in the box: the ico with sizes 48x48, PNGs at 32 and 16, the apple-touch-icon, the 192 and 512 Android icons and the manifest link. Put them in the head and upload the files to the site root, since the paths begin with a slash.",
    },
  ],
  "font-pairing-tool": [
    {
      question: "What's a reliable rule for pairing fonts that work well together?",
      answer: "As a general rule, pairing two fonts from very different categories (like a serif heading with a sans-serif body) tends to work better than pairing two fonts that are similar but not quite matching.",
    },
    {
      question: "Where do the fonts come from?",
      answer: "From Google Fonts. The page adds one stylesheet link to fonts.googleapis.com for the ten pairings that use web fonts, and your browser downloads the faces from Google. Times New Roman and Arial are system fonts already on most devices.",
    },
    {
      question: "Are my heading and body text sent anywhere?",
      answer: "No. The two text boxes only change what is drawn on the cards in your browser. The font request to Google lists font names and weights, not your wording.",
    },
    {
      question: "What exactly does Copy CSS give me?",
      answer: "A commented heading font-family line, a body font-family line, each with a serif or sans-serif fallback, and an @import url line for fonts.googleapis.com with display=swap. The Times New Roman and Arial pairing gets a comment instead of an import.",
    },
    {
      question: "Why do the preview weights differ from the copied import?",
      answer: "The preview loads weights 400, 600 and 700 of every family. The copied import is trimmed to 600 and 700 for the heading and 400 and 500 for the body, plus display=swap. Single-weight faces such as Abril Fatface and Bebas Neue are requested without a weight list.",
    },
    {
      question: "Can I use these fonts without Google's server?",
      answer: "The import line does load from Google. To avoid that, download the font files yourself from Google Fonts and host them with @font-face; check each family's license first. This tool does not generate or bundle font files.",
    },
  ],
  "gradient-generator": [
    {
      question: "Can I preview the gradient before copying the CSS?",
      answer: "Yes — adjust the gradient live and preview it applied to a sample element before copying the generated CSS code into your project.",
    },
    {
      question: "Why did my stop positions change when I added a color?",
      answer: "Adding or removing a stop re-spaces all stops evenly across 0 to 100 percent, so earlier edits to the \"at\" boxes are replaced. Add every color first, then set the exact percentages you want.",
    },
    {
      question: "What is the first line in the copied CSS?",
      answer: "It is a solid background color equal to the stop with the lowest position. Browsers that do not understand gradients use that line, while all modern browsers ignore it and use the gradient on the second line.",
    },
    {
      question: "How do I make a hard edge instead of a blend?",
      answer: "Give two neighboring colors the same percentage in their \"at\" boxes. The CSS then switches color at that point with no transition, which is how stripes and split backgrounds are made.",
    },
    {
      question: "Why is there no Angle slider on Radial?",
      answer: "A circular gradient radiating from the center has no direction to rotate, so the slider is hidden. Linear uses the angle as its direction, and Conic uses it as the starting point of the sweep.",
    },
    {
      question: "How many colors can I use?",
      answer: "From two to six. The X button disappears when only two remain because a gradient needs at least two stops, and Add Color disappears once you reach six.",
    },
  ],
  "image-to-svg": [
    {
      question: "Will this work well on a detailed photo?",
      answer: "Not ideally — this works best on simple, high-contrast images like logos or line drawings. A detailed photo will convert, but often produces an unwieldy, overly complex SVG rather than a clean result.",
    },
    {
      question: "Is this a real vector, or just my image embedded in an SVG wrapper?",
      answer: "A genuine vector — the output is made of real, traced SVG paths, not the original picture pasted inside an SVG file. That's the actual point of vectorizing: true paths that scale to any size with zero quality loss, unlike a raster image embedded in an SVG, which is still just a raster image underneath and looks worse the larger it's scaled.",
    },
    {
      question: "Does scaling the result lose quality the same way a photo does?",
      answer: "No — once traced, the SVG is genuinely resolution-independent and can be scaled to any size with no quality loss at all, since it's made of real vector paths, not pixels. The tracing step itself (turning the original photo into those paths) is the part that simplifies detail, not the later scaling.",
    },
    {
      question: "What does the Colors slider actually control?",
      answer: "How many distinct color regions the tracer groups your image into. Fewer colors produces a simpler, cleaner-looking trace with a smaller file; more colors captures finer detail and gradients, at the cost of a larger, more complex SVG.",
    },
    {
      question: "Will a large or highly detailed image take a long time to convert?",
      answer: "It can — tracing is a computationally intensive process with no progress indicator or way to cancel once started, so a large or highly detailed source image may take noticeably longer than a small, simple logo. A warning appears before converting if the image is unusually large.",
    },
    {
      question: "Are my images uploaded to a server?",
      answer: "No — vectorizing runs entirely in your browser using a local tracing library. Your image never leaves your device.",
    },
    {
      question: "What file types can I upload?",
      answer: "Any standard raster image format your browser can display — JPG, PNG, and WebP all work as source files for tracing.",
    },
    {
      question: "Can I edit the resulting SVG's colors or shapes afterward?",
      answer: "Not here — this tool produces the traced SVG file, but doesn't include SVG editing tools. Any standard vector graphics editor (or even a text/code editor, since SVG is just XML) can open and modify the downloaded file afterward.",
    },
    {
      question: "Is this a real vector, or just my picture inside an SVG file?",
      answer: "It is a real vector. The tool reduces the image to a palette, finds the outlines of each colour area and writes them as scalable paths. The picture is not embedded. That means the result can be enlarged without pixelation, although it is an approximation of the original.",
    },
    {
      question: "Why does my photo give a messy, heavy result?",
      answer: "Tracing works by turning areas of similar colour into shapes. A photo has smooth gradients and noise, so it breaks into a great many small patches. Logos and flat artwork have clear colour areas, which is why they trace so much more cleanly.",
    },
    {
      question: "Why is there a warning about large images?",
      answer: "Pictures longer than 1500 pixels on either side take much longer to trace, and the process cannot be stopped midway or show progress. The warning suggests resizing first. The conversion is still allowed, and the tab has not frozen while it works.",
    },
    {
      question: "Can I get the text in my logo as editable text?",
      answer: "No. Tracing sees only colour shapes, so letters come out as outlines. They look like the letters but cannot be retyped. Keep the original design file for editable text, or recreate the wording in a vector editor using a matching font.",
    },
  ],
  "logo-maker": [
    {
      question: "What size should I export my logo at?",
      answer: "Export at a large size even if you only need it small right now — it's much easier to scale a large logo down later than to try to enlarge a small one without it looking blurry.",
    },
    {
      question: "What file format does the logo come in?",
      answer: "A single PNG, 512 by 512 pixels, with transparency outside the circle or rounded square. There is no SVG or PDF export, so for large print sizes you would need to recreate the design as vector artwork.",
    },
    {
      question: "Whose responsibility is it that my logo does not copy another brand?",
      answer: "Yours. The tool uses common icons and simple shapes that anyone can pick, so another business may end up with a similar mark. Search trademark databases for your name and look before you publish or register it.",
    },
    {
      question: "How does the Pattern style work?",
      answer: "Your brand name is converted to a number, and fifteen bits of it switch squares on or off in the left three columns of a five by five grid, which is mirrored to the right. The same name always gives the same pattern.",
    },
    {
      question: "Why does my Monogram show only one letter?",
      answer: "It takes the first letter of each of the first two words in the name. A single word such as Acme yields A, while Blue Harbor yields BH. Add a space between words to get two letters.",
    },
    {
      question: "Can I search icons by what my business does?",
      answer: "Yes. The box matches both the icon name and its keyword tags, so cafe finds Coffee and salon finds Scissors. If nothing matches, the tool suggests switching to Monogram or Pattern style.",
    },
  ],
  "mockup-generator": [
    {
      question: "Why does my image get cropped in the phone mockup?",
      answer: "The Phone and Tablet frames crop your image to fill the screen area, since real device screens have a fixed shape. Drag to choose which part of your image stays in frame rather than accepting wherever it lands by default.",
    },
    {
      question: "Which frames are available?",
      answer: "Three: Browser Window, Phone and Tablet. The browser frame is a gray title bar with red, amber and green dots above your image, the phone has a dark rounded body with a notch, and the tablet is a dark rounded body with a plain bezel.",
    },
    {
      question: "Why can I not drag the image in the Browser Window frame?",
      answer: "That frame is sized to your picture, with its height following the image's proportions, so nothing is cropped and there is nothing to reposition. Drag, zoom and Fit apply only to Phone and Tablet.",
    },
    {
      question: "What does Fit whole image do?",
      answer: "Normally the picture is enlarged to cover the screen and the overflow is cropped. Fit whole image instead shrinks it until all of it is visible, filling any leftover space on the screen with white. Zoom still works on top of that.",
    },
    {
      question: "What resolution is the downloaded PNG?",
      answer: "Fixed by the frame: the browser file is 1200 pixels wide, the phone 600 by 1220 and the tablet 900 by 1200. The browser height depends on your image plus 44 pixels for the title bar. Small source images will look soft.",
    },
    {
      question: "What does Rotate do?",
      answer: "It turns the source picture by 90 degrees each press, cycling through 0, 90, 180 and 270, then fits it into the chosen frame. The frame itself never rotates, and pressing it resets any drag and zoom position.",
    },
  ],
  "social-media-formatter": [
    {
      question: "Does one size fit all the platforms?",
      answer: "No — the tool crops to fill each target size individually, since every platform expects a different aspect ratio. Use the focus point picker to control which part of your image stays in frame if your main subject isn't centered.",
    },
  ],
  "base64": [
    {
      question: "What is Base64 encoding actually used for?",
      answer: "It's a way to represent binary data as plain text, commonly used for data: URLs that embed small images in HTML/CSS, encoding data in URLs, or passing binary data through systems that only handle text safely. This tool converts text; it does not take image or other files.",
    },
  ],
  "binary-ascii-converter": [
    {
      question: "Does this handle any character, or just basic letters?",
      answer: "It converts standard ASCII characters (letters, numbers, common symbols) between their binary representation and readable text in either direction.",
    },
  ],
  "cron-builder": [
    {
      question: "Which part of a cron expression is most commonly mixed up?",
      answer: "Double-check the day-of-week field specifically before deploying a schedule — it's the field most commonly mixed up, since some systems count Sunday as 0 and others as 7.",
    },
    {
      question: "Which cron formats does it accept?",
      answer: "Standard five-field expressions, shortcut words that start with an at sign such as @daily, and expressions of six or seven fields, which trigger a warning because seconds or year fields follow platform-specific rules. Anything else is rejected with a message.",
    },
    {
      question: "Why am I told that day of month and day of week are both set?",
      answer: "In standard cron, when both fields hold something other than a star or question mark, a job runs when either matches. People often expect both to match. The amber note appears so you can double-check the intent.",
    },
    {
      question: "Which time zones can I choose?",
      answer: "Your browser's detected zone and UTC, nothing else. If your browser already reports UTC, only that one option is offered. The run times use your device clock as the starting point.",
    },
    {
      question: "What does This schedule never runs mean?",
      answer: "The expression is valid, but no real date can match it, such as the 31st of February. The tool finds no upcoming run times and shows that note, so you can fix the day or month field.",
    },
    {
      question: "Is Sunday 0 or 7 in the Day of week field?",
      answer: "The field hint reads 0-7 or SUN-SAT, so this tool accepts both 0 and 7 for Sunday, and names such as MON-FRI. Other systems may number days differently, so check the documentation of your scheduler.",
    },
  ],
  "css-js-minifier": [
    {
      question: "Does this go as far as a real build-tool minifier like Terser?",
      answer: "No, deliberately — the JavaScript minification here is conservative, avoiding aggressive changes like variable renaming and dead-code elimination that risk breaking working code. A build-tool minifier will go further, but this one prioritizes never corrupting your code over squeezing out the last few bytes.",
    },
    {
      question: "Which languages and file types does it handle?",
      answer: "CSS, JavaScript and HTML, chosen with the top buttons. Upload accepts .css, .js, .mjs, .html and .htm files, and picks the mode from the name; any other extension is treated as CSS, so switch the mode yourself if needed.",
    },
    {
      question: "What does the gzip number mean?",
      answer: "It is the byte size of the minified output after gzip compression, calculated in your browser, and it approximates what a server sending compressed text would transfer. It does not appear in browsers that lack compression support.",
    },
    {
      question: "Does JavaScript minification rename my variables?",
      answer: "Yes. The JavaScript mode compresses code and shortens local variable and parameter names, and removes dead code and all comments. Keep your original file, because the minified version is hard to read and has no source map.",
    },
    {
      question: "Why do I get an error with a line and column?",
      answer: "The parser could not read the input, for example an unclosed brace in CSS or an unexpected token in JavaScript. Fix the spot named in the red message; no output is produced until the code parses.",
    },
    {
      question: "What does HTML mode actually change?",
      answer: "It collapses whitespace, removes comments, drops redundant attributes and script and style type attributes, shortens the doctype and minifies inline CSS and JavaScript. Attribute quotes and optional tags are left alone, and conditional comments stay.",
    },
  ],
  "diff-checker": [
    {
      question: "Is this only useful for comparing code?",
      answer: "No — it's useful well beyond code. Comparing two drafts of a document, or two versions of a contract, works the same way and is often faster than manually reading both side by side.",
    },
    {
      question: "How do I compare two text files online?",
      answer: "Open each file in a text editor, copy its contents, and paste one version into the left panel and the other into the right. The differences are marked straight away: added lines, removed lines and changed lines, with the changed words highlighted inside each changed line.",
    },
    {
      question: "How does a diff checker work?",
      answer: "It lines up the two versions and finds the longest run of lines they have in common, then marks everything outside that run as added or removed. Lines that were reworded are then compared word by word, so you can see exactly which words were edited rather than just that the line differs.",
    },
    {
      question: "Can I see which words changed inside a line, not just which lines?",
      answer: "Yes. When a line was edited rather than replaced outright, the individual words that differ are highlighted within it, which makes small edits such as a changed number or a swapped name easy to spot.",
    },
    {
      question: "Is there a size limit when I compare text?",
      answer: "Very large inputs are capped at about 3,000 lines per side so the page stays responsive. If you hit the limit, compare the section you care about instead of the whole document.",
    },
  ],
  "http-status-reference": [
    {
      question: "How can I quickly tell what category a status code falls into?",
      answer: "The first digit tells you the general category at a glance — 2xx means success, 4xx means the client made a mistake, and 5xx means the server had a problem, even before you look up the specific code.",
    },
  ],
  "json-formatter": [
    {
      question: "What's the fastest way to debug a messy API response?",
      answer: "Paste it here first to format and validate it — turning a wall of minified text into properly indented, readable JSON is usually the fastest way to actually spot what's wrong with the data.",
    },
    {
      question: "Does it convert JSON to YAML and back?",
      answer: "Yes. The third button reads JSON to YAML for JSON input and YAML to JSON for YAML input. Pressing Format on YAML input also gives JSON. Indent and Sort keys apply to the conversion as well.",
    },
    {
      question: "What does Fix it for me repair?",
      answer: "Text-level problems only: comments, single-quoted strings, unquoted keys and trailing commas. It then re-checks the result, and if the text is still not valid JSON the button does not appear. It replaces the Input text with the repaired version.",
    },
    {
      question: "Will large numbers or values like 1.10 be changed?",
      answer: "Not when formatting or minifying JSON; they are kept exactly as typed. In a YAML conversion, large whole numbers are kept but decimals become ordinary numbers, so 1.10 turns into 1.1.",
    },
    {
      question: "How does it decide that my input is YAML?",
      answer: "Text starting with a bracket, brace or quote is treated as JSON. Otherwise it tries JSON first, then YAML, and calls it YAML only if the YAML parses into an object or list. The label above the box shows the result.",
    },
    {
      question: "What does Sort keys do exactly?",
      answer: "It orders object keys alphabetically at every nesting level in the output. The order of items inside arrays is not touched. It works with Format, Minify and the conversion button.",
    },
  ],
  "regex-tester": [
    {
      question: "What's the best way to build a complex regular expression?",
      answer: "Build it incrementally — start with a simple pattern that matches the basic case, confirm it works, then add complexity piece by piece rather than trying to write the whole expression at once.",
    },
    {
      question: "Which regular expression syntax does this tester use?",
      answer: "The JavaScript engine of the browser you are using. Features like lookahead, lookbehind in current browsers, named groups and the u flag follow JavaScript rules. Patterns written for Python, PCRE or Java may need small changes, so confirm them in their own environment before relying on them.",
    },
    {
      question: "Why do I only see one match when the text has several?",
      answer: "The Global (g) switch is probably off. With it off the tool stops after the first match on purpose. Turn Global on and the count, shading and list will include every match in the text, up to 1000.",
    },
    {
      question: "What does the message about 1.5 seconds mean?",
      answer: "The pattern ran too long and was cancelled so your tab would not lock up. This usually points to catastrophic backtracking, where nested or overlapping repeats like (a+)+ have to try a huge number of paths. Make the repeated parts less ambiguous and try again.",
    },
    {
      question: "The count says matches were found but nothing is shaded. Why?",
      answer: "Your pattern can match an empty string, for example a* on text without a. Those matches have no characters, so there is nothing to shade, yet they are counted and listed with their index. Require at least one character, such as a+, to see visible highlights.",
    },
    {
      question: "What does the Unicode (u) switch change?",
      answer: "It makes the engine read the text as full Unicode characters instead of 16-bit units, so an emoji counts as one character and property escapes like p{L} become available. It also makes the engine stricter, so a pattern with an unneeded escape can become an error.",
    },
  ],
  "timestamp-converter": [
    {
      question: "How do I know if my timestamp is in seconds or milliseconds?",
      answer: "A 13-digit number is typically milliseconds, while a 10-digit number is typically seconds — mixing them up gives a wildly wrong date, so it's worth checking the digit count before converting.",
    },
  ],
  "user-agent-parser": [
    {
      question: "Can I trust a user-agent string as a security check?",
      answer: "Not fully — user-agent strings are self-reported by the browser and can be changed or spoofed. Treat parsed results as generally reliable for analytics, but not as a security control to rely on.",
    },
    {
      question: "Why is the box already filled in?",
      answer: "On load, the tool reads the user-agent that your own browser reports and puts it in the box, so you immediately see a working example. Clear it to paste another, or press the button to bring yours back.",
    },
    {
      question: "Why can it not tell Windows 10 from Windows 11?",
      answer: "Both send the same user-agent text, so the string holds no difference to detect. The tool prints a note saying so whenever the OS card reads Windows.",
    },
    {
      question: "How does it decide that Bot / script is Yes?",
      answer: "It searches the string for words such as bot, crawler, spider and slurp, and for facebookexternalhit, curl/, wget/, python-requests and headlesschrome. A match gives Yes. A crawler that imitates a normal browser exactly will show No.",
    },
    {
      question: "Why does Device type say Desktop for an unknown device?",
      answer: "Desktop is the fallback label when the parser finds no mobile, tablet or other device category in the string. It means no device type was detected, not that a desktop was positively identified.",
    },
    {
      question: "Can it read a Client Hints header?",
      answer: "No. It expects a classic user-agent string, the text sent in the User-Agent header. Values from headers such as Sec-CH-UA are in a different format and will not parse meaningfully here.",
    },
  ],
  "caption-generator": [
    {
      question: "Should I use the generated caption exactly as written?",
      answer: "Treat it as a first draft, not a final post — the captions that perform best usually have a specific, personal detail that a generic starting point won't include on its own.",
    },
    {
      question: "How are the captions produced?",
      answer: "From templates written ahead of time. Your topic goes into a slot in one of three sentence frames for the chosen platform and tone, then your optional detail, an optional call to action and a few hashtags are added. Nothing is composed from scratch, so read and edit each draft before it goes out.",
    },
    {
      question: "What does Add a call to action add?",
      answer: "A short closing line that depends on the platform: Link in bio. for Instagram, Follow for more. for TikTok, and What do you think? for LinkedIn. X gets none, because its character limit is tight. Edit the line if it does not match what you are actually asking people to do.",
    },
    {
      question: "Where do the hashtags come from?",
      answer: "They are cut from the words in your topic, joined in CamelCase. Instagram gets up to 5, TikTok 4 with #fyp placed first, LinkedIn 3 and X 2. They are not checked against what people are searching for, so look at them inside the platform before you keep them.",
    },
    {
      question: "Why did my X caption lose its hashtags?",
      answer: "X drafts never include hashtags. X posts are limited to 280 characters and hashtags use up that room, so the tool leaves them out and keeps the text. Add one or two yourself if you want them. If the text alone is over 280, the character count line under the draft tells you to shorten the topic.",
    },
    {
      question: "Why do I get the same captions every time?",
      answer: "There is no randomness. The same topic, detail, platform, tone and call-to-action setting always give the same three drafts. To see different wording, change the tone or platform, or reword the topic and the detail line.",
    },
  ],
  "hashtag-generator": [
    {
      question: "Is it better to use more hashtags or fewer?",
      answer: "A smaller set of specific, relevant hashtags generally performs better than a large block of generic ones — quality and relevance matter more than sheer quantity on most platforms.",
    },
    {
      question: "Does it find trending hashtags?",
      answer: "No, it does not look anything up. Every tag is built from the words you typed, plus the endings Tips, Life, Daily, Community, Lovers, Ideas and Inspiration attached to your first key word. Check the tags inside the platform to see whether people are actually using them.",
    },
    {
      question: "Why are some of my words missing from the tags?",
      answer: "Common filler words such as the, and, of, for and your are removed first, so they do not become tags on their own. If your whole topic consists of such words, they are all kept so you still get a result.",
    },
    {
      question: "How are names like C++ handled?",
      answer: "The tool rewrites c++, c#, f# and .net as cplusplus, csharp, fsharp and dotnet before building tags, since symbols cannot appear in a hashtag. A plain plus sign elsewhere is dropped, and an ampersand becomes the word and, which is then treated as a filler word.",
    },
    {
      question: "Why do I only get a few tags?",
      answer: "Pair tags need at least two key words and the list is capped at 15. A one-word topic gives about eight tags and a two-word topic about ten. Add more descriptive words to the topic to unlock more combinations.",
    },
    {
      question: "Do words in other languages work?",
      answer: "Letters and digits from most scripts are kept, so accented and non-Latin words come through unchanged. The filler-word list is English only, so words in other languages are never skipped, and capital letters only show up in alphabets that have them.",
    },
  ],
  "keyword-density": [
    {
      question: "Is there an ideal keyword density percentage to aim for?",
      answer: "There's no single \"correct\" density — writing naturally for a human reader first, then checking this as a sanity check afterward, tends to produce better results than writing to hit a specific percentage target.",
    },
    {
      question: "What do the Density percentages mean?",
      answer: "Density is the number of times a word or phrase appears divided by the total word count of your text, multiplied by 100 and rounded to two decimals. A phrase used 5 times in a 500-word text shows 1.00%. The total includes common words even when they are hidden from the table.",
    },
    {
      question: "Why is a word I know is in my text missing from the table?",
      answer: "Three things can hide it: the common-word tick removes words like the and of, single-word mode drops Latin words shorter than three characters, and only the top twenty rows are shown. A word that appears once may also fall below the cut when many others appear more often.",
    },
    {
      question: "Does it treat capital letters as different words?",
      answer: "No. All text is lowercased before counting, so Apple, APPLE and apple are one entry. Accented letters are kept as written, so cafe and the accented spelling are separate words, and an apostrophe inside a word such as don't stays part of it.",
    },
    {
      question: "Can I use it for text that is not in English?",
      answer: "Yes for counting, since words are recognised from letters and digits in any script. The common-word filter only knows English, so untick Ignore common English words for other languages. A single character in a non-Latin script is kept because it can be a whole word.",
    },
    {
      question: "What density should I aim for?",
      answer: "The tool gives no target and does not color or grade the numbers. It only reports counts and percentages so you can spot overused words. Treat a high figure as a prompt to reread the passage and vary the wording, not as a score to push up or down.",
    },
  ],
  "meta-tag-gen": [
    {
      question: "How long should my meta description be?",
      answer: "Keep it under roughly 160 characters — search engines truncate longer descriptions in results, so anything past that length is effectively wasted.",
    },
    {
      question: "What tags does the generator output?",
      answer: "Charset and viewport, then whichever of these you filled in: title, description, keywords, author, robots, canonical link, Open Graph type, title, description, url and image, plus Twitter card, title, description and image tags.",
    },
    {
      question: "What does the noindex checkbox add?",
      answer: "Ticking it adds a robots meta tag with the value noindex, nofollow. That asks search engines not to list the page or follow its links. Leave it unticked for pages you want found.",
    },
    {
      question: "Why are title and description limited in length?",
      answer: "The title box stops at 60 characters and the description at 160, which roughly match what search results usually display before cutting text off. The counters let you see how much room remains.",
    },
    {
      question: "Do I need to fill in every field?",
      answer: "No. Any empty field is simply skipped in the output. At minimum a title and description are worth adding, plus a canonical URL if the same content could be reached at several addresses.",
    },
    {
      question: "What happens to quotes or ampersands in my text?",
      answer: "They are converted to safe HTML entities, for example an ampersand becomes &amp; and a double quote inside an attribute becomes &quot;. This keeps the tags valid even when your title contains punctuation.",
    },
  ],
  "og-preview": [
    {
      question: "Does this check my page's actual live meta tags automatically?",
      answer: "No — this previews metadata you enter here manually. If you're checking a page that's already live, pull its current title, description, and image from its own meta tags first, then enter them here to preview.",
    },
    {
      question: "Why did downloading my preview fail?",
      answer: "If you pasted an image URL rather than dragging in a file, downloading can occasionally fail, since most image hosts don't allow their images to be exported this way from another site. Drag-dropping the image file directly avoids that limitation entirely.",
    },
    {
      question: "Does the tool fetch my URL or only preview what I type?",
      answer: "Both. Typing in the fields only previews your values. The Read tags button in Check a live page sends the address to this site's server, which requests the page, reads its tags and sends them back to fill the fields. The page itself is not stored.",
    },
    {
      question: "Which tags does Read tags look at?",
      answer: "For the title it uses og:title, then twitter:title, then the page title tag. For the description it uses og:description, twitter:description, then the plain description tag. For the image it uses og:image, og:image:url or twitter:image. Relative image addresses are turned into full ones.",
    },
    {
      question: "Why did Read tags fail on my page?",
      answer: "Common causes are a page behind a login, a private or local address, a port other than 80 or 443, a site that blocks automated visitors, a page that answers with an error, a file that is not HTML, too many redirects, or more than twenty lookups in a minute.",
    },
    {
      question: "Why does downloading the preview sometimes fail with an image URL?",
      answer: "To turn the card into a picture, the browser must read the image pixels, and many image hosts do not allow that from another website. Dropping the image file onto the drop zone avoids the restriction because the file is already on your device.",
    },
    {
      question: "What is in the PDF?",
      answer: "Two pages, each sized to its card plus a header line reading OG / Social Preview, FreeDailyPro. The first page holds the Facebook and LinkedIn style card and the second holds the X card. Each card is embedded as a JPG image, not as live text.",
    },
  ],
  "robots-sitemap": [
    {
      question: "When should I generate these files?",
      answer: "Running this once before launching a new site is a five-minute technical SEO step that's easy to forget entirely if it's not part of a standard pre-launch checklist.",
    },
    {
      question: "What does Block the whole site (staging) do?",
      answer: "It writes a robots.txt with User-agent: * and Disallow: /, which asks all crawlers to stay out of everything, and it ignores your Disallow paths. Remove the block before launch, or real visitors may never find you in search.",
    },
    {
      question: "Which crawlers does the training-crawler checkbox cover?",
      answer: "It adds a Disallow: / block for each of GPTBot, ClaudeBot, CCBot, Google-Extended and PerplexityBot. The list is fixed, and compliance depends on each operator honoring robots.txt.",
    },
    {
      question: "Why were some of my pages skipped?",
      answer: "The note under the sitemap tab lists them. A full address on a different host or scheme than your Domain is skipped, as is text that cannot form a valid address. Duplicates are merged silently.",
    },
    {
      question: "Do I have to type the domain as https://example.com?",
      answer: "No. If you leave out the scheme, https:// is added. A port stays, a path is dropped, and the host must contain a dot or be localhost, otherwise a red message asks for a valid website address.",
    },
    {
      question: "Does robots.txt stop a page from being indexed?",
      answer: "Not reliably. It controls crawling, not indexing, and a blocked URL can still be listed if other pages link to it. To keep a page out of results, use a noindex directive on a crawlable page or password protection.",
    },
  ],
  "schema-generator": [
    {
      question: "Which schema type is the highest-leverage one to add?",
      answer: "FAQ schema in particular can make a page eligible for expanded search result listings — if a page already has a natural FAQ section, adding matching schema markup is one of the higher-leverage quick wins here.",
    },
    {
      question: "Which schema types can I generate?",
      answer: "Five: Article, Product, FAQ Page, Local Business and Recipe. Each has its own fields. Article offers an author type of Person or Organization, and FAQ Page lets you add or remove question and answer rows.",
    },
    {
      question: "Why is Copy as Script Tag greyed out?",
      answer: "A required field for the chosen type is empty, and an amber message says so. Fill in the headline and author, product name and price, one full question and answer, business name and address, or recipe name, ingredients and instructions.",
    },
    {
      question: "What choices does the Product type give for availability and ratings?",
      answer: "Availability can be In Stock, Out of Stock or Pre-Order. The rating block is added only when both Rating and Review Count have values; clear either one to leave it out, which you should do if you have no genuine reviews.",
    },
    {
      question: "How is Recipe totalTime worked out?",
      answer: "The tool adds your Prep Time and Cook Time. It reads durations written as hours and minutes after PT, such as PT1H30M, and writes the sum in the same style. A value in any other format counts as zero minutes.",
    },
    {
      question: "Where do I put the script tag, and will it give me rich results?",
      answer: "Paste it into the HTML of the page the data describes, in the head or body. Whether a search engine shows an enhanced listing is its own decision; check the live page with a rich results or schema testing tool.",
    },
  ],
  "json-csv-converter": [
    { question: "How are nested JSON objects handled?", answer: "They are flattened into dotted columns, so {\"address\":{\"city\":\"Austin\"}} becomes a column called address.city. Going back from CSV to JSON, you can rebuild that nesting." },
    { question: "What JSON shapes work?", answer: "A list of objects works best. An object that contains one list of records, such as an API response with an items list, is also detected. A single object becomes one row." },
    { question: "What happens to lists inside a record?", answer: "A list of simple values is joined with semicolons in one cell. A list of objects is kept as JSON text in the cell." },
    { question: "Will leading zeros in IDs survive?", answer: "Yes. When converting CSV to JSON, only plain numbers become numbers. Values like 007 or long digit strings stay as text." },
    { question: "Is my data uploaded?", answer: "No. The conversion runs in your browser and nothing is sent or stored." },
  ],
  "csv-converter": [
    {
      question: "Will my Excel formatting and formulas survive the conversion to CSV?",
      answer: "No — CSV files don't preserve formatting, formulas, or multiple sheets, since CSV is deliberately a plain, structure-only format. If your Excel file has more than one sheet, only the first sheet is converted.",
    },
    {
      question: "Which file types can I open?",
      answer: "CSV, TSV and text files, plus Excel and OpenDocument spreadsheets: .xlsx, .xls, .xlsm and .ods. Any file that cannot be read produces an error message and returns you to the drop box.",
    },
    {
      question: "How does it know the delimiter and encoding?",
      answer: "For text files, it tests comma, semicolon, tab and pipe on the first lines and picks the one giving the most consistent column count. A .tsv name forces tabs. Encoding is detected as UTF-8, UTF-16 or Windows-1252, and both are shown on screen.",
    },
    {
      question: "What does the BOM option do?",
      answer: "It writes an invisible marker at the start of the CSV that tells Excel the file is UTF-8. Without it, Excel on Windows may misread accented letters. It is on by default and can be switched off for tools that dislike it.",
    },
    {
      question: "Will numbers stay as numbers in the Excel file?",
      answer: "Plain numbers become real numeric cells so you can sum them. Values with leading zeros, long digit strings of more than 15 characters, and anything non-numeric stay as text, so codes and IDs are preserved exactly.",
    },
    {
      question: "What happens with a workbook that has several sheets?",
      answer: "A Sheet menu lists the visible ones, and you convert one at a time. The CSV file name then includes the sheet name when the workbook has more than one. Switching sheets reloads the preview.",
    },
  ],
  "file-size-converter": [
    {
      question: "Why does my drive show less storage than what's printed on the box?",
      answer: "Storage manufacturers and operating systems sometimes use slightly different definitions of a gigabyte (1000³ bytes versus 1024³ bytes) — this discrepancy is exactly why a drive can show less capacity in your OS than advertised.",
    },
    {
      question: "Why is a 500 GB drive shown as about 465 GB?",
      answer: "Drive makers count 1 GB as 1,000,000,000 bytes. Windows divides the same bytes by 1,073,741,824 but still prints GB, so 500 GB appears as about 465.66. No space is lost; macOS counts in 1000s and shows 500 GB.",
    },
    {
      question: "What is the difference between GB and GiB?",
      answer: "GB means 1,000,000,000 bytes and GiB means 1,073,741,824 bytes, which is 1024 cubed. The same pattern holds for KB and KiB, MB and MiB, TB and TiB, and PB and PiB, with each binary step about 2.4 percent larger than its decimal twin per level of the ladder.",
    },
    {
      question: "Which should I use for an upload limit?",
      answer: "It depends on who set the limit. Many services say MB and mean 1,000,000 bytes, while some tools mean 1,048,576. Enter your size both ways, compare the B rows, and when a limit is close to your file size assume the smaller value to be safe.",
    },
    {
      question: "Does it convert bits or megabits?",
      answer: "No, only bytes and their multiples appear. For a connection speed in megabits per second, divide by 8 to get megabytes per second, then enter that result. Lowercase b stands for bits and capital B for bytes.",
    },
    {
      question: "Why do very small numbers show in a strange form?",
      answer: "Any non-zero result below 0.0001 switches to exponent notation, such as 1.000e-7, so tiny values are not rounded down to zero. Everything else shows up to four decimal places with thousands separators in your browser's locale.",
    },
  ],
  "meme-generator": [
    {
      question: "Does this work with video or GIF files?",
      answer: "Not currently — this works with static images (JPG, PNG, etc.). Adding text to a moving video or GIF is a genuinely different, bigger undertaking and isn't available here yet.",
    },
    {
      question: "Does the meme generator include template images?",
      answer: "No. There are no bundled or downloaded templates, so you upload the picture you want to caption. That could be a photo you took, a screenshot or an image you have the right to use. Only the lettering font is fetched from outside.",
    },
    {
      question: "Which font does the text use?",
      answer: "Anton, loaded from Google Fonts when the page opens, because the classic Impact face is missing on most phones. If Anton cannot load, the canvas falls back to Impact, Arial Black or a plain sans-serif font, so the look may differ slightly.",
    },
    {
      question: "Can I change the text colour or move the text?",
      answer: "No. The lettering is always white with a black outline, centred, with the top text hugging the upper edge and the bottom text the lower edge. The only adjustments are the words themselves, which shrink and wrap automatically to fit.",
    },
    {
      question: "What file do I get and how big is it?",
      answer: "A PNG called meme.png, drawn at the same width and height as the image you uploaded. A large photo therefore gives a large file. If you need a smaller one, shrink the picture with a resize tool before adding text.",
    },
    {
      question: "Can I make a meme from a GIF or a video?",
      answer: "Not as a moving file. The tool draws your upload onto a single still canvas, so you receive a still PNG. A video or animated GIF caption needs a video editor, which can keep the motion.",
    },
  ],
  "ocr-tool": [
    {
      question: "Can this extract text from a scanned PDF?",
      answer: "This tool reads images specifically, not PDFs — for a scanned PDF, use the PDF OCR tool instead, which handles multi-page documents properly.",
    },
    {
      question: "Why does the first run need an internet connection?",
      answer: "The recognition engine downloads the language data for your chosen language when it is first needed. If that download cannot happen, the tool tells you it could not start and asks you to check your connection. The image itself stays on your device throughout.",
    },
    {
      question: "Can I use it on a PDF?",
      answer: "No. This tool accepts image files only, and a dropped PDF is turned away with a message pointing to OCR PDF. Use that tool for scanned documents, or export a single page of the PDF as an image and drop that here.",
    },
    {
      question: "What does the low confidence warning mean?",
      answer: "If the engine's overall confidence is under 70 percent, a note shows the figure and suggests a clearer image or the right language. It is an average for the whole picture, so some lines may still be fine, but check the text before you rely on it.",
    },
    {
      question: "What does Keep original line breaks do?",
      answer: "By default the tool joins lines into paragraphs and splits at list items or larger gaps. Ticking the box swaps in the raw reading, which keeps each line where it appeared. That works better for addresses, menus, short lists and table rows.",
    },
    {
      question: "My phone photo is sideways. Will it still work?",
      answer: "Usually. Before reading, the tool applies the orientation stored in the photo, and if the first result is weak it also tries 90, 180 and 270 degree turns and keeps the best one. A photo with no usable orientation data and very little text can still fail.",
    },
  ],
  "yt-thumbnail": [
    {
      question: "Will I always get the highest resolution thumbnail?",
      answer: "Not always — YouTube generates thumbnails at several resolutions, and the highest-resolution version isn't available for every video. Check what's actually offered before assuming you'll get the largest size.",
    },
    {
      question: "How does the tool get the thumbnail?",
      answer: "It finds the 11-character video ID in your link, then asks YouTube's image server at img.youtube.com for the standard cover files of that ID. Those are ordinary image requests made by your browser. The tool does not log in to anything or read the video itself.",
    },
    {
      question: "Why is a size missing from the list?",
      answer: "YouTube does not create every size for every video. The 1280x720 version exists mainly for HD uploads. When a size is missing, YouTube returns a tiny grey placeholder or an error, which the tool detects. That size is then left out of the list (a line says which), out of the large preview and out of the ZIP, leaving the sizes that are real.",
    },
    {
      question: "Can I get every size in one file?",
      answer: "Yes. Download all sizes (ZIP) bundles each size that exists for the video into one ZIP, built in your browser. It counts as one download. If your browser refuses to let the page read the images, no ZIP is made and the message says so; the Download button on each size still works.",
    },
    {
      question: "Can I use a thumbnail I download here however I like?",
      answer: "Not necessarily. The cover belongs to the creator or channel that published the video, and this tool does not change that. Using your own covers is fine. For anyone else's, check permission or the rules that apply to your use, such as quotation in commentary.",
    },
    {
      question: "Why did my browser open a new tab instead of downloading?",
      answer: "Browsers sometimes refuse to let a page read an image served from another site. When that happens the tool opens the picture in a new tab and tells you to right-click it and choose Save image, which gives the same file.",
    },
    {
      question: "Does it work with Shorts links and a bare video ID?",
      answer: "Yes to both. Shorts, youtu.be, watch, embed and live links all work, and so does pasting only the 11 characters that follow v= in a watch link. Anything else, such as a channel page, shows a message asking for a video address.",
    },
  ],
  "zip-unzip": [
    {
      question: "Will zipping already-compressed files like JPGs save much space?",
      answer: "Typically very little — compressing already-compressed file types (like JPGs or MP4s) into a ZIP saves almost nothing, since they're already tightly compressed. ZIP is much more effective on text-heavy or uncompressed files.",
    },
    {
      question: "Can I create and extract in one go?",
      answer: "No. Once a .zip file is in the list, Create ZIP is switched off and only Extract ZIP is available. To build a new archive instead, remove the .zip from the list first, then add the files you want packed.",
    },
    {
      question: "Are folders kept when I extract?",
      answer: "No. Each extracted entry is saved under its file name only, with folder parts and dot-dot segments removed. Entries that end up with the same name are numbered, so two files called notes.txt become notes.txt and notes (2).txt.",
    },
    {
      question: "How much smaller will my ZIP be?",
      answer: "That depends on the content. Text, CSV and uncompressed images shrink a lot. Photos in JPG, MP4 videos and files already in archives shrink very little. The message after creation shows the exact before and after sizes.",
    },
    {
      question: "What happens when two files have the same name?",
      answer: "The tool numbers the later ones, so photo.jpg, photo.jpg and photo.jpg become photo.jpg, photo (2).jpg and photo (3).jpg. The comparison ignores letter case, which matches how most unzip programs treat names.",
    },
    {
      question: "Can it open a password-protected ZIP?",
      answer: "No. The tool has no way to enter a password, and extraction of an encrypted archive fails with a Failed to extract ZIP message. Use the program that created the archive, or ask the sender for an unprotected copy.",
    },
  ],
  "barcode-generator": [
    {
      question: "Why does barcode generation sometimes fail?",
      answer: "Each format has its own rules for valid input — EAN-13 needs exactly 12-13 digits, UPC-A needs 11-12, Code 128 accepts almost any text. If generation fails, that's almost always why, and the tool tells you what went wrong rather than silently producing something that won't scan.",
    },
    {
      question: "Which barcode formats can I choose?",
      answer: "Six: Code 128 for letters, numbers and symbols without accents; EAN-13 for 13-digit retail; EAN-8 for 8-digit retail; UPC-A for 12-digit retail; Code 39 for letters and digits in factory and warehouse use; and ITF-14 for 14-digit shipping and case labels.",
    },
    {
      question: "Why does it complain about the number of digits or the check digit?",
      answer: "Numeric formats accept either the body without the check digit (12 for EAN-13, 7 for EAN-8, 11 for UPC-A, 13 for ITF-14) or the full number. If you type the full number and the last digit is wrong, the message tells you the correct one.",
    },
    {
      question: "Will the caption appear in the SVG file?",
      answer: "No. The caption is drawn only into the PNG, in a strip above the bars. The SVG download contains the barcode and the human-readable digits underneath it, so add any label text in your layout software.",
    },
    {
      question: "Are these real, scannable barcodes?",
      answer: "Yes. They are encoded with the genuine symbology tables and check-digit rules for each format, not a decorative pattern. Whether a shop's system accepts the number is a separate matter, since retail products need numbers allocated under a GS1 prefix.",
    },
    {
      question: "What do the PNG sizes mean?",
      answer: "The size menu scales the whole image: 1x is the small preview size, 2x doubles it and 4x quadruples it, labeled for print. The picture is redrawn at the larger scale rather than enlarged afterward, so the bars stay sharp.",
    },
    {
      question: "Is this barcode valid for Amazon or shops (GS1)?",
      answer: "The bars are real and scan. Whether a shop or marketplace accepts the number is a separate matter: EAN-13 and UPC-A numbers for products you sell are issued under a GS1 company prefix, and marketplaces such as Amazon generally ask for numbers registered to your company. A number made here is fine for internal use, samples and testing, but it does not make a number yours. There is no way to get a GS1 number from this page.",
    },
    {
      question: "Is the check digit added?",
      answer: "Yes, for EAN-13, EAN-8, UPC-A and ITF-14. Type the digits without the last one (12 for EAN-13, for example) and the check digit is added, so 590123412345 becomes 5901234123457. If you type the full number and the last digit is wrong, you get an error with the correct digit; the number is never silently changed.",
    },
    {
      question: "What size should I print?",
      answer: "Choose PNG size 4x for print: it is saved at 300 dpi, and the page states the printed width at 100% (an EAN-13 comes out about 3.1 inches, 7.9 cm, wide). Do not shrink it much below that, because retail scanners need the bars wide enough. Print one sample and scan it with a phone before printing a full sheet.",
    },
    {
      question: "PNG or SVG?",
      answer: "Use SVG when the barcode goes into a layout or label program, because it scales to any size without soft edges. Use the 4x PNG when you want a ready picture for a document or a label printer. Both come from the same value and the same settings.",
    },
    {
      question: "Can I make many barcodes at once?",
      answer: "Yes. Choose A list (ZIP), paste one value per line and download one ZIP with a PNG or SVG file per value, named by the value, plus an index.csv. Up to 200 are made at a time. A line that cannot be encoded, such as one with a wrong check digit, is listed with the reason and left out of the ZIP.",
    },
    {
      question: "Can I hide the numbers under the bars or change the bar height?",
      answer: "Yes. Untick Show the number under the bars to leave the digits off both the PNG and the SVG, and choose Short, Standard or Tall bars. Both apply to the single barcode and to the ZIP.",
    },
  ],
  "bulk-qr": [
    {
      question: "How do I keep track of which QR code links to what once I have a batch?",
      answer: "Every download is one ZIP with numbered files (001-, 002-, ...) in the order of your list, plus an index.csv that lists each file with its value. If you tick \"Lines are name,value\", your own names are used in the file names too.",
    },
    {
      question: "How do I name the files myself?",
      answer: "Tick Lines are name,value and write each line as name,value, such as Shop,https://example.com/shop. The text before the first comma is used in the file name, and the rest becomes the code. Without the box ticked, names come from the value itself.",
    },
    {
      question: "What is in the ZIP I download?",
      answer: "One image per entry, numbered in list order with at least three digits, followed by a short name taken from the value, in PNG or SVG as chosen. There is also index.csv with the columns file, name and value, to match each image to its content.",
    },
    {
      question: "What is the maximum number of codes per batch?",
      answer: "500. If your list is longer, the page shows a warning and generates only the first 500; paste the remaining lines in as a second batch. Blank lines are ignored and exact duplicate entries are counted once, so they do not use up that allowance.",
    },
    {
      question: "Which error correction level should I pick?",
      answer: "M, at 15 percent, is the starting choice and suits most screens and prints. L gives the simplest pattern. Q and H tolerate more damage, dirt or folding, at 25 and 30 percent, but make the pattern busier and therefore harder to scan when printed very small.",
    },
    {
      question: "Should I choose PNG or SVG?",
      answer: "SVG is a vector, so it stays sharp at any print size and is the usual pick for posters and labels. PNG suits slides, documents and web pages, in 300, 600, 1000 or 2000 pixel squares. The PNG size menu is disabled when SVG is selected.",
    },
  ],
  "custom-qr": [
    {
      question: "Will adding a logo or colors affect whether the QR code scans reliably?",
      answer: "It can if contrast is too low — keep contrast high between the QR code's foreground and background colors. An eye-catching design that's too low-contrast may fail to scan reliably, especially in low light.",
    },
    {
      question: "What exactly does the code contain?",
      answer: "It contains the exact text in the Content box and nothing else. A web address opens in the phone's browser, and ordinary text is shown as text. Nothing is routed through a redirect on this site, so the code keeps working for as long as its destination does.",
    },
    {
      question: "Why am I seeing a contrast warning?",
      answer: "The tool compares your code colour with the background. It warns when the code is lighter than the background, because many scanners cannot read inverted codes, and when the contrast ratio falls below about 4 to 1. With a gradient, both gradient colours are checked.",
    },
    {
      question: "Does adding a logo make the code harder to scan?",
      answer: "It makes the code busier, so the tool raises error correction from its medium level to the highest one, clears the dots behind the logo and limits the logo to about a quarter of the width. A scan test on a real phone is still the only reliable check.",
    },
    {
      question: "Should I download PNG or SVG, and which size?",
      answer: "PNG is a fixed grid of pixels at 300 up to 2000 px, good for screens and documents. SVG is vector artwork that stays sharp at any scale, which suits signs and print. For a PNG headed to print, choose the largest size offered.",
    },
    {
      question: "How does the gradient colour the corner squares?",
      answer: "The gradient runs diagonally across the dots only. The three corner squares keep the start colour, and the small centre dots inside them take the end colour. That is why both colours need to be dark enough against the background.",
    },
  ],
  "qr-generator": [
    {
      question: "Should I test my QR code before printing it?",
      answer: "Yes — test it with your own phone's camera before using it anywhere public or printed. It takes a few seconds and catches any typos in the underlying URL before they become permanent on a printed flyer.",
    },
  ],
  "qr-scanner": [
    {
      question: "Does live camera scanning work in every browser?",
      answer: "Yes — scanning runs through a JavaScript decoder rather than relying on any one browser's native barcode detection, so it works the same way in Chrome, Safari, Firefox, and Edge, on both desktop and mobile.",
    },
    {
      question: "The scanned result looks like a link — can I open it directly?",
      answer: "Yes — if the decoded content is a real link (a web address, email, phone number, or text message link), it's shown as a clickable link that opens in a new tab, not just plain text.",
    },
    {
      question: "Can I save or share what was scanned?",
      answer: "Yes — Copy puts it on your clipboard, Download .txt saves it as a text file, and Share opens your device's share sheet on mobile (or copies it, on browsers without a share sheet).",
    },
    {
      question: "What do I do if the camera says access was denied?",
      answer: "Browsers ask only once. Click the lock or info icon in the address bar, set Camera to Allow, reload, and try again. In an installed app, check the app's permissions in your phone settings. You can always use the upload box instead.",
    },
    {
      question: "Which camera does it use?",
      answer: "It first asks for the rear camera, which suits phones. If the device has only a front camera, as most laptops do, the tool retries with whatever camera is available. A separate message appears if another app or tab is already using the camera.",
    },
    {
      question: "What kinds of codes can it read?",
      answer: "It reads QR codes, Data Matrix, Aztec and PDF417, plus the common one-dimensional barcodes: EAN-13, EAN-8, UPC-A, UPC-E, Code 39, Code 93, Code 128, ITF (Interleaved 2 of 5) and GS1 DataBar (RSS-14). Codabar, GS1 DataBar Expanded and MaxiCode are not read. The card shows the detected format, for example QR code or EAN-13.",
    },
    {
      question: "What happens when the code holds a Wi-Fi login or a contact card?",
      answer: "A Wi-Fi code is shown as network name, security type and password. A contact card is shown as name, company, phones, emails and website, with a button that downloads it as a .vcf file you can import into your address book.",
    },
    {
      question: "Why does it warn me about some links?",
      answer: "Two cases are flagged: addresses that start with http instead of https, which are not encrypted, and links from a built-in list of shorteners such as bit.ly, where the final destination is hidden. The warning is advice only; the link still works if you click it.",
    },
  ],
  "grocery-list-planner": [
    {
      question: "When should I add items to my grocery list?",
      answer: "Add items throughout the week as you notice you're running low on something, rather than trying to remember everything in one sitting right before you shop.",
    },
  ],
  "inventory-tracker": [
    {
      question: "How should I set my low-stock threshold?",
      answer: "Base it on how often you actually shop, not an arbitrary round number — if you shop weekly, a threshold that gives you at least a week's buffer avoids running out between trips.",
    },
    {
      question: "When does an item show as low stock?",
      answer: "When the number in Qty is less than or equal to the number in Low Stock At. With a threshold of 2, an item with exactly 2 left is flagged, and so is one with 1 or 0. Empty or non-numeric boxes are treated as 0.",
    },
    {
      question: "Will my list still be here tomorrow?",
      answer: "Only while the tab is open. Edits are kept for the visit and erased when you close the tab; nothing is synced or stored online. Use Back up to save a file and Restore to continue later or on another device.",
    },
    {
      question: "How do I get the list into Excel or Google Sheets?",
      answer: "Press Copy as CSV, then paste into a blank cell. The columns arrive as Item, Category, Location, Quantity and Low Stock At. Text that begins with =, + or @ gets a leading apostrophe so a spreadsheet will not run it as a formula.",
    },
    {
      question: "Can I import a spreadsheet I already have?",
      answer: "Not directly. Restore accepts only the JSON backup this tool wrote itself. For an existing sheet, press Add Item for each row and type or paste the values into each box, one field at a time.",
    },
    {
      question: "Can I keep track of several rooms or shelves?",
      answer: "Lightly, yes. Use the Location box for the room or shelf and Category for the kind of item. There is only one list and no tabs or filters, so you find things by scanning the Location column rather than switching views.",
    },
  ],
  "monthly-budget-planner": [
    {
      question: "Should I set my budget categories once and leave them?",
      answer: "Revisit and adjust the categories every few months rather than setting them once — spending patterns shift, and a category that made sense six months ago sometimes doesn't reflect how money is actually being spent now.",
    },
  ],
  "shopping-list-planner": [
    {
      question: "Should I enter exact prices or estimates when planning?",
      answer: "Add prices as estimates when you first create the list, then update them with actual prices as you shop — comparing the estimate against the real cost is a quick way to spot where a budget assumption was off.",
    },
  ],
  "citation-generator": [
    {
      question: "How do I create a citation?",
      answer: "Pick a style (APA, MLA, Chicago or Harvard), choose the source type and fill in author, title, year and other fields. The formatted citation appears as you type and can be copied or added to your reference list.",
    },
    {
      question: "Does this guarantee my citations are completely accurate?",
      answer: "It handles the structural formatting reliably, but the accuracy of the underlying details (author names, dates, publishers) is still on the person entering them. Double-check formatting and capitalization against the original source before finalizing.",
    },
    {
      question: "Which styles does it support?",
      answer: "Four: APA 7th edition, MLA 9th edition, Chicago 17th edition in Notes-Bibliography form (the bibliography entry, not footnotes), and Harvard. Each can be used for a website, a book or a journal article, which are the only three source types offered.",
    },
    {
      question: "Can I rely on the result without checking it?",
      answer: "No. Check every citation against your institution's, course's or publisher's own style guide before you submit. Instructors and journals often add local rules, and Harvard in particular differs between universities. The tool formats what you type and cannot confirm that your details are correct.",
    },
    {
      question: "What if there is no author or no year?",
      answer: "Leave the author boxes empty and the title moves to the front of the entry, and a blank Year prints n.d. in APA and Harvard, or is left out in MLA and Chicago. Check whether your style guide wants a different handling.",
    },
    {
      question: "Does it look up a source from a URL or a DOI?",
      answer: "No. It never visits the address and cannot find missing details. Every field is typed by you, and the URL is only printed in the citation as you entered it, with the protocol removed in MLA.",
    },
    {
      question: "In what order does the reference list appear?",
      answer: "Alphabetical by the start of each entry, which is the author's last name or the title when there is no author, ignoring an opening quotation mark. The order does not depend on when you added each entry, and Copy Full List follows the same order.",
    },
    {
      question: "Is my reference list stored online?",
      answer: "No. Everything stays in your browser.",
    },
  ],
  "college-529-calculator": [
    {
      question: "Does this account for my specific state's 529 plan tax benefits?",
      answer: "No — this is a general savings projection, not specific to any one state's 529 plan. Tax benefits and investment options vary by state, so check your specific plan's terms for the tax-advantage details.",
    },
    {
      question: "How much should I save per month for college?",
      answer: "It depends on the target, the years left and the return you assume. Enter the amount you expect college to cost by the time it is needed, what you have saved so far, and how many years remain, and the calculator returns the fixed monthly contribution that reaches the goal. Starting earlier lowers the monthly figure because contributions have longer to grow.",
    },
    {
      question: "What is a 529 plan?",
      answer: "A 529 plan is a US savings account designed for education costs, with tax advantages that vary by state and by plan. This calculator does not apply any state's rules. It projects how a steady monthly contribution grows toward your goal, so check your plan's documents for tax treatment and allowed uses.",
    },
    {
      question: "What rate of return should I use in a 529 savings calculator?",
      answer: "Use a cautious figure you could live with, and try a few. The projection assumes the same return every year, while real returns rise and fall. Running the numbers at a lower rate shows how much extra you would need to save if the market underperforms.",
    },
    {
      question: "Can I use this if I already have money saved?",
      answer: "Yes. Enter your current balance and it counts that balance and its growth toward the goal, so the monthly contribution it shows covers only the gap that remains.",
    },
    {
      question: "What does the first contribution date do in the 529 calculator?",
      answer: "It is optional. Enter the date of your first monthly contribution and the result shows your last contribution and the date your goal is reached. Leave it empty and you see only the monthly amount, as before.",
    },
    {
      question: "Does the first contribution date change the monthly amount needed?",
      answer: "No. The date only labels the plan. The monthly amount is worked out from the goal, your savings, the number of years and the return, so it is the same for any date. A start day of the 29th to 31st moves to the last day of shorter months in the dates shown.",
    },
  ],
  "college-cost-calculator": [
    {
      question: "Should I use national average costs, or my specific school's numbers?",
      answer: "Check your specific target school's published cost of attendance rather than relying on national averages — tuition and fees vary enormously between institutions, even within the same state.",
    },
    {
      question: "What does the first year starts date do in the College Cost Calculator?",
      answer: "It is optional. Enter the date the first year begins, such as the start of the term, and each year in the list shows the months it covers, with the start and end of the whole program above. Leave it empty and the years are shown as Year 1, Year 2 and so on, as before.",
    },
    {
      question: "Does the start date change the cost?",
      answer: "No. The date only labels the years. Each year's cost still comes from the costs you enter and the annual increase, so the total is the same for any date. Years are counted as 12 months from the date you choose, which may not match your school's own term dates.",
    },
    {
      question: "How much will I need to borrow?",
      answer: "Enter the family contribution and work-study you can put in each year, and the calculator subtracts them from each year's cost after aid. What is left each year is the amount to borrow. It then adds the interest that builds up while you are in school to give the debt at graduation, and works out the monthly payment for the loan rate and repayment term you choose. The loan rate starts at the federal undergraduate rate for 2025-26; change it if your loan differs.",
    },
    {
      question: "How do I compare two colleges?",
      answer: "Choose Compare another school and enter the other school's total Year 1 cost and yearly aid. You can compare up to four schools. They all use the same number of years and the same yearly increase, and a table shows each school's net total, the amount to borrow, the debt at graduation and the monthly payment.",
    },
  ],
  "paper-format-checker": [
    {
      question: "Does this catch formatting I applied to individual paragraphs?",
      answer: "No — this checks your document's default formatting (margins and the Normal style's font, size, and spacing). If you've manually formatted individual paragraphs differently, those won't be caught, so a final visual check is still worth doing.",
    },
  ],
  "student-loan-calculator": [
    {
      question: "Does this model income-driven repayment plans?",
      answer: "No — this models standard fixed repayment. Federal income-driven repayment plans use a completely different, income-based formula, so check studentaid.gov directly if you're on or considering one of those plans.",
    },
    {
      question: "What should I enter as the first payment date?",
      answer: "Enter the date your first payment is due, which your servicer shows on your account. For many federal loans it comes after a grace period, often six months after you leave school. If you do not know it, leave the suggested date, the 1st of next month. The table, the payoff date and the CSV then show real calendar dates from it.",
    },
    {
      question: "Does the first payment date change my payment or total interest?",
      answer: "No. The date only labels the schedule. The payment and the interest are worked out on whole monthly periods, so they stay the same for any date. Interest that builds up before your first payment is not added by this calculator, so enter the larger balance you will owe at that point if it applies to you.",
    },
    {
      question: "What happens if my payment date is the 31st?",
      answer: "A payment on the 29th, 30th or 31st moves to the last day of shorter months, such as Feb 28 (Feb 29 in a leap year) or Apr 30, and goes back to your day in longer months, so the dates never drift earlier. The payment amount does not change.",
    },
  ],
  "text-similarity-checker": [
    {
      question: "Is this the same as my school's plagiarism-detection software?",
      answer: "No — this is a text comparison tool for two texts you provide directly, not a web-search plagiarism checker, since it has no connection to the internet or any academic database. It's not a substitute for your school's actual plagiarism-detection tool.",
    },
    {
      question: "What is a text similarity checker?",
      answer: "A text similarity checker compares two pieces of text and reports how much wording they share. This one takes the two texts you paste in, highlights the phrases that appear in both, and shows a match percentage. It is useful for comparing a draft with a source you are citing, or an old version with a new one.",
    },
    {
      question: "How is the match percentage calculated?",
      answer: "The tool breaks both texts into overlapping runs of six words and looks for runs that appear in both. The percentage is the share of words in your first text that fall inside one of those shared runs, so copied phrases raise it and a few common words do not.",
    },
    {
      question: "Can I compare two documents written in another language?",
      answer: "Yes. Letters and numbers in any script are recognised, and Chinese and Japanese text is compared character by character because those languages do not put spaces between words.",
    },
    {
      question: "Does a high percentage mean the text was copied?",
      answer: "Not necessarily. Quotations, citations, standard phrases and shared terminology all count as matches. Read the highlighted passages to judge whether the overlap is quoted and cited or needs rewording.",
    },
  ],
  "break-even-calculator": [
    {
      question: "When should I run this, before or after setting my price?",
      answer: "Run it before finalizing a price, not after — it's much easier to adjust a price on paper before launch than to explain a price change to customers once you're already selling.",
    },
  ],
  "marketplace-fee-calculator": [
    {
      question: "Are marketplace fees the same for every product category?",
      answer: "No — check your marketplace's current published fee schedule for your specific product category before relying on the default starting percentage. Fees frequently vary by category, not just by platform.",
    },
    {
      question: "How do I evaluate marketplace fees?",
      answer: "Put the same price, cost and shipping into each marketplace and compare what is left. Use Compare marketplaces to see 2 to 4 of them side by side, each fee on its own line, with the payout, profit, margin, ROI and the break-even price. Then check the rates against your own account, because fees depend on category and seller plan.",
    },
    {
      question: "What are Etsy's fees in 2026?",
      answer: "For a US seller the tool uses a $0.20 listing fee, a 6.5% transaction fee on the item price plus shipping, and 3% + $0.25 payment processing on the order total. Offsite Ads add 15% (12% once a shop has $10,000 or more in sales over 365 days) only on orders that came from an ad click, capped at $100 per order. These were read from Etsy's own pages on 1 November 2026; Etsy can change them.",
    },
    {
      question: "What does eBay take per sale?",
      answer: "For most categories about 13.6% of the item price plus shipping and sales tax (2.35% on the part above $7,500), plus $0.40 per order, or $0.30 on orders of $10 or less. The tool's eBay figures were read from ebay.com on 30 October 2026. Some categories are lower or higher, so edit the percentage for yours.",
    },
    {
      question: "What price do I need for a 30% margin?",
      answer: "Enter your cost, shipping and fees, then set the target margin to 30 in the box under the results. The tool solves for the selling price that gives that margin on the revenue you keep after returns, and the Use this price button copies it into the price box.",
    },
    {
      question: "How is ROI worked out here?",
      answer: "ROI is the profit if the order is kept divided by what you put in per order: your cost, your own shipping and any ad cost (including an Etsy Offsite Ads fee). A $12 item that costs $5 to ship and leaves $26 of profit has an ROI of about 153%.",
    },
    {
      question: "Can I share my numbers with someone?",
      answer: "Yes. Copy link to these inputs makes a web address that holds your price, cost, shipping, return rate and the marketplaces you compare. Whoever opens it sees the same screen. The inputs live in the address only; nothing is saved on a server.",
    },
  ],
  "profit-margin-calculator": [
    {
      question: "What's the difference between margin and markup?",
      answer: "They're calculated differently and often confused — margin is profit as a percentage of the selling price, while markup is profit as a percentage of the cost, so the same numbers give two different percentages.",
    },
  ],
  "shipping-cost-estimator": [
    {
      question: "Why did my shipping cost more than expected for a light package?",
      answer: "A large, lightweight box can cost more to ship than a small, heavy one because of dimensional weight — carriers charge based on whichever is greater, actual weight or dimensional (box-size-based) weight.",
    },
    {
      question: "What is dimensional weight and how is it calculated?",
      answer: "Dimensional weight is a size-based weight that carriers compare with the actual weight, and they bill whichever is greater. It is length times width times height divided by a divisor. In pounds and inches the common divisor is 139, and in kilograms and centimetres it is 5000. The estimator shows both weights and the one that is billed.",
    },
    {
      question: "Which dimensional weight divisor should I use?",
      answer: "Use the divisor in your carrier's rate guide. The tool starts with 139 for pounds and inches and 5000 for kilograms and centimetres, and you can change it, because carriers and service levels differ.",
    },
    {
      question: "Does the estimate include fuel, residential or insurance charges?",
      answer: "No. It calculates from the weight, the dimensions and the rate you enter, so surcharges and insurance your carrier adds are not included. Use it to compare box sizes, then confirm the final price with your carrier.",
    },
    {
      question: "How do I lower shipping cost on a light but bulky package?",
      answer: "Try a smaller box. Because the billable weight is the greater of actual and dimensional weight, a box with empty space can cost more than a smaller one holding the same item. Enter two box sizes and compare the totals.",
    },
  ],
  "event-budget-planner": [
    {
      question: "Which currencies does it support?",
      answer: "Any of the currencies in the menu, such as US dollars, euros, pounds, rupees and yen. Amounts are shown in your choice using your language's number format. Changing the currency relabels your amounts; it does not convert them. Items entered in a different currency are left out of the totals and flagged.",
    },
    {
      question: "Where does 'Spent so far' come from?",
      answer: "From the expenses you record in this tool, which are saved in your project with the rest of your event data. Each expense is matched to a planned line by its category name, ignoring capital letters and extra spaces. Costs count with tax included.",
    },
    {
      question: "What does 'Add typical categories' do?",
      answer: "You enter a total and pick an event type, and it adds a rule-of-thumb split of that total (venue, catering, contingency and so on) that adds up exactly to your total. The shares are a starting point; edit any amount afterwards.",
    },
    {
      question: "Why does it say money was spent in categories with no planned amount?",
      answer: "You recorded an expense with a category that has no budget line, or with no category at all. Press the Plan button next to it to give that category a planned amount, or fix the category on the expense.",
    },
    {
      question: "How do I get the budget out, or in?",
      answer: "Download Excel for a Budget sheet (planned, spent, left, percent used) and an Expenses sheet, or download one CSV with planned, spent and remaining per category plus every expense (importing it again restores both). To bring planned amounts in from a spreadsheet, download the blank CSV, fill in Category and Planned, and use Import budget CSV.",
    },
    {
      question: "Will my budget be here next time?",
      answer: "Only for this visit unless you save the project file. It is kept in your project with your other event data and erased when you close the tab. Nothing is sent to a server.",
    },
  ],
  "event-checklist-planner": [
    {
      question: "Which event types have a ready-made timeline?",
      answer: "Wedding, birthday, corporate event, conference, exhibition or trade show, and fundraiser. Each lists the main tasks and counts back from your event date to give every task a due date. Pressing Add these tasks again does not duplicate tasks already on your list.",
    },
    {
      question: "Can I add my own tasks and change the dates?",
      answer: "Yes. Type a task and press Enter, then edit its title, due date or owner in place. Tick a task when it is done. If the event date moves, enter the number of days and press Move dates to shift every dated task together.",
    },
    {
      question: "What counts as overdue or due soon?",
      answer: "A task that is not ticked and has a due date before today is overdue. Due soon means today or within the next seven days. Tasks with no date are never overdue, and done tasks drop to the bottom of the list.",
    },
    {
      question: "Can I assign tasks to people?",
      answer: "Yes. Type a name in the Owner box. Once owners exist, a menu appears so you can show one person's tasks. There are no emails or reminders sent; share the downloaded list or the text instead.",
    },
    {
      question: "How do I get my checklist out, or in?",
      answer: "Copy it as text, or download Excel or CSV. To bring tasks in from a spreadsheet, download the blank CSV, fill in Task, Due (year-month-day), Done and Owner, and use Import CSV. Rows with a problem are skipped and listed.",
    },
    {
      question: "Will my checklist be here next time?",
      answer: "Only for this visit unless you save the project file. The tasks live in your project with the rest of your event data and are erased when you close the tab. Nothing is sent to a server.",
    },
  ],
  "event-day-schedule": [
    {
      question: "Can I plan more than one day?",
      answer: "Yes. Use Add another day, pick the date, and switch between days with the Day menu. Copy and the downloads include every day, grouped by date.",
    },
    {
      question: "What does the overlap warning mean?",
      answer: "Two sessions on the same day overlap in time and share a room or a presenter. Back-to-back sessions are fine. The warning needs end times, so give each session one.",
    },
    {
      question: "What does Move times do?",
      answer: "It shifts every start and end on the day shown by the number of minutes you enter, later for a positive number and earlier for a negative one. Use it when a delay pushes the whole day. Times stop at the edge of the day instead of wrapping around.",
    },
    {
      question: "Can I bring a schedule in from a spreadsheet?",
      answer: "Yes. Download the blank CSV, fill in Title, Day, Start and optionally End, Room, Presenter and Notes, and use Import CSV. Dates must be written year-month-day, such as 2026-11-13, and times as 24-hour hours and minutes. Rows with a problem are skipped and listed so you can fix them.",
    },
    {
      question: "Is this schedule shared with the other event tools?",
      answer: "Yes. The sessions live in your project alongside your guest list, so the same agenda can feed other event tools. Save the project file to keep it; without that it is erased when you close the tab.",
    },
    {
      question: "What does the copied text and the Excel file contain?",
      answer: "The copied text has the event name, then each day with one line per session showing the times, the title, and the room and presenter in brackets. The Excel file has a Schedule sheet with Day, Start, End, Length, Session, Room, Presenter and Notes columns in time order.",
    },
  ],
  "name-badge-maker": [
    {
      question: "Which badge sheets does it support?",
      answer: "Name badge inserts with 6 per sheet at 4 x 3 inches (Avery 5392 and equivalents) on US Letter, and large 8-per-sheet labels (Avery L7165 and equivalents) on A4. For any other sheet, choose My own grid and enter your measurements. Print the test page on plain paper first and hold it against a real sheet.",
    },
    {
      question: "Where do the names come from?",
      answer: "From the guest list in your project (the same one the Guest List Tracker uses), or from names you type, one per line as Name | Organisation | Role. You can pick confirmed guests, everyone except declined, or every guest.",
    },
    {
      question: "What is the QR code on the badge?",
      answer: "A signed pass code for that guest. It holds a random guest ID and a signature made with a key stored in your project file, not the guest's name or email. A door check using the same project file can confirm a pass is genuine without any internet connection.",
    },
    {
      question: "Can I print extra badges for walk-ins?",
      answer: "Yes. Add blank badges and they print with the event band and no name, ready to fill in by hand. Use 'Start at badge' to finish a part-used sheet.",
    },
    {
      question: "Is my guest list uploaded anywhere?",
      answer: "No. The names and the PDF are made in your browser, nothing is uploaded or kept, and the project is erased when you close the tab unless you save the project file.",
    },
  ],
  "certificate-maker": [
    {
      question: "Who can I make certificates for?",
      answer: "Anyone in your project's guest list (confirmed and attended guests, everyone except declined, or every guest) or a list of names you type, one per line. Up to 500 in one go.",
    },
    {
      question: "How do I put each person's name in the wording?",
      answer: "Type {name} where the name should go, {event} for the event name and {date} for the date shown. The big name under the title is always filled in for you.",
    },
    {
      question: "Can I get one file per person?",
      answer: "Yes. Choose ZIP, one file each, and every certificate comes as its own PDF named in order, ready to email separately. Or keep One PDF to print them all together.",
    },
    {
      question: "Can I add my logo and signature?",
      answer: "Yes. Add a PNG or JPEG logo and, for each of the two signers, a signature picture. The logo is printed at the top of every certificate and each signature sits above its line. Two signature lines are always printed, empty if you name no signer, so you can also sign by hand.",
    },
    {
      question: "Can I make certificates from a spreadsheet?",
      answer: "Yes. Import a CSV or Excel file (the column headed Name, or else the first column), or paste rows from Excel or a CSV into the names box. The names appear in the box so you can check them before you download. Up to 500 in one go.",
    },
    {
      question: "Can I number the certificates?",
      answer: "Yes. Tick Number the certificates, add an optional prefix such as 2026- and the first number. Each certificate gets the next number, printed small at the bottom left, and the files in a ZIP keep the same numbers.",
    },
    {
      question: "What paper does it use?",
      answer: "Landscape US Letter or A4, following the paper set in your project. Print at 100% or Actual size, ideally on heavier paper.",
    },
    {
      question: "Are the names uploaded anywhere?",
      answer: "No. The certificates are made in your browser, nothing is uploaded or kept, and the project is erased when you close the tab unless you save the project file.",
    },
  ],
  "business-days-calculator": [
    {
      question: "Does it know my country's public holidays?",
      answer: "No. Holidays differ by country, region and company, so you paste the dates you want skipped (one per line, as YYYY-MM-DD). Reuse the same list for every check.",
    },
    {
      question: "Can I change the weekend?",
      answer: "Yes. Choose Saturday and Sunday, Friday and Saturday, Sunday only, or no weekend at all. A holiday that falls on a weekend day is counted as a weekend day, not twice.",
    },
    {
      question: "Is the start date counted?",
      answer: "When counting, the start date is included and the end date is not, unless you tick Count the end date too. When adding days, day one is the next working day after the start date.",
    },
    {
      question: "Can I go backwards from a date?",
      answer: "Yes. Enter a negative number of working days in Add working days to find the date that many working days earlier.",
    },
    {
      question: "Is anything stored?",
      answer: "No. The dates are worked out in your browser and nothing is uploaded or kept.",
    },
  ],
  "dietary-access-summary": [
    {
      question: "Where do the food and access notes come from?",
      answer: "From the Diet and Access fields of the guests in your project, the same ones you fill in with the Guest List Tracker. Change a note there and the summary updates.",
    },
    {
      question: "How are the notes grouped?",
      answer: "Common needs such as vegetarian, vegan, halal, kosher, gluten-free, dairy-free and nut, shellfish, egg, soy or sesame allergies are counted automatically. A person can count in more than one group. Anything it does not recognise is listed under Other notes so nothing is lost.",
    },
    {
      question: "Does it show names?",
      answer: "Not unless you tick the box. By default the caterer sheet has counts only. With names on, each person's note appears, with allergies and intolerances first.",
    },
    {
      question: "What about plus-ones?",
      answer: "They are counted in the number to feed, but their food needs are not known, so they are not counted in any group. Add them as their own guests if you know their needs.",
    },
    {
      question: "Is this medical or legal advice?",
      answer: "No. It sorts the notes your guests gave you. For allergies, always confirm the details directly with the guest and the kitchen.",
    },
    {
      question: "Which allergens are officially recognized in the US?",
      answer: "The FDA lists nine major food allergens: milk, eggs, fish, Crustacean shellfish, tree nuts, peanuts, wheat, soybeans and sesame. Peanuts and tree nuts are separate allergens, and labels name the specific tree nut. This is general information, not medical advice. Check the official source and confirm needs directly with each guest.",
      source: { label: "FDA: Food Allergies", url: "https://www.fda.gov/food/nutrition-food-labeling-and-critical-foods/food-allergies" },
    },
  ],
  "calendar-invite-maker": [
    {
      question: "Which calendars can open the file?",
      answer: "Google Calendar, Apple Calendar, Outlook and most other calendar apps open .ics files. Guests open the file or tap it in an email, and the event is added to their calendar.",
    },
    {
      question: "Will guests in other time zones see the right time?",
      answer: "Yes. You choose the time zone the event happens in, and the file stores that exact moment, so every guest sees it in their own local time. Daylight saving changes are handled.",
    },
    {
      question: "Can I make one file for the whole schedule?",
      answer: "Yes. Choose My project schedule and every session in your project goes into one file, each with its room as the location and the presenter and track in the details.",
    },
    {
      question: "Does the invite work in Outlook and Google?",
      answer: "Yes. Open or attach the .ics file for any calendar app, or use the Google Calendar, Outlook.com or Microsoft 365 buttons to open that calendar with the event filled in. A repeat is carried by the Google button and the .ics file; the two Outlook buttons add a single event.",
    },
    {
      question: "Will the time be right for guests in other zones?",
      answer: "Yes. You set the zone the event happens in, for example 09:00 in New York. Each guest's calendar shows that same moment in their own local time, and repeats keep their local time when the clocks change.",
    },
    {
      question: "Does it send the invitation?",
      answer: "No. It only makes the file. Attach it to an email or put it on your event page yourself. Nothing is uploaded, and no one is told anything.",
    },
    {
      question: "What if I change the event later?",
      answer: "Make the file again and send it. Each event keeps the same identifier, so most calendar apps update the existing entry instead of adding a second one.",
    },
  ],
  "event-landing-page-generator": [
    {
      question: "What do I get?",
      answer: "One HTML file for a single-page event website: title, date, venue, about text, a programme by day, an RSVP button and an add-to-calendar link. It works on phones and computers and has no scripts.",
    },
    {
      question: "Where do I put it online?",
      answer: "On any web host or static site service: upload the file as index.html. You can also email it as an attachment or open it from a shared drive. This tool does not host the page for you.",
    },
    {
      question: "Where does the programme come from?",
      answer: "From the sessions in your project, the same ones the Event Day Schedule uses. Untick the box if you do not want a programme on the page.",
    },
    {
      question: "How do RSVPs work?",
      answer: "The button opens the link or email address you give it, such as a form you already use. The page does not collect or store replies.",
    },
    {
      question: "Is anything tracked?",
      answer: "No. The page has no scripts, no analytics and loads nothing from other sites. Everything you type stays in your browser while you build it, and nothing is uploaded.",
    },
    {
      question: "Does the page follow accessibility guidelines?",
      answer: "Web accessibility means people with disabilities can perceive, understand, navigate and interact with a website. W3C's WCAG guidelines are the common international reference. The generated page is a starting point and is not an accessibility audit, so test your final page. Check the official source.",
      source: { label: "W3C: Introduction to Web Accessibility", url: "https://www.w3.org/WAI/fundamentals/accessibility-intro/" },
    },
  ],
  "catering-quantity-calculator": [
    {
      question: "Where do the amounts per person come from?",
      answer: "They are common planning amounts for each type of event, for example one main course per person at a seated meal and six canapés in the first hour of a standing reception. They are a starting point, so change any of them in the small box beside the item.",
    },
    {
      question: "What does the extra percentage do?",
      answer: "It adds a margin on top of every line to cover seconds, late guests and spills, and the result is rounded up. Set it to 0 if your caterer already adds their own.",
    },
    {
      question: "Can it use my guest list?",
      answer: "Yes. Press the link under People to take the confirmed guests and their plus-ones from your project. Special meals from the guest list (vegetarian, halal, gluten-free and so on) are listed below the order so you can tell the caterer.",
    },
    {
      question: "How are drinks counted?",
      answer: "Water and soft drinks are counted for everyone and grow with each extra hour. Coffee and tea grow with the hours at a coffee break, but stay at one cup each at a seated meal or buffet. Alcohol is counted only for the share of adults you say drink it, and shown as drinks with an estimate in bottles.",
    },
    {
      question: "How do I count children?",
      answer: "Enter them in Children, on top of People. Each child counts as half a portion of food and drink and none of the alcohol. For example, 100 people plus 20 children is planned like 110 adults.",
    },
    {
      question: "How much food for 100 people?",
      answer: "At a seated meal of three hours with 10 percent extra, about 110 starters, 110 main courses and 110 desserts, plus 165 bread rolls. A buffet needs more per person because guests take several dishes, so pick Buffet for that count.",
    },
    {
      question: "How many drinks per guest per hour?",
      answer: "For guests who drink alcohol, about two drinks in the first hour and one for each hour after, so four in three hours. Water starts at half a litre in the first hour and a quarter of a litre for each hour after; soft drinks start at 0.3 litres and add 0.15 litres an hour.",
    },
    {
      question: "Is my guest list uploaded?",
      answer: "No. Everything is worked out in your browser, and the project is erased when you close the tab unless you save the project file.",
    },
  ],
  "event-profit-ledger": [
    {
      question: "Where do the numbers come from?",
      answer: "From the income and cost entries in your project, the same ones you record in the Expense Tracker, the Invoice and Receipt makers and the Purchase Order tool. Planned amounts come from the Event Budget Planner.",
    },
    {
      question: "Does it handle tax?",
      answer: "Amounts are shown with tax included. The statement also shows how much tax sits inside the income and the costs, using the rates you entered. This is information only and not tax advice.",
    },
    {
      question: "What are the per-head figures?",
      answer: "Income, cost and profit divided by the number of heads. By default that is confirmed and attended guests with their plus-ones from your guest list, and you can type your own number.",
    },
    {
      question: "What if I used more than one currency?",
      answer: "Only entries in the project currency are counted. The tool tells you how many were left out. Change the project currency to see the others.",
    },
    {
      question: "Is anything stored?",
      answer: "No. The statement is built in your browser and the project is erased when you close the tab unless you save the project file.",
    },
  ],
  "receipt-to-expense-sheet": [
    {
      question: "Are my receipts uploaded?",
      answer: "No. The photos are read in your browser. The first time, the reading engine downloads its language data, so you need an internet connection once, but your images never leave your device.",
    },
    {
      question: "How accurate is the reading?",
      answer: "Good on flat, well-lit, printed receipts and weaker on crumpled, faded or handwritten ones. Every row is shown for you to check, and rows with no clear total or date are marked.",
    },
    {
      question: "Can I paste text instead of using a photo?",
      answer: "Yes. Open the paste box, paste the text of an e-receipt or email, and it becomes a row the same way.",
    },
    {
      question: "What happens to the tax?",
      answer: "If a tax line was found it is kept, and the total is treated as tax included, so your profit and budget tools count what you actually paid. Edit or clear the tax box if it was read wrongly.",
    },
    {
      question: "Where do the rows go?",
      answer: "Add them to your project and they appear in the Expense Tracker, Event Budget Planner and Event Profit Ledger, or download a CSV. The project is erased when you close the tab unless you save the project file.",
    },
  ],
  "timesheet-generator": [
    {
      question: "Can I print blank timesheets to fill in by hand?",
      answer: "Yes. Leave Pre-fill unticked and every day is a blank row with the date, start, end, break and hours columns, plus totals and signature lines.",
    },
    {
      question: "How are hours worked out?",
      answer: "End time minus start time minus the unpaid break. If the end is earlier than the start the shift is counted as ending after midnight, so 22:00 to 06:00 is 8 hours.",
    },
    {
      question: "Can I make sheets for many people at once?",
      answer: "Yes. Type one name per line, or take the names from your guest list, and you get one page per person in a single PDF.",
    },
    {
      question: "Does it add up pay?",
      answer: "If you enter an hourly rate, pre-filled sheets show the pay for the period in your project currency. It is a simple hours times rate and ignores overtime, tax and holiday rules.",
    },
    {
      question: "Is anything stored?",
      answer: "No. The PDF is made in your browser, nothing is uploaded or kept, and the project is erased when you close the tab unless you save the project file.",
    },
  ],
  "event-closure-report-maker": [
    {
      question: "What goes into the report?",
      answer: "Overview, attendance, programme, finance, tasks and vendors, taken from your project, followed by the highlights, problems, lessons and thanks that you type. Untick any section you do not want.",
    },
    {
      question: "How is attendance worked out?",
      answer: "From the guest statuses in your project. The rate is guests marked attended divided by those attended or confirmed. Mark attendance in the Guest List Tracker first, otherwise the report says attendance has not been marked.",
    },
    {
      question: "Where do the finance figures come from?",
      answer: "From the income and cost entries and the budget lines in your project, in the project currency. Entries in other currencies are left out and the report says how many.",
    },
    {
      question: "Can I send it by email?",
      answer: "Download the PDF to attach it, or copy the text into the body of an email.",
    },
    {
      question: "Is anything stored?",
      answer: "No. The report is built in your browser and the project is erased when you close the tab unless you save the project file.",
    },
  ],
  "markdown-editor": [
    {
      question: "How do the toolbar buttons work?",
      answer: "Select some text and press a button to format it, or press the button again to take the format off. With nothing selected, a button inserts a placeholder you can type over. Ctrl or Cmd with B, I and K gives bold, italic and link.",
    },
    {
      question: "What Markdown is supported?",
      answer: "Headings, bold, italic, strikethrough, links, images, quotes, lists, task lists, tables and code, following CommonMark with GitHub-style tables and task lists.",
    },
    {
      question: "Is the HTML safe to publish?",
      answer: "The preview and the exported HTML have scripts, event handlers and javascript: links removed. Still read your page before you publish it.",
    },
    {
      question: "Is my writing saved?",
      answer: "No. Everything stays in your browser and is gone when you close the tab, so download the .md file before you leave.",
    },
    {
      question: "Can I open an existing file?",
      answer: "Yes. Use Open .md file for Markdown or plain text files up to 2 MB.",
    },
  ],
  "booth-layout-planner": [
    {
      question: "How do I move a booth?",
      answer: "Drag it on the plan. It snaps to half a metre (or one foot) and stays inside the hall. Tap a booth to rename it, change its size or position, add the exhibitor's name or delete it.",
    },
    {
      question: "Can it lay out the booths for me?",
      answer: "Yes. Enter the booth size, how many you need, the aisle width and the wall margin, then press Fill rows. Tick back-to-back to pair rows so aisles only appear between pairs. It tells you if some booths did not fit.",
    },
    {
      question: "What do the red outlines mean?",
      answer: "Red marks booths that overlap each other or stick out of the hall. Aisle markers can overlap anything because they only show where people walk.",
    },
    {
      question: "Does it check fire or access rules?",
      answer: "No. It is a drawing tool. Check aisle widths, exits and access with your venue and local rules before you finalise a plan.",
    },
    {
      question: "How do I keep my plan?",
      answer: "Nothing is stored, so download the PDF, PNG or CSV before you close the tab. The CSV lists every booth with its position, size and exhibitor note.",
    },
  ],
  "exhibitor-booth-tracker": [
    {
      question: "What do the statuses mean?",
      answer: "Enquiry is someone interested, Booked has agreed to take a booth, Paid has settled the fee in full, and Cancelled has withdrawn. Only booked and paid exhibitors count towards the fees agreed and the amount to collect.",
    },
    {
      question: "How does it spot problems?",
      answer: "It warns when one booth number is given to more than one live exhibitor, when a booked or paid exhibitor has no booth, and when money is held for a cancelled exhibitor.",
    },
    {
      question: "Can I import my exhibitor list?",
      answer: "Yes. Import a CSV with columns such as Company, Booth, Contact, Email, Status, Fee and Paid. Words like Confirmed, Reserved, Lead or Withdrawn are understood. Use Blank CSV for a template.",
    },
    {
      question: "Does it take payments?",
      answer: "No. You record what each exhibitor has paid. It works out the balance but does not collect money or send invoices.",
    },
    {
      question: "Is anything stored?",
      answer: "No. The list lives in your browser and is erased when you close the tab unless you save the project file or download the CSV.",
    },
  ],
  "visitor-lead-capture-sheet": [
    {
      question: "Where are the leads saved?",
      answer: "Only in this browser tab. Nothing is uploaded or stored on our side, so download the CSV before you close the tab or the leads are gone.",
    },
    {
      question: "What do hot, warm and cold mean?",
      answer: "A common rule: hot is ready to buy or book soon, warm is interested and worth a follow-up, cold is just browsing. The CSV lists the hottest first.",
    },
    {
      question: "Does it warn about duplicates?",
      answer: "Yes. If the email or phone number matches a lead you already captured, you are asked whether to add it anyway.",
    },
    {
      question: "Can I print sheets instead?",
      answer: "Yes. Open Print sheets, choose the columns, rows per page and number of pages, and download a landscape PDF on Letter or A4 with tick circles for the rating.",
    },
    {
      question: "Do I need the visitor's permission?",
      answer: "Yes. Only collect and keep contact details with the visitor's agreement and follow the privacy rules where you operate. A simple sign saying how you will use their details helps.",
    },
    {
      question: "Are there official rules for emailing invitations or leads?",
      answer: "If a message is commercial, the FTC's CAN-SPAM guidance says to use accurate sender details and a non-deceptive subject line, identify the message as an ad, include your physical postal address, give a clear way to opt out and honor opt-outs promptly. This is general information, not legal advice, and other countries have their own rules. Check the official source.",
      source: { label: "FTC: CAN-SPAM Act compliance guide", url: "https://www.ftc.gov/business-guidance/resources/can-spam-act-compliance-guide-business" },
    },
  ],
  "merchandise-order-sheet": [
    {
      question: "How is the quantity worked out?",
      answer: "People times the quantity per person, plus the spare percentage, rounded up. Then the supplier's pack size and minimum order are applied, so you may order more than you need. The extra is shown with its cost.",
    },
    {
      question: "How are sizes split?",
      answer: "Sized items are divided by a typical size mix, adding up exactly to your order. Open Size mix to change the shares to suit your crowd.",
    },
    {
      question: "Can I use a fixed number instead of per person?",
      answer: "Yes. Choose Fixed number for items such as 25 speaker gifts. Spare percentage does not apply to fixed items.",
    },
    {
      question: "Can it add the cost to my budget?",
      answer: "Yes. Add total to my budget sets a Merchandise line in your project budget, which then shows in the Event Budget Planner and Event Profit Ledger.",
    },
    {
      question: "Is anything stored?",
      answer: "No. Everything is worked out in your browser and the project is erased when you close the tab unless you save the project file.",
    },
  ],
  "badge-pass-maker": [
    { question: "What does this app do?", answer: "It joins three steps on one guest list: add guests, print name badges with a QR pass, and check people in at the door by scanning the passes." },
    { question: "Do I need internet at the door?", answer: "No. The door check reads the passes and checks them against your project in the browser, so it keeps working without a connection once the page is open." },
    { question: "Does the door check show where the guest sits?", answer: "Yes. When a pass is valid, the door check shows the guest's table from the guest list, or says that no table is set yet. It reads the table from the project open on your device, so seat guests first and open the latest project file at the door." },
    { question: "Do I need to save the project?", answer: "Yes, save the project file. The passes are signed with it. Open the same file on the door device so real passes are recognised." },
    { question: "Does it use one usage per step?", answer: "Each export counts one usage, as it does in the single tools. Looking around and editing does not." },
    { question: "Is guest data uploaded?", answer: "No. The guest list stays in this browser tab and is erased when you close it." },
  ],
  "invite-calendar-maker": [
    { question: "What is the calendar file?", answer: "An .ics file. Guests open it and the event goes into Apple, Google or Outlook calendar with the time and place." },
    { question: "Can I use the same details for the card and the calendar?", answer: "Yes. Both steps work from the same project, so you type the guest list once." },
    { question: "Does it send the invitations?", answer: "No. It creates the card and the file. You send them yourself, so your guest contacts are never uploaded." },
    { question: "Which time zone is used?", answer: "The one you set for the event, so guests in other zones see their own local time." },
    { question: "Is anything stored?", answer: "No. Everything stays in this browser tab and is erased when you close it." },
  ],
  "expense-capture-sheet": [
    { question: "How do receipts become expenses?", answer: "Paste the receipt text or read a photo. The tool finds the vendor, date, total and currency and adds an expense you can correct." },
    { question: "Are receipts uploaded?", answer: "No. Reading happens in your browser and nothing is stored." },
    { question: "How does it connect to the budget?", answer: "Expenses go into the same project the budget planner uses, so you can see spending against each budget line." },
    { question: "What is in the profit statement?", answer: "Income, costs by category, tax lines and the result, in the currency you choose." },
    { question: "Does it work in any currency?", answer: "Yes, with region-neutral tax lines such as sales tax, VAT or GST." },
  ],
  "agenda-cue-sheet-builder": [
    { question: "What is a cue sheet?", answer: "A run-of-show page for your crew with start times, durations, who is on, and notes for each cue." },
    { question: "Do the steps share sessions?", answer: "Yes. The schedule, printed agenda, cue sheet and stage timer all use the same sessions in your project." },
    { question: "Can I import a schedule?", answer: "Yes. Import a CSV in the schedule step and every other step updates." },
    { question: "How does the stage timer know the length of a talk?", answer: "From the session start and end times. Use my agenda loads them in order." },
    { question: "Is anything uploaded?", answer: "No. Everything stays in your browser tab." },
  ],
  "rooming-dietary-sheet": [
    { question: "What does the summary show?", answer: "Counts of each dietary need and access need, with the names behind them, ready to send to a caterer or venue." },
    { question: "Can I print labels?", answer: "Yes. The label step prints sheets of labels from the guest list, for example meal or seat tags." },
    { question: "Where do the needs come from?", answer: "From the guest list. You can import them from a CSV or type them in." },
    { question: "Is it private?", answer: "Yes. Guest details stay in your browser tab and are erased when you close it." },
    { question: "Does it handle room assignments?", answer: "This version covers needs and labels. Room lists are in the planned Accommodation Manager." },
    {
      question: "Which allergens are officially recognized in the US?",
      answer: "The FDA lists nine major food allergens: milk, eggs, fish, Crustacean shellfish, tree nuts, peanuts, wheat, soybeans and sesame. Peanuts and tree nuts are separate allergens, and labels name the specific tree nut. This is general information, not medical advice. Check the official source and confirm needs directly with each guest.",
      source: { label: "FDA: Food Allergies", url: "https://www.fda.gov/food/nutrition-food-labeling-and-critical-foods/food-allergies" },
    },
  ],
  "vendor-quote-comparison": [
    { question: "How are quotes grouped?", answer: "By the item name, ignoring capital letters and extra spaces. Use the same wording on each quote so they line up." },
    { question: "What about expired quotes?", answer: "They are shown but left out of the ranking, so an old low price does not mislead you." },
    { question: "Can I compare quotes in different currencies?", answer: "They are shown together but not ranked, because the tool does not guess exchange rates." },
    { question: "What happens when I accept a quote?", answer: "Other open quotes for the same item are marked rejected. You can then add the accepted amount to your budget." },
    { question: "Is anything stored?", answer: "Quotes stay in this browser tab and are erased when you close it. Save the project file to keep them." },
  ],
  "quote-comparison-builder": [
    { question: "What does this app do?", answer: "It takes you from collected quotes to a purchase order: compare, accept, budget, then create the order." },
    { question: "Can I go back a step?", answer: "Yes. Use the step buttons at any time. Your quotes and budget are shared by every step." },
    { question: "Why is the purchase order step locked?", answer: "It needs an accepted quote. Accept one in step 1." },
    { question: "Does each step cost a usage?", answer: "Each export counts one usage, as in the single tools." },
    { question: "Is my data uploaded?", answer: "No. It stays in your browser tab." },
  ],
  "guest-manager": [
    { question: "What is the Guest Manager?", answer: "One place for everything about your guests: the list, their dietary and access needs, printed badges with QR passes, labels and a door check that runs on your own device." },
    { question: "Do the steps share data?", answer: "Yes. Every step works on the same project in your browser tab, so you enter things once." },
    { question: "Does each step cost a usage?", answer: "Each export counts one usage, the same as using the single tool. Browsing and editing do not." },
    { question: "Can I try it first?", answer: "Yes. Use Try a sample to fill the project with an example event, then replace it with your own." },
    { question: "Is my data stored?", answer: "No. Everything stays in your browser tab and is erased when you close it. Save the project file to keep your work." },
  ],
  "invitation-automator": [
    { question: "What is the Invitation Automator?", answer: "Everything you send to announce an event: the invitation card, a calendar file, a simple event web page and QR codes, all from the same details." },
    { question: "Do the steps share data?", answer: "Yes. Every step works on the same project in your browser tab, so you enter things once." },
    { question: "Does each step cost a usage?", answer: "Each export counts one usage, the same as using the single tool. Browsing and editing do not." },
    { question: "Can I try it first?", answer: "Yes. Use Try a sample to fill the project with an example event, then replace it with your own." },
    { question: "Is my data stored?", answer: "No. Everything stays in your browser tab and is erased when you close it. Save the project file to keep your work." },
    {
      question: "Are there official rules for emailing invitations or leads?",
      answer: "If a message is commercial, the FTC's CAN-SPAM guidance says to use accurate sender details and a non-deceptive subject line, identify the message as an ad, include your physical postal address, give a clear way to opt out and honor opt-outs promptly. This is general information, not legal advice, and other countries have their own rules. Check the official source.",
      source: { label: "FTC: CAN-SPAM Act compliance guide", url: "https://www.ftc.gov/business-guidance/resources/can-spam-act-compliance-guide-business" },
    },
  ],
  "programme-show-day-planner": [
    { question: "What is the Programme and Show-Day Planner?", answer: "Plan the programme and run the day: schedule, printed agenda, crew cue sheet, checklist, catering quantities and a stage timer for speakers." },
    { question: "Do the steps share data?", answer: "Yes. Every step works on the same project in your browser tab, so you enter things once." },
    { question: "Does each step cost a usage?", answer: "Each export counts one usage, the same as using the single tool. Browsing and editing do not." },
    { question: "Can I try it first?", answer: "Yes. Use Try a sample to fill the project with an example event, then replace it with your own." },
    { question: "Is my data stored?", answer: "No. Everything stays in your browser tab and is erased when you close it. Save the project file to keep your work." },
  ],
  "profit-expense-manager": [
    { question: "How do I know my break-even point?", answer: "Open Profit statement, type your ticket price and, if you like, a capacity. The tool shows how many tickets cover your costs, using the larger of what you planned and what you have spent, and credits other income you have received. With a capacity it also shows the profit or loss at a sell-out and at 60 percent sold. Costs are treated as fixed." },
    { question: "Can I track sponsorship income?", answer: "Yes. In the Income step add a line for Tickets, Sponsorship, Exhibitor fees, Merchandise, Food and bar, Donations, Grants or Other income, and mark it Received or Pledged. Pledged money is listed apart and left out of profit until you mark it Received; the statement also shows what profit would be if every pledge is paid." },
    { question: "What is the Profit and Expense Manager?", answer: "Follow the money for an event: budget, receipts as expenses, merchandise orders, crew timesheets and a profit statement in any currency, with region-neutral tax lines." },
    { question: "Do the steps share data?", answer: "Yes. Every step works on the same project in your browser tab, so you enter things once." },
    { question: "Does each step cost a usage?", answer: "Each export counts one usage, the same as using the single tool. Browsing and editing do not." },
    { question: "Can I try it first?", answer: "Yes. Use Try a sample to fill the project with an example event, then replace it with your own." },
    { question: "Is my data stored?", answer: "No. Everything stays in your browser tab and is erased when you close it. Save the project file to keep your work." },
    {
      question: "Is this tax or accounting advice?",
      answer: "No. The manager helps you track income and costs for an event. It is not tax or accounting advice, so ask a qualified professional about how to report them.",
    },
  ],
  "event-report-generator": [
    { question: "What is the Event Report Generator?", answer: "Wrap up an event with a closure report you can share, plus the profit statement, a guest summary and certificates for attendees." },
    { question: "Do the steps share data?", answer: "Yes. Every step works on the same project in your browser tab, so you enter things once." },
    { question: "Does each step cost a usage?", answer: "Each export counts one usage, the same as using the single tool. Browsing and editing do not." },
    { question: "Can I try it first?", answer: "Yes. Use Try a sample to fill the project with an example event, then replace it with your own." },
    { question: "Is my data stored?", answer: "No. Everything stays in your browser tab and is erased when you close it. Save the project file to keep your work." },
  ],
  "rooming-list-planner": [
    { question: "How are rooms counted when two people share?", answer: "If each names the other in Shared with and the dates and hotel match, the pair counts as one room. Otherwise each person counts as a room." },
    { question: "What is a room night?", answer: "One room for one night. Three rooms for two nights is six room nights. Hotels price and contract blocks this way." },
    { question: "What does the block request contain?", answer: "The number of rooms needed each night, the peak night, totals and a breakdown by room type, as plain text you can paste into an email to the hotel." },
    { question: "Can it fill rooms from my guest list?", answer: "Yes. Guests marked as needing a stay with arrival and departure dates become rooms in one step." },
    { question: "Is my data stored?", answer: "No. It stays in your browser tab and is erased when you close it. Save the project file to keep it." },
  ],
  "seating-chart-planner": [
    { question: "How does automatic seating work?", answer: "Guests without a table are grouped by the Keep together lines you wrote or the Group column of your file; people with no group are grouped by organisation instead. The biggest groups go first, each at the emptiest table with room for the whole group, and two guests you want kept apart are never put at the same table. A group too big for one table is packed into full tables first, for example 8 and 2. Anyone already at a table stays put, and anything that could not be done is listed when it finishes." },
    { question: "How do I keep people together or apart?", answer: "Open Keep together and keep apart. Write one group per line such as Ann Lee + Bob Lee, or Smith family: Ann Lee + Bob Lee to give it a name, and one pair per line to keep apart such as Dan Roe + Eve Ng, using the names exactly as on the guest list. Press Save rules. A CSV or Excel file can carry the same rules in columns named Group and Keep apart from, with several names separated by semicolons. The rules are saved in the project file, and a warning appears if you later move people so that a rule is broken." },
    { question: "Which guests get seats?", answer: "Everyone whose reply is not Declined: invited, confirmed, waitlisted and attended guests are all seated, so you can plan before replies are in. Declined guests are left out of the seated and unseated counts and of every export." },
    { question: "Do plus ones take seats?", answer: "Yes. A guest with two plus ones uses three seats at the same table." },
    { question: "What if a table is too full?", answer: "It is outlined and a warning says by how many seats it is over. A party larger than a table is flagged too." },
    { question: "Can I import guests from Excel?", answer: "Yes. Use Import guests with a CSV, TSV, Excel (XLSX or XLS) or ODS file. A workbook is read from its first visible sheet, only Name is required, and common headers such as Full name, Company, RSVP and Table are recognised. The file is read in your browser and is not uploaded." },
    { question: "My Table column has plain numbers. Will that make extra tables?", answer: "No. A cell that is just a number, such as 3, and any spelling of the name such as table 3 or TABLE #3, are all treated as Table 3, so they land at the same table. Other names, such as Head table, are kept as you typed them and matched without regard to capital letters." },
    { question: "How can guests find their table?", answer: "Two ways, both from the For your guests section. The find-your-table list is a PDF of every seated guest from A to Z, sorted by last or first name, to print for the entrance. The table cards are a PDF sheet with one card per guest, each with a QR code. Scanning it opens a small page that shows that guest's table. Declined guests and guests without a table are left out, so make the cards after seating is final." },
    { question: "Does the guest QR code send my guest list anywhere?", answer: "No. The guest's name and table are written into the link after the # sign, which browsers never send to any server. The page that opens reads them on the guest's phone and shows them. There is no lookup and nothing is stored, and the page loads no ads or analytics. Anyone with a card sees only the details on that card. If you move a guest to another table, print that card again." },
    { question: "Is this a floor plan?", answer: "No. It plans who sits at which table. For booths and stand layouts use the Booth Layout Planner." },
    { question: "Is my guest list uploaded?", answer: "No. It stays in your browser tab and is erased when you close it." },
  ],
  "arrivals-departures-board": [
    { question: "Where do the dates come from?", answer: "From the arrival and departure dates on your guest list. You can set them in the table at the bottom of this tool or in the Guest List Tracker, which share the same list." },
    { question: "Can I add flight numbers?", answer: "Yes. Each guest has an arrival and a departure time and flight number next to the dates. They show on the board, in the copied text and in the CSV and PDF pickup sheet, and they are saved in the project file." },
    { question: "Does it track flights?", answer: "No. It shows the times you type and does not look up delays or gate changes, so confirm flights with the airline or the traveller before a driver leaves." },
    { question: "How are guests grouped into pickups?", answer: "Guests landing within the number of minutes you choose, counted from the first guest of the pickup, go into one pickup with a head count that includes plus ones. Guests with no time are listed last in a group of their own. The CSV and PDF have one block per pickup." },
    { question: "Are plus ones counted?", answer: "Yes. Each guest's plus ones are included in the people count for the day." },
    { question: "What does the busiest day tell me?", answer: "It is the day with the most arrivals, so the day to plan the most vehicles and welcome desk staff." },
    { question: "Is my data stored?", answer: "No. It stays in your browser tab and is erased when you close it." },
  ],
  "vendor-quote-manager": [
    { question: "What is the Vendor Quote Manager?", answer: "Manage vendors from quote to delivery: compare quotes, accept one, add it to the budget, raise the purchase order and check the delivery with a packing slip." },
    { question: "Do the steps share data?", answer: "Yes. Every step works on the same project in your browser tab, so you enter things once." },
    { question: "Does each step cost a usage?", answer: "Each export counts one usage, the same as using the single tool. Browsing and editing do not." },
    { question: "Can I try it first?", answer: "Yes. Use Try a sample to fill the project with an example event, then replace it with your own." },
    { question: "Is my data stored?", answer: "No. Everything stays in your browser tab and is erased when you close it. Save the project file to keep your work." },
  ],
  "travel-planner": [
    { question: "What is the Travel Planner?", answer: "Plan the travel side of an event: who arrives and leaves each day, the itinerary, time zone differences, a travel budget and a packing list." },
    { question: "Do the steps share data?", answer: "Yes. Every step works on the same project in your browser tab, so you enter things once." },
    { question: "Does each step cost a usage?", answer: "Each export counts one usage, the same as using the single tool. Browsing and editing do not." },
    { question: "Can I try it first?", answer: "Yes. Use Try a sample to fill the project with an example event, then replace it with your own." },
    { question: "Is my data stored?", answer: "No. Everything stays in your browser tab and is erased when you close it. Save the project file to keep your work." },
    {
      question: "Is there an official rule for liquids in carry-on bags?",
      answer: "In the US, TSA allows liquids, gels and aerosols in carry-on bags in travel-size containers of 3.4 ounces (100 ml) or less, with each passenger limited to one quart-size bag (the 3-1-1 rule). This tip is general information and rules can change or differ by country and airline, so check the official source before you fly.",
      source: { label: "TSA: liquids, aerosols and gels", url: "https://www.tsa.gov/travel/frequently-asked-questions" },
    },
  ],
  "accommodation-manager": [
    { question: "What is the Accommodation Manager?", answer: "Run the accommodation side of an event: who needs a room, the rooming list, the room block request for the hotel, arrivals and departures, and labels." },
    { question: "Do the steps share data?", answer: "Yes. Every step works on the same project in your browser tab, so you enter things once." },
    { question: "Does each step cost a usage?", answer: "Each export counts one usage, the same as using the single tool. Browsing and editing do not." },
    { question: "Can I try it first?", answer: "Yes. Use Try a sample to fill the project with an example event, then replace it with your own." },
    { question: "Is my data stored?", answer: "No. Everything stays in your browser tab and is erased when you close it. Save the project file to keep your work." },
  ],
  "seating-floor-planner": [
    { question: "What is the Seating and Floor Planner?", answer: "Plan the room: who sits at which table, a layout drawing of the floor, and labels for place cards and tables." },
    { question: "Do the steps share data?", answer: "Yes. Every step works on the same project in your browser tab, so you enter things once." },
    { question: "Does each step cost a usage?", answer: "Each export counts one usage, the same as using the single tool. Browsing and editing do not." },
    { question: "Can I try it first?", answer: "Yes. Use Try a sample to fill the project with an example event, then replace it with your own." },
    { question: "Is my data stored?", answer: "No. Everything stays in your browser tab and is erased when you close it. Save the project file to keep your work." },
    {
      question: "Does this check building codes or accessibility rules?",
      answer: "No. The planner helps you arrange seats and tables. It is not a building, fire-code or accessibility (ADA) review, so confirm capacity, aisle widths and exits with the venue or your local authority.",
    },
  ],
  "event-tax-summary": [
    { question: "Does this work in my country?", answer: "It works in any currency and with sales tax, VAT or GST, using the rates you enter. It does not apply any country's rules, so it is a summary of your own entries." },
    { question: "Is this tax advice?", answer: "No. It is information only. Tax rules differ by country, region and type of sale. Check the figures with a qualified tax professional before you file or pay." },
    { question: "What does Net mean?", answer: "Tax charged on your income minus tax paid on your costs. A positive number means you charged more than you paid." },
    { question: "Why is an entry missing from the totals?", answer: "An entry marked as taxed with no rate has no tax counted. The tool lists these so you can add the rate." },
    { question: "Is my data stored?", answer: "No. It stays in your browser tab and is erased when you close it." },
  ],
  "booth-manager": [
    { question: "What is the Booth Manager?", answer: "Manage an exhibition: who has which booth and who has paid, the floor layout, lead capture sheets for exhibitors and badges for their staff." },
    { question: "Do the steps share data?", answer: "Yes. Every step works on the same project in your browser tab, so you enter things once." },
    { question: "Does each step cost a usage?", answer: "Each export counts one usage, the same as using the single tool. Browsing and editing do not." },
    { question: "Can I try it first?", answer: "Yes. Use Try a sample to fill the project with an example event, then replace it with your own." },
    { question: "Is my data stored?", answer: "No. Everything stays in your browser tab and is erased when you close it. Save the project file to keep your work." },
  ],
  "marketing-materials-manager": [
    { question: "What is the Marketing Materials Manager?", answer: "Make everything that promotes your event: invitation, flyer, banner and signs, a simple web page and QR codes, with the same details throughout." },
    { question: "Do the steps share data?", answer: "Yes. Every step works on the same project in your browser tab, so you enter things once." },
    { question: "Does each step cost a usage?", answer: "Each export counts one usage, the same as using the single tool. Browsing and editing do not." },
    { question: "Can I try it first?", answer: "Yes. Use Try a sample to fill the project with an example event, then replace it with your own." },
    { question: "Is my data stored?", answer: "No. Everything stays in your browser tab and is erased when you close it. Save the project file to keep your work." },
  ],
  "giveaway-branding-manager": [
    { question: "What is the Giveaway and Branding Manager?", answer: "Handle the giveaway and branded items: a fair prize draw with backups, merchandise orders, certificates and labels." },
    { question: "Do the steps share data?", answer: "Yes. Every step works on the same project in your browser tab, so you enter things once." },
    { question: "Does each step cost a usage?", answer: "Each export counts one usage, the same as using the single tool. Browsing and editing do not." },
    { question: "Can I try it first?", answer: "Yes. Use Try a sample to fill the project with an example event, then replace it with your own." },
    { question: "Is my data stored?", answer: "No. Everything stays in your browser tab and is erased when you close it. Save the project file to keep your work." },
    {
      question: "Do giveaways have official rules to check?",
      answer: "The FTC says it is illegal to require someone to pay or buy something to enter a prize drawing or to improve their odds of winning. Many places also have rules on sweepstakes and contests, so state your entry method, prize details and odds clearly. This is general information, not legal advice. Check the official source and the rules where you operate.",
      source: { label: "FTC: Fake Prize, Sweepstakes, and Lottery Scams", url: "https://consumer.ftc.gov/articles/fake-prize-sweepstakes-and-lottery-scams" },
    },
  ],
  "event-suite": [
    { question: "What is the Event Suite?", answer: "One subscription for planning any event. You pick the kind of event, add a starter checklist, budget and running order if you want one, and use thirteen products that share one project: guests, invitations, programme, budget, reports, vendors, travel, rooms, seating, booths, marketing, giveaways and tax summary." },
    { question: "How much does it cost?", answer: "Event Suite is $29.99 per month and Event Suite Premium is $49.99 per month, each billed monthly, with a 7-day trial the first time. Both plans include 1 GB of account storage to save and reopen your projects from any device. Premium adds the event apps, email invitations to your guests with RSVP links and scheduled reminders, and optional text reminders sent from your own Twilio account. If you cancel, your plan and saved data continue to the end of the paid month, then saved data is erased. There is no day pass." },
    { question: "Can my whole team use it?", answer: "Yes. An organization plan covers many people, by company email domain or a list of addresses. Sign in with your work email and the Event Suite opens for you." },
    { question: "Do the products share data?", answer: "Yes. All of them work on the same project in your browser tab, so you enter guests, sessions and costs once." },
    { question: "Is my data stored?", answer: "Not by default. Everything stays in your browser tab and is erased when you close it, and you save the project file to keep your work. With an Event Suite plan you can also choose to save projects to your account, up to 1 GB. If you publish an event with Premium event apps, we keep the published event, the door guest list and check-ins, and each booth's leads until you delete them or Premium ends, counted in the same 1 GB. If you use Premium guest messaging, we also keep your guests' RSVP answers (and the mobile numbers of guests who chose text) for up to 180 days, and your Twilio connection, with the key encrypted. We never see your Twilio password. Saved projects are erased when your plan ends, and Premium guest data when Premium ends, or when you delete your account, so download a copy first." },
  ],
  "giveaway-winner-picker": [
    { question: "Is the draw really random?", answer: "Yes. It uses your browser's cryptographically secure random number source and avoids the small bias that simple methods can add, so every ticket has an equal chance." },
    { question: "Can someone win twice?", answer: "No. Each person can win once. Anyone who is drawn is taken out before the next pick." },
    { question: "How do backup winners work?", answer: "Backups are drawn in the same draw, after the winners, in order. If a winner cannot be reached, go to Backup 1." },
    { question: "Can some people have more chances?", answer: "Yes. Turn on extra tickets and write a name as Ana x3 or Ana, 3. Three tickets means three times the chance, but still only one win." },
    { question: "Can I keep a record?", answer: "Download the record as a CSV with the draw name, places, names and date. Nothing is stored by the site." },
  ],
  "online-timer": [
    { question: "Do I need to sign up to use the timer?", answer: "No. Open the page, tap a preset such as 10 minutes and press Start. Nothing is sent anywhere and nothing is saved." },
    { question: "How do I set a custom time?", answer: "Type it in the custom box as minutes (7), minutes and seconds (7:30) or hours, minutes and seconds (1:05:00). The clock updates at once." },
    { question: "What is Pomodoro mode?", answer: "It alternates focus rounds with breaks: 25 minutes of work, a 5 minute break, and a longer 15 minute break after four rounds. You can change every length, and it moves to the next round by itself." },
    { question: "Can I keep the timer visible while I work in another window?", answer: "In Chrome and Edge, Pop out opens a small floating timer that stays above your other windows. In other browsers, the remaining time also shows in the browser tab title." },
    { question: "Will it still ring if the tab is in the background?", answer: "It works from the clock, so the time is always right when you look. Press Start once so the browser allows sound, and keep the tab open." },
  ],
  "focus-stage-timer": [
    {
      question: "How does the stage timer warn the speaker?",
      answer: "The screen is green while there is plenty of time, turns amber when your first warning time is reached, red at the final warning, and dark red when time is up, when it counts up with a plus sign. A short beep marks each change if sound is on.",
    },
    {
      question: "Can I show it on a second screen?",
      answer: "Yes. Press Full screen, or open the page on a tablet or laptop facing the speaker. The numerals are large enough to read from across a room.",
    },
    {
      question: "Can it follow my agenda?",
      answer: "Yes. Use my agenda loads your project's sessions that have an end time, and Next session moves to the following one with its own length.",
    },
    {
      question: "Does it stay accurate if the screen sleeps?",
      answer: "It works from the clock, so the time shown is always correct when you look. It also asks the browser to keep the screen on while it runs, where the browser allows it. Keep the tab open.",
    },
    {
      question: "How does focus mode work?",
      answer: "It alternates focus rounds with short breaks and a long break after every few rounds. You choose the lengths, and it can start each next round by itself.",
    },
  ],
  "agenda-builder": [
    {
      question: "Where do the sessions come from?",
      answer: "From the schedule in your project (the Event Day Schedule fills it), or from lines you type here: a time, the title, then optionally the presenter and room separated by |.",
    },
    {
      question: "Can I hide rooms or presenters?",
      answer: "Yes. Tick or untick Presenters, Rooms and Tracks and the preview updates. Use the subtitle for the dates and place and the footer for a website or contact.",
    },
    {
      question: "Does it handle several days?",
      answer: "Yes. Each day gets its own heading in your language and date format, and a long programme continues on more pages with page numbers.",
    },
    {
      question: "What paper size does it use?",
      answer: "US Letter or A4, following the paper set in your project. Print at 100% or Actual size.",
    },
    {
      question: "Is my schedule uploaded anywhere?",
      answer: "No. The PDF is made in your browser, nothing is uploaded or kept, and the project is erased when you close the tab unless you save the project file.",
    },
  ],
  "run-of-show-cue-sheet": [
    {
      question: "What is a run-of-show cue sheet?",
      answer: "A crew document listing every item of the event in order with its time, length, who is on, where, and what cue or action happens, so stage managers, sound and lights all work from the same timeline.",
    },
    {
      question: "Where do the cue notes come from?",
      answer: "From the Notes of each session in your project. Write things like 'Lights down, play intro video' there and they appear in the Cue / notes column.",
    },
    {
      question: "What does it check?",
      answer: "It flags items that overlap the one before, gaps longer than a number of minutes you choose, and items with no end time. Overlaps are also marked in red on the printed sheet.",
    },
    {
      question: "Can I tick things off on paper?",
      answer: "Yes. Every row has a small box at the right to tick when the item is done.",
    },
    {
      question: "Is the schedule uploaded anywhere?",
      answer: "No. The sheet is made in your browser and nothing is uploaded or kept.",
    },
  ],
  "guest-list-tracker": [
    {
      question: "How do I improve my RSVP response rate?",
      answer: "Filter the list to Invited and follow up with those guests a set number of weeks before your reply deadline, rather than waiting passively. This tends to get a meaningfully higher response rate than hoping everyone replies on their own.",
    },
    {
      question: "How is People coming calculated?",
      answer: "Every guest whose reply is Confirmed or Attended counts as one person, and that guest's plus-ones are added. Two Confirmed guests with 1 and 0 plus-ones give 3. Invited, Waitlist and Declined guests add nothing, even if their plus-ones box holds a number.",
    },
    {
      question: "What can I import, and what happens to bad rows?",
      answer: "Import reads a CSV, TSV, Excel (XLSX or XLS) or ODS file; for a workbook it uses the first visible sheet, and CSV text may use commas, semicolons or tabs. Only Name is required; Email, Phone, Organisation, Role, Reply, Dietary, Access needs, Needs stay, Arrival, Departure, Table, Plus ones and Notes are optional, in any order, and common spellings such as Full name or RSVP are recognised. A row with no name is skipped. A bad optional value, such as an email without an @, is left blank. The summary lists each problem by spreadsheet row.",
    },
    {
      question: "Which date format does it accept?",
      answer: "Year-month-day, like 2026-11-12. Dates written 01/02/2026 are refused because they mean 2 January in some countries and 1 February in others, and guessing wrong would quietly put a guest in the wrong week.",
    },
    {
      question: "How does the duplicate check work?",
      answer: "Two guests with the same email count as one person. When there is no email, the same name does, ignoring capital letters, extra spaces and accents. The banner lists the matches, and Keep the first of each removes the later copies. Two different people with the same name and different emails are left alone.",
    },
    {
      question: "What is inside the CSV and Excel files?",
      answer: "Both have every column on the list, using the same header names the importer reads, so a downloaded CSV can be imported again unchanged. The Excel sheet is named Guest List and adds a People coming column and summary rows for each reply, head count, rooms needed, dietary notes and access needs.",
    },
    {
      question: "Why can't I type letters into the plus-ones box?",
      answer: "The box removes anything that is not a digit as you type, so a stray character cannot quietly turn the count into zero. For a couple bringing three children, enter 3 as the total number of extra people.",
    },
    {
      question: "Is my guest list still there after I close the page?",
      answer: "Only for this visit: the list is kept while the tab is open and erased when you close it. Nothing is stored online. Use Save project at the bottom to download a project file, and Open project to continue later. Other event tools on the site open the same project.",
    },
  ],
  "mesh-3d-converter": [
    {
      question: "Can this convert a file into GLB and then back out again?",
      answer: "Not yet — GLB (binary glTF) is currently export-only here. You can convert an STL, OBJ, or PLY file into a GLB, but not the other direction.",
    },
    {
      question: "Does this preserve materials, textures, or colors?",
      answer: "No — this covers geometry only (positions and triangle indices). The source formats parsed here don't carry material or texture information either, so there's nothing to preserve on that front.",
    },
    {
      question: "Which formats can it read and write?",
      answer: "It reads STL (ASCII or binary), OBJ, PLY (ASCII or binary, either byte order), GLB and 3MF. It writes STL binary, STL ASCII, OBJ, PLY binary, PLY ASCII, GLB and 3MF. The format is detected from the file extension, and a loose .gltf file is not accepted.",
    },
    {
      question: "Will I keep my colours and textures?",
      answer: "No. Only vertex positions and triangle connections are kept. Colours, textures, material files, UV coordinates and source normals are ignored, and the OBJ, PLY, GLB and 3MF outputs carry none of them. STL files contain face normals worked out from the triangles.",
    },
    {
      question: "Why does my model come out the wrong size?",
      answer: "GLB assumes metres and 3MF assumes millimetres, while STL, OBJ and PLY have no unit. Set Model units to what the source used before converting, and the tool scales to metres for GLB or millimetres for 3MF. For the other targets the setting is not applied to the coordinates.",
    },
    {
      question: "What does the Reduce triangles slider do?",
      answer: "It removes triangles by merging the least important edges first, aiming for the percentage of the original count that you chose, down to 5 percent. A note after conversion shows the before and after counts. Lower values mean smaller files with less detail, so inspect the result.",
    },
    {
      question: "What does the watertight check mean?",
      answer: "After duplicate vertices are merged, the tool counts the faces that meet at each edge. If every edge joins exactly two faces, the mesh is reported watertight and volume and surface area are shown. Otherwise you see the open-edge count and any edges shared by three or more faces. Nothing is repaired unless you tick \"Fill small holes\", which closes small open loops when you convert and re-checks the result.",
    },
  ],
  "mesh-3d-viewer": [
    {
      question: "Why should I check bounding box dimensions before 3D printing?",
      answer: "It's a quick way to catch a model that's the wrong scale entirely — a mesh modeled in millimeters versus inches can be off by a large factor, and checking the dimensions here catches that before you've wasted material printing it.",
    },
    {
      question: "Which 3D formats does it open?",
      answer: "STL in ASCII or binary form, OBJ, PLY in ASCII or either binary byte order, GLB, and 3MF. These are the only extensions the file picker accepts, and anything else produces an unsupported file type message.",
    },
    {
      question: "Why does my model appear as one plain colour?",
      answer: "The viewer reads geometry only. Vertex colours, textures, materials and lighting from the source file are discarded, and every model is shown in the same blue shade with fixed lights so shape and surface detail are easy to judge.",
    },
    {
      question: "What does the watertight check mean?",
      answer: "A mesh is watertight when every edge joins exactly two faces, which is what a slicer needs to treat it as a solid. If it passes, the report adds volume and surface area and notes if the faces point inward. Otherwise it counts open edges.",
    },
    {
      question: "Why is there no bed-fit result for my OBJ?",
      answer: "OBJ and PLY files carry no unit, so the report labels their size as plain units and hides the bed boxes. STL and 3MF are treated as millimetres, so those files show the bed boxes and a fits-or-too-large message.",
    },
    {
      question: "Can I open a .gltf file?",
      answer: "Not as a separate .gltf. The tool accepts the binary GLB form only, which packs geometry into a single file. A .gltf that depends on external files is rejected as an unsupported type, so export or convert it to GLB first.",
    },
  ],
  "stl-ascii-binary-converter": [
    {
      question: "Should I use ASCII or binary STL format?",
      answer: "Binary STL files are significantly smaller than the equivalent ASCII version for the same geometry — if file size matters for storage or transfer, binary is almost always the better choice.",
    },
  ],
  "travel-budget-planner": [
    {
      question: "Should I budget for unexpected costs on a trip?",
      answer: "Add a miscellaneous buffer category for unplanned spending — even careful trip budgets tend to run into a few unexpected costs, and budgeting for that upfront avoids the whole plan feeling blown by one surprise expense.",
    },
  ],
  "travel-countdown-planner": [
    {
      question: "Which pre-trip tasks are easiest to forget?",
      answer: "The tasks closest to departure (packing, checking in for flights) tend to get remembered on their own — it's the tasks 4-8 weeks out, like insurance and bank notifications, that are easiest to forget entirely without a reminder.",
    },
    {
      question: "Is there an official rule for liquids in carry-on bags?",
      answer: "In the US, TSA allows liquids, gels and aerosols in carry-on bags in travel-size containers of 3.4 ounces (100 ml) or less, with each passenger limited to one quart-size bag (the 3-1-1 rule). This tip is general information and rules can change or differ by country and airline, so check the official source before you fly.",
      source: { label: "TSA: liquids, aerosols and gels", url: "https://www.tsa.gov/travel/frequently-asked-questions" },
    },
  ],
  "travel-itinerary-planner": [
    {
      question: "Should every day of the trip be scheduled in detail?",
      answer: "Leave at least one day intentionally loose rather than scheduling every single day tightly — travel plans often shift once you're actually there, and an open day absorbs that naturally instead of derailing the rest of the schedule.",
    },
  ],
  "travel-packing-list": [
    {
      question: "Can I create a packing list for a trip type that isn't in the presets?",
      answer: "Yes — use \"New Trip Type\" to create your own named preset (like \"Ski Trip\" or \"Wedding Guest\") and build its item list from scratch. It's saved on this device for next time.",
    },
    {
      question: "What is on the lists?",
      answer: "Every list starts with five essentials. Beach adds 8 items, Business 7, Camping 8, International 5 and Winter 7. Selecting several types merges them and removes duplicates, so Phone charger or Travel adapter is shown once. Items such as Tent or Visa documents appear only when their type is selected.",
    },
    {
      question: "Can I make my own trip type and edit it later?",
      answer: "Yes. New Trip Type creates a named type that starts empty. While it is the only type selected, items you add join it and stay saved, and the small X beside a custom item removes it. The X on the type button deletes the whole type. Built-in presets cannot be edited.",
    },
    {
      question: "Where is my list stored?",
      answer: "In this tab's memory for the visit only, not on a server; it is erased when you close the tab. Back up saves a JSON file, custom trip types included, that you can load with Restore.",
    },
    {
      question: "What does Copy List give me?",
      answer: "Plain text with one item per line, starting with [x] for packed items and [ ] for unpacked ones, in the order shown on screen. It suits pasting into a chat, notes app or email. Only the items are copied, not the trip type names.",
    },
    {
      question: "Why did my ticks stay when I changed trip type?",
      answer: "Ticks are remembered by item name, not by trip type, so an item with the same name stays ticked wherever it appears. Use Untick all to clear every tick before planning a new trip. Your custom items and selected types are left in place.",
    },
    {
      question: "Is there an official rule for liquids in carry-on bags?",
      answer: "In the US, TSA allows liquids, gels and aerosols in carry-on bags in travel-size containers of 3.4 ounces (100 ml) or less, with each passenger limited to one quart-size bag (the 3-1-1 rule). This tip is general information and rules can change or differ by country and airline, so check the official source before you fly.",
      source: { label: "TSA: liquids, aerosols and gels", url: "https://www.tsa.gov/travel/frequently-asked-questions" },
    },
  ],
  "cost-of-living-comparison": [
    {
      question: "Where does the auto-filled cost data come from?",
      answer: "From the U.S. Bureau of Economic Analysis's Regional Price Parities, a real government cost-of-living index, not a crowdsourced or made-up number — though it's still a metro-area-wide average, not neighborhood-specific, and about a year old like every free cost-of-living source.",
    },
    {
      question: "How do I compare the cost of living between two cities?",
      answer: "Choose your current city and the one you are considering. The monthly figures for housing, food, transport and other categories are filled in from government data, and the tool shows the difference and the salary that would give you the same standard of living. Change any figure that does not match your own spending.",
    },
    {
      question: "What salary do I need to move to a more expensive city?",
      answer: "The equivalent salary is your current pay adjusted for the price difference between the two metros, so your purchasing power stays the same. A raise smaller than that difference is a pay cut in real terms. Compare the result with the offer you have.",
    },
    {
      question: "Which cities does the cost of living comparison cover?",
      answer: "It covers 127 US metropolitan areas using Bureau of Economic Analysis regional price data. For a smaller town, a ZIP code lookup uses the nearest covered metro as an estimate, which is close but not specific to that town.",
    },
    {
      question: "How current is the cost of living data?",
      answer: "The regional price data is from 2023, the latest available when it was added. Housing can move quickly, so check current listings in the neighborhood you are considering before you decide.",
    },
  ],
  "moving-cost-estimator": [
    {
      question: "Is this the same as a real quote from a moving company?",
      answer: "No — treat this as a planning estimate, not a quote. Get actual quotes from real moving companies once you're ready to book, since real pricing varies significantly by company, season, and the specifics of your move.",
    },
  ],
  "relocation-checklist-planner": [
    {
      question: "Which relocation tasks are easiest to forget?",
      answer: "The address-change and utility-transfer tasks are the easiest ones to forget until the last minute — starting those a few weeks earlier than feels necessary avoids a stressful gap in service right after you move.",
    },
  ],
  "avatar-builder": [
    {
      question: "Is this AI-generated, or based on a photo I upload?",
      answer: "Neither — this is entirely parametric and hand-drawn, built from a selection of hair styles, eyes, colors, and accessories you choose. No photo upload or AI image generation is involved.",
    },
    {
      question: "What options can I change?",
      answer: "Background (7 colors), skin tone (6), face shape (round, oval, square), hair style (5), hair color (7), eyes (8), mouth (8) and a Glasses checkbox. That is the full list of controls.",
    },
    {
      question: "What file formats can I download?",
      answer: "PNG at 256, 512 or 1024 pixels square, named like avatar-512.png, and an SVG named avatar.svg whose width and height follow the selected size. Both respect the Circle crop and Transparent background checkboxes.",
    },
    {
      question: "Does Transparent background remove the circle too?",
      answer: "No. Transparent background only drops the colored backdrop. Circle crop is a separate switch that clips the picture to a circle. Used together you get a face on a round shape with clear surroundings.",
    },
    {
      question: "Does Randomize change my export size?",
      answer: "No. It randomizes the look: colors, face, hair, eyes, mouth and glasses. Size, Circle crop and Transparent background stay exactly as you set them, so you can keep rolling until you like the face.",
    },
    {
      question: "Can I use the avatar commercially?",
      answer: "The drawings are created by this page from your selections, so there is no photo or third-party image involved. Check any platform's own rules for profile images, and note that other people can build an identical avatar from the same options.",
    },
  ],
  "emoji-picker": [
    {
      question: "What if my first search doesn't find the emoji I'm looking for?",
      answer: "Try a few different keywords — emoji are tagged by common descriptive words, and different people describe the same emoji slightly differently, so a second search term often finds what the first one missed.",
    },
    {
      question: "How many emoji are in the picker?",
      answer: "The count above the grid tells you how many match your current search and category. With nothing typed it shows the whole data set, which is a little under 2,000 base emoji, including the flags and the people and body group.",
    },
    {
      question: "Why do I only see a small set of emoji at first?",
      answer: "The full list loads a moment after the page opens. If that file cannot be loaded, the picker falls back to a built-in set of about 180 common emoji, and the category chips then show little beyond the first group.",
    },
    {
      question: "Why did the skin tone not change some emoji?",
      answer: "Only emoji that have a full set of five tone variants in the data are switched. Faces, animals, objects, symbols and flags have none, so they appear the same in every tone, and Default always shows the standard yellow versions.",
    },
    {
      question: "What does the search look at?",
      answer: "It checks whether your text appears anywhere in the emoji's English name or in any of its tags, ignoring capital letters. Partial words work, so a short fragment like sun can match several emoji. Words from other languages will not match.",
    },
    {
      question: "Why does a copied emoji look different when I paste it?",
      answer: "What gets copied is the emoji character itself, not a picture. Each app and operating system draws it in its own style, so a pasted emoji can look different from the one in the grid, even though it is the same character.",
    },
  ],
  "emoji-unicode-converter": [
    {
      question: "Why does a single emoji sometimes show multiple codepoints?",
      answer: "Some emoji are actually made up of multiple combined Unicode codepoints, like a family emoji, which combines several individual person emoji into one visual symbol. This tool shows each underlying codepoint separately for exactly that reason.",
    },
  ],
  "sprite-sheet-tool": [
    {
      question: "Do my source images need to be the same size before combining them?",
      answer: "Yes — keep all your source frames the same pixel dimensions before combining them into a sheet. Mismatched frame sizes will throw off the grid alignment in the combined result.",
    },
    {
      question: "In what order are frames placed in the sheet?",
      answer: "In the order the files were supplied by your selection, left to right and then top to bottom. Naming files with leading zeros, like run_01 and run_02, helps because many file pickers list names alphabetically.",
    },
    {
      question: "What if my frames are different sizes?",
      answer: "Every cell takes the width of the widest frame and the height of the tallest. Smaller frames are centered in their cell at their original size, with no stretching, and smoothing is turned off so pixel art stays crisp.",
    },
    {
      question: "How are split frames named and saved?",
      answer: "As PNG files called frame_01.png, frame_02.png and so on, numbered row by row from the top left, and delivered together in sprite-frames.zip. The preview thumbnails follow the same order.",
    },
    {
      question: "Can the tool work out my sheet's grid by itself?",
      answer: "No. You type the number of Columns and Rows, and the sheet is divided into equal cells. If the tiles have spacing between them, type that gap in Padding (px) and the slices line up; outer margins are not handled, so crop them first.",
    },
    {
      question: "Can it write a JSON atlas, padding or extruded edges?",
      answer: "Yes. Padding (px) leaves a gap between frames, Extrude (px) repeats each frame's edge pixels outwards so game engines do not bleed neighbours into a sprite, and the optional JSON atlas lists every frame's x, y, width and height in the TexturePacker JSON (Hash) layout. With both at 0 you get the plain grid. It does not trim or rotate frames.",
    },
    {
      question: "How do I save the combined sheet?",
      answer: "Press Open / Save Sprite Sheet and the PNG opens in a new browser tab, from which you can use the browser's save option. The result is a transparent PNG with no extra metadata.",
    },
  ],
  "banner-sign-maker": [
    {
      question: "Can this create a banner with a custom photo or logo?",
      answer: "Yes — upload a background photo and/or a separate logo/image, and both get placed directly into the banner alongside your text. A design built entirely from custom illustration (rather than a photo or logo you already have) is a different kind of tool, one that needs AI image generation.",
    },
    {
      question: "Can I change the font?",
      answer: "Yes — choose from a small set of genuinely distinct fonts (a default sans-serif, a serif option, a wider sans-serif, and a rounded display face), applied to all the text on the banner.",
    },
    {
      question: "Is there a preset for a YouTube channel banner specifically?",
      answer: "Yes — 2560\u00d71440, YouTube's own required channel art dimensions, alongside the existing YouTube Thumbnail preset (1280\u00d7720) for video thumbnails specifically.",
    },
    {
      question: "Can I set an exact print size in inches, not just pixels?",
      answer: "Not directly — dimensions are set in pixels only, with no DPI concept. For something meant to be printed at a specific physical size, check with your print shop about the pixel dimensions they need at their intended DPI, since pixels and inches aren't the same thing without a DPI target connecting them.",
    },
    {
      question: "Can I set completely custom dimensions instead of using a preset?",
      answer: "Yes — enter any width and height in pixels directly, not just the listed presets.",
    },
    {
      question: "Are my uploaded photos or logos sent to a server?",
      answer: "No — the background photo, logo, and the final banner rendering all happen entirely in your browser. Nothing is uploaded anywhere.",
    },
    {
      question: "What file format does the banner download as?",
      answer: "PNG — a real image file, not a screenshot or a low-resolution preview.",
    },
    {
      question: "Can I remove or replace a background photo or logo after adding it?",
      answer: "Yes — an X button appears on each once uploaded, letting you remove it and upload a different one without starting the whole banner over.",
    },
    {
      question: "Which presets are there and what sizes are they?",
      answer: "Web Banner 1200x300, Social Post 1080x1080, Storefront Sign 1200x800, Large Poster 1000x1400, Instagram Story 1080x1920, YouTube Thumbnail 1280x720, YouTube Channel Banner 2560x1440, Facebook Cover 851x315, X Post 1200x675, LinkedIn Post 1200x627, Yard Sign 3600x2700 and Flyer (A4) 1240x1754, all in pixels.",
    },
    {
      question: "What does the DPI line mean?",
      answer: "It divides the banner's pixel width by the finished width you typed. Below 150 it turns amber and warns the print will look soft, from 150 up it says the size suits large signs seen from a distance, and at 300 or more it reads print quality. The Yard Sign preset at 24 inches comes to 150.",
    },
    {
      question: "What does the bleed checkbox do?",
      answer: "Ticked, the PDF page is the finished size plus 0.125 inch on every side, with the edges of the design stretched into that margin so no white sliver appears after cutting. Unticked, the page is the exact finished size. Keep important text well inside either way.",
    },
    {
      question: "Can I use my own font?",
      answer: "No. The Font menu offers only Arial, Georgia, Verdana and Trebuchet, which are read from your device. If you need a special typeface for a logo-style heading, add it to the Logo / Image upload as a picture instead.",
    },
    {
      question: "Why does my photo look tinted?",
      answer: "When a Background Photo is present, the colour gradient is drawn over it at 55 percent opacity so the white text stays readable. Choose a darker gradient swatch for more contrast, or a lighter photo with quiet areas behind the words.",
    },
  ],
  "brochure-flyer-maker": [
    {
      question: "What's the most important design decision on a flyer?",
      answer: "Lead with the single most compelling point first, not last — a flyer typically gets a few seconds of attention, so the most important message needs to be immediately visible, not buried in a bullet list.",
    },
  ],
  "invitation-card-maker": [
    {
      question: "Does this generate a calendar reminder for guests?",
      answer: "Yes — setting the structured event date and time generates a real calendar file (.ics) that guests can add to their own calendar app, defaulting to a 1-day-before reminder they can adjust once added.",
    },
  ],
  "menu-card-maker": [
    {
      question: "Can I create a multi-page menu?",
      answer: "Yes — check \"Start a new printed page here\" on any section after the first to force a page break when using Print/Save as PDF. Note that Download as Image produces one continuous image instead, since a single PNG has no real concept of separate pages.",
    },
    {
      question: "How do I make a menu with more than one page?",
      answer: "Tick 'Start a new printed page here' on any section after the first, then press Print / Save as PDF and choose Save as PDF in the browser's print dialog. Page margins are 0.5 inch, and the theme colours are set to print exactly as shown.",
    },
    {
      question: "What is the difference between the image and PDF buttons?",
      answer: "Download as Image gives one tall PNG sized for about 300 pixels per inch at 8.5 inches wide. Download as PDF (Letter) fits the whole menu onto one Letter page with a 0.5 inch margin. The print-shop PDF scales it to a finished width you enter, from 2 to 24 inches.",
    },
    {
      question: "What does the print-shop PDF add?",
      answer: "Its page is the finished size plus 0.125 inch of bleed on each side, with the edge of the design extended into the bleed and the trim and bleed boxes set. The default finished width is 5.5 inches. Check your printer's own bleed and colour-mode requirements first.",
    },
    {
      question: "Which images can I add?",
      answer: "You can add a logo above the heading, a background photo behind the whole menu and a small thumbnail beside each item. The photo is covered by an overlay in the background colour so the text stays legible, which means it appears faint.",
    },
    {
      question: "Is the text in my saved file searchable?",
      answer: "Only if you save through the browser's Print / Save as PDF, which keeps real text. Download as Image, Download as PDF (Letter) and the print-shop PDF are pictures of the menu, so their words cannot be selected, copied or searched.",
    },
  ],
  "password-strength": [
    {
      question: "Does a strong password check mean my account is safe?",
      answer: "Not entirely — a strength checker can only evaluate patterns, not whether a password has already been leaked in a data breach. Using a unique password for every account matters at least as much as the password's raw complexity.",
    },
    {
      question: "What is the crack time based on?",
      answer: "The tool reports the library's offline slow-hashing scenario: an attacker with a stolen database and many guesses per second, against a password stored with a deliberately slow hash. It is a rough comparison figure, not a prediction for your account.",
    },
    {
      question: "How random is a generated password?",
      answer: "Characters are picked with the browser's cryptographic random generator, using rejection sampling to avoid bias. If the length allows, the tool retries until each switched-on character type appears at least once.",
    },
    {
      question: "Why does a long password still score low?",
      answer: "Length counts for less when the content follows a pattern. A repeated block, a keyboard run, a common word or a date is cheap to guess, so the estimator discounts it. The warning line under the bar names the pattern it found.",
    },
    {
      question: "Does it check whether my password has been leaked?",
      answer: "No. It only recognizes patterns and common words inside its bundled lists, and it never contacts a breach database. A password can score Very strong and still be exposed if it was used somewhere that got breached.",
    },
    {
      question: "Is my whole password analyzed if it is very long?",
      answer: "Only the first 100 characters are passed to the estimator, because the analysis gets slow on very long input. The rest of the text is ignored for scoring, but it stays in the box and is copied by the copy icon.",
    },
  ],
  "url-encoder": [
    {
      question: "Does every character need to be URL-encoded?",
      answer: "No — encoding matters most for characters like spaces, ampersands, and special symbols that have specific meaning in a URL's structure. Plain alphanumeric text generally doesn't need encoding at all.",
    },
    {
      question: "What is the difference between Whole URL mode on and off?",
      answer: "Off, the tool escapes everything except letters, digits and a few marks such as hyphen, underscore, period and tilde, so it is right for one value. On, characters that give a URL its structure, like colon, slash, question mark, ampersand, equals and hash, stay as typed, so a full address is not broken.",
    },
    {
      question: "Why did a plus sign turn into a space when I decoded?",
      answer: "The Decode + as a space checkbox is on by default, and it replaces every plus sign with a space before decoding. That matches how HTML forms write spaces. Untick it if your text contains real plus signs, such as in a phone number or a math expression.",
    },
    {
      question: "Why does Decode say Invalid encoded string?",
      answer: "The browser's decoder rejects a percent sign that is not followed by two hexadecimal digits, and byte sequences that are not valid UTF-8. Look for a stray percent sign, such as a literal 50% in the middle of text, and encode that part first.",
    },
    {
      question: "Are spaces encoded as plus signs or as %20?",
      answer: "Always as %20. The Encode button never writes a plus sign for a space. Many servers accept %20 in both paths and query strings, but if a form handler expects plus signs, replace them yourself after encoding.",
    },
    {
      question: "How are accented letters and emoji encoded?",
      answer: "As UTF-8 bytes, each written as a percent sign and two hex digits. An e with an acute accent becomes %C3%A9, and an emoji becomes four such groups. Decode reverses this, provided the bytes form valid UTF-8.",
    },
  ],
  "hash-generator": [
    {
      question: "Is MD5 safe to use for security purposes?",
      answer: "No — MD5 and SHA-1 are considered cryptographically broken for security purposes, though still fine for basic integrity checks. Use SHA-256 or stronger if the hash needs to actually resist tampering, not just detect accidental corruption.",
    },
    {
      question: "Which algorithms does the tool calculate?",
      answer: "MD5, SHA-1, SHA-256, SHA-384 and SHA-512, all in one go. There is no option to pick only one, so just copy the row you need.",
    },
    {
      question: "Why doesn't my text hash match the one from my terminal?",
      answer: "Most often a trailing newline differs. Commands like echo add a line break unless told not to, while the text box hashes exactly what you typed. The tool encodes text as UTF-8, so other encodings give different values.",
    },
    {
      question: "Can I hash a large file?",
      answer: "Yes. Files are read in 4 megabyte pieces rather than loaded whole, and a percentage shows progress. Very big files still take time and depend on your device, but there is no upload size limit imposed by a server.",
    },
    {
      question: "How does the checksum comparison work?",
      answer: "Your pasted value is trimmed, lowercased and stripped of spaces, then compared against all five results. If one agrees, the page shows Match plus that algorithm's name. Otherwise it says there is no match.",
    },
    {
      question: "Can I reverse a hash to get the original text?",
      answer: "No. A hash is a one-way fingerprint. Short or common inputs can sometimes be guessed by trying candidates, but this tool only creates hashes and has no way to decode them.",
    },
  ],
  "jwt-decoder": [
    {
      question: "Does decoding a JWT here confirm it's a valid, untampered token?",
      answer: "No — decoding reveals a JWT's contents but doesn't verify its cryptographic signature. A token can be decoded and read without confirming it's actually valid or unaltered.",
    },
    {
      question: "Does this tool verify the signature of the token?",
      answer: "No. It decodes the header and payload and nothing more; no key is involved and the signature part is not even shown. A token with a tampered payload decodes just as easily as a genuine one, so verification must happen in your own backend.",
    },
    {
      question: "Why does it say Invalid JWT format (expected 3 parts)?",
      answer: "The text split on dots did not produce exactly three pieces. Typical causes are a copied token cut short, an extra space or quote, a Bearer prefix, or an encrypted token, which has five parts.",
    },
    {
      question: "What do the Issued at, Not before and Expires lines mean?",
      answer: "They come from the iat, nbf and exp claims, which hold seconds since 1970. The tool shows each as a UTC time. Not before is the earliest moment the token should be accepted, and Expires is when it stops being valid.",
    },
    {
      question: "Can it show a payload with non-English characters?",
      answer: "Yes. The decoded bytes are read as strict UTF-8, so names and messages in other scripts display correctly. If the bytes are not valid UTF-8, the tool reports Failed to decode JWT instead of showing garbled text.",
    },
    {
      question: "Why is a token marked Expired when my server still accepts it?",
      answer: "The marker compares exp with your device's clock. If your computer's time is off, or the server allows some clock leeway, the two verdicts can differ. Check the exact timestamp shown in the Expires line against a trusted clock.",
    },
  ],
  "uuid-generator": [
    {
      question: "Can UUIDs generated separately in different places collide?",
      answer: "In practice, no — standard UUIDs are designed to be unique across systems without any central coordination. You can generate them independently in different places and trust they won't collide.",
    },
    {
      question: "How many UUIDs can I generate at once?",
      answer: "Up to 1,000 per press of Generate. If you enter a larger number, the tool shows a note and produces 1,000. Entering zero, a negative number, a decimal or text shows a message asking for a whole number from 1 up, and nothing is generated until you fix it.",
    },
    {
      question: "Which UUID version does this create?",
      answer: "Version 4, which is built from random numbers. The tool does not offer version 1 (time based), version 5 (name based) or the newer time-ordered versions.",
    },
    {
      question: "Could two generated UUIDs ever be the same?",
      answer: "A repeat is astronomically unlikely, because a version 4 UUID has 122 random bits. For normal projects you can treat them as unique without checking, though a database unique constraint is still good practice.",
    },
    {
      question: "Can I get uppercase or braces around them?",
      answer: "Uppercase, yes: tick Uppercase, and untick Hyphens if you want the 32 characters without dashes. Braces, no: the tool does not add them. Paste the list into any text editor and use find and replace if another system needs braces.",
    },
    {
      question: "Is the list kept after I leave the page?",
      answer: "No. The values live only in the page while it is open. Reloading, or pressing Generate again, replaces them, so copy the ones you want to keep first.",
    },
  ],
  "html-entity-converter": [
    {
      question: "Why do angle brackets need to be encoded in HTML?",
      answer: "Characters like angle brackets and ampersands have special meaning in HTML itself — encoding them correctly (like converting < into &lt;) prevents them from being misinterpreted as actual HTML markup.",
    },
  ],
  "flatten-pdf": [
    {
      question: "Does this work on any PDF, or only ones from this site's own tools?",
      answer: "Any PDF with real fillable form fields works — whether it was filled in using this site's Sign PDF or Fillable Forms tools, Adobe Acrobat, or a browser's built-in PDF viewer. If a PDF has no fillable fields at all, there's nothing here to flatten.",
    },
    {
      question: "Can I undo a flatten if I make a mistake?",
      answer: "Not from the flattened copy itself — flattening is one-way. Keep your original, still-editable file until you're sure the flattened version is exactly what you want to send.",
    },
    {
      question: "Does this also flatten annotations, highlights, or comments, not just form fields?",
      answer: "No — this specifically flattens fillable form field values (text fields, checkboxes, signature fields). Other annotation types like highlights, sticky notes, or comments stay as separate, still-editable annotations in the output, not locked in place by this tool.",
    },
    {
      question: "What does the timestamp on each page actually prove?",
      answer: "It records when flattening happened, giving anyone reviewing the document later a real, verifiable record to check against. It can't prove who flattened the file, since there's no server here to verify identity — it's a timestamp of the action, not a certified signature of the person who performed it.",
    },
    {
      question: "What's the difference between this and Sign PDF?",
      answer: "Sign PDF is for adding a signature (or other fields) to a document. Flatten PDF is the step after — locking in whatever's already been filled in or signed so it can't be changed further. Use Sign first to fill in a document, then Flatten to lock the result.",
    },
    {
      question: "Are my files uploaded to a server?",
      answer: "No — flattening runs entirely in your browser, including generating the page previews you see before committing. Your PDF never leaves your device.",
    },
    {
      question: "What happens if the PDF has no fillable fields at all?",
      answer: "Flattening fails with a clear message explaining there's nothing to flatten, rather than silently producing an unchanged file — so you know right away if this tool genuinely doesn't apply to your document instead of wondering why the output looks the same as the input.",
    },
    {
      question: "What should I do after flattening if the file also needs to be smaller or password-protected?",
      answer: "Use Compress PDF to shrink the file for easier sharing, or PDF Password to require a password to open it — either works fine on the flattened result, since flattening only locks form field content, not the file's overall size or access.",
    },
    {
      question: "What exactly gets flattened?",
      answer: "Interactive form fields: text boxes, checkboxes, radio buttons, dropdowns and similar. Their current values are drawn into the page and the field objects are removed. Highlights, comments and other annotations stay as they were, and ordinary text and images are untouched.",
    },
    {
      question: "Does it break digital signatures?",
      answer: "Yes. If signature fields are present, a warning appears before you start. Rewriting the file changes it, so a cryptographic signature stops validating. Keep the signed original, and flatten a copy only when you do not need the signature to verify.",
    },
    {
      question: "Why is the Flatten PDF button disabled?",
      answer: "The tool counts fields as soon as you drop the file. If it finds none, it says the PDF may already be flattened and leaves the button off. It also stays off while the page previews are still loading.",
    },
    {
      question: "What does the optional date note look like?",
      answer: "Small grey text, 6.5 points, near the bottom left of every page, reading Flattened on followed by the UTC date and time. It comes from your device clock and does not show who flattened the file.",
    },
    {
      question: "What if a field's answer cannot be drawn?",
      answer: "The tool stops before changing anything, names up to three fields it could not draw, and produces no file, so no entry is silently lost. Correct that field in a form viewer and run it again.",
    },
  ],
  "image-quality-enhancer": [
    {
      question: "What does each option actually change?",
      answer: "Sharpness applies an edge-aware unsharp mask. Clarity gives a mild contrast and saturation lift. Color balance scales red, green and blue so the average colour becomes neutral. Shadow remove lifts pixels darker than about the middle grey. Pixel increase resizes the canvas to 1.5, 2 or 4 times its width and height, and shows the output size before you apply.",
    },
    {
      question: "Will upscaling make my blurry photo sharp?",
      answer: "No. Pixel increase makes more pixels by smoothing between the existing ones, so the picture gets bigger but not more detailed. A blurry source stays blurry at 2x or 4x. Sharpness afterwards can improve edge contrast, but cannot bring back detail that was never recorded.",
    },
    {
      question: "What do the Amount and Noise protection sliders do?",
      answer: "Amount sets how strongly real edges are boosted. Noise protection sets how big a difference from the blurred copy must be to count as an edge; below that, areas are pulled slightly smoother instead of sharpened. Raise it for grainy or heavily compressed photos.",
    },
    {
      question: "Can I apply several adjustments together?",
      answer: "Yes. Tick as many as you like and press Apply once; they run in order from upscale to shadows. You can also press Apply again afterwards to work on the result; the ticks clear after each Apply, so nothing is applied a second time by accident. Apply stays off until something is ticked or an upscale is chosen.",
    },
    {
      question: "What format is the result?",
      answer: "It matches your original: JPEG stays JPEG at 92 percent quality, PNG stays PNG, and iPhone HEIC photos are saved as JPG. If the browser cannot write the original type, the file is saved as PNG and named accordingly. The name gets -enhanced added (for example photo-enhanced-2x.jpg), so it never overwrites your original.",
    },
  ],
  "universal-file-converter": [
    {
      question: "What files can I convert, and what can each become?",
      answer: "Image: PDF, PNG, JPG, WebP, ICO, Base64 text, Text (OCR). HEIC photo: PDF, PNG, JPG, WebP, ICO, Base64 text, Text (OCR). PDF: JPG, Excel, Word, Text, HTML. Text file: PDF, Word, HTML. Spreadsheet: PDF, CSV, JSON, Word (table). CSV: Excel, JSON, PDF. PowerPoint presentation: PDF, JPG. Word document: PDF, Text, HTML. HTML file: PDF, Markdown, Text. Markdown file: HTML, PDF.",
    },
    {
      question: "Are my files uploaded?",
      answer: "No. Conversion runs in your browser and your files are not uploaded for converting. Text (OCR) downloads its English language data the first time it runs, and PDF output with text can load a font file from this site, but your document contents are not sent.",
    },
    {
      question: "Why isn't my format listed?",
      answer: "The page only offers conversions that really work, so a format is missing when there is no reliable way to do it in a browser. Video, audio, SVG, GIF and TIFF files are not accepted here. For video use Convert Video, for audio the Audio Converter and for SVG Convert Image.",
    },
    {
      question: "Can I keep editing after a conversion?",
      answer: "Yes. Every step is kept as its own stage with a Download button, starting with Original. Conversions and edits act on the newest stage, so you can crop an image, convert it to PDF, then add page numbers. Edits apply only to images and PDFs and wait for your Apply or Deny.",
    },
    {
      question: "How does Multiple files mode work?",
      answer: "Switch the toggle, add up to 20 files in any mix of types, then use Convert all to or choose a format per file. Results can be downloaded singly or as one zip, merged into one PDF when everything is a PDF or an image, or merged into one text file when all are .txt.",
    },
    {
      question: "Why did PDF to Excel say no tables were found?",
      answer: "This route reads real tables from the PDF's text layer and does not run OCR. A scanned PDF, or a document of prose with no table structure, returns the message that no tables were found. For scans, try the site's dedicated OCR PDF tool instead.",
    },
    {
      question: "What do the PDF compression levels do?",
      answer: "There are four modes. Balanced tidies the file and shrinks the pictures inside, first to about 140 dpi and then picture by picture, keeping text selectable. If a stronger mode finds the file is already about 98% smaller, it says so and offers to run the heavier levels anyway. Aggressive also turns pages into pictures in finer steps. Extreme also converts pages to grey at 70 dpi and then to black and white. Target size runs the steps until your limit is met and stops there. Every level is a complete file you can download. If the file is already compact and no real saving is possible, the tool tells you and keeps the original.",
    },
  ],
  "text-cleaner": [
    {
      question: "What counts as an invisible character?",
      answer: "Zero-width spaces, zero-width joiners, the word joiner, the byte-order mark, soft hyphens and left-to-right or right-to-left direction marks. They are not visible but can break searches, comparisons and file names. Normal spaces, tabs and line breaks are not touched by this option.",
    },
    {
      question: "Why does my text come out as one long paragraph after joining lines?",
      answer: "Joining treats a blank line as the end of a paragraph and merges every other line break into a space. If your text has no blank lines between paragraphs, everything is one paragraph. Add a blank line between paragraphs first, or leave the join option off.",
    },
    {
      question: "Will it remove the formatting from text I paste?",
      answer: "The box only holds plain text, so bold, colours and fonts are already gone when you paste. What the cleaner adds is tidying of the characters themselves: spaces, line breaks, quotes, dashes and hidden characters.",
    },
    {
      question: "Does it change my words?",
      answer: "No. Only whitespace, quote and dash characters, invisible characters, bullets and HTML tags are changed. Your words, numbers and punctuation such as full stops and commas are left as they were.",
    },
    {
      question: "Is the text I paste sent anywhere?",
      answer: "No. The cleaning happens in your browser and the text is not uploaded or saved.",
    },
  ],
  "number-to-words": [
    {
      question: "Does it use the American or the British way of saying numbers?",
      answer: "Both are available. The default is American ('one hundred one'). Switch on British 'and' for 'one hundred and one'. The scale is the short scale in both cases, so one billion means a thousand million.",
    },
    {
      question: "What is the largest number it can write?",
      answer: "Numbers up to 21 digits, which reaches the quintillions. Anything longer is refused with a message rather than being written wrongly.",
    },
    {
      question: "How are decimals written?",
      answer: "Digit by digit after the word 'point', so 12.05 becomes 'twelve point zero five'. For money, use the cheque line, which writes the part after the decimal as cents, pence or paise.",
    },
    {
      question: "How does the cheque amount round?",
      answer: "It uses two decimal places and rounds half up, so 0.999 becomes one dollar only and 2.005 becomes two dollars and one cent. Check the result against the figure you plan to write.",
    },
    {
      question: "Why is there no ordinal for my number?",
      answer: "Ordinals such as 'twenty-first' are shown only for whole numbers that are not negative. Decimals and negative numbers have no ordinal form.",
    },
  ],
  "morse-code-translator": [
    {
      question: "How do I separate letters and words in Morse?",
      answer: "Use a single space between letters and a slash with spaces around it (/) or three spaces between words. The translator writes words with ' / ' and reads either style.",
    },
    {
      question: "Which characters are supported?",
      answer: "Letters A to Z, digits 0 to 9, and the punctuation . , ? ' ! / ( ) & : ; = + - _ \" $ @. Characters without a code are listed under the box and left out.",
    },
    {
      question: "What does the speed setting mean?",
      answer: "It is in words per minute using the standard PARIS timing. At 15 WPM a dot lasts 80 milliseconds, at 20 WPM 60 milliseconds. The dash, gaps and word spaces scale with it.",
    },
    {
      question: "Why can't I hear anything when I press Play?",
      answer: "Browsers only start audio after a click, so press Play once more if needed. Check that your device volume is up and it is not muted. The tone is 620 Hz, which small phone speakers play quietly.",
    },
    {
      question: "Can it decode Morse with different symbols?",
      answer: "It accepts dots and dashes, plus middle dots (· or •) for dots and en dashes, em dashes or underscores for dashes. A code it cannot match is shown as a question mark and listed.",
    },
  ],
  "sql-formatter": [
    {
      question: "Does formatting change what my query does?",
      answer: "No. Only spacing, line breaks, indentation and, if you choose, the capitalisation of keywords change. Names, strings, numbers and operators stay the same.",
    },
    {
      question: "Which SQL dialects are supported?",
      answer: "Standard SQL, PostgreSQL, MySQL, MariaDB, SQLite, SQL Server (T-SQL), Oracle (PL/SQL), BigQuery and Snowflake. Choosing the right one matters for dialect-specific quoting and functions.",
    },
    {
      question: "Why does it say it could not format my query?",
      answer: "The formatter reads the statement before laying it out and stops if it meets something it does not recognise, such as an unclosed quote or syntax from another dialect. Try a different dialect, or check the query near the position in the message.",
    },
    {
      question: "Does it check that my SQL is valid?",
      answer: "No. It reads the structure well enough to indent it but does not know your tables or whether the database would accept it. Run the query in your own database to validate it.",
    },
    {
      question: "Is my query uploaded?",
      answer: "No. It is formatted in your browser and is not sent or stored, which matters if it contains internal table or column names.",
    },
  ],
  "curl-converter": [
    {
      question: "Where do I get a cURL command from?",
      answer: "In the browser's developer tools open the Network tab, right-click a request and choose Copy as cURL. API documentation often shows ready cURL examples as well.",
    },
    {
      question: "Which cURL options are converted?",
      answer: "The URL, method, headers, data and JSON bodies, form fields and file uploads, basic auth, user agent, referer, cookies, insecure mode and following redirects. Anything else is listed as not converted.",
    },
    {
      question: "Why does the code include my token or cookie?",
      answer: "Because the command did. The converter copies what it is given. Remove or replace secrets such as tokens and cookies before sharing the code, and prefer environment variables.",
    },
    {
      question: "Does it make a request to the address?",
      answer: "No. It only rewrites the command as code. Nothing is sent to the URL and nothing is uploaded.",
    },
    {
      question: "Does the fetch code work in Node as well as the browser?",
      answer: "Yes for current Node versions, which include fetch. Basic auth uses btoa, which is also available there. The file upload example refers to a browser file input and needs adapting in Node.",
    },
  ],
  "subnet-calculator": [
    {
      question: "What is the difference between the subnet mask and the wildcard mask?",
      answer: "The subnet mask has ones for the network part (255.255.255.0 for /24). The wildcard mask is its inverse (0.0.0.255) and is used by some router access lists to say which bits may vary.",
    },
    {
      question: "Why are there two fewer usable hosts than addresses?",
      answer: "The first address identifies the network and the last is the broadcast address, so neither can be given to a device. The exceptions are /31 (two usable, for point-to-point links) and /32 (a single host).",
    },
    {
      question: "Can I enter a mask instead of a prefix?",
      answer: "Yes. Use the address, a slash and a dotted mask like 255.255.240.0, or type the address and the mask. The mask must be contiguous; something like 255.0.255.0 is rejected.",
    },
    {
      question: "Does it support IPv6?",
      answer: "Not yet. This calculator is for IPv4 addresses and prefixes from /0 to /32.",
    },
    {
      question: "How does splitting a network work?",
      answer: "Enter a longer prefix than the current one. A /24 split with 26 gives four /26 subnets, each with its own network, first, last and broadcast address. The tool lists up to 256 subnets at a time.",
    },
  ],
  "chmod-calculator": [
    {
      question: "What do the numbers in 755 mean?",
      answer: "Each digit is the sum of read (4), write (2) and execute (1). The three digits are owner, group and others, so 755 is owner read-write-execute and group and others read-execute (rwxr-xr-x).",
    },
    {
      question: "What is the difference between 644 and 755?",
      answer: "644 gives the owner read and write and everyone else read only, which suits normal files. 755 adds execute for everyone, which suits programs and folders.",
    },
    {
      question: "What are setuid, setgid and sticky?",
      answer: "Special bits shown as a leading fourth digit. Setuid (4) and setgid (2) make a program run as its owner or group. Sticky (1) on a shared folder lets only a file's owner delete it, as on /tmp (1777).",
    },
    {
      question: "Why does the symbolic text show s, S, t or T?",
      answer: "A lower-case s or t means the special bit is set and execute is also set. An upper-case S or T means the special bit is set but execute is not, which is usually a mistake worth checking.",
    },
    {
      question: "Does this change files on my computer?",
      answer: "No. It only calculates the value and writes the command for you to run yourself in a terminal.",
    },
  ],
  "string-escape": [
    {
      question: "What is the difference between escaping for JSON and for JavaScript?",
      answer: "JSON needs double quotes, backslashes and control characters escaped. JavaScript strings can also be single-quoted or template literals, so this option escapes single quotes, backticks and the ${ marker as well.",
    },
    {
      question: "Why are the outer quotes missing?",
      answer: "For JSON, JavaScript and Python only the inside of the string is escaped so you can place it between whichever quotes you use. CSV and shell escaping include their quotes because they are part of the rule.",
    },
    {
      question: "Is the SQL escape enough to stop SQL injection?",
      answer: "No. Doubling single quotes is a quick manual aid. In real programs use parameterised queries or prepared statements, which are the dependable defence.",
    },
    {
      question: "What does the URL option encode?",
      answer: "It uses percent-encoding for everything except letters, digits and - _ . ! ~ * ' ( ), the same as encodeURIComponent. That is right for a single query value, not for a whole address.",
    },
    {
      question: "Why did unescaping give an error?",
      answer: "The text has an incomplete or invalid sequence, such as a \\u followed by fewer than four hex digits, or a percent code like %E0%A4%A that is cut short. Fix or complete the sequence and try again.",
    },
  ],
  "unlock-pdf": [
    {
      question: "Can this tool crack a PDF password I forgot?",
      answer: "No. To open a file that asks for a password, you must type the correct one; this tool does not guess or crack passwords. If the file opens without a password and only blocks printing or copying, those restrictions are removed. Use this only on documents you are allowed to change.",
    },
    {
      question: "What if my PDF opens fine but won't let me print or copy?",
      answer: "That means it has restrictions but no open password. Drop it here and it unlocks at once, without asking for a password; the copy you download has no restrictions. Do this only for files you are allowed to change.",
    },
    {
      question: "Will unlocking change how my document looks?",
      answer: "No. Every page keeps its size, text, images and links, and bookmarks and the document title are kept too. Keep your original anyway, because the unlocked copy no longer has any protection.",
    },
    {
      question: "Is the password sent to a server?",
      answer: "No. The password is used only in your browser to open the file, and nothing is uploaded.",
    },
    {
      question: "Why does it say my PDF is not protected?",
      answer: "The file has no password or restriction to remove. If another program still asks for a password, the file might be a different type, or you may be looking at a different copy.",
    },
  ],
  "remove-pdf-pages": [
    {
      question: "Does removing pages change the other pages?",
      answer: "No. The pages you keep are copied as they are, in the same order, with the same size, text and images.",
    },
    {
      question: "Can I remove several ranges at once?",
      answer: "Yes. Type them separated by commas, for example 1, 4-6, 10. You can mix clicking on thumbnails with typing; both stay in step.",
    },
    {
      question: "Can I remove every page?",
      answer: "No, at least one page must remain. If you want an empty result there is nothing to build, so the tool stops and tells you.",
    },
    {
      question: "Is the removed content really gone from the file?",
      answer: "The removed pages are not in the new file. This is a page-removal tool, not a redaction tool: it does not clean information that is stored elsewhere in the document, such as the title or file properties.",
    },
    {
      question: "What if my PDF is password protected?",
      answer: "Unlock it first with Unlock PDF, then remove the pages. A protected or damaged file may not open here and the tool tells you.",
    },
  ],
  "exif-remover": [
    {
      question: "What is EXIF data?",
      answer: "It is information a camera or phone stores inside a picture file: when and where it was taken, the device model, settings and sometimes the software used. Anyone who receives the file can read it.",
    },
    {
      question: "Does removing metadata reduce picture quality?",
      answer: "No. JPEG, PNG and WebP files are cleaned by taking out the metadata sections without decoding or re-compressing the picture, so the image is unchanged.",
    },
    {
      question: "Why do you keep a rotation flag?",
      answer: "Many phones save a portrait photo sideways plus a note saying how to turn it. Without the note it would show sideways. The tool keeps only that one number; untick the box to remove it too.",
    },
    {
      question: "Does it work with iPhone HEIC photos?",
      answer: "Not directly. Convert them to JPEG with Convert Image Format first, which also leaves the camera data behind, then run the EXIF Remover if you want to check the result.",
    },
    {
      question: "Is my photo uploaded to be cleaned?",
      answer: "No. Everything happens in your browser and the files never leave your device.",
    },
  ],
  "image-splitter": [
    {
      question: "How does the Instagram order option work?",
      answer: "Instagram shows the newest post top-left. With the option ticked, files are named post01, post02 and so on, from the bottom-right tile to the top-left. Post them in that order and the grid builds up correctly.",
    },
    {
      question: "Will the tiles fit together exactly?",
      answer: "Yes. Together the tiles cover the whole picture with no gaps or overlaps. If the size does not divide evenly, some tiles are one pixel larger than others.",
    },
    {
      question: "Can I choose PNG or JPEG?",
      answer: "Yes. PNG keeps full quality and transparency; JPEG makes smaller files and fills transparent areas with white. Choose PNG when you will process the tiles further.",
    },
    {
      question: "Is there a limit on rows and columns?",
      answer: "Up to 20 rows and 20 columns, and never more tiles than pixels across the picture. Very large pictures with many tiles take longer and need more memory.",
    },
    {
      question: "Are my pictures uploaded?",
      answer: "No. The picture is cut and zipped in your browser and never leaves your device.",
    },
  ],
  "mute-video": [
    {
      question: "Does muting reduce video quality?",
      answer: "Usually not. For common MP4 and MOV files with H.264 picture the video is copied as it is and only the audio is dropped. If the picture format cannot be copied, it is re-encoded and the page tells you.",
    },
    {
      question: "What happens if my video has no sound already?",
      answer: "The tool tells you there is nothing to remove instead of producing a copy.",
    },
    {
      question: "Can I get the sound back later?",
      answer: "No. The new file has no audio. Keep your original if you might need the sound.",
    },
    {
      question: "Is my video uploaded?",
      answer: "No. It is processed in your browser. Only the video engine itself is downloaded once, about 25 MB, from a public CDN.",
    },
    {
      question: "Why did my phone video fail to process?",
      answer: "Some .mov files from phones use a codec the browser engine cannot read. Exporting or sharing the video from the phone's own app usually produces a file that works.",
    },
  ],
  "rotate-video": [
    {
      question: "Does rotating lose quality?",
      answer: "The video has to be re-encoded, so it is not bit-identical. The tool uses a high-quality setting, so the difference is hard to see for normal clips.",
    },
    {
      question: "What is the difference between rotate and mirror?",
      answer: "Rotate turns the picture around, like turning a photo. Mirror swaps left and right as in a mirror, which is what you want when text on a selfie clip reads backwards.",
    },
    {
      question: "Why is the output an MP4?",
      answer: "MP4 with H.264 plays on almost every device and browser. Whatever the input format, the rotated video is saved as MP4.",
    },
    {
      question: "Is the sound kept?",
      answer: "Yes. AAC and MP3 audio is copied unchanged and other audio is converted to AAC. A silent video stays silent.",
    },
    {
      question: "Is my video uploaded?",
      answer: "No. It is processed in your browser. Only the video engine is downloaded once.",
    },
  ],
  "video-speed-changer": [
    {
      question: "Does the sound speed up too?",
      answer: "Yes. The audio is sped up or slowed down with the picture and keeps its pitch, so voices do not become high or low.",
    },
    {
      question: "Why does my slowed-down video look choppy?",
      answer: "Slowing a video does not create extra frames; it shows each existing frame for longer. For smooth slow motion you need a clip filmed at a high frame rate.",
    },
    {
      question: "What speeds are available?",
      answer: "Anything from 0.25 times (four times slower) to 4 times (four times faster), using the presets or the slider.",
    },
    {
      question: "Does it change the frame rate?",
      answer: "The output keeps the original frame rate, up to 60, so a sped-up clip does not become a huge 120 fps file.",
    },
    {
      question: "Is my video uploaded?",
      answer: "No. It is processed in your browser. Only the video engine is downloaded once.",
    },
  ],
  "reverse-video": [
    {
      question: "Why is there a length limit?",
      answer: "To play a video backwards the tool must hold all its frames in memory at once. A short 720p clip fits; a long or high-resolution one does not, so the page tells you the limit instead of crashing.",
    },
    {
      question: "How long a clip can I reverse?",
      answer: "About 16 seconds at 720p and 30 frames per second, less at higher resolutions or frame rates. The 480p option allows a longer clip.",
    },
    {
      question: "Is the sound reversed too?",
      answer: "Yes. The audio plays backwards along with the picture. Mute the video first if you do not want that.",
    },
    {
      question: "Can I make a clip play forward and then backward?",
      answer: "Reverse the clip, then use Merge Videos to join the original and the reversed copy.",
    },
    {
      question: "Is my video uploaded?",
      answer: "No. It is processed in your browser. Only the video engine is downloaded once.",
    },
  ],
  "ringtone-maker": [
    {
      question: "How long can an iPhone ringtone be?",
      answer: "This tool makes ringtones up to 1 minute long for both iPhone and Android. Pick 15, 30, 45 or 60 seconds (Android also offers 10). Clips longer than a minute are shortened to a minute.",
    },
    {
      question: "What is an M4R file?",
      answer: "It is the file type iPhones use for ringtones: AAC audio in an MP4 container with the extension .m4r. The tool encodes the clip that way for you.",
    },
    {
      question: "How do I put the ringtone on my phone?",
      answer: "On Android, copy the MP3 to the Ringtones folder or choose it in Sound settings. On iPhone, add the M4R in Finder (Mac) or the Apple Devices app (Windows), or import it with GarageBand, then select it under Sounds and Haptics.",
    },
    {
      question: "Why do I want a fade in and fade out?",
      answer: "A short fade stops the ringtone starting or ending with a harsh click and makes it sound finished. Half a second in and one second out is a good start.",
    },
    {
      question: "Is my song uploaded?",
      answer: "No. The clip is cut in your browser. Only the processing engine is downloaded once.",
    },
  ],
  "volume-booster": [
    {
      question: "How many dB should I add?",
      answer: "Start with +6 dB for a clearly quiet recording and +10 dB for a very quiet one. The page shows where the loudest point will land, so you can see the largest gain that stays clean.",
    },
    {
      question: "What does the limiter do?",
      answer: "It holds the loudest peaks just under full scale so a boost does not turn into harsh clipping. The cost is that very loud moments are flattened if you push the gain too far.",
    },
    {
      question: "Will boosting make the background noise louder too?",
      answer: "Yes. Gain lifts everything, including hiss and room noise. If noise is the problem, treat it first with the Background Noise Remover, then raise the volume.",
    },
    {
      question: "What is the difference between this and the Audio Volume Normalizer?",
      answer: "The Volume Booster applies the number of dB you choose. The Audio Volume Normalizer measures the file and sets it to a target loudness for you.",
    },
    {
      question: "Does it keep my file format?",
      answer: "Yes. MP3 stays MP3, WAV stays WAV and so on. Lossy formats are encoded again, so keep the original.",
    },
  ],
  "quote-generator": [
    {
      question: "What is the difference between a quote and an invoice?",
      answer: "A quote offers a price before the work or sale. An invoice asks for payment after it. Use the same lines for both so the numbers match.",
    },
    {
      question: "Can I add tax and a discount?",
      answer: "Yes. Enter a discount percentage and a tax percentage with a label such as VAT or GST. Both are applied in the totals.",
    },
    {
      question: "Can I use my own currency?",
      answer: "Yes. Choose from the currency list; amounts are formatted for it, including symbols such as ₹ and €.",
    },
    {
      question: "Does a quote have to be signed?",
      answer: "Not legally in every case, but an Accepted by line makes agreement clear. Tick the option to add it and the client can sign the PDF.",
    },
    {
      question: "Is anything saved or uploaded?",
      answer: "No. The PDF is made in your browser and nothing you type leaves your device.",
    },
  ],
  "purchase-order-generator": [
    {
      question: "What is a purchase order?",
      answer: "A document from a buyer to a supplier listing what is being ordered, the agreed prices and the delivery terms. It becomes a record both sides can check against the invoice.",
    },
    {
      question: "Do I need a PO number?",
      answer: "It is strongly advised. The supplier quotes it on the invoice and delivery note, which makes matching and payment faster.",
    },
    {
      question: "Can I add tax and shipping?",
      answer: "Yes. Enter a tax percentage with a label and a shipping amount; both are added to the total.",
    },
    {
      question: "Can I order in another currency?",
      answer: "Yes. Choose the currency from the list and amounts are formatted for it.",
    },
    {
      question: "Is anything saved or uploaded?",
      answer: "No. The PDF is made in your browser and nothing you type leaves your device.",
    },
  ],
  "packing-slip-generator": [
    {
      question: "What is a packing slip?",
      answer: "A sheet in a parcel that lists what is inside. It lets the receiver check the delivery against the order.",
    },
    {
      question: "Does it show prices?",
      answer: "No. A packing slip lists quantities only, so the receiver, for example a gift recipient, does not see what was paid.",
    },
    {
      question: "What is the difference between a packing slip and an invoice?",
      answer: "A packing slip says what was sent. An invoice says what is owed. Use Invoice Generator for the second.",
    },
    {
      question: "Can I tick items off?",
      answer: "Yes. Every line has a Packed checkbox you can tick on the printout while packing.",
    },
    {
      question: "Is anything saved or uploaded?",
      answer: "No. The PDF is made in your browser and nothing you type leaves your device.",
    },
  ],
  "final-grade-calculator": [
    {
      question: "How is the score needed worked out?",
      answer: "The final's weight is applied to the final score and the rest to your current grade. The tool solves that for the final score that gives your target grade.",
    },
    {
      question: "What if the answer is more than 100%?",
      answer: "Then the grade cannot be reached with the final alone. Ask whether extra credit or a re-sit is available, or aim for the next grade down.",
    },
    {
      question: "What does Secured mean?",
      answer: "Your current grade is high enough that you reach that grade even with 0 on the final.",
    },
    {
      question: "Can I use points instead of percentages?",
      answer: "Convert points to a percentage first: divide by the total available and multiply by 100.",
    },
    {
      question: "Is anything saved or uploaded?",
      answer: "No. The calculation runs in your browser and nothing you enter leaves your device.",
    },
  ],
  "email-signature-generator": [
    {
      question: "How do I add the signature to Gmail?",
      answer: "Click Copy signature, then open Settings, See all settings, scroll to Signature, create one and paste into the box.",
    },
    {
      question: "How do I add it to Outlook?",
      answer: "Click Copy signature, then open Settings, Mail, Compose and reply, and paste into the Email signature box.",
    },
    {
      question: "Can I add my logo?",
      answer: "Yes, if the logo is already online. Paste its web address, starting with https, and it appears in the preview. Images pasted from your computer often do not show for recipients.",
    },
    {
      question: "Why does it look slightly different in some programs?",
      answer: "Email programs handle styling in their own way. This signature uses plain tables and inline styles, which are the most widely supported.",
    },
    {
      question: "Is anything saved or uploaded?",
      answer: "No. The signature is made in your browser and nothing you type leaves your device.",
    },
  ],
  "listing-photo-pack": [
    {
      question: "Does it make photos bigger?",
      answer: "No. A photo is only ever made smaller. If the original is below the size the marketplace recommends, it stays as it is and the tool tells you.",
    },
    {
      question: "Where do the sizes come from?",
      answer: "Etsy and eBay sizes were read from their own help pages in October 2026. Amazon's page needs a seller login, so that entry follows widely published guidance; check it in Seller Central. Use Custom if your marketplace has changed its rules.",
    },
    {
      question: "Will it give me a white background?",
      answer: "It places the photo on white and turns see-through areas white, but it does not cut the product out. Use Remove Background first if your photo has a busy background.",
    },
    {
      question: "Is location data removed from my photos?",
      answer: "Yes. Each picture is drawn fresh into a new JPEG, so hidden data such as GPS position and camera details is not kept.",
    },
    {
      question: "How many photos can I do at once?",
      answer: "Up to 5 on the trial, counted as one use when you download. A paid plan removes the limit on how many photos go in one batch.",
    },
  ],
  "label-sheet-maker": [
    {
      question: "Which label sheets does it support?",
      answer: "Avery 5160 (30 per sheet) and 5163 (10 per sheet) for US Letter, and L7160 (21 per sheet) and L7163 (14 per sheet) for A4, plus other brands that use the same layouts. If yours is not listed, choose My own grid and enter the measurements from the pack.",
    },
    {
      question: "Why do my labels print slightly off?",
      answer: "Almost always the print dialog is scaling the page. Choose 100% or Actual size, not Fit to page. If it is still a little off, your printer has its own margin; the test page shows how far.",
    },
    {
      question: "Can I finish a sheet I already used?",
      answer: "Yes. Set Start at label to the first free label. The first sheet begins there and any further sheets start from the top.",
    },
    {
      question: "How do I print many copies of one label?",
      answer: "Type the label once and press Fill the sheet, or set Copies of each to the number you need.",
    },
    {
      question: "Is my address list uploaded?",
      answer: "No. The labels are built in your browser, and the text is not sent anywhere or kept.",
    },
  ],
  "seller-pricing-desk": [
    {
      question: "Why are no fees filled in?",
      answer: "Marketplaces change their fees often and differ by seller type, country and category. Copy the current numbers from your marketplace's own fee page so the result is right for you.",
    },
    {
      question: "What is the difference between margin and markup?",
      answer: "Margin is profit divided by the selling price. Markup is profit divided by your cost. A product that costs 10 and sells for 20 has a 50% margin and a 100% markup. This tool uses margin.",
    },
    {
      question: "Why is the suggested price higher than I expected?",
      answer: "Fees are taken from the selling price, so to keep a given share of it as profit the price has to rise by more than the fees alone. The tool solves this exactly.",
    },
    {
      question: "Is my product list stored?",
      answer: "Not by us. It stays in this browser tab while it is open and is erased when you close it. Use Back up to keep a copy on your own device.",
    },
    {
      question: "Can I print a price for many products?",
      answer: "Yes. Add each product as a line and download the Excel or CSV file; one download counts as one use.",
    },
  ],
  "site-launch-kit": [
    {
      question: "Which files does the kit contain?",
      answer: "robots.txt, sitemap.xml, head-tags.html, structured-data.html, site.webmanifest, a README, and .well-known/security.txt when you enter an email.",
    },
    {
      question: "Will this make my site rank higher?",
      answer: "No. These files help search engines find and understand your pages, but they do not guarantee ranking or indexing.",
    },
    {
      question: "Where do I upload the files?",
      answer: "The README lists each one. robots.txt and sitemap.xml go in the site root. The head tags go inside the head of your pages.",
    },
    {
      question: "What if my website builder already makes these?",
      answer: "Then check what it produces before replacing anything. Many builders create robots.txt and sitemap.xml for you.",
    },
    {
      question: "Is my information stored?",
      answer: "No. The kit is made in your browser. Your entries stay in the tab and are erased when you close it.",
    },
  ],
  "brand-kit": [
    {
      question: "What does AA or AAA mean?",
      answer: "They are levels from the web accessibility guidelines. AA needs a contrast ratio of 4.5:1 for normal text and 3:1 for large text. AAA needs 7:1 and 4.5:1.",
    },
    {
      question: "Can I use the suggested fonts commercially?",
      answer: "The suggested families are open-licence fonts from Google Fonts. Check the licence on the font's page if you need to be sure for your use.",
    },
    {
      question: "What is in the ZIP?",
      answer: "A brand sheet PDF, a CSS file of colour and font variables, a JSON file of design tokens, and an SVG showing every colour.",
    },
    {
      question: "Can I use any colour?",
      answer: "Yes, any six-digit code such as #2563EB. Short codes like #26E also work.",
    },
    {
      question: "Is my brand stored anywhere?",
      answer: "No. It is built in your browser and erased when you close the tab. Use Back up to keep it on your device.",
    },
  ],
  "paper-submission-desk": [
    {
      question: "Does it know my journal's rules?",
      answer: "No. You enter the limits from the journal's author instructions, so the checks stay right when those rules change.",
    },
    {
      question: "Does it check that my DOIs and ORCID iDs are real?",
      answer: "No. It checks the format, and for ORCID also the check digit, which catches most typing mistakes. It does not look anything up.",
    },
    {
      question: "What do I get when I download?",
      answer: "A ZIP with a PDF (cover letter, title page and checklist), the cover letter as text and the title page as text.",
    },
    {
      question: "Will the cover letter claim things that are not true?",
      answer: "No. It states only the declarations you tick, such as originality or that all authors approved.",
    },
    {
      question: "Is my manuscript uploaded?",
      answer: "No. You enter the title, abstract and details, not the manuscript file, and everything stays in your browser tab until you close it.",
    },
    {
      question: "What do journals expect about authorship and AI use?",
      answer: "The ICMJE recommends that authorship rest on four criteria, including substantial contribution, drafting or critical review, final approval and accountability. It also advises disclosing AI-assisted technology use and not listing AI tools as authors. Journals differ, so check the official source and your target journal's instructions.",
      source: { label: "ICMJE Recommendations", url: "https://www.icmje.org/icmje-recommendations.pdf" },
    },
  ],
  "book-interior-maker": [
    {
      question: "Which file types can I add as chapters?",
      answer: "Word (.docx), Markdown (.md) and plain text (.txt), or text you paste. Each file becomes one chapter, except a Markdown file with several lines starting with \"# \", which is split into one chapter per heading. Headings, bold, italic, lists and block quotes are kept. Images and tables in a .docx are not.",
    },
    {
      question: "How does the gutter margin work?",
      answer: "For Amazon KDP, and when no platform is specific, the inside margin is raised to the minimum for the final page count: 0.375 in up to 150 pages, 0.5 in up to 300, 0.625 in up to 500, 0.75 in up to 700 and 0.875 in beyond. The tool builds the book, checks the page count, widens the margin if needed and builds again. IngramSpark and Lulu use a flat 0.5 in, and Barnes & Noble Press uses 0.75 in inside.",
    },
    {
      question: "Will KDP or IngramSpark accept the PDF?",
      answer: "The tool cannot promise that. It checks trim size, page count and margins against rules it holds, and it embeds Literata in the file. Platforms also check bleed, fonts, color and other details, so upload the file to the platform's own previewer before publishing.",
    },
    {
      question: "Does every chapter really start on a right-hand page?",
      answer: "Yes, when that option is on. The first page is a right-hand page, and the tool inserts a blank left-hand page whenever the next chapter would otherwise start on the left. Blank pages carry no running head or page number. Turn the option off to save pages.",
    },
    {
      question: "Why are some characters shown as question marks?",
      answer: "Times and Helvetica are standard PDF fonts that only draw Western European characters, so other characters are replaced with a question mark and the tool tells you how many. Literata, the default, covers Latin, Greek and Cyrillic, and the browser draws other scripts as small pictures in the file.",
    },
  ],
  "book-page-estimator": [
    {
      question: "How is the page count worked out?",
      answer: "The text width and height come from the trim size minus the margins. Characters per line come from the font size and an average character width, and lines per page from the font size times the line spacing. Lines per word then gives the body pages, and chapter openings, front matter and back matter are added. Each step is shown under The maths.",
    },
    {
      question: "Why is there a range instead of one number?",
      answer: "Character width and characters per word are averages, and chapter openings land differently on each page. The low and high cases move character width and characters per word by 5 percent and each chapter opening by a quarter of a page. The real count can still fall outside the range if the layout has many headings or images.",
    },
    {
      question: "Why is the printed total always an even number?",
      answer: "Pages print on both sides of a sheet, so a book has an even number of pages. The estimate rounds up to the next even number. Your printer may round differently, for example to a multiple of 4 or 8 for some binding methods.",
    },
    {
      question: "Can I work out how many words I need for a target page count?",
      answer: "Yes. Choose Pages to words and enter the target. The tool subtracts front matter, back matter and chapter openings, then converts the body pages into words, with a range. If the target is smaller than the fixed pages alone, it says so.",
    },
    {
      question: "Is my manuscript uploaded when I paste it?",
      answer: "No. Words and paragraphs are counted in your browser, and the text is not sent anywhere or saved.",
    },
    {
      question: "How many pages is 80,000 words?",
      answer: "With the starting settings (6 x 9 in, 11 pt Times New Roman on 14.3 pt leading, 0.75 in top and bottom margins, 25 chapters starting on right-hand pages, 10 pages of front and back matter) about 234 printed pages, likely 210 to 258, or roughly 410 words on a full page. Rules of thumb of 250 to 300 words a page assume larger type, wider margins or looser spacing, so change the settings to match your own layout.",
    },
    {
      question: "Does the font matter for the page count?",
      answer: "Yes. A wider typeface or a larger size puts fewer words on each line. For 80,000 words at the starting settings the estimate is about 226 pages in Garamond, 234 in Times New Roman and 258 in Georgia. Only Times New Roman and Arial widths were measured; the others are estimates, so check them against your own file.",
    },
    {
      question: "What does line spacing mean here, and how does it compare with Word?",
      answer: "It is the line height divided by the font size, so 1.3 at 11 pt is 14.3 pt of leading. Word's single spacing is about 1.15 times the font size for Times New Roman, so a Word Multiple of 1.2 is about 1.4 here. Enter the value that matches how your book is set.",
    },
  ],
  "book-spine-calculator": [
    {
      question: "Can I download a cover template to design on?",
      answer: "Yes. Download the cover template as a PDF at true size, or as a PNG at the pixel size shown. It has the bleed, the trim line, the two fold lines of the spine, a dashed safe-area guide and, if you leave the box ticked, a barcode area. Place it under your artwork as a guide and hide it before you export the final cover. Platforms generate their own templates too, so compare the sizes with theirs.",
    },
    {
      question: "How is spine width calculated?",
      answer: "Spine width is the page count multiplied by the thickness of one page of the chosen paper, plus any allowance you enter. Paper thickness is given per page, and one sheet is two pages.",
    },
    {
      question: "How big is a full wrap cover?",
      answer: "The width is the back cover trim width, the spine and the front cover trim width, plus the bleed on the left and right edges. The height is the trim height plus the bleed at the top and bottom. The tool shows the result in inches, millimeters and pixels.",
    },
    {
      question: "Are the paper thickness and bleed values exact?",
      answer: "They are starting values, not guaranteed ones, and you can edit them. Platforms change paper specifications and differ by paper and ink type. Confirm the numbers against the cover template the platform generates for your book.",
    },
    {
      question: "Why does the tool warn about an odd page count?",
      answer: "Printed books are built from sheets with a page on each side, so the count is normally even and printers round up. Using the final even count gives the correct spine width.",
    },
    {
      question: "Can I use this for hardcover books?",
      answer: "No. The calculation is for paperback wrap covers. Hardcover covers add board, wrap and hinge allowances that differ by printer, so use the printer's own hardcover template.",
    },
  ],
  "copyright-page-maker": [
    {
      question: "Is the text legal advice?",
      answer: "No. The rights statements and disclaimers are common templates. Copyright law and licences differ by country, so ask a qualified attorney if the wording matters for your situation.",
    },
    {
      question: "Does the tool check my ISBN?",
      answer: "It checks the check digit of ISBN-13 and ISBN-10 numbers and warns about typos. A passing check does not prove the number was issued to your book; confirm that with your ISBN agency or platform.",
    },
    {
      question: "Can I get a Library of Congress number here?",
      answer: "No. The Library of Congress assigns its control number and CIP data through its own programs. If you have them, enter them and they are printed exactly as typed.",
    },
    {
      question: "Which trim sizes are available?",
      answer: "The same list as Book Writing Toolbox, including common paperback trims from 4.25 x 7 in up to 8.5 x 8.5 in, plus US Letter and A4. Choose the size you set up with your printer.",
    },
    {
      question: "What is the number line?",
      answer: "It is the traditional printing line that counts down from 10 to the current printing number, so a first printing reads 10 9 8 7 6 5 4 3 2 1. You can turn it off or pick another printing number.",
    },
  ],
  "epub-checker": [
    {
      question: "Is this the same as EPUBCheck?",
      answer: "No. EPUBCheck is the official validator from the W3C and checks the full specification, including schemas for XHTML, CSS and SVG. This tool is a practical pre-check of structure and common quality problems. A file can pass here and still fail EPUBCheck or a store's review, so use EPUBCheck before you publish.",
    },
    {
      question: "Why does the mimetype file matter?",
      answer: "The EPUB specification requires a file named mimetype containing exactly application/epub+zip, stored first in the archive without compression, so readers can recognize the format. Some readers and stores reject a file where it is compressed or not first. The tool reports which of these is wrong.",
    },
    {
      question: "What does a warning mean compared with a fail?",
      answer: "A fail is something the specification requires or that stops content from showing, such as a missing file or a malformed page. A warning is a quality or accessibility issue that does not stop the book opening, such as a missing author, images without alt text or accessibility metadata that is not declared.",
    },
    {
      question: "Why is an empty alt attribute a warning?",
      answer: "An alt attribute left empty tells screen readers to skip the image, which is right for ornaments but wrong for pictures that carry meaning. The tool cannot know which you meant, so it asks you to review them. Images with no alt attribute at all are counted separately.",
    },
    {
      question: "Is my book uploaded anywhere?",
      answer: "No. The file is read in your browser tab and the report is built there. Nothing is sent to a server.",
    },
  ],
  "epub-maker": [
    {
      question: "Which file types can I add as chapters?",
      answer: "Word .docx files, Markdown .md files and plain .txt files. Each file becomes one chapter, and you can also paste text. Headings, bold, italic and lists are kept; images inside a .docx are not imported.",
    },
    {
      question: "How is the chapter title chosen?",
      answer: "If the file starts with a single top-level heading, its text becomes the chapter title and is not repeated in the body. Otherwise the title comes from the file name. You can rename any chapter before building.",
    },
    {
      question: "What does the accessibility note mean?",
      answer: "The EPUB builder only declares EPUB Accessibility 1.1 (WCAG 2.1 AA) conformance in the file when every chapter image has alt text. A text-only book passes. If an image has no alt text the note shows fail and the file is built without that declaration. It is not a full accessibility audit.",
    },
    {
      question: "Will Amazon, Apple or other stores accept the file?",
      answer: "The tool produces a standard EPUB 3 package, but it cannot promise that any store accepts it, because each has its own checks. Run the file through an EPUB validator and the store's own preview before publishing.",
    },
    {
      question: "Is my manuscript uploaded anywhere?",
      answer: "No. Files are read and the EPUB is assembled in your browser. Nothing is sent to a server or kept, and the project disappears when you close the page, so download the EPUB before you leave.",
    },
  ],
  "isbn-checker": [
    {
      question: "How is the ISBN check digit calculated?",
      answer: "For an ISBN-13, the first twelve digits are multiplied alternately by 1 and 3, added up, and the check digit is whatever brings the total to a multiple of 10. For an ISBN-10, the first nine digits are multiplied by 10 down to 2, added up, and the check digit makes the total a multiple of 11, with X standing for 10.",
    },
    {
      question: "How do I convert an ISBN-10 to an ISBN-13?",
      answer: "Put 978 in front of the first nine digits of the ISBN-10, drop its old check digit and calculate a new ISBN-13 check digit. The tool does this for you. The reverse works only for numbers that start with 978, because 979 numbers have no ISBN-10.",
    },
    {
      question: "What does the tool do when the check digit is wrong?",
      answer: "It shows the digit the check digit should be, assuming the other digits are right, and shows the converted forms using that digit. It cannot tell which digit you mistyped, so confirm the number against the book or the publisher.",
    },
    {
      question: "Why are some ISBNs not hyphenated?",
      answer: "Where the hyphens go depends on range tables from the International ISBN Agency. The tool includes the English groups and several large groups. For any other group it shows the clean digits and states that hyphenation was not done, instead of guessing.",
    },
    {
      question: "Is the barcode the same as the ISBN?",
      answer: "The barcode on a book is an EAN-13 that encodes the ISBN-13 digits, so the digits under the bars are the 13 digits of the ISBN. The preview in the tool lets you check them. Printers and distributors usually generate the barcode graphic for the cover.",
    },
  ],
  "manuscript-formatter": [
    {
      question: "What is Shunn manuscript format?",
      answer: "It is the layout described by William Shunn that many fiction markets and agents expect: Times New Roman 12 point, double spaced, 1 inch margins, paragraphs marked by a first-line indent, a title page with contact details and an approximate word count, and a running head on later pages. It is a convention, and each market may ask for small changes.",
    },
    {
      question: "Which part of my file gets changed?",
      answer: "The text is rebuilt in the manuscript layout: fonts, spacing and margins are set by the format. Bold and italics are kept; colors, alignment, images and tables are dropped, because a standard manuscript has none of them.",
    },
    {
      question: "How is the word count worked out and rounded?",
      answer: "The tool counts the words in your chapter headings, paragraphs and list items, then rounds to the step you pick: exact, 10, 100, 500 or 1,000. The title page shows the rounded figure as about N words. Your word processor may count slightly differently, for example with hyphenated words.",
    },
    {
      question: "How accurate is the page count?",
      answer: "It is an estimate. It divides your word count by a words-per-page figure (250 by default) and adds room for the title page and chapter openings. Real page counts depend on paragraph lengths and on the program that opens the file.",
    },
    {
      question: "Why does my file show no chapter headings?",
      answer: "Headings are found from Markdown # lines, a line like Chapter 2 on its own, or heading styles in a .docx. Without any of them the text is treated as one chapter with no heading, which suits a short story. Add a heading line where you want a chapter to start.",
    },
  ],
  "onix-generator": [
    {
      question: "How is the ISBN-13 checked?",
      answer: "The tool removes hyphens and spaces, requires 13 digits starting with 978 or 979, and recomputes the check digit by weighting the first 12 digits alternately by 1 and 3. If the last digit does not match, it tells you what it should be. An ISBN-10 is rejected with an offer to convert it.",
    },
    {
      question: "Which date formats can I use?",
      answer: "Use YYYY-MM-DD, for example 2026-03-15. YYYY-MM and YYYY are also accepted for a month or a year only. The date must exist on the calendar, so 2025-02-29 is rejected. The XML carries it without hyphens, with the matching ONIX date format code.",
    },
    {
      question: "Will a retailer accept this file?",
      answer: "The tool cannot promise that. ONIX is a shared standard, but each retailer and distributor sets its own required fields, code lists and ONIX version. The file is not checked against the full schema or any one platform, so read the target's metadata requirements and compare.",
    },
    {
      question: "What are BISAC and Thema codes?",
      answer: "They are two subject classification schemes. BISAC codes are three letters and six digits, such as FIC027020, and are common in North America. Thema codes are short, such as FBA or 1KBB, and are used more widely abroad. The tool checks the pattern only; look up the right code in the official lists.",
    },
    {
      question: "Can I add more than one price or contributor?",
      answer: "Yes. Add a price for each currency, each with its own price type, and as many contributors as you need as author, editor, illustrator or translator. The tool refuses two prices with the same currency and type, and it marks the first code of each subject scheme as the main subject.",
    },
  ],
  "pdf-bookmarks-editor": [
    {
      question: "Does it change my original file?",
      answer: "No. It writes a new copy with the edited outline, and your original stays as it was.",
    },
    {
      question: "Can it add bookmarks automatically from headings?",
      answer: "No. You add each bookmark yourself by typing a title and a page number. The page preview helps you check the page.",
    },
    {
      question: "Why does a bookmark show no page?",
      answer: "Some bookmarks point at a named destination instead of a page, which this tool cannot resolve. Type a page number to give it a target, or leave it as a heading that does not jump anywhere.",
    },
    {
      question: "Will my original zoom settings in bookmarks be kept?",
      answer: "No. Every bookmark is saved to open its page fitted to the window. Titles, order, nesting and target pages are kept.",
    },
    {
      question: "Can I remove all bookmarks?",
      answer: "Yes. Click Remove all and download. The new PDF has no outline.",
    },
  ],
  "bates-numbering": [
    {
      question: "What is Bates numbering?",
      answer: "It is a running control number stamped on every page of a set of documents, usually a prefix plus a padded number such as ABC000001. Lawyers use it for productions and exhibits so that any page can be cited by its number. The same idea is used in audits, data rooms and records requests.",
    },
    {
      question: "Can the numbers continue from one file to the next?",
      answer: "Yes. With Keep counting across files on, the second file starts where the first ended, so no two pages share a number. Turn it off if each file should start again at the first number. The index preview shows the first and last number of every file before you run it.",
    },
    {
      question: "What do I get when I number several files at once?",
      answer: "A ZIP file with each numbered PDF, named after the original with -bates added, and a bates-index.csv listing every document with its page count and its first and last number. For a single file you get the numbered PDF and a separate index to download.",
    },
    {
      question: "Does it add a mark when I use it on a trial?",
      answer: "No. The numbered PDF is left exactly as stamped, because a production has to be clean. The tool still counts as one use of the daily trial allowance, however many files you number in the run.",
    },
    {
      question: "Are my documents uploaded?",
      answer: "No. The files are read and stamped in your browser tab, and the numbered copies are made on your device. Nothing is sent to a server, and the files are gone when you close the page.",
    },
    {
      question: "Will it work on scanned or rotated pages?",
      answer: "Yes. The stamp is placed on the page the way it looks when you open it, so a page stored sideways still gets its number in the chosen corner. Look at the page strip before you download to check that the stamp does not cover anything that matters, and use the white box option if the page is dark.",
    },
    {
      question: "Does Bates numbering make a document court-ready?",
      answer: "It only adds the numbers and the index. Courts and parties have their own rules for the format of production numbers, confidentiality legends and file types, so check the rule that applies to your matter. The tool makes no claim about compliance.",
    },
  ],
  "etsy-listing-checker": [
    {
      question: "What are the limits for an Etsy title and tags?",
      answer: "A listing title can be up to 140 characters, and a listing can have up to 13 tags of up to 20 characters each. The checker shows how many characters and tags you are using and marks anything over the limit in red.",
    },
    {
      question: "Why does it say my tag has a symbol Etsy usually rejects?",
      answer: "Etsy tags normally accept letters, numbers, spaces, hyphens and apostrophes, and the marks for trademark, copyright and registered. Tags with symbols such as & or % are often refused when you save the listing. Etsy can change this, so confirm in the listing editor.",
    },
    {
      question: "Does it tell me which tags or title words will rank?",
      answer: "No. It has no search data. It checks the limits, repeats and gaps, and leaves the choice of words to you.",
    },
    {
      question: "How do I separate the tags?",
      answer: "Use commas, semicolons or one tag per line. Extra spaces are removed, and a repeated tag is found even when the capital letters differ.",
    },
    {
      question: "Why does it list title words that none of my tags use?",
      answer: "The title and the tags are two chances to match what a buyer types. If a word in the title is something buyers search for and no tag carries it, a tag could add another way to be found. It is a prompt to think about, not a rule.",
    },
    {
      question: "Is my listing text uploaded?",
      answer: "No. The checks run in your browser tab. Nothing is sent to a server, and the text is cleared when you close the page.",
    },
  ],
  "print-royalty-calculator": [
    {
      question: "How is the royalty worked out?",
      answer: "There are two ways to choose from. With a royalty rate, the royalty is the rate times the list price, minus the printing cost. With a wholesale discount, it is the list price less the discount, minus the printing cost. Pick the one your platform uses and type its figures.",
    },
    {
      question: "Where do I get the printing cost?",
      answer: "From your platform's own calculator or pricing page, for your trim size, page count and ink. The tool asks for a fixed charge per copy and a charge per page. The built-in Amazon KDP black and white example is only a starting point, so confirm it before you rely on it.",
    },
    {
      question: "Why can the royalty be negative?",
      answer: "If the list price is too low, the share you keep does not cover the printing cost. The tool shows that in red and gives you the break-even price, which is the lowest list price that earns nothing.",
    },
    {
      question: "Does it include tax, shipping or currency conversion?",
      answer: "No. It does the arithmetic on the figures you type. Taxes, shipping, currency conversion, expanded distribution and minimum or maximum prices are not included.",
    },
    {
      question: "What does the upfront cost box do?",
      answer: "Enter what you spent on editing, the cover and the ISBN, and the tool tells you how many copies you must sell at this price to earn that back, rounding up. If the royalty is zero or negative it says you never will at this price.",
    },
    {
      question: "Is anything I type uploaded?",
      answer: "No. The sums are done in your browser tab, nothing is sent to a server, and the figures are cleared when you close the page.",
    },
  ],
  "kdp-description-formatter": [
    {
      question: "Which HTML tags does a KDP description accept?",
      answer: "Amazon KDP commonly accepts paragraphs, line breaks, bold, italic, underline, the heading tags h4 to h6, and bulleted and numbered lists. This tool keeps exactly that set. Stores change their rules, so look at the store's preview before you publish.",
    },
    {
      question: "How do I make bold text, a list or a heading?",
      answer: "In Plain text to HTML, put ** around words for bold, * around words for italic and __ around words for underline. Start a line with a dash for a bullet, with 1. for a numbered list, or with # for a heading. A blank line starts a new paragraph.",
    },
    {
      question: "What does Clean existing HTML do?",
      answer: "It keeps the allowed tags, turns divs into paragraphs and large headings into the heading size you choose, and removes everything else, such as styles, classes, links, images and scripts. The text inside removed tags is kept, and the tool lists what it changed.",
    },
    {
      question: "How long can a description be?",
      answer: "Amazon KDP allows up to 4,000 characters. The tool counts the HTML you paste, tags included, and tells you how far over you are, so you are safe even when the store counts the tags.",
    },
    {
      question: "Why did my link disappear?",
      answer: "Book store descriptions do not accept links, so the link tag is removed and its text kept. Put your website in your author profile or in the book's text instead.",
    },
    {
      question: "Is my description uploaded?",
      answer: "No. The conversion happens in your browser tab, nothing is sent to a server, and the text is cleared when you close the page.",
    },
  ],
  "acx-audiobook-checker": [
    {
      question: "What does this check?",
      answer: "Loudness (RMS), peak level, noise floor, sample rate, MP3 bit rate and constant bit rate, and length, each compared with the ACX audio requirements. A table shows your value, the limit and the result.",
    },
    {
      question: "Does a pass mean ACX will accept my file?",
      answer: "No. It means the measured numbers are inside the limits. ACX also checks the sound itself, such as the opening and ending, room tone, noise and clipping, so listen to the file as well.",
    },
    {
      question: "Why does the noise floor still fail after the fix?",
      answer: "Changing the volume raises or lowers the noise together with the voice. A noisy room has to be fixed at the recording, or with a noise reduction pass, and then the file can be checked again.",
    },
    {
      question: "Is my recording uploaded?",
      answer: "No. The file is decoded and measured in your browser, and the corrected MP3 is built in your browser too.",
    },
  ],
  "resume-keyword-matcher": [
    {
      question: "What does the coverage percentage mean?",
      answer: "It is the share of the posting's repeated keywords that also appear in your resume. A term counts up to 5 times, so one word repeated many times does not dominate. It is a guide to what you may have left out, not a score any employer uses.",
    },
    {
      question: "Will this tell me if my resume passes an applicant tracking system?",
      answer: "No. Systems differ, and many people read the resume too. This only shows which of the posting's words your resume uses and which it does not.",
    },
    {
      question: "Should I add every missing keyword?",
      answer: "No. Add a term only if it is true for you, and use it where it fits, such as a bullet that describes the work. Adding skills you do not have can cost you later in the process.",
    },
    {
      question: "Are my resume and the posting uploaded?",
      answer: "No. The comparison runs in your browser, and the text is cleared when you close the page.",
    },
  ],
  "heic-to-jpg": [
    {
      question: "Is my photo uploaded?",
      answer: "No. The conversion runs in your browser, so the photo stays on your device.",
    },
    {
      question: "Why are my iPhone photos HEIC?",
      answer: "iPhones save photos as HEIC by default because the files are smaller. Many sites and older programs only take JPG. In the iPhone Camera settings, Most Compatible saves JPG for new photos.",
    },
    {
      question: "Can I convert several photos at once?",
      answer: "Yes. Choose Multiple files at the top of the converter and drop them together.",
    },
    {
      question: "Does converting reduce the quality?",
      answer: "JPG is a lossy format, so there is a small change, usually not visible. Keep the original HEIC file if you want it exactly as shot.",
    },
  ],
  "open-house-sign-in-sheet": [
    {
      question: "Can I choose which columns appear?",
      answer: "Yes. Tick the columns you need, from name, phone and email to address or ZIP, working with an agent, how they heard about you, time in and notes.",
    },
    {
      question: "What does the QR code do?",
      answer: "It encodes the web address or text you type, such as a listing page or a contact form, so visitors can scan it with a phone. Test it before you print many copies.",
    },
    {
      question: "Does it store what visitors write?",
      answer: "No. It only makes a blank sheet to print. Nothing you type is sent to a server.",
    },
    {
      question: "How do I print it at the right size?",
      answer: "Print the PDF at 100% or Actual size, on US Letter paper.",
    },
  ],
  "mail-merge-pdf": [
    {
      question: "How do I add a name or detail to the template?",
      answer: "Type the column name from your table inside double curly braces, for example {{First Name}}. Matching ignores capital letters and extra spaces.",
    },
    {
      question: "What happens if a field name is wrong?",
      answer: "It stays visible as {{Name}} in the preview and the tool lists it as not in your table, so you can fix the spelling before you download.",
    },
    {
      question: "Can I get a separate PDF for each person?",
      answer: "Yes. Choose the ZIP download. Files are named from the column you pick, or by row number if you leave it empty.",
    },
    {
      question: "Can I use Excel?",
      answer: "Yes. Drop an .xlsx file (the first sheet is read), or copy the cells in Excel or Google Sheets and paste them into the table box. The first row has to be the column names.",
    },
    {
      question: "Can I see the first record before I download?",
      answer: "Yes. As soon as the table is in, a first template is made from your columns and the preview shows record 1. Step through the records with Previous and Next. Only the download uses a trial use.",
    },
    {
      question: "How many letters or certificates can I make at once?",
      answer: "Up to 500 rows in one go. For a longer list, split the table and run it in parts. A record that needs more than one page continues on the next, and the tool tells you which rows did.",
    },
    {
      question: "Is my list uploaded?",
      answer: "No. The table and the template stay in your browser.",
    },
  ],
  "digital-download-packager": [
    {
      question: "What goes into the ZIP?",
      answer: "Your files, a LICENSE.txt with the wording you choose and a README.txt that lists the files and your how-to-use note. You can turn the license, the README and the folder off.",
    },
    {
      question: "Is the license legally valid?",
      answer: "It is a plain template, not legal advice. Change it to match what you actually allow, and have it reviewed if the terms matter.",
    },
    {
      question: "Why does it warn about file count and size?",
      answer: "Some marketplaces limit how many digital files a listing can hold and how large each can be. The warnings use commonly published figures, so check the current limits on your marketplace.",
    },
    {
      question: "Are my files uploaded?",
      answer: "No. The ZIP is built in your browser.",
    },
  ],
  "exhibit-stamper": [
    {
      question: "Can I stamp every page or only the first?",
      answer: "Either. Choose First page only for a label on the front of each exhibit, or Every page, with an option to add page 2 of 5 after the label.",
    },
    {
      question: "What is the index?",
      answer: "A CSV listing each exhibit label, the original file name, the stamped file name and the page count, with an empty Description column you can fill in.",
    },
    {
      question: "How is this different from Bates Numbering?",
      answer: "Bates Numbering gives every page its own running number. The Exhibit Stamper labels each document as an exhibit. Many productions use both.",
    },
    {
      question: "Are my documents uploaded?",
      answer: "No. The stamping happens in your browser.",
    },
  ],
  "low-content-interior-maker": [
    {
      question: "Which page types can I make?",
      answer: "Lined, dot grid, graph squares, checklist (a box and a line per row) and blank pages, at any of the standard book trim sizes.",
    },
    {
      question: "How is the margin next to the binding set?",
      answer: "The inside margin is at least your margin setting and rises with the page count to the usual print-on-demand minimums. It sits on the left of right-hand pages and the right of left-hand pages.",
    },
    {
      question: "How many pages do I need?",
      answer: "Print-on-demand paperbacks usually need at least 24 pages, and an even number avoids a blank last side. The tool warns if your count is short or odd. Check your platform for its exact rules.",
    },
    {
      question: "Is anything uploaded?",
      answer: "No. The interior is built in your browser.",
    },
  ],
  "bank-statement-to-csv": [
    {
      question: "Why does it say the check failed?",
      answer: "The opening balance plus the rows did not equal the closing balance, or a running balance did not follow from the row above. A missing row, a doubled row or a wrong sign is the usual cause. Fix the highlighted cells and the check runs again.",
    },
    {
      question: "Can it read a scanned statement?",
      answer: "No. A scan is a picture with no text to read. Run it through OCR first, then paste the text here.",
    },
    {
      question: "Are my statements uploaded?",
      answer: "No. The PDF is read in your browser and the rows are gone when you close the page.",
    },
  ],
  "audio-to-subtitles": [
    {
      question: "Is my recording uploaded?",
      answer: "No. A small speech model runs inside your browser, so the sound stays on your device. The only download is the model itself, about 100 MB, which your browser keeps after the first time.",
    },
    {
      question: "Which languages does it understand?",
      answer: "English only. Speech in another language will come out as wrong or empty text.",
    },
    {
      question: "How accurate is it, and how long does it take?",
      answer: "It is a small model, so it handles clear speech well and makes mistakes on names, product words, numbers and noisy recordings. Read the text while the file plays and fix what is wrong. Expect it to take about half the length of the recording.",
    },
    {
      question: "How long can the file be?",
      answer: "Up to 60 minutes. Split longer recordings and do the parts one by one. Video files work if your browser can play their sound, which is usually MP4 and WebM.",
    },
  ],
  "blind-cv-redactor": [
    {
      question: "What does it remove?",
      answer: "The candidate's name, email addresses, phone numbers, links and profiles, street addresses, birth dates and age, lines such as marital status or nationality, and gender words. Years can be removed too if you tick that box.",
    },
    {
      question: "Is the resume uploaded?",
      answer: "No. The text is processed in your browser and is gone when you close the page.",
    },
    {
      question: "Will it catch everything?",
      answer: "No. It finds common formats. Add nicknames, schools, hometowns or club names as extra terms and read the result before you share it.",
    },
  ],
  "legal-production-desk": [
    {
      question: "Does it add the Bates numbers to my files?",
      answer: "Yes. Attach each PDF to its row, check the numbers, and Build production ZIP stamps every produced and redacted document with the running numbers and puts them in a folder with the logs and cover letter.",
    },
    {
      question: "Why do withheld documents get no numbers?",
      answer: "They are not handed over, so they are listed on the withheld list and the privilege log with the reason instead. Set a document back to produce and the numbers below it shift.",
    },
    {
      question: "Does it redact documents?",
      answer: "No. Redact them first with Redact PDF, then attach the redacted copy to its row. The desk links to the tools for scanning, redacting, merging, labelling exhibits and bookmarking.",
    },
    {
      question: "Are my files uploaded?",
      answer: "No. Everything is read and stamped in your browser, and the lists are cleared when you close the tab.",
    },
    {
      question: "How does the subscription work?",
      answer: "It is $29.99 a month with a 7-day trial the first time. You can cancel any time from the link under the tool and keep access to the end of the paid month.",
    },
  ],
  "seller-studio": [
    {
      question: "How is the profit worked out?",
      answer: "Price, minus your cost, minus the shipping you pay, minus the percentage and fixed fee you entered. It uses only those figures, so check them against your marketplace's fee page.",
    },
    {
      question: "What does the price finder do?",
      answer: "It works out the lowest price that leaves the margin you typed after your cost, shipping and fees, and lets you copy it into the product.",
    },
    {
      question: "Are my photos uploaded?",
      answer: "No. Photos are checked and resized in your browser and only leave as the ZIP you download.",
    },
    {
      question: "Does it connect to my shop?",
      answer: "No. You type the details in, and the board checks them and keeps track of the steps. Nothing is sent anywhere.",
    },
    {
      question: "How does the subscription work?",
      answer: "It is $29.99 a month with a 7-day trial the first time. You can cancel any time from the link under the tool and keep access to the end of the paid month.",
    },
  ],
};

export function getFaqForTool(toolId: string): FaqEntry[] | undefined {
  return FAQ_CONTENT[toolId];
}
